import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import DataGrid, { InsightDataTable } from './InsightDataTable';
import { FormControl, InputLabel, Select, MenuItem, Button, createTheme, ThemeProvider, Box, Typography, Card, CardContent, Chip, Divider, CircularProgress, Alert, Tabs, Tab, Slider, Grid, Paper, TextField, InputAdornment, Autocomplete, Checkbox, ListItemText, ListSubheader, OutlinedInput, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Input, FormControlLabel, Tooltip, Popover, ListItemButton, List, ListItem } from '@mui/material';
import { useParams, Link as RouterLink } from "react-router-dom";
import axios from 'axios';
import { API_URL, COLOR_PALETTE, STICK_CATEGORY_FILTER_OPTIONS } from './constants';
import { buildProductDetailPath, toSqlDateKey, resolveProductImageUrlFromFields, mergeJtiProductRowOverrides, indexOverridesByTitle, PRODUCT_FLAG_LABEL_FOR_NEW_USERS_ONLY, PRODUCT_FLAG_FOR_NEW_USERS_ONLY, pickLatestRowOnOrBeforeDate, rowsOnLatestDateOnOrBefore, filterRowsForProductPopup, productRowDateKey, productTitleCountryKey, productDetailCountrySlug } from './productUtils';
import ProductDetailPopup from './ProductDetailPopup';
import { ProductImageHoverOverlay } from './ProductImageHoverOverlay';
import { saveAs } from 'file-saver';
import { useAuth } from './AuthContext';
import { useUserModules } from './UserModulesContext';
import {
  buildMatchingGroupsMapFromTables,
  groupProductsByMatching,
  getFlatVariantsFromGroup,
  extractVariantsFromRaw,
  forEachMatchingInfo,
  matchingInfosForSku,
  matchesStickCategoryFilter,
  expandProductSelections,
  parseModelGroupSelection,
  parseModelSelection,
  modelSelectionValue,
  productSelectionLabel,
  upgradeLegacyModelSelections,
  itemMatchesSelectedVariant,
} from './matchingUtils';
import ModelProductTreeSelect from './ModelProductTreeSelect';
import useSkuTitleTranslations from './hooks/useSkuTitleTranslations';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
 
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from 'chart.js';

import { Globe, TrendUp, ChatCircle, CurrencyDollar, MapPin } from '@phosphor-icons/react';
import { FilterList, Search as SearchIcon, Close as CloseIcon, Add, Remove, ShowChart, TableRows, RssFeed, FileDownload, Tune, Settings as SettingsIcon, InfoOutlined, AutoFixHigh, ExpandLess, ExpandMore, Warning, SettingsOutlined, ArrowUpward, ArrowDownward, DeleteOutline } from '@mui/icons-material';
import { MarketAnnouncementBannerList } from './MarketAnnouncementBanner';
import MarketRegulationsPanel from './MarketRegulationsPanel';
import { useResolvedMarketAnnouncements } from './utils/marketAnnouncementsUtils';
import {
  BASE_PRODUCT_AUTO,
  AUTO_BASE_QUEUE_SKU_PREFIX,
  DEFAULT_BASE_PRODUCT_AUTO_QUEUES,
  readBaseProductAutoQueuesFromStorage,
  persistBaseProductAutoQueues,
  buildAutoBaseProductQueueChoices,
  migrateLegacyQueueEntry,
  pickBaseProductFromAutoQueue,
  buildAutoBaseProductByCountryMap,
  formatBaseProductSelectLabel,
} from './utils/baseProductAutoQueueUtils';
import { CrosshairPlugin } from 'chartjs-plugin-crosshair';
import ChartDataLabels from 'chartjs-plugin-datalabels';

const CHART_IMAGE_SIZE = 24;
const CHART_IMAGE_GAP = 6;
/** Raster flags — compact for legend rows + chart overlays (avoid overlapping plot / multi-line legend). */
const FLAG_IMG_DISPLAY_W = 10;
const FLAG_IMG_DISPLAY_H = 7;
/** Gap from chart plot right edge to start of flag+text row (labels sit in layout padding, not over grid lines). */
const CHART_END_LABEL_OUTSIDE_GAP = 6;
/** Max vertical span (px) for labels on the same chart line (diameter clustering, not chain). */
const COUNTRY_END_LABEL_CLUSTER_PX = 18;
/** Stagger spacing for pairs on the same Y band. */
const COUNTRY_END_LABEL_STAGGER_STEP_PX = 10;
/** Same-band groups with this many or more lines use the "N products" chip + hover list. */
const COUNTRY_END_LABEL_CLUSTER_CHIP_MIN = 3;
const MAX_GROUPS = 30; // Show Top: max groups to display

/** Global forward-fill — when true, all users get gap forward-fill on price charts. */
const TEMP_FORWARD_FILL_ENABLED = true;
/** Max calendar days after a gap starts to bridge if the same SKU is observed again. */
const GAP_FILL_MAX_DAYS = 30;

/** Series summary table: tooltips for price / stock columns (last available date). */
const PRICE_TABLE_LAST_DATE_INFO =
  'The price / status shown in the table is on the last available date.';

// CrosshairPlugin must NOT be registered globally: it breaks other charts (e.g. TOC KPI Doughnut)
// when chart.crosshair is never set — afterDestroy throws on syncEventHandler.
// Register it only on the JTI price Line chart via the plugins prop.
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
  ChartDataLabels
);

/** Drop price-bucket segment like " · 8.7" from line labels (no currency) so hover matches legend. */
const stripPriceBucketFromChartHoverLabel = (raw) => {
  if (!raw || typeof raw !== 'string') return raw || '';
  return raw.replace(/\s*·\s*[\d.,]+\s*/g, ' ').replace(/\s+/g, ' ').trim();
};

/** Title for product image hover: prefer a real SKU (variant list) on country lines so preview + images resolve; else legend / labels. */
const chartHoverProductTitle = (dataset, dataIndex = null) => {
  const ds = dataset || {};
  const strip = (s) => stripPriceBucketFromChartHoverLabel(s || '');
  let variantList = null;
  if (
    dataIndex != null &&
    Array.isArray(ds.groupedVariantTitlesAtIndex) &&
    Array.isArray(ds.groupedVariantTitlesAtIndex[dataIndex]) &&
    ds.groupedVariantTitlesAtIndex[dataIndex].length
  ) {
    variantList = ds.groupedVariantTitlesAtIndex[dataIndex];
  } else if (Array.isArray(ds.groupedVariantTitles) && ds.groupedVariantTitles.length) {
    variantList = ds.groupedVariantTitles;
  }
  const firstSku = variantList && variantList.length ? String(variantList[0]).trim() : '';

  if (ds.countryChartLineFormat) {
    if (firstSku) return strip(firstSku);
    const leg = strip(ds.legendLabel || '');
    if (leg) return leg;
    return strip(
      ds.productName ||
        ds.displayLabel ||
        ds.label ||
        (ds.tooltipTitle && String(ds.tooltipTitle).trim()) ||
        ''
    );
  }
  return strip(ds.displayLabel || ds.productName || ds.label || '');
};

/** Remove trailing " - N variant(s)" from legend labels so counts can be re-derived from filtered names. */
const stripVariantCountSuffix = (label) => {
  if (!label || typeof label !== 'string') return '';
  return label.replace(/\s*-\s*\d+\s+variants?$/i, '').trim();
};

/**
 * Sticks: append ` (N units)` after country code when every line title shares the same effective pack size > 1
 * (product detail overrides + Units (#) column via getItemPackSize).
 */
function formatStickPackUnitsBracketForTitles(titles, packSizeByTitle) {
  if (!titles?.length || !packSizeByTitle || packSizeByTitle.size === 0) return '';
  const sizes = titles.map((t) => packSizeByTitle.get(t));
  if (sizes.some((s) => s == null || s <= 1)) return '';
  const unique = [...new Set(sizes)];
  if (unique.length !== 1) return '';
  return ` (${unique[0]} units)`;
}

/** Append " - Bundle" for bundle category lines (matches Starter Kit suffix pattern). */
const appendBundleLabelSuffix = (text, ds) => {
  if (!text || typeof text !== 'string' || !ds?.isBundleLine) return text || '';
  if (/\bbundle\b/i.test(text)) return text;
  return `${text} - Bundle`;
};

/**
 * Panel row title: must match the variant list (e.g. stock-split line shows 2, not cluster total 10).
 */
const buildPinRowLineTitle = (ds, filteredNames) => {
  const raw = ds.displayLabel || ds.legendLabel || ds.label || '';
  const base = stripPriceBucketFromChartHoverLabel(stripVariantCountSuffix(raw));
  const n = filteredNames.length;
  const title =
    n <= 1
      ? base
      : `${base} - ${n} variant${n === 1 ? '' : 's'}`;
  return appendBundleLabelSuffix(title, ds);
};

/** Same string as chartjs-plugin-datalabels formatter (PNG flag row is HTML; canvas label hidden for country lines). */
function getDatasetEndLabelDisplayText(ds, chart, endDataIndex) {
  const labels = chart?.data?.labels || [];
  const lastChartIdx = labels.length > 0 ? labels.length - 1 : -1;
  const idx =
    typeof endDataIndex === 'number' && endDataIndex >= 0 && lastChartIdx >= 0
      ? Math.min(endDataIndex, lastChartIdx)
      : lastChartIdx;
  let raw = '';
  if (idx >= 0 && ds.stockSegment && Array.isArray(ds.groupedVariantTitlesAtIndex)) {
    const at = ds.groupedVariantTitlesAtIndex[idx];
    if (Array.isArray(at) && at.length) {
      raw = buildPinRowLineTitle(ds, at);
    }
  }
  if (!raw) {
    raw =
      (ds.countryChartLineFormat && ds.tooltipTitle && String(ds.tooltipTitle).trim()) ||
      ds.legendLabel ||
      ds.displayLabel ||
      ds.label ||
      '';
    raw = appendBundleLabelSuffix(raw, ds);
  }
  return stripFlagEmoji(raw);
}

/** Edge-safe PNG + text in one row (replaces separate canvas datalabel + floating flag overlay). */
function CountryChartEndLabelRow({ iso, text, color }) {
  const [flagHidden, setFlagHidden] = useState(false);
  useEffect(() => {
    setFlagHidden(false);
  }, [iso]);
  const showFlag = Boolean(iso && !flagHidden);
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        pointerEvents: 'none',
        whiteSpace: 'nowrap',
        minWidth: 0,
        maxWidth: 'min(100vw - 48px, 520px)',
      }}
    >
      {showFlag ? (
        <Box
          component="img"
          src={getCountryFlagImageUrl(iso)}
          alt=""
          referrerPolicy="no-referrer"
          loading="lazy"
          onError={() => setFlagHidden(true)}
          sx={{
            width: FLAG_IMG_DISPLAY_W,
            height: FLAG_IMG_DISPLAY_H,
            objectFit: 'cover',
            borderRadius: '1px',
            flexShrink: 0,
            display: 'block',
          }}
        />
      ) : null}
      <Typography
        component="span"
        sx={{
          fontWeight: 600,
          fontSize: '10px',
          lineHeight: 1.25,
          color: color || '#666',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          minWidth: 0,
        }}
      >
        {text}
      </Typography>
    </Box>
  );
}

/** Vertical span of label anchor points in a candidate cluster (px). */
function countryEndLabelClusterSpreadPx(members) {
  if (!members?.length) return 0;
  const tops = members.map((m) => m.top);
  return Math.max(...tops) - Math.min(...tops);
}

/**
 * Group country chart end labels on the same Y band (tight vertical span).
 * 1 line: single label; 2 on same band: staggered; 3+ on same band: "N products" chip.
 * Labels on different bands become separate chips (e.g. 3×3 not one ×9).
 */
function clusterCountryEndLabelsByTop(rows, thresholdPx = COUNTRY_END_LABEL_CLUSTER_PX) {
  if (!rows?.length) return [];
  // Deduplicate identical labels (same country + rendered text), e.g. a listed and a promo line
  // for the same product, so the "N products" count and member list don't repeat the same entry.
  const seenLabelKeys = new Set();
  const dedupedRows = [];
  rows.forEach((r) => {
    const labelKey = `${r.iso || ''}\u0001${r.text || ''}`;
    if (r.text && seenLabelKeys.has(labelKey)) return;
    if (r.text) seenLabelKeys.add(labelKey);
    dedupedRows.push(r);
  });
  const sorted = [...dedupedRows].sort((a, b) => a.top - b.top || (a.left || 0) - (b.left || 0));
  const buckets = [];
  let cur = [sorted[0]];
  for (let i = 1; i < sorted.length; i += 1) {
    const next = sorted[i];
    const trial = [...cur, next];
    if (countryEndLabelClusterSpreadPx(trial) <= thresholdPx) {
      cur = trial;
    } else {
      buckets.push(cur);
      cur = [next];
    }
  }
  buckets.push(cur);
  return buckets.flatMap((members, gi) => {
    if (members.length === 1) {
      return [{ type: 'single', ...members[0], lineTop: members[0].top }];
    }
    const centerTop = members.reduce((s, m) => s + m.top, 0) / members.length;
    const left = members[0].left;
    if (members.length < COUNTRY_END_LABEL_CLUSTER_CHIP_MIN) {
      const n = members.length;
      const mid = (n - 1) / 2;
      return members.map((m, slot) => ({
        type: 'single',
        key: m.key,
        left,
        top: centerTop + (slot - mid) * COUNTRY_END_LABEL_STAGGER_STEP_PX,
        lineTop: m.top,
        text: m.text,
        iso: m.iso,
        color: m.color,
        productImage: m.productImage,
        productName: m.productName,
        groupProductNames: m.groupProductNames,
      }));
    }
    return [
      {
        type: 'cluster',
        key: `ce-cluster-${gi}-${members.map((m) => m.key).join('|')}`,
        left,
        top: centerTop,
        count: members.length,
        members: members.map((m) => ({
          key: m.key,
          text: m.text,
          iso: m.iso,
          color: m.color,
          lineTop: m.top,
          productImage: m.productImage,
          productName: m.productName,
          groupProductNames: m.groupProductNames,
        })),
      },
    ];
  });
}

/** Product hover on a single gutter label (tight hit target, not the full gutter width). */
function CountryChartLabelHoverTarget({
  productImage,
  productName,
  groupProductNames,
  country,
  asOfDate,
  onProductClick,
  children,
}) {
  const [cursorPos, setCursorPos] = useState(null);
  const track = (e) => setCursorPos({ x: e.clientX, y: e.clientY });
  const clear = () => setCursorPos(null);
  const handleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onProductClick && productName) {
      onProductClick(productName, productImage, { groupProductNames, country, chartPinnedDateLabel: asOfDate });
    }
  };
  return (
    <>
      <Box
        onMouseEnter={track}
        onMouseMove={track}
        onMouseLeave={clear}
        onClick={onProductClick ? handleClick : undefined}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          pointerEvents: 'auto',
          cursor: onProductClick && productName ? 'pointer' : 'default',
          maxWidth: 'min(100vw - 48px, 520px)',
        }}
      >
        {children}
      </Box>
      <ProductImageHoverOverlay
        cursorPos={cursorPos}
        productImage={productImage}
        productName={productName}
        groupProductNames={groupProductNames}
      />
    </>
  );
}

/** Collapsed gutter chip; click pins the product list; per-SKU preview in the popover rows only (not over the plot). */
function CountryChartEndLabelCluster({ count, members, open, onToggleOpen, onProductClick, asOfDate }) {
  const anchorRef = useRef(null);
  const label = `${count} product${count === 1 ? '' : 's'}`;
  const handleChipClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onToggleOpen?.();
  };
  const handleRowProductClick =
    onProductClick &&
    ((productName, productImage, opts) => {
      onProductClick(productName, productImage, opts);
      onToggleOpen?.(false);
    });
  const listBody = (
    <Box sx={{ py: 0.5, px: 0.5, maxHeight: 300, overflowY: 'auto', maxWidth: 360 }}>
      {members.map((m) => {
        const rowHoverName =
          m.productName ||
          (Array.isArray(m.groupProductNames) && m.groupProductNames.length ? m.groupProductNames[0] : '');
        return (
        <Box
          key={m.key}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            py: 0.35,
            '&:not(:last-child)': { mb: 0.25 },
          }}
        >
          <Box
            sx={{
              width: 10,
              height: 2,
              borderRadius: 1,
              bgcolor: m.color || '#666',
              flexShrink: 0,
            }}
          />
          {handleRowProductClick && rowHoverName ? (
            <CountryChartLabelHoverTarget
              productImage={m.productImage}
              productName={rowHoverName}
              groupProductNames={m.groupProductNames}
              country={m.iso || m.country}
              asOfDate={asOfDate}
              onProductClick={handleRowProductClick}
            >
              <CountryChartEndLabelRow iso={m.iso} text={m.text} color={m.color} />
            </CountryChartLabelHoverTarget>
          ) : (
            <CountryChartEndLabelRow iso={m.iso} text={m.text} color={m.color} />
          )}
        </Box>
        );
      })}
    </Box>
  );
  return (
    <>
      <Typography
        ref={anchorRef}
        component="span"
        onClick={handleChipClick}
        sx={{
          display: 'inline-block',
          fontWeight: 600,
          fontSize: '10px',
          lineHeight: 1.25,
          color: open ? 'primary.main' : 'text.secondary',
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: open ? 'primary.main' : 'divider',
          borderRadius: '4px',
          px: 0.75,
          py: 0.25,
          cursor: 'pointer',
          pointerEvents: 'auto',
          whiteSpace: 'nowrap',
          boxShadow: open ? 2 : '0 0 0 1px rgba(255,255,255,0.6)',
        }}
      >
        {label}
      </Typography>
      <Popover
        open={Boolean(open)}
        anchorEl={anchorRef.current}
        onClose={() => onToggleOpen?.(false)}
        anchorOrigin={{ vertical: 'center', horizontal: 'left' }}
        transformOrigin={{ vertical: 'center', horizontal: 'right' }}
        disableRestoreFocus
        PaperProps={{
          sx: {
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: 3,
          },
        }}
      >
        {listBody}
      </Popover>
    </>
  );
}

/** Match listed + promo lines for tooltip / pin dedupe (country×group, stick unit bucket, stock split). */
const countryChartTooltipMergeKey = (ds) => {
  const gid = ds?.legendGroupId != null ? String(ds.legendGroupId) : '';
  const units = ds?.stickPackUnitsBracket || '';
  const seg = ds?.stockSegment || '';
  return `${gid}\0${units}\0${seg}`;
};

/** Dedupe key: same legend group + unit bucket + same variant names at pin → one row when promo & listed lines both exist. */
const pinRowDedupeKey = (row) => {
  const v = (row.variants || []).map((x) => x.name).sort().join('\0');
  const g = row.legendGroupId != null ? String(row.legendGroupId) : row.lineTitle;
  const u = row.stickPackUnitsBracket || '';
  const c = productDetailCountrySlug(row.country) || '';
  return `${g}\0${u}\0${c}\0${v}`;
};

/** One panel row per dataset that has a value at the pinned date index. */
const buildPinRowsForChart = (chartData, idx, mode, productImageUrlByTitle, options = {}) => {
  if (!chartData?.datasets || idx == null || idx < 0) return [];
  const { dedupePromoListedPairs } = options;
  const rows = [];
  for (const ds of chartData.datasets) {
    const raw = ds.data?.[idx];
    if (raw == null || raw === undefined) continue;
    if (typeof raw === 'number' && Number.isNaN(raw)) continue;
    let names = [];
    if (mode === 'visibility') {
      const productName = (ds.label || '').replace(/\s*-\s*Position\s*$/i, '').trim();
      if (productName) names = [productName];
    } else {
      const at = Array.isArray(ds.groupedVariantTitlesAtIndex) ? ds.groupedVariantTitlesAtIndex[idx] : null;
      if (Array.isArray(at) && at.length) names = [...at];
      else if (Array.isArray(ds.groupedVariantTitles) && ds.groupedVariantTitles.length) names = [...ds.groupedVariantTitles];
      else {
        const n = chartHoverProductTitle(ds, idx);
        if (n) names = [n];
      }
    }
    if (!names.length) continue;
    const lineTitle = mode === 'visibility'
      ? (ds.legendLabel || ds.displayLabel || ds.label || '')
      : buildPinRowLineTitle(ds, names);
    const chartSortValue =
      typeof raw === 'number'
        ? raw
        : Number.parseFloat(String(raw).replace(/[^\d.,-]/g, '').replace(',', '.'));
    rows.push({
      lineTitle,
      legendGroupId: ds.legendGroupId,
      legendPrimary: ds.legendPrimary,
      priceType: ds.priceType,
      stickPackUnitsBracket: ds.stickPackUnitsBracket || '',
      country: ds.legendCountryIso || ds.baseProductCountry || '',
      chartSortValue: Number.isFinite(chartSortValue) ? chartSortValue : null,
      variants: names.map((name) => ({
        name,
        image: productImageUrlByTitle?.get?.(name) || null,
        country: ds.legendCountryIso || ds.baseProductCountry || '',
      })),
    });
  }
  if (mode !== 'price' || !dedupePromoListedPairs || rows.length < 2) {
    return rows.map(({ legendGroupId, legendPrimary, priceType, stickPackUnitsBracket, ...rest }) => rest);
  }
  const byKey = new Map();
  for (const r of rows) {
    const key = pinRowDedupeKey(r);
    const prev = byKey.get(key);
    if (!prev) {
      byKey.set(key, r);
      continue;
    }
    const prevPrimary = prev.legendPrimary !== false ? 1 : 0;
    const rPrimary = r.legendPrimary !== false ? 1 : 0;
    if (rPrimary > prevPrimary) byKey.set(key, r);
  }
  return Array.from(byKey.values()).map(({ legendGroupId, legendPrimary, priceType, stickPackUnitsBracket, ...rest }) => rest);
};

/** Build local-currency pricing snapshot from a raw dataset row (optional per-unit adjustment). */
const buildLocalCurrencyPricingFromRow = (row, { priceDisplayMode, getItemPackSize }) => {
  if (!row?.currency) return null;
  let actual = row.actual_price ?? row.price ?? row.current_price;
  let regular = row.regular_price ?? row.original_price;
  const toNum = (v) => {
    if (v == null || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  actual = toNum(actual);
  regular = toNum(regular);
  if (priceDisplayMode === 'per_unit' && getItemPackSize) {
    const packSize = getItemPackSize(row);
    if (packSize && packSize > 1) {
      if (actual != null) actual = actual / packSize;
      if (regular != null) regular = regular / packSize;
    }
  }
  if (actual == null && regular == null) return null;
  return {
    currency: row.currency,
    ...(actual != null ? { actual_price: actual } : {}),
    ...(regular != null ? { regular_price: regular } : {}),
  };
};

const resolveLocalCurrencyPricing = (snap, sourceRow, options = {}) => {
  const displayedCurrency = normalizeCurrencyCode(snap?.currency);
  if (snap?.local_currency) {
    const localCurrency = normalizeCurrencyCode(snap.local_currency);
    if (localCurrency && displayedCurrency && localCurrency !== displayedCurrency) {
      return {
        currency: snap.local_currency,
        ...(snap.local_actual_price != null && snap.local_actual_price !== ''
          ? { actual_price: snap.local_actual_price }
          : {}),
        ...(snap.local_regular_price != null && snap.local_regular_price !== ''
          ? { regular_price: snap.local_regular_price }
          : {}),
      };
    }
  }
  const fromRow = buildLocalCurrencyPricingFromRow(sourceRow, options);
  if (!fromRow?.currency) return null;
  const localCurrency = normalizeCurrencyCode(fromRow.currency);
  if (!localCurrency || !displayedCurrency || localCurrency === displayedCurrency) return null;
  return fromRow;
};

const attachLocalCurrencyPricing = (mergedRow, snap, sourceRow, options = {}) => {
  if (!mergedRow) return mergedRow;
  const localCurrencyPricing = resolveLocalCurrencyPricing(snap, sourceRow, options);
  if (!localCurrencyPricing) return mergedRow;
  return { ...mergedRow, localCurrencyPricing };
};

/** Pin panel product groups: highest chart value first. */
const sortPinPanelRowsByChartValue = (rows) =>
  [...rows].sort((a, b) => {
    const va = Number.isFinite(a.chartSortValue) ? a.chartSortValue : -Infinity;
    const vb = Number.isFinite(b.chartSortValue) ? b.chartSortValue : -Infinity;
    return vb - va;
  });

/** X position of the vertical line at `index` (container-relative px), aligned with the pinned-line plugin. */
const getCrosshairLineXInContainer = (chart, index, containerEl) => {
  if (!chart?.canvas || !containerEl || index == null || index < 0) return null;
  const labels = chart.data?.labels;
  if (!labels || index >= labels.length) return null;
  const containerRect = containerEl.getBoundingClientRect();
  const canvas = chart.canvas;
  const canvasRect = canvas.getBoundingClientRect();
  const ch = chart;
  let ptX = null;
  for (let di = 0; di < chart.data.datasets.length; di += 1) {
    const meta = chart.getDatasetMeta(di);
    if (meta.hidden) continue;
    const pt = meta.data[index];
    if (pt && Number.isFinite(pt.x)) {
      ptX = pt.x;
      break;
    }
  }
  if (ptX == null) return null;
  const scaleX = canvasRect.width / ch.width;
  return canvasRect.left - containerRect.left + ptX * scaleX;
};

const PIN_PANEL_MAX_W = 380;
const PIN_PANEL_GAP = 10;

const computePinPanelPosition = (clientY, chart, index, containerEl) => {
  const containerRect = containerEl.getBoundingClientRect();
  const lineX = getCrosshairLineXInContainer(chart, index, containerEl);
  const cx = lineX != null ? lineX : containerRect.width / 2;
  const panelW = Math.min(PIN_PANEL_MAX_W, containerRect.width - 16);
  let left = cx + PIN_PANEL_GAP;
  if (left + panelW > containerRect.width - 8) {
    left = cx - PIN_PANEL_GAP - panelW;
  }
  left = Math.max(8, Math.min(left, containerRect.width - panelW - 8));
  const clientYVal = clientY ?? containerRect.top + containerRect.height / 3;
  let top = clientYVal - containerRect.top - 20;
  // Keep panel inside chart container (matches Paper maxHeight: min(50%, 360px)) so it does not
  // extend under the legend; legend is a sibling and would paint on top of overflow.
  const maxPanelH = Math.min(containerRect.height * 0.5, 360);
  const maxTop = Math.max(8, containerRect.height - maxPanelH - 8);
  top = Math.max(8, Math.min(top, maxTop));
  return { left, top };
};

const pinnedVerticalLinePlugin = {
  id: 'pinnedVerticalLine',
  afterDatasetsDraw(chart) {
    const pinOpts = chart.options.plugins?.pinnedVerticalLine;
    const idx = pinOpts?.index;
    if (idx === undefined || idx === null || idx < 0) return;
    const labels = chart.data?.labels;
    if (!labels || idx >= labels.length) return;
    const { ctx, chartArea } = chart;
    if (!chartArea) return;
    let x = null;
    for (let di = 0; di < chart.data.datasets.length; di += 1) {
      const meta = chart.getDatasetMeta(di);
      if (meta.hidden) continue;
      const pt = meta.data[idx];
      if (pt && Number.isFinite(pt.x)) {
        x = pt.x;
        break;
      }
    }
    if (x == null) return;
    ctx.save();
    ctx.beginPath();
    ctx.strokeStyle = pinOpts?.color || STYLE_PRESETS.classic.accent;
    ctx.lineWidth = 2;
    ctx.setLineDash([6, 4]);
    ctx.moveTo(x, chartArea.top);
    ctx.lineTo(x, chartArea.bottom);
    ctx.stroke();
    ctx.restore();
  },
};

/** Calendar days between first and last scrape for a (country, retailer) with no rows in the dataset. */
const computeScrapeGapDateKeys = (chartDateKeys, sortedPairDays) => {
  if (!Array.isArray(chartDateKeys) || !chartDateKeys.length) return [];
  if (!Array.isArray(sortedPairDays) || sortedPairDays.length < 2) return [];
  const scrapeSet = new Set(sortedPairDays);
  const first = sortedPairDays[0];
  const last = sortedPairDays[sortedPairDays.length - 1];
  return chartDateKeys.filter((dk) => dk >= first && dk <= last && !scrapeSet.has(dk));
};

const buildAdminScrapeGapMeta = (chartDateKeys, pairMarketDaysSortedMap, pairFilter) => {
  const pairs = [];
  pairMarketDaysSortedMap.forEach((sortedDays, pairKey) => {
    if (pairFilter && !pairFilter(pairKey)) return;
    const gapDateKeys = computeScrapeGapDateKeys(chartDateKeys, sortedDays);
    if (!gapDateKeys.length) return;
    const ix = pairKey.indexOf('\x00');
    const country = ix === -1 ? pairKey : pairKey.slice(0, ix);
    const retailer = ix === -1 ? '' : pairKey.slice(ix + 1);
    const countryLabel = formatCountryName(country) || String(country || '').toUpperCase();
    const label = retailer ? `${retailer} · ${countryLabel}` : countryLabel;
    pairs.push({
      country,
      retailer,
      pairKey,
      label,
      gapDateKeys,
      gapCount: gapDateKeys.length,
    });
  });
  pairs.sort((a, b) => a.label.localeCompare(b.label));
  const gapsByDateKey = {};
  const gapIndexSet = new Set();
  pairs.forEach((p) => {
    p.gapDateKeys.forEach((dk) => {
      if (!gapsByDateKey[dk]) gapsByDateKey[dk] = [];
      gapsByDateKey[dk].push(p.label);
    });
  });
  (chartDateKeys || []).forEach((dk, i) => {
    if (gapsByDateKey[dk]) gapIndexSet.add(i);
  });
  return {
    pairs,
    gapsByDateKey,
    gapIndices: Array.from(gapIndexSet).sort((a, b) => a - b),
    totalGapDays: Object.keys(gapsByDateKey).length,
  };
};

/** Admin overlay: shade chart days with no scrape for at least one in-scope retailer·country pair. */
const adminScrapeGapHighlightPlugin = {
  id: 'adminScrapeGapHighlight',
  beforeDatasetsDraw(chart) {
    const opts = chart.options.plugins?.adminScrapeGapHighlight;
    if (!opts?.enabled) return;
    const gapIndices = opts.gapIndices;
    if (!gapIndices?.length) return;
    const { ctx, chartArea } = chart;
    if (!chartArea) return;

    const xAtIndex = (idx) => {
      for (let di = 0; di < chart.data.datasets.length; di += 1) {
        const meta = chart.getDatasetMeta(di);
        if (meta.hidden) continue;
        const pt = meta.data?.[idx];
        if (pt && Number.isFinite(pt.x)) return pt.x;
      }
      return null;
    };

    ctx.save();
    gapIndices.forEach((idx) => {
      const x = xAtIndex(idx);
      if (x == null) return;
      const xPrev = idx > 0 ? xAtIndex(idx - 1) : null;
      const xNext = idx < chart.data.labels.length - 1 ? xAtIndex(idx + 1) : null;
      let halfW = 6;
      if (xPrev != null && xNext != null) halfW = Math.min((xNext - xPrev) / 4, (x - xPrev) / 2, (xNext - x) / 2);
      else if (xPrev != null) halfW = Math.max(4, (x - xPrev) / 2);
      else if (xNext != null) halfW = Math.max(4, (xNext - x) / 2);
      const left = Math.max(chartArea.left, x - halfW);
      const right = Math.min(chartArea.right, x + halfW);
      ctx.fillStyle = opts.fillColor || 'rgba(255, 193, 7, 0.22)';
      ctx.fillRect(left, chartArea.top, right - left, chartArea.bottom - chartArea.top);
      ctx.strokeStyle = opts.borderColor || 'rgba(255, 152, 0, 0.45)';
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.moveTo(x, chartArea.top);
      ctx.lineTo(x, chartArea.bottom);
      ctx.stroke();
    });
    ctx.restore();
  },
};

const ChartProductImage = ({
  left,
  top,
  hoverLeft,
  hoverWidth,
  productImage,
  productName,
  projectKey,
  country,
  asOfDate,
  onProductClick,
  groupProductNames,
}) => {
  const [cursorPos, setCursorPos] = useState(null);
  const hasHoverArea = hoverLeft != null && hoverWidth != null;
  const areaLeft = hasHoverArea ? hoverLeft : left;
  const areaWidth = hasHoverArea ? hoverWidth : CHART_IMAGE_SIZE + CHART_IMAGE_GAP;
  const areaTop = top - 4;
  const areaHeight = CHART_IMAGE_SIZE + 8;

  const handleMouseMove = (e) => {
    setCursorPos({ x: e.clientX, y: e.clientY });
  };
  const handleMouseLeave = () => {
    setCursorPos(null);
  };
  const handleClick = (e) => {
    e.preventDefault();
    if (onProductClick && productName) {
      onProductClick(productName, productImage, { groupProductNames, country, chartPinnedDateLabel: asOfDate });
    }
  };

  return (
    <>
      <Box
        component={onProductClick ? 'div' : RouterLink}
        to={!onProductClick ? buildProductDetailPath(projectKey, country, productName, asOfDate) : undefined}
        onClick={onProductClick ? handleClick : undefined}
        onMouseEnter={handleMouseMove}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        sx={{
          position: 'absolute',
          left: areaLeft,
          top: areaTop,
          width: areaWidth,
          height: areaHeight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          cursor: 'pointer',
          textDecoration: 'none',
          color: 'inherit',
        }}
      />
      <ProductImageHoverOverlay
        cursorPos={cursorPos}
        productImage={productImage}
        productName={productName}
        groupProductNames={groupProductNames}
      />
    </>
  );
};

// Wrapper that renders chart + HTML overlay for product images/placeholders
const ChartWithProductImages = ({
  data,
  options,
  showImages,
  projectKey,
  onProductClick,
  pinnedIndex = null,
  onChartPin,
  onClearPin,
  pinPanel = null,
  onVariantProductClick,
  accentColor = STYLE_PRESETS.classic.accent,
  /** When both promo + listed lines exist for the same SKUs, show one end-of-line image/hover per group. */
  dedupePromoListedPairs = false,
}) => {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const [imagePositions, setImagePositions] = useState([]);
  const [countryChartEndLabels, setCountryChartEndLabels] = useState([]);
  const [pinnedClusterKey, setPinnedClusterKey] = useState(null);
  const [pinPanelStyle, setPinPanelStyle] = useState(null);
  const lastPinClientYRef = useRef(null);

  const chartAsOfDate = useMemo(() => {
    const labels = data?.labels;
    if (!labels?.length) return undefined;
    const idx =
      pinnedIndex != null && pinnedIndex >= 0 && pinnedIndex < labels.length
        ? pinnedIndex
        : labels.length - 1;
    return toSqlDateKey(labels[idx]) || undefined;
  }, [data?.labels, pinnedIndex]);

  useEffect(() => {
    setPinnedClusterKey(null);
  }, [data]);

  const mergedOptions = useMemo(() => {
    const base = options || {};
    const pinActive = pinnedIndex != null;
    return {
      ...base,
      plugins: {
        ...base.plugins,
        pinnedVerticalLine: pinActive ? { index: pinnedIndex, color: accentColor } : {},
        crosshair: pinActive ? false : base.plugins?.crosshair,
      },
      onClick: (event, _elements, chart) => {
        const atIndex = chart.getElementsAtEventForMode(event, 'index', { intersect: false }, true);
        if (!atIndex.length) return;
        const i = atIndex[0].index;
        const container = containerRef.current;
        if (!container) return;
        if (pinnedIndex === i) {
          onChartPin?.(i);
          setPinPanelStyle(null);
          lastPinClientYRef.current = null;
          return;
        }
        const native = event.native || event;
        const containerRect = container.getBoundingClientRect();
        const clientY =
          native.clientY ??
          event.clientY ??
          (containerRect.top + containerRect.height / 3);
        lastPinClientYRef.current = clientY;
        setPinPanelStyle(computePinPanelPosition(clientY, chart, i, container));
        onChartPin?.(i);
      },
    };
  }, [options, pinnedIndex, onChartPin, accentColor]);

  useEffect(() => {
    if (pinnedIndex == null) {
      setPinPanelStyle(null);
      lastPinClientYRef.current = null;
    }
  }, [pinnedIndex]);

  useEffect(() => {
    if (pinnedIndex == null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClearPin?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pinnedIndex, onClearPin]);

  const updatePinPanelPositionForResize = useCallback(() => {
    if (pinnedIndex == null) return;
    const ch = chartRef.current;
    const container = containerRef.current;
    if (!ch || !container) return;
    const cy = lastPinClientYRef.current;
    setPinPanelStyle(computePinPanelPosition(cy, ch, pinnedIndex, container));
  }, [pinnedIndex]);

  useEffect(() => {
    if (pinnedIndex == null) return;
    const container = containerRef.current;
    if (!container) return;
    const ro = new ResizeObserver(() => {
      requestAnimationFrame(updatePinPanelPositionForResize);
    });
    ro.observe(container);
    requestAnimationFrame(updatePinPanelPositionForResize);
    return () => ro.disconnect();
  }, [pinnedIndex, data, updatePinPanelPositionForResize]);

  useLayoutEffect(() => {
    if (pinnedIndex == null) return;
    let id = requestAnimationFrame(() => {
      updatePinPanelPositionForResize();
    });
    return () => cancelAnimationFrame(id);
  }, [pinnedIndex, data?.labels, updatePinPanelPositionForResize]);

  const updatePositions = useCallback(() => {
    if (!showImages) {
      setImagePositions([]);
      setCountryChartEndLabels([]);
      return;
    }
    const ch = chartRef.current;
    if (!ch || !data?.datasets?.length) {
      setImagePositions([]);
      setCountryChartEndLabels([]);
      return;
    }
    const canvas = ch.canvas;
    const chartArea = ch.chartArea;
    if (!chartArea || !canvas) {
      setCountryChartEndLabels([]);
      return;
    }
    const canvasRect = canvas.getBoundingClientRect();
    const container = containerRef.current;
    const containerRect = container?.getBoundingClientRect();
    if (!containerRect) return;
    const scaleY = canvasRect.height / ch.height;
    const scaleX = canvasRect.width / ch.width;

    const labelAreaWidth = 120;
    const imageLeft = containerRect.width - CHART_IMAGE_SIZE - CHART_IMAGE_GAP;
    const hoverLeft = Math.max(0, imageLeft - labelAreaWidth);
    const hoverWidth = labelAreaWidth + CHART_IMAGE_SIZE + CHART_IMAGE_GAP;

    const axisLabels = ch.data.labels || [];
    const lastAxisIdx = axisLabels.length > 0 ? axisLabels.length - 1 : -1;

    const canvasLeftInContainer = canvasRect.left - containerRect.left;
    const canvasTopInContainer = canvasRect.top - containerRect.top;
    /** Right edge of plot area → anchor labels in the gutter (same coords as `meta.data[].x`). */
    const chartPlotRightInContainer = canvasLeftInContainer + chartArea.right * scaleX;

    const raw = [];
    const rawCountryEnds = [];
    ch.data.datasets.forEach((dataset, datasetIndex) => {
      if (dataset.hidden) return;
      const d = dataset.data;
      let lastValidIdx = -1;
      for (let i = d.length - 1; i >= 0; i--) {
        if (d[i] != null && d[i] !== undefined) {
          lastValidIdx = i;
          break;
        }
      }
      if (lastValidIdx < 0) return;
      /** Gutter flag+text only when this series has a point on the chart's last x date (avoids labels for lines that stop early). */
      const seriesReachesChartEnd = lastAxisIdx >= 0 && lastValidIdx === lastAxisIdx;
      const meta = ch.getDatasetMeta(datasetIndex);
      const point = meta.data[lastValidIdx];
      if (!point) return;
      const titlesAtEnd = dataset.groupedVariantTitlesAtIndex?.[lastValidIdx];
      let groupProductNames = dataset.groupedVariantTitles;
      if (Array.isArray(dataset.groupedVariantTitlesAtIndex) && Array.isArray(titlesAtEnd)) {
        groupProductNames = titlesAtEnd.length > 0 ? titlesAtEnd : undefined;
      }
      const top = canvasTopInContainer + point.y * scaleY - CHART_IMAGE_SIZE / 2;
      const isCountryLine = Boolean(dataset.countryChartLineFormat);
      raw.push({
        left: imageLeft,
        top,
        hoverLeft: isCountryLine ? imageLeft : hoverLeft,
        hoverWidth: isCountryLine ? CHART_IMAGE_SIZE + CHART_IMAGE_GAP : hoverWidth,
        productImage: dataset.productImage,
        productName: chartHoverProductTitle(dataset, lastValidIdx),
        groupProductNames,
        country: dataset.legendCountryIso || dataset.baseProductCountry,
        legendGroupId: dataset.legendGroupId,
        legendPrimary: dataset.legendPrimary,
      });

      if (dataset.countryChartLineFormat && seriesReachesChartEnd) {
        const v = d[lastValidIdx];
        if (v != null && v !== undefined) {
          if (!(dataset.priceType != null && typeof v === 'number' && v <= 0)) {
            const pointContainerY = canvasTopInContainer + point.y * scaleY;
            const left = chartPlotRightInContainer + CHART_END_LABEL_OUTSIDE_GAP;
            rawCountryEnds.push({
              legendGroupId: dataset.legendGroupId,
              legendPrimary: dataset.legendPrimary,
              groupProductNames,
              productImage: dataset.productImage,
              productName: chartHoverProductTitle(dataset, lastValidIdx),
              left,
              top: pointContainerY,
              text: getDatasetEndLabelDisplayText(dataset, ch, lastValidIdx),
              iso: (dataset.legendCountryIso || '').trim(),
              color: dataset.borderColor || '#666',
              key: `ce-${datasetIndex}-${dataset.legendGroupId ?? dataset.label ?? ''}`,
            });
          }
        }
      }
    });

    let positions;
    if (dedupePromoListedPairs && raw.length > 1) {
      const byKey = new Map();
      for (const r of raw) {
        const names =
          r.groupProductNames && r.groupProductNames.length
            ? r.groupProductNames
            : r.productName
              ? [r.productName]
              : [];
        const vKey = names.map((n) => n).sort().join('\0');
        const gKey = r.legendGroupId != null ? String(r.legendGroupId) : (r.productName || '');
        const key = `${gKey}\0${vKey}`;
        const prev = byKey.get(key);
        if (!prev) {
          byKey.set(key, r);
          continue;
        }
        const prevPrimary = prev.legendPrimary !== false ? 1 : 0;
        const rPrimary = r.legendPrimary !== false ? 1 : 0;
        if (rPrimary > prevPrimary) byKey.set(key, r);
      }
      positions = Array.from(byKey.values()).map(
        ({
          left,
          top,
          hoverLeft,
          hoverWidth,
          productImage,
          productName,
          groupProductNames,
          country,
        }) => ({
          left,
          top,
          hoverLeft,
          hoverWidth,
          productImage,
          productName,
          groupProductNames,
          country,
        })
      );
    } else {
      positions = raw.map(
        ({
          left,
          top,
          hoverLeft,
          hoverWidth,
          productImage,
          productName,
          groupProductNames,
          country,
        }) => ({
          left,
          top,
          hoverLeft,
          hoverWidth,
          productImage,
          productName,
          groupProductNames,
          country,
        })
      );
    }
    setImagePositions(positions);

    let countryEnds;
    if (dedupePromoListedPairs && rawCountryEnds.length > 1) {
      const byKey = new Map();
      for (const r of rawCountryEnds) {
        const names =
          r.groupProductNames && r.groupProductNames.length
            ? r.groupProductNames
            : r.productName
              ? [r.productName]
              : [];
        const vKey = names.map((n) => n).sort().join('\0');
        const gKey = r.legendGroupId != null ? String(r.legendGroupId) : (r.productName || '');
        const key = `${gKey}\0${vKey}`;
        const prev = byKey.get(key);
        if (!prev) {
          byKey.set(key, r);
          continue;
        }
        const prevPrimary = prev.legendPrimary !== false ? 1 : 0;
        const rPrimary = r.legendPrimary !== false ? 1 : 0;
        if (rPrimary > prevPrimary) byKey.set(key, r);
      }
      countryEnds = Array.from(byKey.values());
    } else {
      countryEnds = rawCountryEnds;
    }
    setCountryChartEndLabels(
      clusterCountryEndLabelsByTop(
        countryEnds.map(
          ({
            left,
            top,
            text,
            iso,
            color,
            key,
            productImage,
            productName,
            groupProductNames,
          }) => ({
            left,
            top,
            text,
            iso,
            color,
            key,
            productImage,
            productName,
            groupProductNames,
          })
        )
      )
    );
  }, [data, showImages, dedupePromoListedPairs]);

  useEffect(() => {
    if (!showImages) {
      setImagePositions([]);
      setCountryChartEndLabels([]);
      return;
    }
    let attempts = 0;
    const tryUpdate = () => {
      const ch = chartRef.current;
      if (ch?.chartArea && ch?.canvas) {
        updatePositions();
      } else if (attempts < 10) {
        attempts += 1;
        setTimeout(tryUpdate, 100);
      }
    };
    const timer = setTimeout(tryUpdate, 100);
    return () => clearTimeout(timer);
  }, [data, options, showImages, updatePositions]);

  useEffect(() => {
    if (!showImages) return;
    const container = containerRef.current;
    if (!container) return;
    const ro = new ResizeObserver(() => {
      setTimeout(updatePositions, 50);
    });
    ro.observe(container);
    return () => ro.disconnect();
  }, [showImages, updatePositions]);

  return (
    <Box ref={containerRef} data-chart-container sx={{ position: 'relative', width: '100%', height: '100%' }}>
      <Line
        ref={chartRef}
        data={data}
        options={mergedOptions}
        plugins={[CrosshairPlugin, pinnedVerticalLinePlugin, adminScrapeGapHighlightPlugin, chartPointerYPlugin, crosshairClosestPointPlugin]}
      />
      {pinnedIndex != null && pinPanel && pinPanelStyle && (
        <Paper
          elevation={4}
          onMouseDown={(e) => e.stopPropagation()}
          sx={{
            position: 'absolute',
            left: pinPanelStyle.left,
            top: pinPanelStyle.top,
            zIndex: 10,
            maxWidth: PIN_PANEL_MAX_W,
            maxHeight: 'min(50%, 360px)',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 1,
              px: 1.25,
              pt: 1.25,
              pb: 0.75,
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.3, pr: 0.5 }}>
              {pinPanel.dateLabel}
            </Typography>
            <IconButton
              size="small"
              onClick={() => onClearPin?.()}
              aria-label="Clear pinned date"
              sx={{ mt: -0.5, color: accentColor }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
              px: 1.25,
              py: 1,
            }}
          >
            {!pinPanel.rows?.length ? (
              <Typography variant="body2" color="text.secondary">
                No series at this date.
              </Typography>
            ) : (
              pinPanel.rows.map((row, ri) => (
                <Box key={`${row.lineTitle}-${ri}`} sx={{ mb: 1.25, '&:last-child': { mb: 0 } }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    {row.lineTitle}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                    {row.variants.map((v) => (
                      <Box
                        key={`${v.name}-${v.country || row.country || ''}-${(v.priceDetails || []).map((p) => `${p.type}:${p.value}`).join('|')}`}
                        component="button"
                        type="button"
                        onClick={() =>
                          onVariantProductClick?.(v.name, v.image, {
                            chartPinnedDateLabel: pinPanel?.dateLabel,
                            pinnedPopupPricing: v.pinnedPopupPricing ?? null,
                            country: v.country || row.country,
                          })
                        }
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          py: 0.5,
                          px: 0.5,
                          borderRadius: 0.5,
                          textAlign: 'left',
                          color: accentColor,
                          fontSize: '0.8125rem',
                          cursor: 'pointer',
                          border: 'none',
                          background: 'transparent',
                          font: 'inherit',
                          width: '100%',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                      >
                        {v.image ? (
                          <Box
                            component="img"
                            src={v.image}
                            alt=""
                            sx={{ width: 28, height: 28, objectFit: 'contain', flexShrink: 0, borderRadius: 0.5 }}
                          />
                        ) : null}
                        <Box component="span" sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                          <span>{v.name}</span>
                          {!!v.priceDetails?.length && (
                            <Typography
                              component="span"
                              variant="caption"
                              sx={{ color: 'text.secondary', lineHeight: 1.2 }}
                            >
                              {v.priceDetails.map((p) => `${p.label}: ${p.value}`).join(' | ')}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))
            )}
            {pinPanel.scrapeGapPairs?.length > 0 && (
              <Box
                sx={{
                  mt: 1,
                  pt: 1,
                  borderTop: '1px dashed',
                  borderColor: 'warning.light',
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 600, color: 'warning.dark', display: 'block', mb: 0.5 }}>
                  Missing scrape (no rows in dataset)
                </Typography>
                {pinPanel.scrapeGapPairs.map((pairLabel) => (
                  <Typography key={pairLabel} variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {pairLabel}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        </Paper>
      )}
      {showImages &&
        countryChartEndLabels.map((row) => {
          const endLabelHoverName =
            row.productName ||
            (Array.isArray(row.groupProductNames) && row.groupProductNames.length
              ? row.groupProductNames[0]
              : '');
          return (
          <Box
            key={row.key}
            sx={{
              position: 'absolute',
              left: row.left,
              top: row.top,
              transform: 'translateY(-50%)',
              zIndex: row.type === 'cluster' ? 7 : 6,
              display: 'flex',
              justifyContent: 'flex-start',
              minWidth: 0,
              pointerEvents: 'none',
            }}
          >
            {row.type === 'cluster' ? (
              <CountryChartEndLabelCluster
                count={row.count}
                members={row.members}
                open={pinnedClusterKey === row.key}
                onProductClick={onProductClick}
                asOfDate={chartAsOfDate}
                onToggleOpen={(close) => {
                  if (close === false) {
                    setPinnedClusterKey(null);
                    return;
                  }
                  setPinnedClusterKey((k) => (k === row.key ? null : row.key));
                }}
              />
            ) : endLabelHoverName ? (
              <CountryChartLabelHoverTarget
                productImage={row.productImage}
                productName={endLabelHoverName}
                groupProductNames={row.groupProductNames}
                country={row.iso || row.country}
                asOfDate={chartAsOfDate}
                onProductClick={onProductClick}
              >
                <CountryChartEndLabelRow iso={row.iso} text={row.text} color={row.color} />
              </CountryChartLabelHoverTarget>
            ) : (
              <CountryChartEndLabelRow iso={row.iso} text={row.text} color={row.color} />
            )}
          </Box>
          );
        })}
      {showImages && imagePositions.map((pos, idx) => (
        <ChartProductImage
          key={idx}
          left={pos.left}
          top={pos.top}
          hoverLeft={pos.hoverLeft}
          hoverWidth={pos.hoverWidth}
          productImage={pos.productImage}
          productName={pos.productName}
          projectKey={projectKey}
          country={pos.country}
          asOfDate={chartAsOfDate}
          onProductClick={onProductClick}
          groupProductNames={pos.groupProductNames}
        />
      ))}
    </Box>
  );
};

export const inSaleOptions = ['All', 'Yes', 'No'];

// Static exchange rates to EUR; update as needed.
const EXCHANGE_RATES_TO_EUR = {
  EUR: 1,
  GBP: 1.16,
  USD: 0.93,
  CHF: 1.04,
  RON: 0.20,
  PLN: 0.23,
  SEK: 0.089,
  NOK: 0.088,
  DKK: 0.134,
  CZK: 0.04,
  HUF: 0.0026,
};

const normalizeCurrencyCode = (value) => (value || '').toString().trim().toUpperCase();
const normalizeValue = (value) => (value || '').toString().trim().toLowerCase();

const PRODUCT_TYPE_ALIASES = {
  devices: ['devices', 'device'],
  sticks: ['sticks', 'stick'],
  accessories: ['accessories', 'accessory'],
};

const resolveDefaultCategoryGroupForRoute = (productType) => {
  const normalizedType = normalizeValue(productType);
  if (!normalizedType) return null;
  if (PRODUCT_TYPE_ALIASES.devices.includes(normalizedType)) return 'Devices';
  if (PRODUCT_TYPE_ALIASES.sticks.includes(normalizedType)) return 'Sticks';
  if (PRODUCT_TYPE_ALIASES.accessories.includes(normalizedType)) return 'Accessories';
  return null;
};

// Preferred default base products for indexed price on /devices (legacy; Auto queue replaces manual default)
const BASE_PRODUCT_SKU_AUTO = '__base_product_sku_auto__';
const BASE_COMPARE_TYPE_AUTO = 'auto';
const BASE_COMPARE_TYPE_LISTED = 'listed';
const BASE_COMPARE_TYPE_PROMO = 'promo';

/** Fallback order when base product lacks the requested price type on a given date. */
const INDEX_COMPARE_BASE_TYPES = ['regular', 'promo'];
const INDEX_COMPARE_BASE_TYPE_LABELS = {
  regular: 'Regular Price',
  promo: 'Promo',
};

const indexCompareRequestForPriceType = (priceType) => {
  if (priceType === 'promo') return 'promo';
  return 'regular';
};

const resolveEffectiveIndexCompareRequest = (requestedBaseType, compareToPriceType) => {
  if (compareToPriceType === BASE_COMPARE_TYPE_LISTED) return 'regular';
  if (compareToPriceType === BASE_COMPARE_TYPE_PROMO) return 'promo';
  return requestedBaseType;
};

const resolveCompareBaseTypeAtIndex = (requestedType, dateIndex, getBaseSeriesForType) => {
  const primary = getBaseSeriesForType(requestedType);
  if (primary && primary[dateIndex] > 0) return requestedType;
  for (const t of INDEX_COMPARE_BASE_TYPES) {
    if (t === requestedType) continue;
    const alt = getBaseSeriesForType(t);
    if (alt && alt[dateIndex] > 0) return t;
  }
  return null;
};

/**
 * Token replaced with a PNG flag by the HTML tooltip renderer. The index baseline is always the
 * reference product in the SKU's own country, so the flag shows which country we are comparing to.
 * Emoji flags are avoided because they do not render on Windows.
 */
const COMPARE_FLAG_TOKEN = (iso) => (iso && String(iso).length === 2 ? `[[CMPFLAG:${String(iso).toLowerCase()}]]` : '');

const formatIndexCompareHoverSuffix = (activeBaseProduct, requestedType, usedType, countryIso) => {
  if (!activeBaseProduct) return '';
  const used = usedType || requestedType;
  const showType =
    used && requestedType && used !== requestedType && INDEX_COMPARE_BASE_TYPE_LABELS[used];
  const typePart = showType ? ` - ${INDEX_COMPARE_BASE_TYPE_LABELS[used]}` : '';
  const flagPart = COMPARE_FLAG_TOKEN(countryIso);
  return ` (Comparing to ${flagPart}${activeBaseProduct}${typePart})`;
};

const indexCompareDatasetMeta = (result) =>
  result
    ? {
        indexCompareBaseTypeAtIndex: result.indexCompareBaseTypeAtIndex,
        indexCompareRequestedBaseType: result.requestedBaseType,
      }
    : {};

const priceSpreadTolerance = (priceDecimalPlaces = 2) =>
  Math.max(1e-9, 5 * Math.pow(10, -Math.max(priceDecimalPlaces, 2)));

/** Promo = actual below listed; actual above listed is stale data, not a promo spread. */
const isPromoPriceSpread = (actual, regular, tol) =>
  Number.isFinite(actual) &&
  Number.isFinite(regular) &&
  actual > 0 &&
  regular > 0 &&
  regular - actual > tol;

/** Fill missing listed from actual; cap stale actual above listed. */
const normalizeActualAndRegularPrices = (actual, regular) => {
  let normalizedActual = Number.isFinite(actual) && actual > 0 ? actual : 0;
  let normalizedRegular = Number.isFinite(regular) && regular > 0 ? regular : 0;
  // Only borrow actual for listed when listed is truly absent — never when listed is lower
  // (promo) or already set; avoids collapsing 39 EUR listed to 19 EUR promo actual.
  if (normalizedRegular <= 0 && normalizedActual > 0) {
    normalizedRegular = normalizedActual;
  }
  if (normalizedRegular > 0 && normalizedActual > normalizedRegular) {
    normalizedActual = normalizedRegular;
  }
  return { actual: normalizedActual, regular: normalizedRegular };
};

const resolveCategoryGroupForType = (productType, options) => {
  if (!productType || !Array.isArray(options) || options.length === 0) {
    return null;
  }
  const normalizedType = normalizeValue(productType);
  if (!normalizedType) return null;
  const isDevices = PRODUCT_TYPE_ALIASES.devices.includes(normalizedType);
  const isSticks = PRODUCT_TYPE_ALIASES.sticks.includes(normalizedType);
  const isAccessories = PRODUCT_TYPE_ALIASES.accessories.includes(normalizedType);
  if (!isDevices && !isSticks && !isAccessories) return null;
  
  if (isDevices) {
    const exactMatch = options.find((option) => option === 'Devices');
    if (exactMatch) return exactMatch;
    const match = options.find((option) => normalizeValue(option).includes('device'));
    return match || resolveDefaultCategoryGroupForRoute(productType);
  }
  if (isSticks) {
    const exactMatch = options.find((option) => option === 'Sticks');
    if (exactMatch) return exactMatch;
    const match = options.find((option) => normalizeValue(option).includes('stick'));
    return match || resolveDefaultCategoryGroupForRoute(productType);
  }
  const exactAccessories = options.find((option) => option === 'Accessories');
  if (exactAccessories) return exactAccessories;
  const accessoriesMatch = options.find((option) => normalizeValue(option).includes('accessories'));
  return accessoriesMatch || resolveDefaultCategoryGroupForRoute(productType);
};

const convertToEur = (value, currency) => {
  const rate = EXCHANGE_RATES_TO_EUR[normalizeCurrencyCode(currency)];
  const numericValue = parseFloat(value);
  if (!rate || Number.isNaN(numericValue)) {
    return Number.isNaN(numericValue) ? value : numericValue;
  }
  return Number((numericValue * rate).toFixed(2));
};

const convertToCurrency = (value, fromCurrency, toCurrency) => {
  if (!toCurrency || normalizeCurrencyCode(fromCurrency) === normalizeCurrencyCode(toCurrency)) {
    return value;
  }
  // First convert to EUR, then to target currency
  const eurValue = convertToEur(value, fromCurrency);
  const toRate = EXCHANGE_RATES_TO_EUR[normalizeCurrencyCode(toCurrency)];
  if (!toRate) {
    return eurValue; // If target currency rate not found, return EUR value
  }
  // Convert from EUR to target currency (inverse of EUR rate)
  const numericValue = parseFloat(eurValue);
  if (Number.isNaN(numericValue)) {
    return eurValue;
  }
  return Number((numericValue / toRate).toFixed(2));
};

const STYLE_PRESETS = {
  classic: {
    accent: '#2e7d32',
    accentSoft: '#c8e6c9',
    panel: '#f5f5f5',
    panelAlt: '#fafafa',
    border: '#e0e0e0',
    chipBg: 'rgba(46, 125, 50, 0.08)',
    headerBg: '#f5f5f5',
    headerBorder: '#e0e0e0',
    gridSeparator: '#e0e0e0',
  },
  whiteLabel: {
    accent: '#3d8f55',
    accentSoft: '#dff0e4',
    panel: '#f7faf8',
    panelAlt: '#ffffff',
    border: '#d9e3dd',
    chipBg: 'rgba(61, 143, 85, 0.12)',
    headerBg: '#f1f5f3',
    headerBorder: '#d9e3dd',
    gridSeparator: '#d9e3dd',
  },
  batWhiteLabel: {
    accent: '#24336B', // BAT primary blue
    accentSoft: '#e8ecf5', // Light blue
    panel: '#f0f4f9', // Very light blue background
    panelAlt: '#ffffff',
    border: '#c4d1e8', // Light blue border
    chipBg: 'rgba(36, 51, 107, 0.12)', // BAT blue with opacity
    headerBg: '#e8ecf5', // Light blue header
    headerBorder: '#c4d1e8',
    gridSeparator: '#c4d1e8',
  },
  pmiWhiteLabel: {
    accent: 'rgb(0, 116, 194)', // PMI primary blue
    accentSoft: '#e6f2fb',
    panel: '#f2f8fe',
    panelAlt: '#ffffff',
    border: '#bfdcf4',
    chipBg: 'rgba(0, 116, 194, 0.12)',
    headerBg: '#e6f2fb',
    headerBorder: '#bfdcf4',
    gridSeparator: '#bfdcf4',
  },
};

function normalizeIso2ForFlagcdn(code) {
  if (!code || typeof code !== 'string') return '';
  const c = code.trim().toLowerCase();
  const iso = c === 'uk' ? 'gb' : c;
  return iso.length === 2 ? iso : '';
}

function resolveCountryIsoForFlagAsset(raw) {
  const n = (raw || '').trim().toLowerCase();
  if (!n) return '';
  const direct = normalizeIso2ForFlagcdn(n);
  if (direct) return direct;
  return COUNTRY_SLUG_TO_ISO2[n] || '';
}

/** Raster flag — reliable on Edge/Windows where emoji flags often render as blank or letters. */
function getCountryFlagImageUrl(iso2) {
  if (!iso2 || iso2.length !== 2) return '';
  // flagcdn supports w20, w40, … — w16 is not a valid path and returns 404 for all flags.
  const code = String(iso2).toLowerCase();
  return `https://flagcdn.com/w20/${code}.png`;
}

function escapeHtmlForTooltip(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Replace `[[CMPFLAG:xx]]` tokens (emitted by formatIndexCompareHoverSuffix) with an inline PNG
 * flag of the country whose reference product the SKU is compared to. Run AFTER escapeHtmlForTooltip
 * (the token is plain ASCII so it survives escaping).
 */
function injectCompareFlagImages(escapedText) {
  return String(escapedText).replace(/\[\[CMPFLAG:([a-z]{2})\]\]/gi, (match, iso) => {
    const url = getCountryFlagImageUrl(iso);
    return url
      ? `<img src="${url}" width="${FLAG_IMG_DISPLAY_W}" height="${FLAG_IMG_DISPLAY_H}" alt="" style="vertical-align:middle;margin:0 3px 0 1px;border-radius:1px;object-fit:cover"/>`
      : '';
  });
}

/** Max vertical distance (px) from cursor to a line point before we stop highlighting. */
const TOOLTIP_HIGHLIGHT_MAX_Y_PX = 36;

/** Keeps canvas Y of the last pointer move for crosshair tooltip row highlighting. */
const chartPointerYPlugin = {
  id: 'chartPointerY',
  beforeEvent(chart, args) {
    const { event } = args;
    if (!event) return;
    if (event.type === 'mouseout' || event.type === 'mouseleave') {
      chart.$hoverPointerY = null;
      return;
    }
    if (event.y != null && Number.isFinite(event.y)) {
      chart.$hoverPointerY = event.y;
    }
  },
};

/** Emphasize all series points on the hovered Y band at the crosshair date. */
const crosshairClosestPointPlugin = {
  id: 'crosshairClosestPoint',
  afterDatasetsDraw(chart) {
    const tooltip = chart.tooltip;
    if (!tooltip?.opacity || !tooltip.dataPoints?.length) return;
    const highlighted = highlightedTooltipDataPointIndices(chart, tooltip.dataPoints);
    if (!highlighted.size) return;
    const ctx = chart.ctx;
    highlighted.forEach((idx) => {
      const dp = tooltip.dataPoints[idx];
      const meta = chart.getDatasetMeta(dp.datasetIndex);
      if (meta?.hidden) return;
      const pt = meta.data?.[dp.dataIndex];
      if (!pt || !Number.isFinite(pt.x) || !Number.isFinite(pt.y)) return;
      const color = dp.dataset?.borderColor || '#666';
      ctx.save();
      ctx.beginPath();
      ctx.arc(pt.x, pt.y, 5, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();
      ctx.lineWidth = 2;
      ctx.strokeStyle = '#ffffff';
      ctx.stroke();
      ctx.restore();
    });
  },
};

/**
 * Crosshair tooltip rows to emphasize: all lines on the Y band nearest the cursor.
 * Returns empty set when the cursor is not close enough to any visible line.
 */
function highlightedTooltipDataPointIndices(chart, dataPoints) {
  const empty = new Set();
  const mouseY = chart?.$hoverPointerY ?? chart?.lastEvent?.y;
  if (mouseY == null || !Number.isFinite(mouseY) || !dataPoints?.length) return empty;
  const plotH = chart?.chartArea
    ? Math.max(1, chart.chartArea.bottom - chart.chartArea.top)
    : 320;
  const maxDist = Math.min(TOOLTIP_HIGHLIGHT_MAX_Y_PX, plotH * 0.12);
  const sameLevelPx = Math.min(COUNTRY_END_LABEL_CLUSTER_PX, plotH * 0.04);
  const candidates = [];
  dataPoints.forEach((dp, idx) => {
    const yVal = dp.parsed?.y;
    if (yVal == null || !Number.isFinite(yVal)) return;
    const meta = chart.getDatasetMeta(dp.datasetIndex);
    if (meta?.hidden) return;
    const pt = meta.data?.[dp.dataIndex];
    if (!pt || !Number.isFinite(pt.y)) return;
    candidates.push({ idx, ptY: pt.y, dist: Math.abs(pt.y - mouseY) });
  });
  if (!candidates.length) return empty;
  candidates.sort((a, b) => a.dist - b.dist);
  const nearest = candidates[0];
  if (nearest.dist > maxDist) return empty;
  const levelY = nearest.ptY;
  const highlighted = new Set();
  candidates.forEach(({ idx, ptY }) => {
    if (Math.abs(ptY - levelY) <= sameLevelPx) highlighted.add(idx);
  });
  return highlighted;
}

/**
 * Chart.js custom HTML tooltip so country rows can show PNG flags (canvas/default tooltip is text-only).
 * Expects `formatLine` to match the shape expected by `priceTooltipFormatter`.
 */
function createPriceChartExternalTooltipHandler(formatLine, { highlightClosestLine = true } = {}) {
  return function externalTooltipHandler(context) {
    const { chart, tooltip } = context;
    const parent = chart?.canvas?.parentNode;
    if (!parent || parent.nodeType !== 1) return;

    let el = parent.querySelector('[data-jti-price-chart-tooltip="1"]');
    if (!el) {
      el = document.createElement('div');
      el.setAttribute('data-jti-price-chart-tooltip', '1');
      el.style.position = 'absolute';
      el.style.pointerEvents = 'none';
      el.style.zIndex = '25';
      el.style.background = 'rgba(255,255,255,0.97)';
      el.style.border = '1px solid rgba(0,0,0,0.08)';
      el.style.borderRadius = '6px';
      el.style.padding = '8px 10px';
      el.style.fontSize = '11px';
      el.style.lineHeight = '1.3';
      el.style.boxShadow = '0 2px 10px rgba(0,0,0,0.12)';
      el.style.maxWidth = '420px';
      if (!parent.style.position || parent.style.position === 'static') {
        parent.style.position = 'relative';
      }
      parent.appendChild(el);
    }

    if (tooltip.opacity === 0) {
      el.style.opacity = '0';
      el.style.visibility = 'hidden';
      return;
    }

    const titleLines = tooltip.title || [];
    const headerHtml =
      titleLines.length > 0
        ? `<div style="font-weight:600;margin-bottom:6px;border-bottom:1px solid rgba(0,0,0,0.08);padding-bottom:4px">${titleLines.map(escapeHtmlForTooltip).join('<br/>')}</div>`
        : '';

    const points = tooltip.dataPoints || [];
    const highlightedIndices = highlightClosestLine
      ? highlightedTooltipDataPointIndices(chart, points)
      : new Set();
    const hasHighlight = highlightedIndices.size > 0;
    // Build row data, then deduplicate identical rows (same flag + rendered text, e.g. a
    // listed and promo line that sit at the same price). Keep the first occurrence and
    // preserve highlighting if any of the merged duplicates was highlighted.
    const dedupedRows = [];
    const seenRowKeys = new Map();
    points.forEach((dp, rowIdx) => {
      const dataset = dp.dataset;
      const dataIndex = dp.dataIndex;
      const fakeCtx = {
        chart,
        dataset,
        parsed: dp.parsed,
        dataIndex,
        datasetIndex: dp.datasetIndex,
      };
      let text = '';
      try {
        text = formatLine(fakeCtx);
      } catch (e) {
        text = '';
      }
      const iso = dataset?.legendCountryIso;
      const url = iso ? getCountryFlagImageUrl(iso) : '';
      const lineColor = dataset?.borderColor || '#666';
      const isHighlighted = highlightedIndices.has(rowIdx);
      const rowEntry = { text, url, lineColor, isHighlighted };
      // Only dedupe rows that actually render text; keep blank rows as-is.
      if (text) {
        const key = `${url}\u0001${text}`;
        const existing = seenRowKeys.get(key);
        if (existing) {
          if (isHighlighted) existing.isHighlighted = true;
          return;
        }
        seenRowKeys.set(key, rowEntry);
      }
      dedupedRows.push(rowEntry);
    });
    const rows = dedupedRows
      .map(({ text, url, lineColor, isHighlighted }) => {
        const img = url
          ? `<img src="${url}" width="${FLAG_IMG_DISPLAY_W}" height="${FLAG_IMG_DISPLAY_H}" alt="" style="vertical-align:middle;margin:0 5px 0 0;border-radius:1px;object-fit:cover;flex-shrink:0"/>`
          : '';
        const rowStyle = isHighlighted
          ? `display:flex;align-items:center;gap:4px;margin:1px -2px;padding:3px 5px;line-height:1.25;font-weight:700;background:rgba(0,0,0,0.06);border-radius:4px;border-left:3px solid ${escapeHtmlForTooltip(lineColor)}`
          : `display:flex;align-items:center;gap:4px;margin:2px 0;line-height:1.25;opacity:${hasHighlight ? '0.55' : '1'}`;
        const textStyle = isHighlighted
          ? 'flex:1;min-width:0;color:inherit'
          : 'flex:1;min-width:0';
        return `<div style="${rowStyle}">${img}<span style="${textStyle}">${injectCompareFlagImages(escapeHtmlForTooltip(text))}</span></div>`;
      })
      .join('');

    el.innerHTML = headerHtml + rows;
    el.style.opacity = '1';
    el.style.visibility = 'visible';

    const canvas = chart.canvas;
    const pad = 6;
    const gapX = 12;
    const gapY = 8;
    const parentW = parent.clientWidth;
    const parentH = parent.clientHeight;
    const maxW = Math.max(80, Math.min(420, parentW - pad * 2));
    el.style.maxWidth = `${maxW}px`;

    const caretLeft = canvas.offsetLeft + (tooltip.caretX || 0);
    const caretTop = canvas.offsetTop + (tooltip.caretY || 0);
    // Prefer to the right of the caret; flip to the left if there is not enough room.
    let x = caretLeft + gapX;
    let y = caretTop + gapY;
    el.style.left = `${Math.max(0, x)}px`;
    el.style.top = `${Math.max(0, y)}px`;

    const tw = el.offsetWidth;
    const th = el.offsetHeight;
    if (x + tw > parentW - pad) {
      x = caretLeft - tw - gapX;
    }
    x = Math.min(Math.max(pad, x), Math.max(pad, parentW - tw - pad));
    if (y + th > parentH - pad) {
      y = caretTop - th - gapY;
    }
    y = Math.min(Math.max(pad, y), Math.max(pad, parentH - th - pad));

    el.style.left = `${x}px`;
    el.style.top = `${y}px`;
  };
}

function CountryLegendFlag({ legendGroupId, emoji }) {
  const iso = useMemo(() => {
    if (typeof legendGroupId !== 'string' || !legendGroupId.startsWith('cg:')) return '';
    const rest = legendGroupId.slice(3);
    const pipeIdx = rest.indexOf('|');
    const raw = pipeIdx >= 0 ? rest.slice(0, pipeIdx) : rest;
    return resolveCountryIsoForFlagAsset(raw);
  }, [legendGroupId]);
  const src = iso ? getCountryFlagImageUrl(iso) : '';
  const [imgBroken, setImgBroken] = useState(false);
  useEffect(() => {
    setImgBroken(false);
  }, [src]);
  if (src && !imgBroken) {
    return (
      <Box
        component="img"
        src={src}
        alt=""
        loading="lazy"
        decoding="async"
        referrerPolicy="no-referrer"
        onError={() => setImgBroken(true)}
        sx={{
          width: FLAG_IMG_DISPLAY_W,
          height: FLAG_IMG_DISPLAY_H,
          objectFit: 'cover',
          flexShrink: 0,
          borderRadius: '1px',
          display: 'block',
        }}
      />
    );
  }
  if (emoji) {
    return (
      <Box component="span" aria-hidden sx={{ fontSize: '0.6rem', lineHeight: 1, flexShrink: 0 }}>
        {emoji}
      </Box>
    );
  }
  return null;
}

const InteractiveLegend = React.memo(({
  datasets,
  visibleDatasets,
  onToggleDataset,
  onShowAll,
  onHideAll,
  searchTerm,
  onSearchChange,
  activePriceTypes,
  onPriceTypeToggle,
  priceTypeStyles,
  onPriceTypeStyleChange,
  stockLineStyles,
  onStockLineStyleChange,
  showStockLineStyles = false,
  onAxisSettingsOpen,
  axisSettingsActive,
  legendExpanded = true,
  onLegendExpandToggle,
  accentColor = STYLE_PRESETS.classic.accent,
}) => {
  const stripTrailingCountryCodeSuffix = (text) => {
    if (!text || typeof text !== 'string') return text || '';
    return text.replace(/\s*\(([A-Z]{2}|[A-Z]{3})\)\s*$/g, '').trim();
  };

  const filteredDatasets = useMemo(() => {
    const allSeries = datasets || [];
    const byGroup = new Map();
    allSeries.forEach((ds) => {
      const key = ds.legendGroupId || ds.label;
      if (!byGroup.has(key)) byGroup.set(key, []);
      byGroup.get(key).push(ds);
    });
    const rows = [];
    byGroup.forEach((group, key) => {
      const primaryGroup = group.filter((d) => d.legendPrimary !== false);
      const sourceGroup = primaryGroup.length ? primaryGroup : group;
      const hasPromoPrimary = sourceGroup.some((d) => d.priceType === 'promo');
      const rep = [...sourceGroup].sort((a, b) =>
        (a.legendLabel || a.displayLabel || a.label || '').localeCompare(b.legendLabel || b.displayLabel || b.label || '')
      )[0];
      const gid = rep.legendGroupId && String(rep.legendGroupId);
      const isPgOrCg = gid && (gid.startsWith('pg:') || gid.startsWith('cg:'));
      const isUngrouped = gid && gid.startsWith('u:');
      const groupTitle = (rep.productGroupName || '').trim();
      const ungroupedTitle = (
        rep.displayLabel ||
        rep.productName ||
        (rep.label || '').replace(/\s*-\s*(Actual Price|Regular Price|Promo Price|Index|Promo Index).*$/i, '').trim()
      );
      let legendDisplayText;
      if (isPgOrCg) {
        if (hasPromoPrimary) {
          legendDisplayText = groupTitle ? `${groupTitle} - Promo Price` : 'Promo Price';
        } else {
          legendDisplayText = groupTitle || rep.legendLabel || rep.displayLabel || rep.label;
        }
      } else if (isUngrouped) {
        const nm = ungroupedTitle || rep.legendLabel || rep.label;
        if (hasPromoPrimary) {
          legendDisplayText = `${nm} - Promo Price`;
        } else {
          legendDisplayText = nm;
        }
      } else {
        legendDisplayText = rep.productGroupName || rep.legendLabel || rep.displayLabel || rep.label;
      }
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        const matchText = (legendDisplayText || '').toLowerCase().includes(q);
        const matchMember = group.some((d) =>
          (d.legendLabel || d.displayLabel || d.label || '').toLowerCase().includes(q)
        );
        if (!matchText && !matchMember) return;
      }
      let legendFlagEmoji = '';
      if (!(gid && gid.startsWith('cg:')) && rep.legendFlagEmoji) {
        legendFlagEmoji = rep.legendFlagEmoji;
      }
      if (gid && gid.startsWith('cg:')) {
        legendDisplayText = stripTrailingCountryCodeSuffix(legendDisplayText);
      }
      legendDisplayText = appendBundleLabelSuffix(legendDisplayText, rep);
      rows.push({
        ...rep,
        legendGroupId: key,
        legendDisplayText,
        legendFlagEmoji,
      });
    });
    return rows.sort((a, b) => (a.legendDisplayText || '').localeCompare(b.legendDisplayText || ''));
  }, [datasets, searchTerm]);

  const labelsForLegendGroup = (all, gid) => {
    if (gid == null || gid === '') return [];
    const g = String(gid);
    return (all || []).filter((d) => d.legendGroupId != null && String(d.legendGroupId) === g).map((d) => d.label);
  };

  const isLegendLabelOn = (label) => isPriceLegendSeriesVisible(visibleDatasets, label);

  const allVisible = filteredDatasets.length > 0 &&
    filteredDatasets.every((ds) => {
      const gid = ds.legendGroupId;
      if (!gid) return isLegendLabelOn(ds.label);
      const labels = labelsForLegendGroup(datasets, gid);
      return labels.length > 0 && labels.every((l) => isLegendLabelOn(l));
    });
  const allHidden = filteredDatasets.length > 0 &&
    filteredDatasets.every((ds) => {
      const gid = ds.legendGroupId;
      if (!gid) return !isLegendLabelOn(ds.label);
      const labels = labelsForLegendGroup(datasets, gid);
      return labels.length > 0 && labels.every((l) => !isLegendLabelOn(l));
    });

  return (
    <Box sx={{ pt: 0.5, borderTop: '1px solid #e0e0e0', mt: 0.5, position: 'relative', zIndex: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: legendExpanded ? 0.5 : 0, flexWrap: 'wrap' }}>
        {legendExpanded && (
        <>
        <Button
          size="small"
          variant="outlined"
          onClick={onShowAll}
          disabled={allVisible}
          sx={{
            fontSize: '0.6rem',
            py: 0.1,
            px: 0.75,
            minWidth: 0,
            height: 20,
            lineHeight: 1,
            color: accentColor,
            borderColor: accentColor,
            '&:hover': { borderColor: accentColor, backgroundColor: `${accentColor}14` },
          }}
        >
          All
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={onHideAll}
          disabled={allHidden}
          sx={{
            fontSize: '0.6rem',
            py: 0.1,
            px: 0.75,
            minWidth: 0,
            height: 20,
            lineHeight: 1,
            color: accentColor,
            borderColor: accentColor,
            '&:hover': { borderColor: accentColor, backgroundColor: `${accentColor}14` },
          }}
        >
          None
        </Button>
        <TextField
          size="small"
          placeholder="Search…"
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: '0.8rem' }} /></InputAdornment> }}
          sx={{ '& .MuiInputBase-root': { height: 20, fontSize: '0.65rem' }, '& .MuiInputBase-input': { py: 0, px: 0.5 }, width: 110 }}
        />
        {/* Per-type line style selectors — only for active price types */}
        {activePriceTypes && activePriceTypes.size > 0 && (
          <>
            <Box sx={{ borderLeft: '1px solid #e0e0e0', height: 16, mx: 0.25, alignSelf: 'center' }} />
            {PRICE_TYPE_OPTIONS.filter(({ key }) => activePriceTypes.has(key)).map(({ key, label }) => {
              const style = (priceTypeStyles && priceTypeStyles[key]) || 'solid';
              const dashArray = { solid: '', dashed: '5 5', dotted: '2 4' }[style] || '';
              return (
                <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                  <svg width="14" height="6" style={{ flexShrink: 0 }}>
                    <line x1="0" y1="3" x2="14" y2="3" stroke="#444" strokeWidth="2"
                      strokeDasharray={dashArray} strokeLinecap="round" />
                  </svg>
                  <Typography sx={{ fontSize: '0.6rem', whiteSpace: 'nowrap', color: 'text.secondary' }}>
                    {label}:
                  </Typography>
                  <Select
                    value={style}
                    onChange={(e) => onPriceTypeStyleChange && onPriceTypeStyleChange(key, e.target.value)}
                    size="small"
                    sx={{
                      height: 18, fontSize: '0.6rem',
                      '& .MuiSelect-select': { py: 0, px: 0.5, fontSize: '0.6rem' },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0,0,0,0.18)' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: accentColor },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: accentColor, borderWidth: 1 },
                      '& .MuiSvgIcon-root': { fontSize: 14, right: 0, color: accentColor },
                    }}
                  >
                    <MenuItem value="solid"  sx={{ fontSize: '0.65rem' }}>─ Solid</MenuItem>
                    <MenuItem value="dashed" sx={{ fontSize: '0.65rem' }}>╌ Dashed</MenuItem>
                    <MenuItem value="dotted" sx={{ fontSize: '0.65rem' }}>⋯ Dotted</MenuItem>
                  </Select>
                </Box>
              );
            })}
          </>
        )}
        {showStockLineStyles && stockLineStyles && onStockLineStyleChange && (
          <>
            <Box sx={{ borderLeft: '1px solid #e0e0e0', height: 16, mx: 0.25, alignSelf: 'center' }} />
            {STOCK_SEGMENT_LINE_OPTIONS.map(({ key, label }) => {
              const style = stockLineStyles[key] || (key === 'oos' ? 'dotted' : 'solid');
              const dashArray = { solid: '', dashed: '5 5', dotted: '2 4' }[style] || '';
              return (
                <Box key={`stock-${key}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                  <svg width="14" height="6" style={{ flexShrink: 0 }}>
                    <line x1="0" y1="3" x2="14" y2="3" stroke="#444" strokeWidth="2"
                      strokeDasharray={dashArray} strokeLinecap="round" />
                  </svg>
                  <Typography sx={{ fontSize: '0.6rem', whiteSpace: 'nowrap', color: 'text.secondary' }}>
                    {label}:
                  </Typography>
                  <Select
                    value={style}
                    onChange={(e) => onStockLineStyleChange(key, e.target.value)}
                    size="small"
                    sx={{
                      height: 18, fontSize: '0.6rem',
                      '& .MuiSelect-select': { py: 0, px: 0.5, fontSize: '0.6rem' },
                      '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0,0,0,0.18)' },
                      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: accentColor },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: accentColor, borderWidth: 1 },
                      '& .MuiSvgIcon-root': { fontSize: 14, right: 0, color: accentColor },
                    }}
                  >
                    <MenuItem value="solid"  sx={{ fontSize: '0.65rem' }}>─ Solid</MenuItem>
                    <MenuItem value="dashed" sx={{ fontSize: '0.65rem' }}>╌ Dashed</MenuItem>
                    <MenuItem value="dotted" sx={{ fontSize: '0.65rem' }}>⋯ Dotted</MenuItem>
                  </Select>
                </Box>
              );
            })}
          </>
        )}
        </>
        )}
        <Box sx={{ flex: 1 }} />
        {onLegendExpandToggle && (
          <Tooltip title={legendExpanded ? 'Collapse legend' : 'Expand legend'} arrow>
            <IconButton
              size="small"
              onClick={onLegendExpandToggle}
              sx={{ color: 'text.secondary', p: 0.25 }}
            >
              {legendExpanded ? <ExpandLess sx={{ fontSize: '0.95rem' }} /> : <ExpandMore sx={{ fontSize: '0.95rem' }} />}
            </IconButton>
          </Tooltip>
        )}
        {onAxisSettingsOpen && (
          <Tooltip title="Axis settings" arrow>
            <IconButton
              size="small"
              onClick={(e) => onAxisSettingsOpen(e.currentTarget)}
              sx={{ color: axisSettingsActive ? accentColor : 'text.disabled', p: 0.25 }}
            >
              <SettingsIcon sx={{ fontSize: '0.95rem' }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {legendExpanded && (
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gridTemplateRows: `repeat(${Math.ceil(filteredDatasets.length / 5)}, auto)`, gridAutoFlow: 'column', gap: '2px 6px', maxHeight: 120, overflowY: 'auto' }}>
        {filteredDatasets.length === 0 ? (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>No matches</Typography>
        ) : (
          filteredDatasets.map((dataset, idx) => {
            const gid = dataset.legendGroupId;
            const labelsInGroup = gid
              ? labelsForLegendGroup(datasets, gid)
              : [dataset.label];
            const isVisible =
              labelsInGroup.length > 0 && labelsInGroup.every((l) => isLegendLabelOn(l));
            return (
              <Box
                key={gid || dataset.label || idx}
                onClick={() => onToggleDataset(gid || dataset.label)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '2px',
                  minWidth: 0,
                  cursor: 'pointer',
                  opacity: isVisible ? 1 : 0.4,
                  px: 0.25,
                  py: '1px',
                  borderRadius: '3px',
                  overflow: 'hidden',
                  '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' },
                }}
              >
                <svg width="14" height="4" style={{ flexShrink: 0 }}>
                  <line
                    x1="0" y1="2" x2="14" y2="2"
                    stroke={dataset.borderColor || dataset.backgroundColor}
                    strokeWidth="2.5"
                    strokeDasharray={
                      dataset.borderDash && dataset.borderDash.length
                        ? dataset.borderDash.join(' ')
                        : undefined
                    }
                    strokeLinecap="round"
                  />
                </svg>
                <CountryLegendFlag legendGroupId={gid} emoji={dataset.legendFlagEmoji} />
                <Typography sx={{
                  fontSize: '0.62rem',
                  lineHeight: 1.25,
                  minWidth: 0,
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  textDecoration: isVisible ? 'none' : 'line-through',
                  color: isVisible ? 'text.primary' : 'text.secondary',
                }}>
                  {dataset.legendDisplayText || dataset.legendLabel || dataset.displayLabel || dataset.label}
                </Typography>
              </Box>
            );
          })
        )}
      </Box>
      )}
    </Box>
  );
});

const HIDE_CHART_TABLE = true; // Hide legacy raw filtered-data grid below chart; series summary table above stays visible

/** Compact chart snapshot for AI price analysis (index or absolute; line labels, variant evolution, promos). */
const buildPriceChartAiPayload = (priceHistoryData, { baseProduct, priceMode, currency, scopeMode } = {}) => {
  if (!priceHistoryData?.datasets?.length) return null;
  const isIndex = priceMode === 'Index';
  const dateLabels = priceHistoryData.labels || [];
  const series = priceHistoryData.datasets
    .filter((ds) => ds && !ds.hidden)
    .map((ds) => {
      const lineLabel = String(
        ds.legendLabel || ds.displayLabel || ds.productName || ds.label || 'Unknown'
      ).trim();
      const data = Array.isArray(ds.data) ? ds.data : [];
      const validIdx = [];
      data.forEach((v, i) => {
        if (v != null && Number.isFinite(v) && v > 0) validIdx.push(i);
      });
      const firstIdx = validIdx[0];
      const lastIdx = validIdx[validIdx.length - 1];

      const labelChanges = [];
      const gv = ds.groupedVariantTitlesAtIndex;
      if (Array.isArray(gv) && gv.length && dateLabels.length) {
        let prevTitles = null;
        for (let i = 0; i < gv.length; i += 1) {
          const curr = Array.isArray(gv[i]) ? [...new Set(gv[i])].sort() : null;
          if (!curr?.length || !dateLabels[i]) continue;
          if (prevTitles) {
            const prevSet = new Set(prevTitles);
            const currSet = new Set(curr);
            const added = curr.filter((t) => !prevSet.has(t));
            const removed = prevTitles.filter((t) => !currSet.has(t));
            if (added.length || removed.length) {
              labelChanges.push({
                date: dateLabels[i],
                active_variants: curr,
                added,
                removed,
              });
            }
          }
          prevTitles = curr;
        }
      }

      const promoEvents = [];
      if (Array.isArray(ds.regularPrices)) {
        ds.regularPrices.forEach((reg, i) => {
          const actual = data[i];
          if (
            reg != null &&
            actual != null &&
            Number.isFinite(reg) &&
            Number.isFinite(actual) &&
            reg > 0 &&
            actual > 0 &&
            Math.abs(reg - actual) > 0.01
          ) {
            const event = {
              date: dateLabels[i] || null,
            };
            if (isIndex) {
              event.listed_index = Math.round(reg * 100) / 100;
              event.promo_index = Math.round(actual * 100) / 100;
            } else {
              event.listed_price = Math.round(reg * 100) / 100;
              event.promo_price = Math.round(actual * 100) / 100;
            }
            promoEvents.push(event);
          }
        });
      }

      const roundVal = (idx) =>
        idx != null ? Math.round(data[idx] * 100) / 100 : null;

      return {
        line_label: lineLabel,
        price_type: ds.priceType || null,
        product_name: ds.productName || null,
        product_group: ds.productGroupName || null,
        country: ds.country || null,
        start_date: firstIdx != null ? dateLabels[firstIdx] : null,
        end_date: lastIdx != null ? dateLabels[lastIdx] : null,
        start_index: isIndex ? roundVal(firstIdx) : null,
        end_index: isIndex ? roundVal(lastIdx) : null,
        start_price: !isIndex ? roundVal(firstIdx) : null,
        end_price: !isIndex ? roundVal(lastIdx) : null,
        label_changes: labelChanges,
        promo_events: promoEvents.slice(0, 30),
        variant_count_at_end:
          Array.isArray(gv) && lastIdx != null && Array.isArray(gv[lastIdx]) ? gv[lastIdx].length : null,
      };
    });

  return {
    price_mode: isIndex ? 'index' : 'absolute',
    base_product: isIndex ? (baseProduct || null) : null,
    currency: currency || null,
    scope: scopeMode || null,
    start_date: dateLabels[0] || null,
    end_date: dateLabels[dateLabels.length - 1] || null,
    date_count: dateLabels.length,
    series_count: series.length,
    series,
  };
};

const CHANGE_FEED_EVENT_META = {
  price_drop: { label: 'Price drop', color: '#f9a825' },
  price_increase: { label: 'Price increase', color: '#ffb74d' },
  sku_price_drop: { label: 'SKU price drop', color: '#f57c00' },
  sku_price_increase: { label: 'SKU price increase', color: '#ffb74d' },
  promo_started: { label: 'Promotion started', color: '#1565c0' },
  promo_ended: { label: 'Promotion ended', color: '#546e7a' },
  stock_oos: { label: 'Out of stock', color: '#9e9e9e' },
  stock_avail: { label: 'Back in stock', color: '#9c27b0' },
  sku_appeared: { label: 'SKU added', color: '#43a047' },
  sku_disappeared: { label: 'SKU removed', color: '#e57373' },
  sku_replaced: { label: 'SKU replaced', color: '#1976d2' },
  variant_added: { label: 'Variant added', color: '#6a1b9a' },
  variant_removed: { label: 'Variant removed', color: '#6a1b9a' },
  appeared: { label: 'First seen', color: '#43a047' },
  position_change: { label: 'Position change', color: '#5d4037' },
};

/** Temporarily hidden from feed + settings UI (re-enable by removing from this set). */
const CHANGE_FEED_TEMPORARILY_HIDDEN_EVENT_TYPES = new Set([
  'price_drop',
  'price_increase',
  'promo_started',
  'promo_ended',
  'variant_added',
  'variant_removed',
  'appeared',
  'position_change',
]);

const isChangeFeedEventTypeTemporarilyHidden = (type) =>
  CHANGE_FEED_TEMPORARILY_HIDDEN_EVENT_TYPES.has(type);

const getChangeFeedConfigurableEventTypes = () =>
  Object.keys(CHANGE_FEED_EVENT_META).filter((k) => !isChangeFeedEventTypeTemporarilyHidden(k));

const CHANGE_FEED_SETTINGS_STORAGE_KEY = 'jti-change-feed-event-settings-v2';
const CHANGE_FEED_SETTINGS_STORAGE_KEY_LEGACY = 'jti-change-feed-event-settings';

const defaultChangeFeedEventSettings = () => {
  const visible = Object.fromEntries(Object.keys(CHANGE_FEED_EVENT_META).map((k) => [k, true]));
  CHANGE_FEED_TEMPORARILY_HIDDEN_EVENT_TYPES.forEach((type) => {
    visible[type] = false;
  });
  return {
    visible,
    colors: Object.fromEntries(
      Object.entries(CHANGE_FEED_EVENT_META).map(([k, meta]) => [k, meta.color])
    ),
  };
};

const loadChangeFeedEventSettings = () => {
  const defaults = defaultChangeFeedEventSettings();
  try {
    const v2Raw = localStorage.getItem(CHANGE_FEED_SETTINGS_STORAGE_KEY);
    const legacyRaw = localStorage.getItem(CHANGE_FEED_SETTINGS_STORAGE_KEY_LEGACY);
    const raw = v2Raw || legacyRaw;
    if (!raw) return defaults;
    const parsed = JSON.parse(raw);
    const visible = { ...defaults.visible };
    if (v2Raw && parsed.visible) {
      getChangeFeedConfigurableEventTypes().forEach((type) => {
        if (Object.prototype.hasOwnProperty.call(parsed.visible, type)) {
          visible[type] = !!parsed.visible[type];
        }
      });
    }
    CHANGE_FEED_TEMPORARILY_HIDDEN_EVENT_TYPES.forEach((type) => {
      visible[type] = false;
    });
    return {
      visible,
      colors: { ...defaults.colors, ...(parsed.colors || {}) },
    };
  } catch {
    return defaults;
  }
};

const resolveChangeFeedEventMeta = (type, eventSettings) => {
  const base = CHANGE_FEED_EVENT_META[type] || { label: type, color: '#607d8b' };
  return {
    label: base.label,
    color: eventSettings?.colors?.[type] || base.color,
  };
};

const ChangeFeedEventSettings = ({ eventSettings, onToggleVisible, onColorChange, onReset }) => (
  <Box
    sx={{
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      bgcolor: 'background.paper',
      px: 1.25,
      py: 1.25,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1, flexShrink: 0 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: '0.04em' }}>
        Event types
      </Typography>
      <Button size="small" onClick={onReset} sx={{ minWidth: 0, fontSize: '0.72rem', py: 0.25 }}>
        Reset colours
      </Button>
    </Box>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, overflow: 'auto', minHeight: 0 }}>
      {getChangeFeedConfigurableEventTypes().map((type) => {
        const meta = CHANGE_FEED_EVENT_META[type];
        const color = eventSettings.colors[type] || meta.color;
        const checked = eventSettings.visible[type] !== false;
        return (
          <Box
            key={type}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 0.75,
              py: 0.5,
              borderRadius: 1,
              border: '1px solid',
              borderColor: checked ? `${color}55` : 'divider',
              bgcolor: checked ? `${color}12` : 'action.hover',
              opacity: checked ? 1 : 0.72,
            }}
          >
            <Checkbox
              size="small"
              checked={checked}
              onChange={() => onToggleVisible(type)}
              sx={{ p: 0.25 }}
            />
            <Box
              component="input"
              type="color"
              value={color}
              onChange={(e) => onColorChange(type, e.target.value)}
              title={`Colour for ${meta.label}`}
              sx={{
                width: 22,
                height: 22,
                p: 0,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 0.5,
                cursor: 'pointer',
                bgcolor: 'transparent',
                flexShrink: 0,
                '&::-webkit-color-swatch-wrapper': { p: 0 },
                '&::-webkit-color-swatch': { border: 'none', borderRadius: 3 },
              }}
            />
            <Typography variant="caption" sx={{ fontSize: '0.72rem', fontWeight: 600, lineHeight: 1.3 }}>
              {meta.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  </Box>
);

const formatChangeFeedDateKey = (dateKey) => {
  if (!dateKey) return 'Unknown date';
  const d = dayjs(dateKey);
  return d.isValid() ? d.format('ddd, MMM D, YYYY') : String(dateKey);
};

const dedupeFeedEvents = (events) => {
  const seen = new Set();
  return events.filter((ev) => {
    const key = [
      ev.date,
      ev.type,
      ev.product || '',
      ev.country || '',
      ev.retailer || '',
      ev.detail || '',
      ev.priceType || '',
      ev.oldValue,
      ev.newValue,
      ev.oldProduct || '',
      ev.newProduct || '',
    ].join('\0');
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const feedParseNumericValue = (value) => {
  if (value === null || value === undefined) return NaN;
  if (typeof value === 'number') return value;
  const raw = (value || '').toString().trim();
  if (!raw) return NaN;
  let cleaned = raw.replace(/[^\d.,-]/g, '');
  if (!cleaned) return NaN;
  const hasComma = cleaned.includes(',');
  const hasDot = cleaned.includes('.');
  if (hasComma && hasDot) cleaned = cleaned.replace(/,/g, '');
  else if (hasComma && !hasDot) cleaned = cleaned.replace(/,/g, '.');
  const num = parseFloat(cleaned);
  return Number.isFinite(num) ? num : NaN;
};

const feedEffectiveCategoryGroup = (item, categoryOverrides) => {
  const title = item?.title;
  return (title && categoryOverrides?.[title]) || item?.category_group || '';
};

const feedFirstRowByTitle = (rows) => {
  const map = new Map();
  (rows || []).forEach((item) => {
    const t = item?.title;
    if (!t || map.has(t)) return;
    map.set(t, item);
  });
  return map;
};

const feedIsStarterKitProduct = (title, firstRowByTitle, productFlags, categoryOverrides) => {
  if (!title) return false;
  if (productFlags?.[title]?.isStarterKitOrRegistration) return true;
  const row = firstRowByTitle.get(title);
  if (!row) return false;
  const catNorm = String(feedEffectiveCategoryGroup(row, categoryOverrides) || '').trim().toLowerCase();
  return catNorm === 'starter kits' || catNorm === 'starter kit';
};

const feedIsBundleProduct = (title, firstRowByTitle, productFlags, categoryOverrides) => {
  if (!title || feedIsStarterKitProduct(title, firstRowByTitle, productFlags, categoryOverrides)) return false;
  const row = firstRowByTitle.get(title);
  if (!row) return false;
  return isBundleCategoryGroup(feedEffectiveCategoryGroup(row, categoryOverrides));
};

/** Match chart legend: bundles on promo; starter kits use regular/promo/actual like other SKUs. */
const feedSkuIncludedForPriceType = (title, priceType, firstRowByTitle, productFlags, categoryOverrides) => {
  const isBundle = feedIsBundleProduct(title, firstRowByTitle, productFlags, categoryOverrides);
  if (isBundle) return priceType === 'promo';
  if (priceType === 'promo') return !!(productFlags?.[title]?.isPromoOffer);
  if (priceType === 'regular') return true;
  if (priceType === 'actual') return !productFlags?.[title]?.isPromoOffer;
  return true;
};

const feedExtractPricesForRow = (item, title, firstRowByTitle, productFlags, categoryOverrides, spreadTol) => {
  let actual = feedParseNumericValue(item?.actual_price || item?.price || item?.current_price);
  let regular = feedParseNumericValue(item?.regular_price || item?.original_price);
  ({ actual, regular } = normalizeActualAndRegularPrices(actual, regular));
  const prices = { actual: null, regular: null, promo: null };
  if (Number.isFinite(actual) && actual > 0) prices.actual = actual;
  if (Number.isFinite(regular) && regular > 0) prices.regular = regular;
  if (isPromoPriceSpread(actual, regular, spreadTol)) prices.promo = actual;
  return prices;
};

const feedBuildPairMarketMaps = (rows) => {
  const pairMarketDaysScratch = new Map();
  const pairDateToTitles = new Map();
  (rows || []).forEach((item) => {
    const country = item?.country;
    const title = item?.title;
    const retailer = (item?.retailer && String(item.retailer).trim()) || '';
    if (!country || !title || !item?.updated_at) return;
    let dateKey;
    try {
      const d = new Date(item.updated_at);
      if (isNaN(d.getTime())) return;
      dateKey = d.toISOString().slice(0, 10);
    } catch (e) {
      return;
    }
    const pk = `${country}\x00${retailer}`;
    if (!pairMarketDaysScratch.has(pk)) pairMarketDaysScratch.set(pk, new Set());
    pairMarketDaysScratch.get(pk).add(dateKey);
    const dkey = `${pk}\x00${dateKey}`;
    if (!pairDateToTitles.has(dkey)) pairDateToTitles.set(dkey, new Set());
    pairDateToTitles.get(dkey).add(title);
  });
  const pairMarketDaysSorted = new Map();
  pairMarketDaysScratch.forEach((set, pk) => {
    pairMarketDaysSorted.set(pk, Array.from(set).sort());
  });
  return { pairMarketDaysSorted, pairDateToTitles };
};

const feedBuildIndexBaseLookup = (
  rows,
  activeBaseProduct,
  groupNameToProducts,
  productFlags,
  categoryOverrides,
  spreadTol
) => {
  if (!activeBaseProduct) return null;
  const groupList = groupNameToProducts?.get?.(activeBaseProduct);
  const baseTitles = new Set(
    groupList?.length ? [...groupList, activeBaseProduct] : [activeBaseProduct]
  );
  const firstRowByTitle = feedFirstRowByTitle(rows);
  const accum = new Map();

  (rows || []).forEach((item) => {
    if (!item?.title || !baseTitles.has(item.title) || !item?.updated_at) return;
    let dateKey = null;
    try {
      const d = new Date(item.updated_at);
      if (isNaN(d.getTime())) return;
      dateKey = d.toISOString().slice(0, 10);
    } catch (e) {
      return;
    }
    const prices = feedExtractPricesForRow(
      item,
      item.title,
      firstRowByTitle,
      productFlags,
      categoryOverrides,
      spreadTol
    );
    const basePrice = prices.regular || prices.actual;
    if (!basePrice || basePrice <= 0) return;
    const country = item.country || '';
    const k = `${dateKey}\x00${country}`;
    const cur = accum.get(k) || { sum: 0, n: 0 };
    cur.sum += basePrice;
    cur.n += 1;
    accum.set(k, cur);
  });

  return (dateKey, country) => {
    const exact = accum.get(`${dateKey}\x00${country || ''}`);
    if (exact?.n) return exact.sum / exact.n;
    let sum = 0;
    let n = 0;
    accum.forEach((v, k) => {
      if (!k.startsWith(`${dateKey}\x00`) || !v.n) return;
      sum += v.sum;
      n += v.n;
    });
    return n ? sum / n : null;
  };
};

const feedAbsoluteToIndexValue = (absolutePrice, dateKey, country, baseLookup) => {
  if (absolutePrice == null || !Number.isFinite(absolutePrice) || absolutePrice <= 0) return absolutePrice;
  if (!baseLookup) return absolutePrice;
  const base = baseLookup(dateKey, country);
  if (!base || base <= 0) return absolutePrice;
  return Math.round((absolutePrice / base) * 100 * 100) / 100;
};

const formatFeedPriceValue = (value, { isIndexMode, currency, priceDecimalPlaces, indexDecimalPlaces, minDecimals } = {}) => {
  if (value == null || !Number.isFinite(Number(value))) return '—';
  if (isIndexMode) {
    const dp = Math.max(indexDecimalPlaces ?? 0, minDecimals ?? 0);
    return `${Number(value).toFixed(dp)}%`;
  }
  const maxD = Math.max(0, priceDecimalPlaces ?? 2);
  const rounded = Math.round(Number(value) * 10 ** maxD) / 10 ** maxD;
  const n = parseFloat(rounded.toFixed(maxD)).toString();
  return currency ? `${n} ${currency}` : n;
};

const formatFeedPriceDelta = (diff, priceFormatOpts) => {
  if (diff == null || !Number.isFinite(Number(diff)) || Math.abs(Number(diff)) < 1e-9) return null;
  if (priceFormatOpts.isIndexMode) {
    const userDp = priceFormatOpts.indexDecimalPlaces ?? 0;
    const roundsToZero = Number(Number(diff).toFixed(userDp)) === 0;
    const minDecimals = roundsToZero ? 1 : 0;
    const formatted = formatFeedPriceValue(diff, { ...priceFormatOpts, minDecimals });
    return Number(diff) > 0 ? `+${formatted}` : formatted;
  }
  const sign = diff > 0 ? '+' : '−';
  const absFmt = formatFeedPriceValue(Math.abs(diff), priceFormatOpts);
  return `${sign}${absFmt}`;
};

const feedMatchingGroupKeys = (matchingGroups, title, country, retailer) => {
  const keys = new Set();
  matchingInfosForSku(matchingGroups, title).forEach((info) => {
    const groupName = (info?.groupName || '').trim();
    if (!groupName) return;
    const gCountry = (info?.country || '').trim();
    const gRetailer = (info?.retailer || '').trim();
    if (gCountry && country && gCountry.toLowerCase() !== String(country).toLowerCase()) return;
    if (gRetailer && retailer && gRetailer.toLowerCase() !== String(retailer).toLowerCase()) return;
    keys.add(groupName.toLowerCase());
  });
  return keys;
};

const feedShareMatchingGroup = (matchingGroups, titleA, titleB, country, retailer) => {
  const groupsA = feedMatchingGroupKeys(matchingGroups, titleA, country, retailer);
  if (!groupsA.size) return false;
  const groupsB = feedMatchingGroupKeys(matchingGroups, titleB, country, retailer);
  for (const g of groupsB) {
    if (groupsA.has(g)) return true;
  }
  return false;
};

const feedSharedActivePriceTypes = (
  titleA,
  titleB,
  activeTypes,
  firstRowByTitle,
  productFlags,
  categoryOverrides
) =>
  activeTypes.filter(
    (priceType) =>
      feedSkuIncludedForPriceType(titleA, priceType, firstRowByTitle, productFlags, categoryOverrides) &&
      feedSkuIncludedForPriceType(titleB, priceType, firstRowByTitle, productFlags, categoryOverrides)
  );

const feedMatchingActivePriceTypes = (
  title,
  activeTypes,
  firstRowByTitle,
  productFlags,
  categoryOverrides
) =>
  activeTypes.filter((priceType) =>
    feedSkuIncludedForPriceType(title, priceType, firstRowByTitle, productFlags, categoryOverrides)
  );

/** Pair same-day appeared/disappeared SKUs into replacement events when group, country, and price type match. */
const mergeSkuReplacementEvents = (events, options = {}) => {
  const {
    matchingGroups,
    activePriceTypes,
    productFlags = {},
    categoryOverrides = {},
    priceTypeLabelByKey = new Map(),
    firstRowByTitle,
  } = options;
  if (!matchingGroups?.size || !events?.length) return events;

  const activeTypes = ['promo', 'regular', 'actual'].filter((key) =>
    activePriceTypes ? activePriceTypes.has(key) : key === 'regular'
  );
  if (!activeTypes.length) return events;

  const otherEvents = [];
  const appeared = [];
  const disappeared = [];

  events.forEach((ev) => {
    if (ev.type === 'sku_appeared') appeared.push(ev);
    else if (ev.type === 'sku_disappeared') disappeared.push(ev);
    else otherEvents.push(ev);
  });
  if (!appeared.length || !disappeared.length) return events;

  const appearedByBucket = new Map();
  appeared.forEach((ev, idx) => {
    const bucket = `${ev.date}\x00${ev.country || ''}\x00${ev.retailer || ''}`;
    if (!appearedByBucket.has(bucket)) appearedByBucket.set(bucket, []);
    appearedByBucket.get(bucket).push({ ev, idx });
  });

  const usedAppeared = new Set();
  const usedDisappeared = new Set();
  const replacements = [];

  disappeared.forEach((dispEv, dispIdx) => {
    if (usedDisappeared.has(dispIdx)) return;
    const bucket = `${dispEv.date}\x00${dispEv.country || ''}\x00${dispEv.retailer || ''}`;
    const candidates = appearedByBucket.get(bucket) || [];

    for (const { ev: appEv, idx: appIdx } of candidates) {
      if (usedAppeared.has(appIdx)) continue;
      if (
        !feedShareMatchingGroup(
          matchingGroups,
          dispEv.product,
          appEv.product,
          dispEv.country,
          dispEv.retailer
        )
      ) {
        continue;
      }
      const sharedTypes = feedSharedActivePriceTypes(
        dispEv.product,
        appEv.product,
        activeTypes,
        firstRowByTitle,
        productFlags,
        categoryOverrides
      );
      if (!sharedTypes.length) continue;
      if (dispEv.priceType && appEv.priceType && dispEv.priceType !== appEv.priceType) continue;

      usedDisappeared.add(dispIdx);
      usedAppeared.add(appIdx);
      const priceType =
        dispEv.priceType && sharedTypes.includes(dispEv.priceType)
          ? dispEv.priceType
          : sharedTypes[0];
      replacements.push({
        date: dispEv.date,
        type: 'sku_replaced',
        product: `${dispEv.product} → ${appEv.product}`,
        oldProduct: dispEv.product,
        newProduct: appEv.product,
        country: dispEv.country,
        retailer: dispEv.retailer,
        brand: dispEv.brand || appEv.brand,
        source: 'sku',
        priceType,
        priceTypeLabel: priceTypeLabelByKey.get(priceType) || priceType,
      });
      break;
    }
  });

  const remainingAppeared = appeared.filter((_, idx) => !usedAppeared.has(idx));
  const remainingDisappeared = disappeared.filter((_, idx) => !usedDisappeared.has(idx));

  return [...otherEvents, ...remainingAppeared, ...remainingDisappeared, ...replacements];
};

const buildSkuFeedEvents = (filteredRows, options = {}) => {
  if (!Array.isArray(filteredRows) || !filteredRows.length) return [];
  const {
    activePriceTypes,
    productFlags = {},
    categoryOverrides = {},
    priceDecimalPlaces = 2,
    priceTypeLabelByKey = new Map(),
    isIndexMode = false,
    activeBaseProduct = null,
    groupNameToProducts = null,
    currency = '',
    matchingGroups = null,
  } = options;
  const activeTypes = ['promo', 'regular', 'actual'].filter((key) =>
    activePriceTypes ? activePriceTypes.has(key) : key === 'regular'
  );
  if (!activeTypes.length) return [];

  const spreadTol = priceSpreadTolerance(priceDecimalPlaces);
  const firstRowByTitle = feedFirstRowByTitle(filteredRows);
  const { pairMarketDaysSorted, pairDateToTitles } = feedBuildPairMarketMaps(filteredRows);
  const indexBaseLookup = isIndexMode
    ? feedBuildIndexBaseLookup(
        filteredRows,
        activeBaseProduct,
        groupNameToProducts,
        productFlags,
        categoryOverrides,
        spreadTol
      )
    : null;
  const toFeedPrice = (absolutePrice, dateKey, country) =>
    isIndexMode
      ? feedAbsoluteToIndexValue(absolutePrice, dateKey, country, indexBaseLookup)
      : absolutePrice;
  const skuObservations = new Map();

  filteredRows.forEach((item) => {
    if (!item?.title || !item?.updated_at) return;
    let dateKey = null;
    try {
      const d = new Date(item.updated_at);
      if (isNaN(d.getTime())) return;
      dateKey = d.toISOString().slice(0, 10);
    } catch (e) {
      return;
    }
    const title = item.title;
    const country = item.country || '';
    const retailer = (item.retailer && String(item.retailer).trim()) || '';
    const key = `${title}\x00${country}\x00${retailer}`;
    if (!skuObservations.has(key)) {
      skuObservations.set(key, {
        title,
        country,
        retailer,
        brand: item.brand || '',
        byDate: new Map(),
      });
    }
    const bucket = skuObservations.get(key);
    const prices = feedExtractPricesForRow(
      item,
      title,
      firstRowByTitle,
      productFlags,
      categoryOverrides,
      spreadTol
    );
    const existing = bucket.byDate.get(dateKey);
    if (existing) {
      activeTypes.forEach((ptype) => {
        if (prices[ptype] != null) existing.prices[ptype] = prices[ptype];
      });
    } else {
      bucket.byDate.set(dateKey, { dateKey, prices: { ...prices } });
    }
  });

  const events = [];

  skuObservations.forEach((sku) => {
    const matchingPriceTypes = feedMatchingActivePriceTypes(
      sku.title,
      activeTypes,
      firstRowByTitle,
      productFlags,
      categoryOverrides
    );
    if (!matchingPriceTypes.length) return;

    const primaryPriceType = matchingPriceTypes[0];
    const primaryPriceTypeLabel = priceTypeLabelByKey.get(primaryPriceType) || primaryPriceType;

    const pairKey = `${sku.country}\x00${sku.retailer}`;
    const marketDays = pairMarketDaysSorted.get(pairKey) || [];
    let wasPresent = false;

    marketDays.forEach((dateKey) => {
      const titlesOnDay = pairDateToTitles.get(`${pairKey}\x00${dateKey}`);
      const isPresent = titlesOnDay?.has(sku.title);
      const base = {
        date: dateKey,
        product: sku.title,
        country: sku.country,
        retailer: sku.retailer,
        brand: sku.brand,
        source: 'sku',
        priceType: primaryPriceType,
        priceTypeLabel: primaryPriceTypeLabel,
      };
      if (isPresent && !wasPresent) {
        events.push({ ...base, type: 'sku_appeared' });
      } else if (!isPresent && wasPresent) {
        events.push({ ...base, type: 'sku_disappeared' });
      }
      wasPresent = !!isPresent;
    });

    const observations = [...sku.byDate.values()].sort((a, b) => a.dateKey.localeCompare(b.dateKey));
    activeTypes.forEach((priceType) => {
      if (
        !feedSkuIncludedForPriceType(
          sku.title,
          priceType,
          firstRowByTitle,
          productFlags,
          categoryOverrides
        )
      ) {
        return;
      }
      let prevPrice = null;
      let prevDate = null;
      observations.forEach((obs) => {
        const val = obs.prices[priceType];
        if (val == null || !Number.isFinite(val) || val <= 0) return;
        const base = {
          date: obs.dateKey,
          product: sku.title,
          country: sku.country,
          retailer: sku.retailer,
          brand: sku.brand,
          priceType,
          priceTypeLabel: priceTypeLabelByKey.get(priceType) || priceType,
          source: 'sku',
        };
        if (prevPrice != null && prevDate !== obs.dateKey && Math.abs(val - prevPrice) > spreadTol) {
          events.push({
            ...base,
            type: val < prevPrice ? 'sku_price_drop' : 'sku_price_increase',
            oldValue: toFeedPrice(prevPrice, prevDate, sku.country),
            newValue: toFeedPrice(val, obs.dateKey, sku.country),
            valueMode: isIndexMode ? 'index' : 'absolute',
            currency: isIndexMode ? null : currency || null,
          });
        }
        prevPrice = val;
        prevDate = obs.dateKey;
      });
    });
  });

  return mergeSkuReplacementEvents(events, {
    matchingGroups,
    activePriceTypes,
    productFlags,
    categoryOverrides,
    priceTypeLabelByKey,
    firstRowByTitle,
  });
};

const feedDateKeyFromHistory = (rawDateKeys, dateLabels, index) => {
  if (Array.isArray(rawDateKeys) && rawDateKeys[index]) {
    return String(rawDateKeys[index]).slice(0, 10);
  }
  const label = dateLabels?.[index];
  if (!label) return null;
  const d = dayjs(label);
  return d.isValid() ? d.format('YYYY-MM-DD') : String(label);
};

const buildStockChangeFeedEvents = (filteredRows, options = {}) => {
  if (!Array.isArray(filteredRows) || !filteredRows.length) return [];
  const {
    activePriceTypes,
    productFlags = {},
    categoryOverrides = {},
    priceTypeLabelByKey = new Map(),
  } = options;
  const activeTypes = ['promo', 'regular', 'actual'].filter((key) =>
    activePriceTypes ? activePriceTypes.has(key) : key === 'regular'
  );
  if (!activeTypes.length) return [];

  const firstRowByTitle = feedFirstRowByTitle(filteredRows);
  const byProduct = new Map();

  filteredRows.forEach((item) => {
    if (!item?.title || !item?.updated_at) return;
    let dateKey = null;
    try {
      const d = new Date(item.updated_at);
      if (isNaN(d.getTime())) return;
      dateKey = d.toISOString().slice(0, 10);
    } catch (e) {
      return;
    }
    const key = `${item.title}\x00${item.country || ''}\x00${item.retailer || ''}`;
    const stockClass = classifyStockClassFromItem(item);
    if (!byProduct.has(key)) {
      byProduct.set(key, { observations: [] });
    }
    const bucket = byProduct.get(key);
    const existing = bucket.observations.find((o) => o.dateKey === dateKey);
    const entry = {
      dateKey,
      stockClass,
      title: item.title,
      country: item.country || '',
      retailer: item.retailer || '',
      brand: item.brand || '',
    };
    if (existing) {
      existing.stockClass = stockClass;
    } else {
      bucket.observations.push(entry);
    }
  });

  const events = [];
  byProduct.forEach((bucket) => {
    bucket.observations.sort((a, b) => a.dateKey.localeCompare(b.dateKey));
    const title = bucket.observations[0]?.title;
    if (!title) return;
    const matchingPriceTypes = feedMatchingActivePriceTypes(
      title,
      activeTypes,
      firstRowByTitle,
      productFlags,
      categoryOverrides
    );
    if (!matchingPriceTypes.length) return;
    const primaryPriceType = matchingPriceTypes[0];
    const primaryPriceTypeLabel = priceTypeLabelByKey.get(primaryPriceType) || primaryPriceType;

    for (let i = 0; i < bucket.observations.length; i += 1) {
      const curr = bucket.observations[i];
      const prev = i > 0 ? bucket.observations[i - 1] : null;
      const base = {
        date: curr.dateKey,
        product: curr.title,
        country: curr.country,
        retailer: curr.retailer,
        brand: curr.brand,
        source: 'stock',
        priceType: primaryPriceType,
        priceTypeLabel: primaryPriceTypeLabel,
      };
      if (!prev) {
        if (curr.stockClass === 'oos') {
          events.push({ ...base, type: 'stock_oos' });
        }
        continue;
      }
      if (prev.stockClass === curr.stockClass) continue;
      if (curr.stockClass === 'oos' && (prev.stockClass === 'avail' || prev.stockClass === 'unknown')) {
        events.push({ ...base, type: 'stock_oos' });
      } else if (curr.stockClass === 'avail' && prev.stockClass === 'oos') {
        events.push({ ...base, type: 'stock_avail' });
      }
    }
  });
  return events;
};

const buildVisibilityHistoryFeedEvents = (visibilityHistoryData, visibleDatasets) => {
  if (!visibilityHistoryData?.datasets?.length) return [];
  const dateLabels = visibilityHistoryData.labels || [];
  const rawDateKeys = visibilityHistoryData.rawDateKeys;
  const events = [];

  const datasets = visibilityHistoryData.datasets.filter((ds) => {
    if (!ds || ds.hidden) return false;
    if (visibleDatasets == null) return true;
    if (visibleDatasets.size === 0) return false;
    return visibleDatasets.has(ds.label);
  });

  datasets.forEach((ds) => {
    const data = Array.isArray(ds.data) ? ds.data : [];
    const product = stripFlagEmoji(ds.displayLabel || ds.label || 'Unknown').replace(/\s*-\s*Position$/, '').trim();
    let prevPos = null;

    for (let i = 0; i < data.length; i += 1) {
      const raw = data[i];
      const pos = typeof raw === 'number' ? raw : Number(raw);
      const date = feedDateKeyFromHistory(rawDateKeys, dateLabels, i);
      if (!date || !Number.isFinite(pos) || pos <= 0) continue;
      if (prevPos != null && pos !== prevPos) {
        events.push({
          date,
          type: 'position_change',
          product,
          oldValue: prevPos,
          newValue: pos,
          source: 'visibility',
        });
      } else if (prevPos == null) {
        events.push({
          date,
          type: 'appeared',
          product,
          newValue: pos,
          source: 'visibility',
        });
      }
      prevPos = pos;
    }
  });

  return events;
};

const buildDatasetChangeFeed = (mode, priceHistoryData, visibilityHistoryData, filteredRows, options = {}) => {
  if (mode === 'visibility') {
    return dedupeFeedEvents(
      buildVisibilityHistoryFeedEvents(visibilityHistoryData, options.visibleVisibilityDatasets)
    ).sort((a, b) => b.date.localeCompare(a.date) || String(a.product || '').localeCompare(String(b.product || '')));
  }
  const events = [
    ...buildSkuFeedEvents(filteredRows, options),
    ...buildStockChangeFeedEvents(filteredRows, options),
  ];
  return dedupeFeedEvents(events).sort(
    (a, b) => b.date.localeCompare(a.date) || String(a.product || '').localeCompare(String(b.product || ''))
  );
};

const describeFeedEvent = (ev, { isIndexMode, currency, priceDecimalPlaces, indexDecimalPlaces } = {}) => {
  const priceFormatOpts = {
    isIndexMode: ev.valueMode === 'index' || isIndexMode,
    currency: ev.currency || currency,
    priceDecimalPlaces,
    indexDecimalPlaces,
  };
  const fmtNum = (v, minDecimals = 0) => formatFeedPriceValue(v, { ...priceFormatOpts, minDecimals });
  switch (ev.type) {
    case 'price_drop':
    case 'price_increase':
    case 'sku_price_drop':
    case 'sku_price_increase': {
      const typeSuffix = ev.priceTypeLabel ? ` (${ev.priceTypeLabel})` : '';
      const diff = Number(ev.newValue) - Number(ev.oldValue);
      const showsAsZeroMove =
        priceFormatOpts.isIndexMode &&
        Math.abs(diff) > 1e-9 &&
        fmtNum(ev.oldValue, 0) === fmtNum(ev.newValue, 0);
      const lineMinDecimals = showsAsZeroMove ? 1 : 0;
      const delta = formatFeedPriceDelta(diff, priceFormatOpts);
      const deltaSuffix = delta ? ` · ${delta}` : '';
      return `${fmtNum(ev.oldValue, lineMinDecimals)} → ${fmtNum(ev.newValue, lineMinDecimals)}${deltaSuffix}${typeSuffix}`;
    }
    case 'promo_started':
      return `Listed ${fmtNum(ev.oldValue)}, promo ${fmtNum(ev.newValue)}`;
    case 'promo_ended':
      return `Back to ${fmtNum(ev.newValue)}`;
    case 'stock_oos':
      return 'Product marked out of stock';
    case 'stock_avail':
      return 'Product back in stock';
    case 'sku_appeared':
      return 'SKU first observed on this market day';
    case 'sku_disappeared':
      return 'SKU no longer observed on this market day';
    case 'sku_replaced':
      return 'SKU replaced on this market day';
    case 'variant_added':
      return ev.detail ? `Added: ${ev.detail}` : 'New variant observed';
    case 'variant_removed':
      return ev.detail ? `Removed: ${ev.detail}` : 'Variant no longer observed';
    case 'appeared':
      if (ev.source === 'visibility') return `Now at position #${ev.newValue}`;
      return ev.newValue != null ? `Starting at ${fmtNum(ev.newValue)}` : 'First observation in range';
    case 'position_change':
      return `#${ev.oldValue} → #${ev.newValue}`;
    default:
      return '';
  }
};

const DatasetChangeFeedPanel = ({
  events,
  emptyMessage = 'No changes in the selected range',
  isLoading = false,
  formatOptions = {},
}) => {
  const [eventSettings, setEventSettings] = useState(() => loadChangeFeedEventSettings());

  useEffect(() => {
    try {
      localStorage.setItem(CHANGE_FEED_SETTINGS_STORAGE_KEY, JSON.stringify(eventSettings));
    } catch {
      // ignore quota / private mode
    }
  }, [eventSettings]);

  const handleToggleEventVisible = useCallback((type) => {
    setEventSettings((prev) => ({
      ...prev,
      visible: {
        ...prev.visible,
        [type]: prev.visible[type] === false,
      },
    }));
  }, []);

  const handleEventColorChange = useCallback((type, color) => {
    setEventSettings((prev) => ({
      ...prev,
      colors: {
        ...prev.colors,
        [type]: color,
      },
    }));
  }, []);

  const handleResetEventColors = useCallback(() => {
    setEventSettings((prev) => ({
      ...prev,
      colors: defaultChangeFeedEventSettings().colors,
    }));
  }, []);

  const visibleEvents = useMemo(
    () =>
      (events || []).filter(
        (ev) =>
          !isChangeFeedEventTypeTemporarilyHidden(ev.type) && eventSettings.visible[ev.type] !== false
      ),
    [events, eventSettings.visible]
  );

  const groupedByDate = useMemo(() => {
    const byDate = new Map();
    visibleEvents.forEach((ev) => {
      const d = ev.date || 'unknown';
      if (!byDate.has(d)) byDate.set(d, []);
      byDate.get(d).push(ev);
    });
    return [...byDate.entries()].sort((a, b) => b[0].localeCompare(a[0]));
  }, [visibleEvents]);

  const settingsColumn = (
    <Box sx={{ width: 200, flexShrink: 0, minHeight: 0 }}>
      <ChangeFeedEventSettings
        eventSettings={eventSettings}
        onToggleVisible={handleToggleEventVisible}
        onColorChange={handleEventColorChange}
        onReset={handleResetEventColors}
      />
    </Box>
  );

  const feedPanelShell = (feedBody) => (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'stretch', height: 528, minHeight: 528 }}>
      <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {feedBody}
      </Box>
      {settingsColumn}
    </Box>
  );

  const feedScrollBoxSx = {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1,
    bgcolor: 'background.paper',
    px: 1.5,
    py: 1,
  };

  if (isLoading) {
    return feedPanelShell(
      <Box sx={{ ...feedScrollBoxSx, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (!events?.length) {
    return feedPanelShell(
      <Box sx={{ ...feedScrollBoxSx, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {emptyMessage}
        </Typography>
      </Box>
    );
  }

  if (!visibleEvents.length) {
    return feedPanelShell(
      <Box sx={{ ...feedScrollBoxSx, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No events match the selected event types
        </Typography>
      </Box>
    );
  }

  return feedPanelShell(
    <Box sx={feedScrollBoxSx}>
      {groupedByDate.map(([dateKey, dayEvents]) => (
        <Box key={dateKey} sx={{ mb: 2 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              mb: 0.75,
              position: 'sticky',
              top: 0,
              zIndex: 1,
              bgcolor: 'background.paper',
              py: 0.5,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            {formatChangeFeedDateKey(dateKey)}
          </Typography>
          <List dense disablePadding>
            {dayEvents.map((ev, idx) => {
              const meta = resolveChangeFeedEventMeta(ev.type, eventSettings);
              const detail = describeFeedEvent(ev, formatOptions);
              const location = [ev.retailer, ev.country].filter(Boolean).join(' · ');
              return (
                <ListItem
                  key={`${dateKey}-${ev.type}-${ev.product}-${idx}`}
                  disableGutters
                  sx={{
                    alignItems: 'flex-start',
                    py: 0.75,
                    pl: 1.25,
                    mb: 0.5,
                    borderLeft: `3px solid ${meta.color}`,
                  }}
                >
                  <Box sx={{ minWidth: 0, width: '100%' }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                      <Chip
                        label={meta.label}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.68rem',
                          fontWeight: 600,
                          bgcolor: `${meta.color}18`,
                          color: meta.color,
                          border: `1px solid ${meta.color}40`,
                        }}
                      />
                      {ev.priceTypeLabel ? (
                        <Chip
                          label={ev.priceTypeLabel}
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.65rem' }}
                        />
                      ) : null}
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem' }}>
                        {ev.product}
                      </Typography>
                    </Box>
                    {detail ? (
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                        {detail}
                      </Typography>
                    ) : null}
                    {location ? (
                      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.25 }}>
                        {location}
                      </Typography>
                    ) : null}
                  </Box>
                </ListItem>
              );
            })}
          </List>
        </Box>
      ))}
    </Box>
  );
};

const COUNTRY_COLORS = [
  '#E63946', // vivid red
  '#2196F3', // vivid blue
  '#FF9800', // vivid orange
  '#4CAF50', // vivid green
  '#9C27B0', // vivid purple
  '#00BCD4', // vivid cyan
  '#F44336', // deep red
  '#3F51B5', // indigo
  '#FF5722', // deep orange
  '#009688', // teal
  '#8BC34A', // light green
  '#673AB7', // deep purple
  '#FFC107', // amber
  '#03A9F4', // light blue
  '#E91E63', // pink
  '#607D8B', // blue grey
];

const COUNTRY_NAME_MAP = {
  uk: 'United Kingdom',
  gb: 'United Kingdom',
  cz: 'Czechia',
  ro: 'Romania',
  it: 'Italy',
  pl: 'Poland',
  de: 'Germany',
  fr: 'France',
  es: 'Spain',
  se: 'Sweden',
  no: 'Norway',
  dk: 'Denmark',
  fi: 'Finland',
  hu: 'Hungary',
  sk: 'Slovakia',
  at: 'Austria',
  ch: 'Switzerland',
  be: 'Belgium',
  nl: 'Netherlands',
  pt: 'Portugal',
  gr: 'Greece',
  tr: 'Turkey',
  rs: 'Serbia',
  hr: 'Croatia',
  bg: 'Bulgaria',
  si: 'Slovenia',
  ua: 'Ukraine',
  ru: 'Russia',
  us: 'United States',
  ca: 'Canada',
  au: 'Australia',
  jp: 'Japan',
  kr: 'South Korea',
  cn: 'China',
};

/** Full-name / slug → ISO 3166-1 alpha-2 for PNG flags (Windows/Edge often omit emoji regional indicators). */
const COUNTRY_SLUG_TO_ISO2 = {
  italy: 'it',
  romania: 'ro',
  poland: 'pl',
  germany: 'de',
  france: 'fr',
  spain: 'es',
  sweden: 'se',
  norway: 'no',
  denmark: 'dk',
  finland: 'fi',
  hungary: 'hu',
  slovakia: 'sk',
  austria: 'at',
  switzerland: 'ch',
  belgium: 'be',
  netherlands: 'nl',
  portugal: 'pt',
  greece: 'gr',
  turkey: 'tr',
  serbia: 'rs',
  croatia: 'hr',
  bulgaria: 'bg',
  slovenia: 'si',
  czech: 'cz',
  czechia: 'cz',
  'czech republic': 'cz',
  england: 'gb',
  britain: 'gb',
  scotland: 'gb',
  wales: 'gb',
  'united kingdom': 'gb',
  uk: 'gb',
};

const formatCountryName = (code) => {
  if (!code) return code;
  const lower = code.toLowerCase().trim();
  return COUNTRY_NAME_MAP[lower] || code.charAt(0).toUpperCase() + code.slice(1);
};

// Strip flag emoji from label (for table view where flags are not desired)
const stripFlagEmoji = (str) => {
  if (!str || typeof str !== 'string') return str || '';
  return str.replace(/[\u{1F1E6}-\u{1F1FF}]{2}\s*/gu, '').trim();
};

/** Merged multi-SKU lines use "Group · <snapshot>"; the summary grid has its own Price column. */
const stripChartLabelAggregatedPriceSuffix = (text) => {
  if (!text || typeof text !== 'string') return text;
  const s = text.trim();
  const next = s.replace(/\s*[·•]\s*(?:[\d.,]+%?)\s*$/u, '').trim();
  return next || s;
};

/** Table-mode column title: product/country only — price lives in the cells. */
const chartTableSeriesHeaderName = (ds) => {
  const raw = stripFlagEmoji(ds?.displayLabel || ds?.label || '');
  return String(raw)
    .replace(/\s*[·•]\s*[\d.,]+%?\s*/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const CHART_TABLE_PRICE_COL_WIDTH = 88;

const isChartTableCellEmpty = (value) => value === null || value === undefined || value === '';

/** Display-only placeholder for table view; export keeps cells empty. */
const renderChartTableCell = ({ value }) =>
  isChartTableCellEmpty(value) ? '-' : value;

const summaryModelTitleFromPriceDataset = (ds) => {
  const group = ds?.productGroupName != null && String(ds.productGroupName).trim();
  if (group) return stripFlagEmoji(group);
  const raw = stripFlagEmoji(String(ds.displayLabel || ds.label || '').trim());
  return stripChartLabelAggregatedPriceSuffix(raw) || raw;
};

/** First / last index with a finite numeric point (for “introduced” / “last seen” on chart lines). */
const getFiniteValueSpanIndices = (data) => {
  let first = -1;
  let last = -1;
  if (!Array.isArray(data)) return { first, last };
  for (let i = 0; i < data.length; i++) {
    const v = data[i];
    if (v === null || v === undefined) continue;
    const n = typeof v === 'number' ? v : Number(v);
    if (!Number.isFinite(n)) continue;
    if (first < 0) first = i;
    last = i;
  }
  return { first, last };
};

/** First retailer/listing URL on a data row (column names from API often contain `url`). */
function pickListingUrlFromRow(item, columnNames) {
  if (!item || typeof item !== 'object') return '';
  const fromCols = Array.isArray(columnNames)
    ? columnNames.filter((c) => String(c).toLowerCase().includes('url'))
    : [];
  const orderedKeys = [...fromCols, 'url', 'product_url', 'listing_url', 'page_url', 'link'];
  const seen = new Set();
  for (const k of orderedKeys) {
    if (!k || seen.has(k)) continue;
    seen.add(k);
    if (!(k in item)) continue;
    const v = item[k];
    if (typeof v === 'string' && /^https?:\/\//i.test(v.trim())) return v.trim();
  }
  for (const k of Object.keys(item)) {
    if (!String(k).toLowerCase().includes('url')) continue;
    const v = item[k];
    if (typeof v === 'string' && /^https?:\/\//i.test(v.trim())) return v.trim();
  }
  return '';
}

function collectSkuTitlesFromPriceDataset(ds, lastFiniteIdx) {
  const lastIdx = lastFiniteIdx >= 0 ? lastFiniteIdx : -1;
  const atLast =
    lastIdx >= 0 && Array.isArray(ds.groupedVariantTitlesAtIndex)
      ? ds.groupedVariantTitlesAtIndex[lastIdx]
      : null;
  if (Array.isArray(atLast) && atLast.length) {
    return [...new Set(atLast.map((t) => String(t).trim()).filter(Boolean))];
  }
  if (Array.isArray(ds.groupedVariantTitles) && ds.groupedVariantTitles.length) {
    return [...new Set(ds.groupedVariantTitles.map((t) => String(t).trim()).filter(Boolean))];
  }
  const single =
    (ds.productName && String(ds.productName).trim()) ||
    stripFlagEmoji(ds.displayLabel || ds.label || '').trim();
  return single ? [single] : [];
}

function collectSkuTitlesFromVisibilityDataset(ds) {
  const productTitle = (ds.label || '').replace(/\s*-\s*Position\s*$/i, '').trim();
  return productTitle ? [productTitle] : [];
}

/**
 * One listing URL per SKU row: avoid merging unrelated URLs from duplicate title rows.
 * Prefers rows matching `countryNorm`, then latest `updated_at` on or before `lastDateKey` (YYYY-MM-DD).
 */
function pickSingleListingUrlForSkuRow(rows, columnNames, title, countryNorm, lastDateKey) {
  if (!title || !Array.isArray(rows) || rows.length === 0) return '';
  const titleTrim = String(title).trim();
  let candidates = rows.filter((item) => item && String(item.title || '').trim() === titleTrim);
  if (!candidates.length) return '';
  if (countryNorm) {
    const narrowed = candidates.filter(
      (item) => String(item.country || '').toLowerCase().trim() === countryNorm
    );
    if (narrowed.length) candidates = narrowed;
  }
  const rowDateKey = (item) => {
    try {
      if (!item.updated_at) return '';
      const dt = new Date(item.updated_at);
      if (Number.isNaN(dt.getTime())) return '';
      return dt.toISOString().slice(0, 10);
    } catch {
      return '';
    }
  };
  const rowTime = (item) => {
    try {
      const dt = new Date(item.updated_at);
      return Number.isNaN(dt.getTime()) ? 0 : dt.getTime();
    } catch {
      return 0;
    }
  };

  let pool = candidates;
  const lk = lastDateKey && String(lastDateKey).slice(0, 10);
  if (lk && /^\d{4}-\d{2}-\d{2}$/.test(lk)) {
    const onOrBefore = candidates.filter((item) => {
      const k = rowDateKey(item);
      return k && k <= lk;
    });
    if (onOrBefore.length) pool = onOrBefore;
  }
  pool = [...pool].sort((a, b) => rowTime(b) - rowTime(a));
  const chosen = pool[0];
  return pickListingUrlFromRow(chosen, columnNames) || '';
}

// Convert country code to flag emoji (ISO 3166-1 alpha-2; uk->gb for UK flag)
const getCountryFlagEmoji = (code) => {
  if (!code || typeof code !== 'string') return '';
  const c = code.trim().toLowerCase();
  const iso = (c === 'uk' ? 'gb' : c).toUpperCase();
  if (iso.length !== 2) return '';
  const codePoints = iso.split('').map((char) => 127397 + char.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
};

// Manufacturer to brand-keyword mapping for filtering. A manufacturer can own multiple brands
// (e.g. Imperial Brands → Pulze, blu), so each entry is a list of brand keywords matched against
// the product's brand value.
const MANUFACTURER_TO_BRANDS = {
  JTI: ['Ploom'],
  BAT: ['glo'],
  PMI: ['IQOS', 'Lil', 'LIL', 'Bonds', 'BONDS'],
  'Imperial Brands': ['Imperial Brands', 'Pulze', 'blu'],
};

// Normalized brand keywords for a manufacturer (used to match products to a manufacturer).
const getManufacturerBrandKeywords = (m) => (MANUFACTURER_TO_BRANDS[m] || []).map((b) => normalizeValue(b));

const MANUFACTURER_OPTIONS = ['JTI', 'BAT', 'PMI', 'Imperial Brands'];
const THIRD_PARTY_RETAILERS = new Set(['eliquidshop', 'vapoo']);

const PRICE_TYPE_OPTIONS = [
  { key: 'regular', label: 'Regular Price', defaultStyle: 'solid',   info: 'The regular shelf price listed by the retailer, before any promotions.' },
  { key: 'promo',   label: 'Promo Price',   defaultStyle: 'dotted',  info: 'The discounted price applied during a promotional period.' },
  { key: 'actual',  label: 'Actual Price',  defaultStyle: 'dashed',  info: 'The minimal price for which it is possible to buy the product, including promo prices.', italic: true },
];

function defaultActivePriceTypes(isMarketScope = false) {
  return isMarketScope ? new Set(['regular', 'promo']) : new Set(['regular']);
}

/** Matching-group model names shown by default in Market Price & Promotions. */
const MARKET_DEFAULT_MODEL_NAMES_BY_ROUTE = {
  devices: ['Ploom AURA', 'IQOS ILUMA i ONE'],
  sticks: ['Evo', 'Lyo', 'Terea', 'Delia'],
};

function resolveMarketDefaultModelNames(productType) {
  const normalizedType = normalizeValue(productType);
  if (PRODUCT_TYPE_ALIASES.devices.includes(normalizedType)) {
    return MARKET_DEFAULT_MODEL_NAMES_BY_ROUTE.devices;
  }
  if (PRODUCT_TYPE_ALIASES.sticks.includes(normalizedType)) {
    return MARKET_DEFAULT_MODEL_NAMES_BY_ROUTE.sticks;
  }
  return [];
}

/** Resolve default Market models to `__model__:` product-filter keys when those groups exist. */
function resolveMarketDefaultProductSelections(productType, groupNameToProducts) {
  const wanted = resolveMarketDefaultModelNames(productType);
  if (!wanted.length || !groupNameToProducts?.size) return [];
  const namesByNorm = new Map();
  groupNameToProducts.forEach((_, name) => {
    if (!name) return;
    const key = normalizeValue(name);
    if (!namesByNorm.has(key)) namesByNorm.set(key, name);
  });
  return wanted
    .map((name) => namesByNorm.get(normalizeValue(name)))
    .filter(Boolean)
    .map((match) => modelSelectionValue(match));
}

/** Drop removed price-type keys (e.g. legacy starter_kit) from a selection set. */
function sanitizeActivePriceTypes(raw) {
  const allowed = new Set(PRICE_TYPE_OPTIONS.map((o) => o.key));
  const next = new Set();
  (raw instanceof Set ? raw : []).forEach((key) => {
    if (allowed.has(key)) next.add(key);
  });
  if (next.size === 0) next.add('regular');
  return next;
}

function findPloomBrand(brandOptions) {
  return (brandOptions || []).find((b) => normalizeValue(b).includes('ploom')) || null;
}

const INCLUDE_BUNDLES_FILTER_OPTIONS_BY_ROUTE = {
  devices: [
    { label: 'Devices', canonical: 'Devices' },
    { label: 'Device Bundle', canonical: 'Device Bundles' },
  ],
  sticks: [
    { label: 'Sticks', canonical: 'Sticks' },
    { label: 'Sticks Bundles', canonical: 'Sticks Bundles' },
  ],
};

const PRODUCT_TRI_STATE_INCLUDE = 'Include';
const PRODUCT_TRI_STATE_EXCLUDE = 'Exclude';
const PRODUCT_TRI_STATE_ONLY = 'Only';
const PRODUCT_TRI_STATE_FILTER_OPTIONS = [
  PRODUCT_TRI_STATE_INCLUDE,
  PRODUCT_TRI_STATE_EXCLUDE,
  PRODUCT_TRI_STATE_ONLY,
];

const CUSTOMIZABLE_FILTER_OPTIONS = PRODUCT_TRI_STATE_FILTER_OPTIONS;
const CUSTOMIZABLE_FILTER_INCLUDE = PRODUCT_TRI_STATE_INCLUDE;
const CUSTOMIZABLE_FILTER_EXCLUDE = PRODUCT_TRI_STATE_EXCLUDE;
const CUSTOMIZABLE_FILTER_ONLY = PRODUCT_TRI_STATE_ONLY;
const CUSTOMIZABLE_FILTER_DEFAULT = CUSTOMIZABLE_FILTER_INCLUDE;

const FOR_NEW_USERS_FILTER_OPTIONS = PRODUCT_TRI_STATE_FILTER_OPTIONS;
const FOR_NEW_USERS_FILTER_INCLUDE = PRODUCT_TRI_STATE_INCLUDE;
const FOR_NEW_USERS_FILTER_EXCLUDE = PRODUCT_TRI_STATE_EXCLUDE;
const FOR_NEW_USERS_FILTER_ONLY = PRODUCT_TRI_STATE_ONLY;
const FOR_NEW_USERS_FILTER_DEFAULT = FOR_NEW_USERS_FILTER_INCLUDE;

/** Map any legacy long-form tri-state label to Include / Exclude / Only. */
function coerceProductTriStateLabel(raw) {
  if (PRODUCT_TRI_STATE_FILTER_OPTIONS.includes(raw)) return raw;
  const s = String(raw || '').trim().toLowerCase();
  if (!s) return null;
  if (s === 'include' || s.startsWith('include ')) return PRODUCT_TRI_STATE_INCLUDE;
  if (s === 'exclude' || s.startsWith('exclude ')) return PRODUCT_TRI_STATE_EXCLUDE;
  if (s === 'only' || s.startsWith('only ')) return PRODUCT_TRI_STATE_ONLY;
  return null;
}

/** Normalize saved/legacy product-flag filter values to the three-way mode. */
function normalizeProductTriStateFilter(raw, options, defaultValue, legacyOnlyKeys = []) {
  const coerced = coerceProductTriStateLabel(raw);
  if (coerced && options.includes(coerced)) return coerced;
  if (options.includes(raw)) return raw;
  if (Array.isArray(raw)) {
    return raw.some((k) => legacyOnlyKeys.includes(k) || coerceProductTriStateLabel(k) === PRODUCT_TRI_STATE_ONLY)
      ? PRODUCT_TRI_STATE_ONLY
      : PRODUCT_TRI_STATE_INCLUDE;
  }
  if (legacyOnlyKeys.includes(raw)) return PRODUCT_TRI_STATE_ONLY;
  return defaultValue;
}

function normalizeCustomizableFilter(raw) {
  return normalizeProductTriStateFilter(
    raw,
    CUSTOMIZABLE_FILTER_OPTIONS,
    CUSTOMIZABLE_FILTER_DEFAULT,
    ['customizable']
  );
}

function normalizeForNewUsersOnlyFilter(raw) {
  return normalizeProductTriStateFilter(
    raw,
    FOR_NEW_USERS_FILTER_OPTIONS,
    FOR_NEW_USERS_FILTER_DEFAULT,
    ['for_new_users_only']
  );
}

/** Look up product_flags for a title (exact, trimmed, then case-insensitive). */
function getProductFlagsForTitle(productFlags, title) {
  if (!productFlags || !title) return null;
  if (productFlags[title]) return productFlags[title];
  const trimmed = String(title).trim();
  if (trimmed && productFlags[trimmed]) return productFlags[trimmed];
  const lower = (trimmed || String(title)).toLowerCase();
  for (const key of Object.keys(productFlags)) {
    if (String(key).trim().toLowerCase() === lower) return productFlags[key];
  }
  return null;
}

/** True when the SKU has the Product Detail "Customizable" tag. */
function isCustomizableTaggedSku(title, productFlags) {
  return !!getProductFlagsForTitle(productFlags, title)?.isCustomFlag;
}

/** True when the SKU has the Product Detail "New Users Only" tag. */
function isForNewUsersOnlyTaggedSku(title, productFlags) {
  return !!getProductFlagsForTitle(productFlags, title)?.[PRODUCT_FLAG_FOR_NEW_USERS_ONLY];
}

function rowMatchesCustomizableFilter(item, filterMode, productFlags) {
  const mode = normalizeCustomizableFilter(filterMode);
  if (mode === CUSTOMIZABLE_FILTER_INCLUDE) return true;
  const title = item?.title;
  if (!title) return mode !== CUSTOMIZABLE_FILTER_ONLY;
  const isCustom = isCustomizableTaggedSku(title, productFlags);
  if (mode === CUSTOMIZABLE_FILTER_ONLY) return isCustom;
  return !isCustom;
}

function rowMatchesForNewUsersOnlyFilter(item, filterMode, productFlags) {
  const mode = normalizeForNewUsersOnlyFilter(filterMode);
  if (mode === FOR_NEW_USERS_FILTER_INCLUDE) return true;
  const title = item?.title;
  if (!title) return mode !== FOR_NEW_USERS_FILTER_ONLY;
  const isNewUsersOnly = isForNewUsersOnlyTaggedSku(title, productFlags);
  if (mode === FOR_NEW_USERS_FILTER_ONLY) return isNewUsersOnly;
  return !isNewUsersOnly;
}

const LIMITED_EDITION_FILTER_OPTIONS = PRODUCT_TRI_STATE_FILTER_OPTIONS;
const LIMITED_EDITION_FILTER_INCLUDE = PRODUCT_TRI_STATE_INCLUDE;
const LIMITED_EDITION_FILTER_EXCLUDE = PRODUCT_TRI_STATE_EXCLUDE;
const LIMITED_EDITION_FILTER_ONLY = PRODUCT_TRI_STATE_ONLY;
const LIMITED_EDITION_FILTER_DEFAULT = LIMITED_EDITION_FILTER_INCLUDE;
// Legacy aliases kept for saved-view migration.
const LIMITED_EDITION_FILTER_ALL = LIMITED_EDITION_FILTER_INCLUDE;
const LIMITED_EDITION_FILTER_REGULAR = LIMITED_EDITION_FILTER_EXCLUDE;
const LIMITED_EDITION_FILTER_LIMITED = LIMITED_EDITION_FILTER_ONLY;

const STARTER_KIT_FILTER_OPTIONS = PRODUCT_TRI_STATE_FILTER_OPTIONS;
const STARTER_KIT_FILTER_INCLUDE = PRODUCT_TRI_STATE_INCLUDE;
const STARTER_KIT_FILTER_EXCLUDE = PRODUCT_TRI_STATE_EXCLUDE;
const STARTER_KIT_FILTER_ONLY = PRODUCT_TRI_STATE_ONLY;
const STARTER_KIT_FILTER_DEFAULT = STARTER_KIT_FILTER_EXCLUDE;

/** Normalize saved/legacy starter-kit filter values to the three-way mode. */
function normalizeStarterKitFilter(raw) {
  const coerced = coerceProductTriStateLabel(raw);
  if (coerced) return coerced;
  // Legacy multi-select: ['starter_kit'] meant only starter kits; [] meant include all.
  if (Array.isArray(raw)) {
    return raw.includes('starter_kit') || raw.includes(STARTER_KIT_FILTER_ONLY)
      ? STARTER_KIT_FILTER_ONLY
      : STARTER_KIT_FILTER_INCLUDE;
  }
  if (raw === 'starter_kit') return STARTER_KIT_FILTER_ONLY;
  return STARTER_KIT_FILTER_DEFAULT;
}

function normalizeLimitedEditionFilter(raw) {
  const coerced = coerceProductTriStateLabel(raw);
  if (coerced) return coerced;
  const s = String(raw || '').trim().toLowerCase();
  if (s === 'all editions' || s === 'all') return LIMITED_EDITION_FILTER_INCLUDE;
  if (s === 'regular editions only' || s.includes('regular')) return LIMITED_EDITION_FILTER_EXCLUDE;
  if (s === 'limited editions only' || s.includes('limited')) return LIMITED_EDITION_FILTER_ONLY;
  return LIMITED_EDITION_FILTER_DEFAULT;
}

function isLimitedEditionProduct(title, productFlags) {
  if (!title) return false;
  return getProductFlagsForTitle(productFlags, title)?.portfolioType === 'Limited edition';
}

function countLimitedEditionTitles(titles, productFlags) {
  if (!Array.isArray(titles) || !titles.length) return { total: 0, limited: 0 };
  const unique = [...new Set(titles.map((t) => String(t || '').trim()).filter(Boolean))];
  let limited = 0;
  unique.forEach((t) => {
    if (isLimitedEditionProduct(t, productFlags)) limited += 1;
  });
  return { total: unique.length, limited };
}

/**
 * Chart hover: "(Limited edition)" when every SKU on the line is limited;
 * "(Including Limited Edition)" when a multi-variant line is mixed.
 */
function appendLimitedEditionLabelSuffix(text, titles, productFlags) {
  if (!text || typeof text !== 'string') return text || '';
  const stripped = text
    .replace(/\s*\(Including Limited Edition\)/gi, '')
    .replace(/\s*\(Limited edition\)/gi, '')
    .trim();
  const { total, limited } = countLimitedEditionTitles(titles, productFlags);
  if (!limited) return stripped;
  if (total > 1 && limited < total) {
    return `${stripped} (Including Limited Edition)`;
  }
  return `${stripped} (Limited edition)`;
}

function rowMatchesLimitedEditionFilter(item, editionFilter, productFlags) {
  const mode = normalizeLimitedEditionFilter(editionFilter);
  if (mode === LIMITED_EDITION_FILTER_INCLUDE) return true;
  const title = item?.title;
  if (!title) return mode !== LIMITED_EDITION_FILTER_ONLY;
  const isLimited = isLimitedEditionProduct(title, productFlags);
  if (mode === LIMITED_EDITION_FILTER_ONLY) return isLimited;
  return !isLimited; // exclude
}

/** True when the SKU has the Product Detail "Starter Kit / Registration Price" tag. */
function isStarterKitTaggedSku(title, productFlags) {
  return !!getProductFlagsForTitle(productFlags, title)?.isStarterKitOrRegistration;
}

function rowMatchesStarterKitProductFilter(item, starterKitFilter, productFlags) {
  const mode = normalizeStarterKitFilter(starterKitFilter);
  if (mode === STARTER_KIT_FILTER_INCLUDE) return true;
  const title = item?.title;
  if (!title) return mode !== STARTER_KIT_FILTER_ONLY;
  const isKit = isStarterKitTaggedSku(title, productFlags);
  if (mode === STARTER_KIT_FILTER_ONLY) return isKit;
  return !isKit; // exclude
}

const BUNDLE_CATEGORY_GROUPS = ['Device Bundles', 'Sticks Bundles'];

function isBundleCategoryGroup(categoryGroup) {
  if (!categoryGroup) return false;
  const norm = normalizeValue(categoryGroup);
  return BUNDLE_CATEGORY_GROUPS.some((g) => normalizeValue(g) === norm);
}

function isBundlesOnlyCategorySelection(selectedCategoryGroups) {
  if (!Array.isArray(selectedCategoryGroups) || selectedCategoryGroups.length === 0) return false;
  return selectedCategoryGroups.every((g) => isBundleCategoryGroup(g));
}

function resolveCategoryGroupOptionValue(canonical, categoryGroupOptions) {
  if (!canonical) return null;
  const exact = (categoryGroupOptions || []).find((option) => option === canonical);
  if (exact) return exact;
  const target = normalizeValue(canonical);
  return (categoryGroupOptions || []).find((option) => normalizeValue(option) === target) || canonical;
}

const PRICE_LINE_STYLE_MAP = { solid: [], dashed: [5, 5], dotted: [2, 4] };

// Out-of-stock lines are always drawn in grey so a stock gap reads as "unavailable"
// independent of the product's assigned colour.
const OOS_LINE_COLOR = '#9e9e9e';

/** Legend line-style for stock split: only "Out of stock" is configurable; "In stock" uses the same style as its price type (Regular / Promo / Actual). */
const STOCK_SEGMENT_LINE_OPTIONS = [{ key: 'oos', label: 'Out of stock', defaultStyle: 'dotted' }];

const TOP_FILTERS = [
  { key: 'retailer', label: 'Retailer' },
  { key: 'brandModel', label: 'Brand' },
  { key: 'currency', label: 'Currency' },
  { key: 'priceType', label: 'Price Type', options: ['Both', 'Actual Price', 'Regular Price'] },
  { key: 'currencyIndex', label: 'Price View', options: ['Absolute', 'Index'] },
  { key: 'availability', label: 'Availability', options: ['Available', 'Out of Stock'] },
  { key: 'categoryGroup', label: 'Product category' },
  { key: 'packSize', label: 'Pack size' },
  { key: 'timeScale', label: 'Time Scale' },
];

const CURRENCY_INDEX_FILTER = TOP_FILTERS.find((filter) => filter.key === 'currencyIndex');

const AVAILABILITY_OPTION_LABELS = ['Available', 'Out of Stock'];
/** Default: both segments selected (matches legacy "Include OOS"). */
const DEFAULT_AVAILABILITY_SELECTION = ['Available', 'Out of Stock'];

/** Legacy string values from saved state / old UI ("Include OOS" = show both). */
function normalizeAvailabilitySelection(raw) {
  if (Array.isArray(raw)) {
    const next = raw.filter((x) => AVAILABILITY_OPTION_LABELS.includes(x));
    return next.length ? next : DEFAULT_AVAILABILITY_SELECTION;
  }
  if (raw === 'Include OOS') return DEFAULT_AVAILABILITY_SELECTION;
  if (raw === 'Available') return ['Available'];
  return DEFAULT_AVAILABILITY_SELECTION;
}

/**
 * Price chart legend: `null` = not synced yet (treat as all on), empty Set = user chose "None" (all off),
 * non-empty Set = only those series are drawn.
 */
function isPriceLegendSeriesVisible(visibleSet, label) {
  if (visibleSet == null) return true;
  if (visibleSet.size === 0) return false;
  return visibleSet.has(label);
}

const PRICE_TABLE_TYPE_RANK = { promo: 0, actual: 1, regular: 2 };

function chartTableGroupKey(ds) {
  if (ds?.legendGroupId != null && String(ds.legendGroupId) !== '') {
    return String(ds.legendGroupId);
  }
  return chartTableSeriesHeaderName(ds) || ds?.label || '';
}

function pickMergedChartTableValueAt(datasets, index) {
  let best = null;
  let bestRank = 99;
  for (const ds of datasets) {
    const n = readChartPointAt(ds, index);
    if (n == null) continue;
    const rank =
      (PRICE_TABLE_TYPE_RANK[ds.priceType] ?? 5) + (ds.legendPrimary === false ? 10 : 0);
    if (best == null || rank < bestRank) {
      best = n;
      bestRank = rank;
    }
  }
  return best;
}

/** One table column per legend series (stock/price-type splits merged); skip empty lines. */
function groupDatasetsForChartTable(datasets, visibleSet) {
  const visible = (datasets || []).filter((ds) => isPriceLegendSeriesVisible(visibleSet, ds.label));
  const byGroup = new Map();
  visible.forEach((ds) => {
    const key = chartTableGroupKey(ds);
    if (!key) return;
    if (!byGroup.has(key)) byGroup.set(key, []);
    byGroup.get(key).push(ds);
  });
  const grouped = [];
  byGroup.forEach((group, field) => {
    const hasValue = group.some((ds) => getFiniteValueSpanIndices(ds.data).first >= 0);
    if (!hasValue) return;
    const primary = group.filter((d) => d.legendPrimary !== false);
    const rep = (primary.length ? primary : group)[0];
    grouped.push({ field, rep, group });
  });
  return grouped;
}

/**
 * Filter rows by stock_status. When both options are selected, use union of the two strict
 * filters (available OR out of stock), not "all rows" — otherwise null/unknown stock rows
 * inflate counts vs selecting each option alone.
 */
function rowMatchesAvailability(item, selection) {
  const sel = normalizeAvailabilitySelection(selection);
  const wantAvail = sel.includes('Available');
  const wantOos = sel.includes('Out of Stock');

  if (wantAvail && wantOos) {
    return (
      rowMatchesAvailability(item, ['Available']) || rowMatchesAvailability(item, ['Out of Stock'])
    );
  }

  const cls = classifyStockClassFromItem(item);

  if (wantAvail && !wantOos) {
    return cls === 'avail';
  }
  if (!wantAvail && wantOos) {
    return cls === 'oos';
  }
  return true;
}

/** Per-observation stock class for chart segmentation (aligned with rowMatchesAvailability). */
function classifyStockClassFromItem(item) {
  return classifyStockClassFromRaw(item?.stock_status ?? item?.availability ?? '');
}

/**
 * Classify retailer stock text. Avoid naive substrings: "not in stock" contains "in stock",
 * and "find out" + "stock" in the same sentence matched the old (out && stock) heuristic.
 */
function classifyStockClassFromRaw(raw) {
  const r = (raw ?? '').toString().trim().toLowerCase();
  if (!r) return 'unknown';

  const hasNotInStock =
    r.includes('not in stock') ||
    r.includes('no longer in stock') ||
    /\bnot\s+in\s+stock\b/.test(r);
  const hasOutOfStockPhrase =
    r.includes('out of stock') ||
    r.includes('out-of-stock') ||
    r.includes('out_of_stock') ||
    /\bout\s+of\s+stock\b/.test(r);
  const hasSoldOut = r.includes('sold out') || r.includes('sold-out') || /\bsold\s+out\b/.test(r);

  if (
    hasNotInStock ||
    hasOutOfStockPhrase ||
    hasSoldOut ||
    r.includes('not available') ||
    r === 'oos' ||
    r === 'unavailable' ||
    /\bunavailable\b/.test(r)
  ) {
    return 'oos';
  }

  const hasInStockWord =
    /\bin[\s_-]*stock\b/.test(r) ||
    r === 'available' ||
    r.includes('in_stock') ||
    r === 'yes' ||
    r === 'instock' ||
    r.includes('instock');

  if (hasInStockWord) {
    return 'avail';
  }

  if (r.includes('low stock') || r.includes('limited stock')) {
    return 'avail';
  }

  return 'unknown';
}

/** After merging multiple product lines at one date index: any OOS → oos. */
function mergeStockClassesAtIndex(stockArrays, i) {
  const classes = stockArrays.map((arr) => arr[i] || 'unknown');
  return mergeStockClassesList(classes);
}

function mergeStockClassesList(classes) {
  if (classes.some((c) => c === 'oos')) return 'oos';
  if (classes.some((c) => c === 'avail')) return 'avail';
  return 'unknown';
}

/** Match datasets that belong to the same chart cluster (same legend group + stock segment line). */
function clusterSeriesKeyForSummary(ds) {
  const gid =
    ds.legendGroupId != null && String(ds.legendGroupId) !== ''
      ? String(ds.legendGroupId)
      : [ds.productGroupName, ds.productName].find(Boolean) ||
        stripFlagEmoji(ds.displayLabel || ds.label || '');
  return `${gid}\0${ds.stockSegment || ''}`;
}

function readChartPointAt(dataset, idx) {
  if (!dataset?.data || idx < 0 || idx >= dataset.data.length) return null;
  const v = dataset.data[idx];
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Values at index from each price-type line in the same cluster (listed / promo / registration / actual). */
function snapshotPricesFromClusterAtIndex(datasets, idx) {
  const snap = { actual: null, regular: null, promo: null };
  datasets.forEach((d) => {
    const pt = d.priceType;
    if (!pt || !(pt in snap)) return;
    const val = readChartPointAt(d, idx);
    if (val != null) snap[pt] = val;
  });
  return snap;
}

/** Pick cluster snapshot value for this chart line’s price type (`ds.priceType`). */
function snapshotValueForLinePriceType(snap, priceTypeKey) {
  if (!snap || !priceTypeKey) return null;
  const key = String(priceTypeKey);
  if (!(key in snap)) return null;
  const v = snap[key];
  return v != null && Number.isFinite(v) ? v : null;
}

function formatSummaryPriceDisplay(value, indexMode, indexDp, priceDp) {
  if (value == null || !Number.isFinite(value) || value <= 0) return '—';
  if (indexMode) return `${Number(value).toFixed(Math.max(0, indexDp))}%`;
  const maxD = Math.max(0, priceDp);
  const rounded = Math.round(value * Math.pow(10, maxD)) / Math.pow(10, maxD);
  return parseFloat(rounded.toFixed(maxD)).toString();
}

function extractCountryFilterFromPriceDataset(ds) {
  if (ds.baseProductCountry) return String(ds.baseProductCountry).toLowerCase().trim();
  const gid = ds.legendGroupId;
  if (typeof gid === 'string' && gid.startsWith('cg:')) {
    const pipe = gid.indexOf('|');
    if (pipe > 3) return gid.slice(3, pipe).toLowerCase().trim();
  }
  return '';
}

function summarizeRetailersCountriesBrands(rows, titleSet, countryNorm) {
  const retailers = new Set();
  const countries = new Set();
  const brands = new Set();
  rows.forEach((item) => {
    if (!item?.title || !titleSet.has(item.title)) return;
    const c = (item.country || '').toString().toLowerCase().trim();
    if (countryNorm && c && c !== countryNorm) return;
    if (item.retailer) retailers.add(String(item.retailer).trim());
    if (item.country) countries.add(String(item.country).trim());
    if (item.brand) brands.add(String(item.brand).trim());
  });
  return {
    retailers: [...retailers].sort().join('; ') || '—',
    countries: [...countries].sort().join('; ') || '—',
    brands: [...brands].sort().join('; ') || '—',
  };
}

function stockLabelFromMergedClass(merged) {
  if (merged === 'oos') return 'Out of stock';
  if (merged === 'avail') return 'In stock';
  return 'Unknown';
}

/** Latest observation at chart last date (or nearest on/before) per SKU title for stock text. */
function stockStatusAtLastObservation(rows, titles, countryNorm, lastDateKey) {
  if (!titles?.length || !rows?.length) return '—';
  const titleSet = new Set(titles);
  const pool = [];
  titles.forEach((t) => {
    const candidates = rows
      .filter((item) => {
        if (item.title !== t) return false;
        if (countryNorm && String(item.country || '').toLowerCase().trim() !== countryNorm) return false;
        return Boolean(item.updated_at);
      })
      .map((item) => ({
        item,
        dk: new Date(item.updated_at).toISOString().slice(0, 10),
        ts: new Date(item.updated_at).getTime(),
      }))
      .filter((x) => !Number.isNaN(x.ts))
      .sort((a, b) => b.ts - a.ts);
    let chosen = null;
    if (lastDateKey) {
      chosen = candidates.find((x) => x.dk === lastDateKey);
      if (!chosen) chosen = candidates.find((x) => x.dk <= lastDateKey);
    }
    if (!chosen && candidates.length) chosen = candidates[0];
    if (chosen) pool.push(chosen.item);
  });
  if (!pool.length) return '—';
  const classes = pool.map((item) => classifyStockClassFromItem(item));
  return stockLabelFromMergedClass(mergeStockClassesList(classes));
}

/**
 * Chart line for "Available" (when stock split / single-segment mode): count unknown stock as
 * eligible — many feeds omit or use non-matching text for stock_status; strict `avail` only hid whole markets.
 * Grid filter "Available" remains strict via {@link rowMatchesAvailability}; this is chart-only.
 */
function stockClassMatchesAvailChartSegment(sc) {
  return sc === 'avail' || sc === 'unknown';
}

function createInitialTopFilters(productType, isMarketScope = false) {
  const isDevicesRoute = productType && PRODUCT_TYPE_ALIASES.devices.includes(normalizeValue(productType));
  return TOP_FILTERS.reduce((acc, filter) => {
    let defaultValue = Array.isArray(filter.options) ? filter.options[0] : '';
    if (filter.key === 'priceType') {
      defaultValue = 'Both';
    }
    if (filter.key === 'availability') {
      defaultValue = [...DEFAULT_AVAILABILITY_SELECTION];
    }
    if (filter.key === 'currencyIndex' && isDevicesRoute && !isMarketScope) {
      defaultValue = 'Index';
    }
    return { ...acc, [filter.key]: defaultValue };
  }, {});
}

/** Warning icon opens a popover with index baseline issues and one-click fixes (date range / exclude country on HQ). */
function BaseProductIndexAlertFixes({
  styleTokens,
  baseProductMissingInCountries = [],
  baseProductImputedGapLabels = [],
  baseProductGapSuggestions = [],
  isHqScope,
  onApplySuggestedDateRange,
  onExcludeCountry,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const formatRangeLabel = (startKey, endKey) => {
    if (!startKey || !endKey) return '';
    const a = dayjs(startKey);
    const b = dayjs(endKey);
    if (!a.isValid() || !b.isValid()) return `${startKey} – ${endKey}`;
    return `${a.toDate().toLocaleDateString()} – ${b.toDate().toLocaleDateString()}`;
  };

  const hasCountryMissing = baseProductMissingInCountries.length > 0;
  const hasDateGapInfo =
    baseProductImputedGapLabels.length > 0 || baseProductGapSuggestions.length > 0;
  const hasAlerts = hasCountryMissing || hasDateGapInfo;

  if (!hasAlerts) return null;

  const triggerTooltip = hasCountryMissing
    ? 'Base product missing in some countries — click for fixes'
    : 'Base product reference price was extended from known values — click for date-range suggestions to filter only dates where base product was available';

  return (
    <>
      <Tooltip title={triggerTooltip}>
        {hasCountryMissing ? (
          <IconButton
            size="small"
            onClick={(e) => setAnchorEl(e.currentTarget)}
            aria-label="Base product missing in countries — open fixes"
            sx={{ mt: 0.75, p: 0.25, color: 'warning.main' }}
          >
            <Warning sx={{ fontSize: 20 }} />
          </IconButton>
        ) : (
          <IconButton
            size="small"
            onClick={(e) => setAnchorEl(e.currentTarget)}
            aria-label="Base product date coverage — open info and fixes"
            sx={{ mt: 0.75, p: 0.25, color: 'info.main' }}
          >
            <InfoOutlined sx={{ fontSize: 20 }} />
          </IconButton>
        )}
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: { maxWidth: 440, p: 1.5 } }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          {hasCountryMissing
            ? 'Base product (100%) — issues and fixes'
            : 'Base product (100%) — date coverage'}
        </Typography>
        {baseProductMissingInCountries.length > 0 && (
          <Box sx={{ mb: 1.5, borderLeft: 3, borderColor: 'warning.main', pl: 1.25 }}>
            <Typography variant="caption" sx={{ display: 'block', mb: 0.75, lineHeight: 1.4 }}>
              Base product is not available in{' '}
              {baseProductMissingInCountries.map((c) => formatCountryName(c)).join(', ')}
            </Typography>
            {isHqScope &&
              baseProductMissingInCountries.map((code) => (
                <ListItemButton
                  key={`miss-${code}`}
                  dense
                  sx={{ borderRadius: 1, py: 0.75 }}
                  onClick={() => {
                    onExcludeCountry(code);
                    setAnchorEl(null);
                  }}
                >
                  <Typography variant="body2" sx={{ color: styleTokens.accent, fontWeight: 600 }}>
                    Exclude {formatCountryName(code)} from country filter
                  </Typography>
                </ListItemButton>
              ))}
          </Box>
        )}

        {baseProductGapSuggestions.length > 0
          ? baseProductGapSuggestions.map((sug, idx) => (
              <Box
                key={sug.id || `sug-${idx}`}
                sx={{ mb: 1.5, borderLeft: 3, borderColor: 'info.light', pl: 1.25 }}
              >
                {sug.summaryLine && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75, lineHeight: 1.45 }}>
                    {sug.summaryLine}
                  </Typography>
                )}
                {sug.suggestedStartKey && sug.suggestedEndKey && (
                  <ListItemButton
                    dense
                    sx={{ borderRadius: 1, py: 0.75, mb: 0.25 }}
                    onClick={() => {
                      onApplySuggestedDateRange(sug.suggestedStartKey, sug.suggestedEndKey);
                      setAnchorEl(null);
                    }}
                  >
                    <Typography variant="body2" sx={{ color: styleTokens.accent, fontWeight: 600 }}>
                      Click to change date range to {formatRangeLabel(sug.suggestedStartKey, sug.suggestedEndKey)}
                    </Typography>
                  </ListItemButton>
                )}
                {isHqScope && sug.countryCode && (
                  <ListItemButton
                    dense
                    sx={{ borderRadius: 1, py: 0.75 }}
                    onClick={() => {
                      onExcludeCountry(sug.countryCode);
                      setAnchorEl(null);
                    }}
                  >
                    <Typography variant="body2" sx={{ color: styleTokens.accent, fontWeight: 600 }}>
                      Exclude {formatCountryName(sug.countryCode)} from country filter
                    </Typography>
                  </ListItemButton>
                )}
              </Box>
            ))
          : baseProductImputedGapLabels.map((line, idx) => (
              <Typography key={`gap-fallback-${idx}`} variant="caption" sx={{ display: 'block', mb: 0.75, lineHeight: 1.45 }}>
                {line}
              </Typography>
            ))}
      </Popover>
    </>
  );
}

const CompactTopFilters = ({
  styleTokens,
  values,
  onChange,
  isMarketScope,
  selectedCountries,
  onCountryChange,
  countryOptions,
  countrySearchTerm,
  onCountrySearchChange,
  selectedRetailers,
  onRetailerChange,
  retailerOptions,
  retailerSearchTerm,
  onRetailerSearchChange,
  formatLabel,
  dateRange,
  previewDateRange,
  setPreviewDateRange,
  setDateRange,
  dateSliderValue,
  onDateSliderChange,
  onDateSliderCommit,
  availableDates,
  sanitizeSelectValues,
  selectedBrands,
  onBrandChange,
  brandOptions,
  brandSearchTerm,
  onBrandSearchChange,
  filteredProducts,
  matchingGroups,
  groupNameToProducts,
  modelGroupToProducts,
  groupNames,
  selectedProducts,
  onProductsChange,
  getSkuCountry,
  getSkuDisplayTitle,
  isSkuTranslationActive,
  isSkuTitleTranslating,
  onToggleSkuTitleTranslate,
  currencyOptions,
  selectedCurrency,
  onCurrencyChange,
  localCurrencyCode,
  selectedCategoryGroups,
  onCategoryGroupChange,
  categoryGroupOptions,
  categoryGroupSearchTerm,
  onCategoryGroupSearchChange,
  savedViews,
  selectedViewName,
  onViewChange,
  onDeleteView,
  onOpenSaveView,
  onResetView,
  baseProduct,
  baseProductSkuOverride,
  effectiveBaseProduct,
  compareToPriceType,
  onCompareToPriceTypeChange,
  onBaseProductSkuOverrideChange,
  specificBaseSkuOptions = [],
  onBaseProductChange,
  onOpenBaseProductAutoQueueDialog,
  baseProductAutoQueueRouteKey = 'sticks',
  baseProductOptions,
  baseProductMissingInCountries = [],
  baseProductImputedGapLabels = [],
  baseProductGapSuggestions = [],
  isHqScope = false,
  onApplyBaseProductSuggestedDateRange,
  onExcludeCountryForBase,
  selectedManufacturers,
  onManufacturerChange,
  manufacturerOptions = MANUFACTURER_OPTIONS,
  manufacturerSearchTerm,
  onManufacturerSearchChange,
  selectedPackSizes,
  onPackSizeChange,
  packSizeOptions,
  packSizeSearchTerm,
  onPackSizeSearchChange,
  customizableFilter = CUSTOMIZABLE_FILTER_DEFAULT,
  onCustomizableFilterChange,
  forNewUsersOnlyFilter = FOR_NEW_USERS_FILTER_DEFAULT,
  onForNewUsersOnlyFilterChange,
  starterKitProductFilter = STARTER_KIT_FILTER_DEFAULT,
  onStarterKitProductFilterChange,
  deviceColourOptions = ['All'],
  stickFlavourOptions = ['All'],
  productType,
  isStickRoute,
  priceDisplayMode,
  onPriceDisplayModeChange,
  activePriceTypes,
  onPriceTypeToggle,
  onPriceTypeSelect,
  allowMultiplePriceTypes = true,
  listedPriceDisabled = false,
  groupBy,
  onGroupByChange,
  scopeMode,
  additionalFilters = {},
  onAdditionalFiltersChange,
  additionalFiltersLayout = 'side',
  onAdditionalFiltersLayoutToggle,
}) => {
  const effectiveDateRange = previewDateRange || dateRange;
  const normalizedProductType = normalizeValue(productType);
  const isDevicesRoute = PRODUCT_TYPE_ALIASES.devices.includes(normalizedProductType);
  const showIncludeBundlesFilter = isDevicesRoute || isStickRoute;
  const includeBundlesFilterOptions = useMemo(() => {
    if (!showIncludeBundlesFilter) return [];
    const defs = isDevicesRoute
      ? INCLUDE_BUNDLES_FILTER_OPTIONS_BY_ROUTE.devices
      : INCLUDE_BUNDLES_FILTER_OPTIONS_BY_ROUTE.sticks;
    return defs
      .map(({ label, canonical }) => ({
        label,
        value: resolveCategoryGroupOptionValue(canonical, categoryGroupOptions),
      }))
      .filter((option) => option.value);
  }, [showIncludeBundlesFilter, isDevicesRoute, categoryGroupOptions]);
  const includeBundlesValue = useMemo(() => {
    const allowed = new Set(includeBundlesFilterOptions.map((option) => option.value));
    return (selectedCategoryGroups || []).filter((group) => allowed.has(group));
  }, [includeBundlesFilterOptions, selectedCategoryGroups]);

  const [retailerGroupsExpanded, setRetailerGroupsExpanded] = useState({
    manufacturer: true,
    thirdParty: true,
  });
  const selectedRetailersSet = useMemo(() => new Set(selectedRetailers || []), [selectedRetailers]);
  const filteredRetailers = useMemo(
    () => (retailerOptions || []).filter((retailer) =>
      retailer.toLowerCase().includes((retailerSearchTerm || '').toLowerCase())
    ),
    [retailerOptions, retailerSearchTerm]
  );
  const manufacturerRetailers = useMemo(
    () => filteredRetailers.filter((retailer) => !THIRD_PARTY_RETAILERS.has(normalizeValue(retailer))),
    [filteredRetailers]
  );
  const thirdPartyRetailers = useMemo(
    () => filteredRetailers.filter((retailer) => THIRD_PARTY_RETAILERS.has(normalizeValue(retailer))),
    [filteredRetailers]
  );
  const toggleRetailerGroupSelection = useCallback((groupRetailers) => {
    if (!groupRetailers.length) return;
    const allSelected = groupRetailers.every((retailer) => selectedRetailersSet.has(retailer));
    if (allSelected) {
      const removalSet = new Set(groupRetailers);
      onRetailerChange((selectedRetailers || []).filter((retailer) => !removalSet.has(retailer)));
      return;
    }
    onRetailerChange(Array.from(new Set([...(selectedRetailers || []), ...groupRetailers])));
  }, [onRetailerChange, selectedRetailers, selectedRetailersSet]);

  const compactFieldSx = {
    '& .MuiInputBase-root': { height: 30, fontSize: '0.7rem' },
    '& .MuiInputBase-input': { padding: '4px 8px' },
    '& .MuiInputLabel-root': { fontSize: '0.65rem', transform: 'translate(14px, 8px) scale(1)' },
    '& .MuiInputLabel-shrink': { transform: 'translate(14px, -6px) scale(0.85)' },
  };

  const compactFilterMultiSelectSx = {
    '& .MuiInputBase-root': { height: 30, fontSize: '0.7rem' },
    '& .MuiInputBase-input': { padding: '4px 8px' },
    '& .MuiSelect-select': {
      fontSize: '0.7rem',
      fontWeight: 400,
      py: 0,
      pr: '34px !important',
      display: 'flex',
      alignItems: 'center',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    '& .MuiInputLabel-root': { fontSize: '0.65rem', transform: 'translate(14px, 8px) scale(1)' },
    '& .MuiInputLabel-shrink': { transform: 'translate(14px, -6px) scale(0.85)' },
  };

  // Price View + Currency/Base Product — rendered in the fourth main-filter column (under Price Type).
  const priceViewAndCurrencyBlock = (
    <>
      {CURRENCY_INDEX_FILTER ? (
        <FormControl fullWidth size="small" sx={compactFieldSx}>
          <InputLabel>{CURRENCY_INDEX_FILTER.label}</InputLabel>
          <Select
            value={values[CURRENCY_INDEX_FILTER.key]}
            label={CURRENCY_INDEX_FILTER.label}
            onChange={(event) => onChange(CURRENCY_INDEX_FILTER.key, event.target.value)}
          >
            {(CURRENCY_INDEX_FILTER.options || []).map((option) => (
              <MenuItem key={`${CURRENCY_INDEX_FILTER.key}-${option}`} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : null}
      {values.currencyIndex === 'Absolute' && (
        <FormControl fullWidth size="small" sx={compactFieldSx}>
          <InputLabel id="top-currency-select-label">Currency</InputLabel>
          <Select
            labelId="top-currency-select-label"
            id="top-currency-select"
            value={selectedCurrency || ''}
            label="Currency"
            renderValue={(value) => {
              if (!value) return '';
              if (isMarketScope && localCurrencyCode && value === localCurrencyCode) {
                return `${value} (Local)`;
              }
              return value;
            }}
            onChange={(event) => {
              const value = event.target.value;
              if (value) onCurrencyChange(value);
            }}
          >
            {currencyOptions.map((option) => (
              <MenuItem key={`currency-${option}`} value={option}>
                {isMarketScope && localCurrencyCode && option === localCurrencyCode
                  ? `${option} (Local)`
                  : option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {values.currencyIndex === 'Index' && (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
          <FormControl fullWidth size="small" sx={compactFieldSx}>
            <InputLabel>Base Product (100%)</InputLabel>
            <Select
              value={
                baseProduct === BASE_PRODUCT_AUTO
                  ? BASE_PRODUCT_AUTO
                  : baseProduct || ''
              }
              label="Base Product (100%)"
              renderValue={(selected) => {
                if (!selected) return '';
                const compareLabelMap = {
                  [BASE_COMPARE_TYPE_AUTO]: 'Auto',
                  [BASE_COMPARE_TYPE_LISTED]: 'Regular',
                  [BASE_COMPARE_TYPE_PROMO]: 'Promo',
                };
                const compareLabel = compareLabelMap[compareToPriceType] || compareLabelMap[BASE_COMPARE_TYPE_AUTO];
                if (selected === BASE_PRODUCT_AUTO) {
                  const resolvedLabel = effectiveBaseProduct || 'no match';
                  const baseLabel = baseProductSkuOverride
                    ? `Auto (${resolvedLabel}) — ${baseProductSkuOverride}`
                    : `Auto (${resolvedLabel})`;
                  return `${baseLabel} (${compareLabel})`;
                }
                const baseLabel = baseProductSkuOverride ? `${selected} - ${baseProductSkuOverride}` : selected;
                return `${baseLabel} (${compareLabel})`;
              }}
              onChange={(event) => onBaseProductChange(event.target.value || null)}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              <MenuItem value={BASE_PRODUCT_AUTO}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, width: '100%' }}>
                  <span>Auto (by availability)</span>
                  <Tooltip
                    title="Configure priority order (shared with Brand Board). First product or SKU available in current filters becomes 100%."
                    arrow
                    placement="right"
                  >
                    <IconButton
                      size="small"
                      aria-label="Auto base product priority settings"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onOpenBaseProductAutoQueueDialog?.();
                      }}
                    >
                      <SettingsOutlined sx={{ fontSize: '0.95rem' }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </MenuItem>
              {(baseProductOptions || []).map((option) => {
                const isGroup = groupNames && groupNames.has(option);
                return (
                  <MenuItem key={`base-product-${option}`} value={option}>
                    {isGroup
                      ? <span style={{ fontWeight: 700, color: '#000' }}>{option}</span>
                      : option}
                  </MenuItem>
                );
              })}
              <Divider />
              <ListSubheader disableSticky sx={{ lineHeight: '20px', fontSize: '0.68rem', fontWeight: 700, color: 'text.secondary' }}>
                Base product settings
              </ListSubheader>
              <MenuItem
                dense
                disableRipple
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                sx={{ whiteSpace: 'normal', alignItems: 'flex-start', py: 0.5, cursor: 'default', '&:hover': { backgroundColor: 'transparent' } }}
              >
                <FormControl
                  fullWidth
                  size="small"
                  sx={{
                    mt: 0.25,
                    '& .MuiInputBase-root': { height: 30, fontSize: '0.7rem' },
                    '& .MuiInputLabel-root': { fontSize: '0.65rem', transform: 'translate(14px, 8px) scale(1)' },
                    '& .MuiInputLabel-shrink': { transform: 'translate(14px, -6px) scale(0.85)' },
                  }}
                >
                  <InputLabel>Compare to price type</InputLabel>
                  <Select
                    value={compareToPriceType || BASE_COMPARE_TYPE_AUTO}
                    label="Compare to price type"
                    onClick={(e) => e.stopPropagation()}
                    onChange={(event) => onCompareToPriceTypeChange(event.target.value)}
                  >
                    <MenuItem value={BASE_COMPARE_TYPE_AUTO} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                      <span>Automatically</span>
                      <Tooltip title="Compares regular to regular and promo to promo. If the base product lacks that price type on a date, falls back to regular, then promo (shown in chart hover)." arrow placement="right">
                        <InfoOutlined sx={{ fontSize: '0.85rem', color: 'text.disabled', flexShrink: 0 }} onClick={(e) => e.stopPropagation()} />
                      </Tooltip>
                    </MenuItem>
                    <MenuItem value={BASE_COMPARE_TYPE_LISTED}>Regular price</MenuItem>
                    <MenuItem value={BASE_COMPARE_TYPE_PROMO}>Promo price</MenuItem>
                  </Select>
                </FormControl>
              </MenuItem>
              <MenuItem
                dense
                disableRipple
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
                sx={{ whiteSpace: 'normal', alignItems: 'flex-start', py: 0.5, cursor: 'default', '&:hover': { backgroundColor: 'transparent' } }}
              >
                <FormControl
                  fullWidth
                  size="small"
                  sx={{
                    mt: 0.25,
                    '& .MuiInputBase-root': { height: 30, fontSize: '0.7rem' },
                    '& .MuiInputLabel-root': { fontSize: '0.65rem', transform: 'translate(14px, 8px) scale(1)' },
                    '& .MuiInputLabel-shrink': { transform: 'translate(14px, -6px) scale(0.85)' },
                  }}
                >
                  <InputLabel>Specific SKU (optional)</InputLabel>
                  <Select
                    value={baseProductSkuOverride || BASE_PRODUCT_SKU_AUTO}
                    label="Specific SKU (optional)"
                    onClick={(e) => e.stopPropagation()}
                    onChange={(event) => onBaseProductSkuOverrideChange(event.target.value === BASE_PRODUCT_SKU_AUTO ? '' : event.target.value)}
                    disabled={!effectiveBaseProduct || specificBaseSkuOptions.length === 0}
                  >
                    <MenuItem value={BASE_PRODUCT_SKU_AUTO}>
                      <em>Auto (use selected base product)</em>
                    </MenuItem>
                    {specificBaseSkuOptions.map((sku) => (
                      <MenuItem key={`base-product-sku-${sku}`} value={sku}>
                        {sku}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </MenuItem>
            </Select>
          </FormControl>
          {(baseProductMissingInCountries?.length > 0 ||
            baseProductImputedGapLabels?.length > 0 ||
            baseProductGapSuggestions?.length > 0) && (
            <BaseProductIndexAlertFixes
              styleTokens={styleTokens}
              baseProductMissingInCountries={baseProductMissingInCountries}
              baseProductImputedGapLabels={baseProductImputedGapLabels}
              baseProductGapSuggestions={baseProductGapSuggestions}
              isHqScope={isHqScope}
              onApplySuggestedDateRange={onApplyBaseProductSuggestedDateRange}
              onExcludeCountry={onExcludeCountryForBase}
            />
          )}
        </Box>
      )}
    </>
  );

  // Bundle Selection — third main-filter column (with Limited Edition + Starter Kit).
  const bundleSelectionBlock = showIncludeBundlesFilter ? (
    <FormControl
      fullWidth
      size="small"
      sx={{
        '& .MuiInputBase-root': { height: 30, fontSize: '0.7rem' },
        '& .MuiInputBase-input': { padding: '4px 8px' },
        '& .MuiSelect-select': {
          fontSize: '0.7rem',
          fontWeight: 400,
          py: 0,
          pr: '34px !important',
          display: 'flex',
          alignItems: 'center',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
        '& .MuiInputLabel-root': { fontSize: '0.65rem', transform: 'translate(14px, 8px) scale(1)' },
        '& .MuiInputLabel-shrink': { transform: 'translate(14px, -6px) scale(0.85)' },
      }}
    >
      <InputLabel id="top-include-bundles-select-label">Bundle Selection</InputLabel>
      <Select
        labelId="top-include-bundles-select-label"
        id="top-include-bundles-select"
        value={includeBundlesValue}
        label="Bundle Selection"
        multiple
        onChange={(e) => onCategoryGroupChange(sanitizeSelectValues(e.target.value))}
        input={<OutlinedInput label="Bundle Selection" />}
        renderValue={(selected) => {
          const keys = Array.isArray(selected) ? selected : [];
          return keys
            .map(
              (value) =>
                includeBundlesFilterOptions.find((option) => option.value === value)?.label || value
            )
            .join(', ');
        }}
        MenuProps={{ PaperProps: { style: { maxHeight: 280, width: 260 } } }}
        endAdornment={(
          <InputAdornment position="end" sx={{ mr: 2.25 }}>
            <Tooltip
              title={
                isDevicesRoute
                  ? 'Device bundle is defined as device + sticks/accessories'
                  : 'Sticks bundle is defined as multiple brands offer, for multiple packs use number of units filter'
              }
              arrow
              placement="right"
            >
              <InfoOutlined sx={{ fontSize: '0.95rem', color: 'text.disabled' }} />
            </Tooltip>
          </InputAdornment>
        )}
      >
        {includeBundlesFilterOptions.map(({ label, value }) => (
          <MenuItem
            key={`include-bundles-${value}`}
            value={value}
            dense
            sx={{
              py: 0.25,
              minHeight: 'auto',
              '& .MuiListItemText-primary': { fontSize: '0.875rem' },
              '& .MuiCheckbox-root': { py: 0.5 },
            }}
          >
            <Checkbox checked={includeBundlesValue.includes(value)} size="small" sx={{ py: 0 }} />
            <ListItemText primary={label} sx={{ my: 0 }} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  ) : null;

  const editionTypeBlock = (
    <FormControl fullWidth size="small" sx={compactFilterMultiSelectSx}>
      <InputLabel id="top-edition-type-select-label">Limited Edition</InputLabel>
      <Select
        labelId="top-edition-type-select-label"
        id="top-edition-type-select"
        value={normalizeLimitedEditionFilter(additionalFilters?.limitedEdition)}
        label="Limited Edition"
        onChange={(e) =>
          onAdditionalFiltersChange?.((prev) => ({
            ...prev,
            limitedEdition: e.target.value,
          }))
        }
      >
        {LIMITED_EDITION_FILTER_OPTIONS.map((opt) => (
          <MenuItem key={opt} value={opt} dense sx={{ py: 0.25, minHeight: 'auto', fontSize: '0.875rem' }}>
            {opt}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const priceTypeBlock = (
    <FormControl
      fullWidth
      size="small"
      sx={{
        '& .MuiInputBase-root': { height: 30, fontSize: '0.7rem' },
        '& .MuiInputBase-input': { padding: '4px 8px' },
        '& .MuiSelect-select': {
          fontSize: '0.7rem',
          fontWeight: 400,
          py: 0,
          display: 'flex',
          alignItems: 'center',
        },
        '& .MuiInputLabel-root': { fontSize: '0.65rem', transform: 'translate(14px, 8px) scale(1)' },
        '& .MuiInputLabel-shrink': { transform: 'translate(14px, -6px) scale(0.85)' },
      }}
    >
      <InputLabel>Price Type</InputLabel>
      <Select
        multiple={allowMultiplePriceTypes}
        label="Price Type"
        value={
          allowMultiplePriceTypes
            ? PRICE_TYPE_OPTIONS.filter(({ key }) => activePriceTypes?.has(key)).map((o) => o.key)
            : (PRICE_TYPE_OPTIONS.find(({ key }) => activePriceTypes?.has(key))?.key || 'regular')
        }
        onChange={(e) => {
          if (!allowMultiplePriceTypes) {
            onPriceTypeSelect && onPriceTypeSelect(e.target.value);
            return;
          }
          const next = new Set(e.target.value);
          PRICE_TYPE_OPTIONS.forEach(({ key }) => {
            const was = activePriceTypes ? activePriceTypes.has(key) : false;
            const is = next.has(key);
            if (was !== is) onPriceTypeToggle && onPriceTypeToggle(key, is);
          });
        }}
        input={<OutlinedInput label="Price Type" />}
        renderValue={(selected) => {
          const keys = allowMultiplePriceTypes
            ? (Array.isArray(selected) ? selected : [])
            : (selected ? [selected] : []);
          return keys
            .map((k) => PRICE_TYPE_OPTIONS.find((o) => o.key === k)?.label || k)
            .join(', ');
        }}
        MenuProps={{ PaperProps: { style: { maxHeight: 300, width: 260 } } }}
      >
        {PRICE_TYPE_OPTIONS.flatMap(({ key, label, italic, info, dividerBefore }) => {
          const isListedDisabled = listedPriceDisabled && key === 'regular';
          const items = [];
          if (dividerBefore) items.push(<Divider key={`divider-${key}`} sx={{ my: 0.5 }} />);
          items.push(
            <MenuItem
              key={key}
              value={key}
              dense
              disabled={isListedDisabled}
              sx={{ py: 0.25, minHeight: 'auto', '& .MuiListItemText-primary': { fontSize: '0.875rem', ...(italic ? { fontStyle: 'italic', color: 'text.secondary' } : {}) } }}
            >
              {allowMultiplePriceTypes && (
                <Checkbox checked={!!(activePriceTypes && activePriceTypes.has(key))} size="small" sx={{ py: 0 }} />
              )}
              <ListItemText primary={label} sx={{ my: 0 }} />
              {info && (
                <Tooltip
                  title={
                    isListedDisabled
                      ? 'Regular price applies only to standalone products (Devices / Sticks), not bundles.'
                      : info
                  }
                  arrow
                  placement="right"
                  onClick={(e) => e.stopPropagation()}
                >
                  <InfoOutlined sx={{ fontSize: '0.9rem', color: 'text.disabled', ml: 0.5, flexShrink: 0, '&:hover': { color: 'text.secondary' } }} />
                </Tooltip>
              )}
            </MenuItem>
          );
          return items;
        })}
      </Select>
    </FormControl>
  );

  const starterKitFilterBlock = isDevicesRoute ? (
    <FormControl fullWidth size="small" sx={compactFilterMultiSelectSx}>
      <InputLabel id="top-starter-kit-filter-label">Starter Kit</InputLabel>
      <Select
        labelId="top-starter-kit-filter-label"
        id="top-starter-kit-filter"
        value={normalizeStarterKitFilter(starterKitProductFilter)}
        label="Starter Kit"
        onChange={(e) => onStarterKitProductFilterChange?.(e.target.value)}
      >
        {STARTER_KIT_FILTER_OPTIONS.map((opt) => (
          <MenuItem key={opt} value={opt} dense sx={{ py: 0.25, minHeight: 'auto', fontSize: '0.875rem' }}>
            {opt}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  ) : null;

  return (
  <Paper
    elevation={0}
    sx={{
      p: 1.5,
      mb: 2,
      backgroundColor: styleTokens.panelAlt,
      border: `1px solid ${styleTokens.border}`,
    }}
  >
    <Box
      sx={{
        display: 'grid',
        // Brand/Manufacturer/Product is ~30% thinner than a former 2-col span (1.4fr vs 2fr).
        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.4fr) repeat(8, minmax(0, 1fr))',
        gap: 1,
        alignItems: 'flex-start',
      }}
    >
      {TOP_FILTERS.map((filter) => {
        // Currency is rendered inside the Price View column (Absolute mode).
        if (filter.key === 'currency') {
          return null;
        }
        
        // Skip filters that live in Additional Filters / elsewhere — avoid empty grid columns.
        if (filter.key === 'categoryGroup' || filter.key === 'packSize' || filter.key === 'availability') {
          return null;
        }

        return (
          <Box
            key={filter.key}
            sx={
              filter.key === 'brandModel'
                ? { gridColumn: 'span 1', minWidth: 0, width: '100%' }
                : { gridColumn: 'span 1' }
            }
          >
            {filter.key === 'priceType' ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.75,
              }}
            >
              {bundleSelectionBlock}
              {editionTypeBlock}
              {starterKitFilterBlock}
            </Box>
          ) : filter.key === 'currencyIndex' ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.75,
              }}
            >
              {priceTypeBlock}
              {priceViewAndCurrencyBlock}
            </Box>
          ) : filter.key !== 'currency' ? (
            <>
              {filter.key === 'retailer' ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {!isMarketScope && (
                <FormControl
                  fullWidth
                  size="small"
                  sx={{
                    '& .MuiInputBase-root': {
                      height: 30,
                      fontSize: '0.7rem',
                    },
                    '& .MuiInputBase-input': {
                      padding: '4px 8px',
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '0.65rem',
                      transform: 'translate(14px, 8px) scale(1)',
                    },
                    '& .MuiInputLabel-shrink': {
                      transform: 'translate(14px, -6px) scale(0.85)',
                    },
                  }}
                >
                  <InputLabel id="top-country-multiple-select-label">Countries</InputLabel>
                  <Select
                    labelId="top-country-multiple-select-label"
                    id="top-country-multiple-select"
                    value={selectedCountries}
                    label="Countries"
                    multiple
                    onChange={(e) => onCountryChange(sanitizeSelectValues(e.target.value))}
                    input={<OutlinedInput label="Countries" />}
                    renderValue={(selected) => selected.map(formatLabel).join(', ')}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: '400px',
                          width: '280px',
                        },
                      },
                      autoFocus: false,
                    }}
                    onClose={() => onCountrySearchChange('')}
                  >
                    <ListSubheader>
                      <TextField
                        size="small"
                        autoFocus
                        placeholder="Search countries..."
                        fullWidth
                        value={countrySearchTerm}
                        onChange={(e) => onCountrySearchChange(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon fontSize="small" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </ListSubheader>
                    <Box sx={{ px: 1, py: 0.5 }}>
                      <Button
                        variant="text"
                        size="small"
                        fullWidth
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (selectedCountries.length === countryOptions.length) {
                            onCountryChange([]);
                          } else {
                            onCountryChange(countryOptions);
                          }
                        }}
                      >
                        Select all
                      </Button>
                    </Box>
                    {countryOptions
                      .filter((country) =>
                        country.toLowerCase().includes(countrySearchTerm.toLowerCase())
                      )
                      .map((country) => (
                        <MenuItem
                          key={country}
                          value={country}
                          dense
                          sx={{
                            py: 0.25,
                            minHeight: 'auto',
                            '& .MuiListItemText-primary': {
                              fontSize: '0.875rem',
                            },
                            '& .MuiCheckbox-root': {
                              py: 0.5,
                            },
                          }}
                        >
                          <Checkbox checked={selectedCountries.includes(country)} size="small" sx={{ py: 0 }} />
                          <ListItemText primary={formatLabel(country)} sx={{ my: 0 }} />
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              )}
              <FormControl
                fullWidth
                size="small"
                sx={{
                  '& .MuiInputBase-root': {
                    height: 30,
                    fontSize: '0.7rem',
                  },
                  '& .MuiInputBase-input': {
                    padding: '4px 8px',
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.65rem',
                    transform: 'translate(14px, 8px) scale(1)',
                  },
                  '& .MuiInputLabel-shrink': {
                    transform: 'translate(14px, -6px) scale(0.85)',
                  },
                }}
              >
                <InputLabel id="top-retailer-multiple-select-label">Retailer</InputLabel>
                <Select
                  labelId="top-retailer-multiple-select-label"
                  id="top-retailer-multiple-select"
                  value={selectedRetailers}
                  label="Retailer"
                  multiple
                  onChange={(e) => onRetailerChange(sanitizeSelectValues(e.target.value))}
                  input={<OutlinedInput label="Retailer" />}
                  renderValue={(selected) => selected.map(formatLabel).join(', ')}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: '400px',
                        width: '280px',
                      },
                    },
                    autoFocus: false,
                  }}
                  onClose={() => onRetailerSearchChange('')}
                >
                  <ListSubheader>
                    <TextField
                      size="small"
                      autoFocus
                      placeholder="Search retailers..."
                      fullWidth
                      value={retailerSearchTerm}
                      onChange={(e) => onRetailerSearchChange(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </ListSubheader>
                  <Box sx={{ px: 1, py: 0.5 }}>
                    <Button
                      variant="text"
                      size="small"
                      fullWidth
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (selectedRetailers.length === retailerOptions.length) {
                          onRetailerChange([]);
                        } else {
                          onRetailerChange(retailerOptions);
                        }
                      }}
                    >
                      Select all
                    </Button>
                  </Box>
                  {(() => {
                    const renderRetailerItem = (retailer) => (
                      <MenuItem
                        key={retailer}
                        value={retailer}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const isSelected = selectedRetailersSet.has(retailer);
                          if (isSelected) {
                            onRetailerChange((selectedRetailers || []).filter((r) => r !== retailer));
                          } else {
                            onRetailerChange([...(selectedRetailers || []), retailer]);
                          }
                        }}
                        dense
                        sx={{
                          py: 0.25,
                          minHeight: 'auto',
                          '& .MuiListItemText-primary': {
                            fontSize: '0.875rem',
                          },
                          '& .MuiCheckbox-root': {
                            py: 0.5,
                          },
                        }}
                      >
                        <Checkbox checked={selectedRetailersSet.has(retailer)} size="small" sx={{ py: 0 }} />
                        <ListItemText primary={formatLabel(retailer)} sx={{ my: 0 }} />
                      </MenuItem>
                    );
                    return (
                      <>
                        {manufacturerRetailers.length > 0 && (
                          <ListSubheader
                            disableSticky
                            sx={{
                              fontSize: '0.72rem',
                              color: 'text.secondary',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              py: 0.25,
                            }}
                          >
                            <Button
                              size="small"
                              variant="text"
                              sx={{ minWidth: 0, p: 0, textTransform: 'none', fontSize: '0.75rem', fontWeight: 600 }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                toggleRetailerGroupSelection(manufacturerRetailers);
                              }}
                            >
                              Manufacturer
                            </Button>
                            <IconButton
                              size="small"
                              sx={{ p: 0.25 }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setRetailerGroupsExpanded((prev) => ({
                                  ...prev,
                                  manufacturer: !prev.manufacturer,
                                }));
                              }}
                            >
                              {retailerGroupsExpanded.manufacturer ? <ExpandLess sx={{ fontSize: 14 }} /> : <ExpandMore sx={{ fontSize: 14 }} />}
                            </IconButton>
                          </ListSubheader>
                        )}
                        {retailerGroupsExpanded.manufacturer && manufacturerRetailers.map(renderRetailerItem)}
                        {thirdPartyRetailers.length > 0 && (
                          <ListSubheader
                            disableSticky
                            sx={{
                              fontSize: '0.72rem',
                              color: 'text.secondary',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              py: 0.25,
                            }}
                          >
                            <Button
                              size="small"
                              variant="text"
                              sx={{ minWidth: 0, p: 0, textTransform: 'none', fontSize: '0.75rem', fontWeight: 600 }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                toggleRetailerGroupSelection(thirdPartyRetailers);
                              }}
                            >
                              3rd party
                            </Button>
                            <IconButton
                              size="small"
                              sx={{ p: 0.25 }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setRetailerGroupsExpanded((prev) => ({
                                  ...prev,
                                  thirdParty: !prev.thirdParty,
                                }));
                              }}
                            >
                              {retailerGroupsExpanded.thirdParty ? <ExpandLess sx={{ fontSize: 14 }} /> : <ExpandMore sx={{ fontSize: 14 }} />}
                            </IconButton>
                          </ListSubheader>
                        )}
                        {retailerGroupsExpanded.thirdParty && thirdPartyRetailers.map(renderRetailerItem)}
                      </>
                    );
                  })()}
                </Select>
              </FormControl>
            </Box>
          ) : null}
          {filter.key === 'brandModel' ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <FormControl
                fullWidth
                size="small"
                sx={{
                  '& .MuiInputBase-root': {
                    height: 30,
                    fontSize: '0.7rem',
                  },
                  '& .MuiInputBase-input': {
                    padding: '4px 8px',
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.65rem',
                    transform: 'translate(14px, 8px) scale(1)',
                  },
                  '& .MuiInputLabel-shrink': {
                    transform: 'translate(14px, -6px) scale(0.85)',
                  },
                }}
              >
                <InputLabel id="top-manufacturer-multiple-select-label">Manufacturer</InputLabel>
                <Select
                  labelId="top-manufacturer-multiple-select-label"
                  id="top-manufacturer-multiple-select"
                  value={selectedManufacturers || []}
                  label="Manufacturer"
                  multiple
                  onChange={(e) => onManufacturerChange(sanitizeSelectValues(e.target.value))}
                  input={<OutlinedInput label="Manufacturer" />}
                  renderValue={(selected) => (selected || []).join(', ')}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: '400px',
                        width: '280px',
                      },
                    },
                    autoFocus: false,
                  }}
                  onClose={() => onManufacturerSearchChange('')}
                >
                  <ListSubheader>
                    <TextField
                      size="small"
                      autoFocus
                      placeholder="Search manufacturers..."
                      fullWidth
                      value={manufacturerSearchTerm || ''}
                      onChange={(e) => onManufacturerSearchChange(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </ListSubheader>
                  <Box sx={{ px: 1, py: 0.5 }}>
                    <Button
                      variant="text"
                      size="small"
                      fullWidth
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const sel = selectedManufacturers || [];
                        if (sel.length === manufacturerOptions.length) {
                          onManufacturerChange([]);
                        } else {
                          onManufacturerChange([...manufacturerOptions]);
                        }
                        onProductsChange([]);
                      }}
                    >
                      Select all
                    </Button>
                  </Box>
                  {manufacturerOptions
                    .filter((m) =>
                      (manufacturerSearchTerm || '') === '' ||
                      m.toLowerCase().includes((manufacturerSearchTerm || '').toLowerCase())
                    )
                    .map((manufacturer) => (
                      <MenuItem
                        key={manufacturer}
                        value={manufacturer}
                        dense
                        sx={{
                          py: 0.25,
                          minHeight: 'auto',
                          '& .MuiListItemText-primary': {
                            fontSize: '0.875rem',
                          },
                          '& .MuiCheckbox-root': {
                            py: 0.5,
                          },
                        }}
                      >
                        <Checkbox checked={(selectedManufacturers || []).includes(manufacturer)} size="small" sx={{ py: 0 }} />
                        <ListItemText primary={manufacturer} sx={{ my: 0 }} />
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              <FormControl
                fullWidth
                size="small"
                sx={{
                  '& .MuiInputBase-root': {
                    height: 30,
                    fontSize: '0.7rem',
                  },
                  '& .MuiInputBase-input': {
                    padding: '4px 8px',
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '0.65rem',
                    transform: 'translate(14px, 8px) scale(1)',
                  },
                  '& .MuiInputLabel-shrink': {
                    transform: 'translate(14px, -6px) scale(0.85)',
                  },
                }}
              >
                <InputLabel id="top-brand-multiple-select-label">Brand</InputLabel>
                <Select
                  labelId="top-brand-multiple-select-label"
                  id="top-brand-multiple-select"
                  value={selectedBrands}
                  label="Brand"
                  multiple
                  onChange={(e) => onBrandChange(sanitizeSelectValues(e.target.value))}
                  input={<OutlinedInput label="Brand" />}
                  renderValue={(selected) => selected.join(', ')}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: '400px',
                        width: '280px',
                      },
                    },
                    autoFocus: false,
                  }}
                  onClose={() => onBrandSearchChange('')}
                >
                  <ListSubheader>
                    <TextField
                      size="small"
                      autoFocus
                      placeholder="Search brands..."
                      fullWidth
                      value={brandSearchTerm}
                      onChange={(e) => onBrandSearchChange(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </ListSubheader>
                  <Box sx={{ px: 1, py: 0.5 }}>
                    <Button
                      variant="text"
                      size="small"
                      fullWidth
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (selectedBrands.length === brandOptions.length) {
                          onBrandChange([]);
                        } else {
                          onBrandChange(brandOptions);
                        }
                        onProductsChange([]);
                      }}
                    >
                      Select all
                    </Button>
                  </Box>
                  {brandOptions
                    .filter((brand) =>
                      brand.toLowerCase().includes(brandSearchTerm.toLowerCase())
                    )
                    .map((brand) => (
                      <MenuItem
                        key={brand}
                        value={brand}
                        dense
                        sx={{
                          py: 0.25,
                          minHeight: 'auto',
                          '& .MuiListItemText-primary': {
                            fontSize: '0.875rem',
                          },
                          '& .MuiCheckbox-root': {
                            py: 0.5,
                          },
                        }}
                      >
                        <Checkbox checked={selectedBrands.includes(brand)} size="small" sx={{ py: 0 }} />
                        <ListItemText primary={brand} sx={{ my: 0 }} />
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              <ModelProductTreeSelect
                filteredSkuTitles={filteredProducts}
                matchingGroups={matchingGroups}
                groupNameToProducts={groupNameToProducts}
                modelGroupToProducts={modelGroupToProducts}
                selectedProducts={selectedProducts}
                onProductsChange={onProductsChange}
                label="Model / Product(s)"
                placeholder={selectedProducts.length > 0 ? '' : 'Search models / products...'}
                compact
                fullWidth
                popperMinWidth={420}
                getSkuCountry={getSkuCountry}
                getDisplayTitle={getSkuDisplayTitle}
                isTranslationActive={isSkuTranslationActive}
                isTranslatingTitle={isSkuTitleTranslating}
                onToggleTranslate={onToggleSkuTitleTranslate}
              />
            </Box>
          ) : null}
          {filter.key === 'currency' ? (
            <FormControl
              fullWidth
              size="small"
              sx={{
                '& .MuiInputBase-root': {
                  height: 30,
                  fontSize: '0.7rem',
                },
                '& .MuiInputBase-input': {
                  padding: '4px 8px',
                },
                '& .MuiInputLabel-root': {
                  fontSize: '0.65rem',
                  transform: 'translate(14px, 8px) scale(1)',
                },
                '& .MuiInputLabel-shrink': {
                  transform: 'translate(14px, -6px) scale(0.85)',
                },
              }}
            >
              <InputLabel id="top-currency-select-label">Currency</InputLabel>
              <Select
                labelId="top-currency-select-label"
                id="top-currency-select"
                value={selectedCurrency || ''}
                label="Currency"
                onChange={(event) => {
                  const value = event.target.value;
                  if (value) onCurrencyChange(value);
                }}
                renderValue={(value) => {
                  if (!value) return '';
                  if (isMarketScope && localCurrencyCode && value === localCurrencyCode) {
                    return `${value} (Local)`;
                  }
                  return value;
                }}
              >
                {currencyOptions.map((option) => (
                  <MenuItem key={`currency-${option}`} value={option}>
                    {isMarketScope && localCurrencyCode && option === localCurrencyCode
                      ? `${option} (Local)`
                      : option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : null}
          {filter.key === 'timeScale' ? (
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.75,
                }}
              >
                <DatePicker
                  label="Start Date"
                  value={effectiveDateRange[0]}
                  onChange={(newValue) => {
                    setPreviewDateRange(null);
                    setDateRange([newValue, dateRange[1]]);
                  }}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      InputLabelProps: { sx: { fontSize: '0.65rem' } },
                      sx: {
                        '& .MuiInputBase-root': { height: 30, fontSize: '0.7rem' },
                        '& .MuiInputBase-input': { padding: '4px 8px' },
                      },
                    },
                  }}
                />
                <DatePicker
                  label="End Date"
                  value={effectiveDateRange[1]}
                  onChange={(newValue) => {
                    setPreviewDateRange(null);
                    setDateRange([dateRange[0], newValue]);
                  }}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      InputLabelProps: { sx: { fontSize: '0.65rem' } },
                      sx: {
                        '& .MuiInputBase-root': { height: 30, fontSize: '0.7rem' },
                        '& .MuiInputBase-input': { padding: '4px 8px' },
                      },
                    },
                  }}
                />
                <Slider
                  value={Array.isArray(dateSliderValue) ? dateSliderValue : [dateSliderValue, dateSliderValue]}
                  onChange={onDateSliderChange}
                  onChangeCommitted={onDateSliderCommit}
                  min={0}
                  max={Math.max(availableDates.length - 1, 0)}
                  step={1}
                  disableSwap
                  valueLabelDisplay="off"
                  sx={{
                    px: 0.5,
                    mx: '5px',
                    width: 'calc(100% - 20px)',
                    color: styleTokens.accent,
                    '& .MuiSlider-track': { backgroundColor: styleTokens.accent },
                    '& .MuiSlider-rail': { backgroundColor: styleTokens.accentSoft },
                    '& .MuiSlider-thumb': { borderColor: styleTokens.accent },
                  }}
                />
              </Box>
            </LocalizationProvider>
          ) : null}
            </>
          ) : null}
        </Box>
        );
      })}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
          gridColumn: 'span 1',
          width: 'calc(100% + 20px)',
          minWidth: 0,
        }}
      >
        <Autocomplete
          options={['Unsaved view', ...(savedViews || []).map((view) => view.name)]}
          value={selectedViewName || null}
          onChange={onViewChange}
          getOptionDisabled={(option) => option === 'Unsaved view'}
          size="small"
          sx={{
            '& .MuiInputBase-root': {
              height: 30,
              fontSize: '0.7rem',
            },
            '& .MuiInputBase-input': {
              padding: '4px 8px',
            },
            '& .MuiInputLabel-root': {
              fontSize: '0.65rem',
              transform: 'translate(14px, 8px) scale(1)',
            },
            '& .MuiInputLabel-shrink': {
              transform: 'translate(14px, -6px) scale(0.85)',
            },
          }}
          renderOption={(props, option) => (
            <Box
              component="li"
              {...props}
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
              <Typography variant="body2">{option}</Typography>
              {option !== 'Unsaved view' && (
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onDeleteView(option);
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Saved views"
              size="small"
              placeholder="Select a view"
              inputProps={{
                ...params.inputProps,
                readOnly: true,
              }}
            />
          )}
        />
        <Button
          variant="outlined"
          size="small"
          onClick={onOpenSaveView}
          sx={{
            minHeight: 30,
            py: 0.3,
            whiteSpace: 'nowrap',
            color: styleTokens.accent,
            borderColor: styleTokens.accent,
            '&:hover': {
              borderColor: styleTokens.accent,
              backgroundColor: styleTokens.chipBg,
            },
          }}
        >
          Save current view
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={onResetView}
          sx={{
            minHeight: 30,
            py: 0.3,
            whiteSpace: 'nowrap',
            color: styleTokens.accent,
            borderColor: styleTokens.accent,
            '&:hover': {
              borderColor: styleTokens.accent,
              backgroundColor: styleTokens.chipBg,
            },
          }}
        >
          Reset view
        </Button>
      </Box>
      {additionalFiltersLayout === 'top' && !isStickRoute && (
        <Box
          sx={{
            // brandModel is 1 col (was 2), so additional filters start one column earlier.
            gridColumn: '7 / -1',
            justifySelf: 'stretch',
            alignSelf: 'start',
            width: '100%',
            ml: '20px',
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            borderLeft: '1px solid',
            borderColor: 'divider',
            pl: '9px',
            mt: '-10px',
            // Keep outlined labels / title inside the scrollport (overflow-x:auto otherwise clips them).
            pt: '10px',
            minWidth: 0,
            overflowX: 'auto',
            scrollbarWidth: 'thin',
            scrollbarGutter: 'stable',
            '&::-webkit-scrollbar': { height: 8 },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.28)',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0,0,0,0.06)',
              borderRadius: 4,
            },
            position: 'relative',
          }}
        >
          <MockFiltersPanel
            styleTokens={styleTokens}
            isStickRoute={isStickRoute}
            packSizeOptions={packSizeOptions}
            selectedPackSizes={selectedPackSizes}
            onPackSizeChange={onPackSizeChange}
            priceDisplayMode={priceDisplayMode}
            onPriceDisplayModeChange={onPriceDisplayModeChange}
            deviceColourOptions={deviceColourOptions}
            stickFlavourOptions={stickFlavourOptions}
            additionalFilters={additionalFilters}
            onAdditionalFiltersChange={onAdditionalFiltersChange}
            customizableFilter={customizableFilter}
            onCustomizableFilterChange={onCustomizableFilterChange}
            forNewUsersOnlyFilter={forNewUsersOnlyFilter}
            onForNewUsersOnlyFilterChange={onForNewUsersOnlyFilterChange}
            layoutMode="top"
            onLayoutToggle={onAdditionalFiltersLayoutToggle}
            renderHeader
            renderControls
            showAvailabilityControl
            showProductAttributesFilter
            showPackSizeControl={false}
            showUnitPriceControl={false}
            availabilityValue={values.availability}
            onAvailabilityChange={(next) => onChange('availability', next)}
            groupBy={groupBy}
            onGroupByChange={onGroupByChange}
            scopeMode={scopeMode}
          />
        </Box>
      )}
      {additionalFiltersLayout === 'top' && isStickRoute && (
        <Box
          sx={{
            // brandModel is 1 col (was 2), so additional filters start one column earlier.
            gridColumn: '7 / -1',
            justifySelf: 'stretch',
            alignSelf: 'start',
            width: '100%',
            ml: '20px',
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            borderLeft: '1px solid',
            borderColor: 'divider',
            pl: '9px',
            mt: '-10px',
            // Keep outlined labels / title inside the scrollport (overflow-x:auto otherwise clips them).
            pt: '10px',
            minWidth: 0,
            overflowX: 'auto',
            scrollbarWidth: 'thin',
            scrollbarGutter: 'stable',
            '&::-webkit-scrollbar': { height: 8 },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.28)',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0,0,0,0.06)',
              borderRadius: 4,
            },
            position: 'relative',
          }}
        >
          <MockFiltersPanel
            styleTokens={styleTokens}
            isStickRoute={isStickRoute}
            packSizeOptions={packSizeOptions}
            selectedPackSizes={selectedPackSizes}
            onPackSizeChange={onPackSizeChange}
            priceDisplayMode={priceDisplayMode}
            onPriceDisplayModeChange={onPriceDisplayModeChange}
            deviceColourOptions={deviceColourOptions}
            stickFlavourOptions={stickFlavourOptions}
            additionalFilters={additionalFilters}
            onAdditionalFiltersChange={onAdditionalFiltersChange}
            customizableFilter={customizableFilter}
            onCustomizableFilterChange={onCustomizableFilterChange}
            forNewUsersOnlyFilter={forNewUsersOnlyFilter}
            onForNewUsersOnlyFilterChange={onForNewUsersOnlyFilterChange}
            layoutMode="top"
            onLayoutToggle={onAdditionalFiltersLayoutToggle}
            renderHeader
            renderControls
            showAvailabilityControl
            showProductAttributesFilter
            showPackSizeControl
            showUnitPriceControl
            availabilityValue={values.availability}
            onAvailabilityChange={(next) => onChange('availability', next)}
            groupBy={groupBy}
            onGroupByChange={onGroupByChange}
            scopeMode={scopeMode}
          />
        </Box>
      )}
    </Box>
  </Paper>
  );
};

const MockFiltersPanel = ({
  styleTokens,
  isStickRoute,
  packSizeOptions,
  selectedPackSizes,
  onPackSizeChange,
  priceDisplayMode,
  onPriceDisplayModeChange,
  deviceColourOptions = ['All'],
  stickFlavourOptions = ['All'],
  additionalFilters = {},
  onAdditionalFiltersChange,
  customizableFilter = CUSTOMIZABLE_FILTER_DEFAULT,
  onCustomizableFilterChange,
  forNewUsersOnlyFilter = FOR_NEW_USERS_FILTER_DEFAULT,
  onForNewUsersOnlyFilterChange,
  layoutMode = 'side',
  onLayoutToggle,
  renderHeader = true,
  renderControls = true,
  controlKeys = null,
  includeStaticControls = true,
  showPackSizeControl = true,
  showUnitPriceControl = true,
  showAvailabilityControl = false,
  showProductAttributesFilter = false,
  availabilityValue,
  onAvailabilityChange,
  groupBy,
  onGroupByChange,
  scopeMode,
}) => {
  const inlineMode = layoutMode === 'top';
  // Match main top-filter control size when inline; keep slightly denser controls in the side panel.
  const compactSelectSx = inlineMode
    ? {
        '& .MuiInputBase-root': {
          height: 30,
          fontSize: '0.7rem',
          minWidth: 0,
          width: '100%',
          maxWidth: '100%',
        },
        '& .MuiInputBase-input': {
          padding: '4px 8px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
        '& .MuiOutlinedInput-root': { minWidth: 0, width: '100%', maxWidth: '100%' },
        '& .MuiSelect-select': {
          overflow: 'hidden !important',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          minWidth: '0 !important',
          fontSize: '0.7rem',
          py: 0,
          display: 'flex',
          alignItems: 'center',
        },
        '& .MuiInputLabel-root': {
          fontSize: '0.65rem',
          maxWidth: 'calc(100% - 28px)',
          transform: 'translate(14px, 8px) scale(1)',
          '&.MuiInputLabel-shrink': {
            // Sit fully above the outline so the field border doesn't clip the label.
            transform: 'translate(14px, -8px) scale(0.85)',
            maxWidth: 'calc(133% - 32px)',
          },
        },
      }
    : {
        '& .MuiInputBase-root': {
          height: 30,
          fontSize: '0.7rem',
          minWidth: 0,
          width: '100%',
          maxWidth: '100%',
        },
        '& .MuiInputBase-input': {
          padding: '4px 8px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
        '& .MuiOutlinedInput-root': { minWidth: 0, width: '100%', maxWidth: '100%' },
        '& .MuiSelect-select': {
          overflow: 'hidden !important',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          minWidth: '0 !important',
          fontSize: '0.7rem',
          py: 0,
          display: 'flex',
          alignItems: 'center',
        },
        '& .MuiInputLabel-root': {
          fontSize: '0.65rem',
          maxWidth: 'calc(100% - 28px)',
          transform: 'translate(14px, 8px) scale(1)',
          '&.MuiInputLabel-shrink': {
            transform: 'translate(14px, -6px) scale(0.85)',
            maxWidth: 'calc(133% - 32px)',
          },
        },
      };

  const deviceFilters = [
    { key: 'deviceType', label: 'Device Type', options: ['All', 'Solo', 'Duo'], enabled: true, disabledOptions: ['Solo', 'Duo'] },
    { key: 'deviceStickType', label: 'Device Stick Type', options: ['All', 'KS', 'Other'], enabled: true, disabledOptions: ['KS', 'Other'] },
    { key: 'deviceColour', label: 'Device Colour', options: deviceColourOptions.length > 1 ? deviceColourOptions : ['All'], enabled: true },
  ];

  const stickFilters = [
    { key: 'stickFlavour', label: 'Stick Flavour', options: stickFlavourOptions.length > 1 ? stickFlavourOptions : ['All'], enabled: true },
    { key: 'stickCategory', label: 'HNS / HTS', options: STICK_CATEGORY_FILTER_OPTIONS, enabled: true },
  ];

  const staticFiltersBase = isStickRoute ? stickFilters : deviceFilters;
  const staticFilters = Array.isArray(controlKeys) && controlKeys.length > 0
    ? staticFiltersBase.filter((f) => controlKeys.includes(f.key))
    : staticFiltersBase;

  const values = additionalFilters;
  const handleChange = (key, val) => onAdditionalFiltersChange?.((prev) => ({ ...prev, [key]: val }));
  const shouldShowKey = (key) => !Array.isArray(controlKeys) || controlKeys.length === 0 || controlKeys.includes(key);
  const renderProductAttributesFilter = showProductAttributesFilter && shouldShowKey('productAttributes');
  // Full additional-filters set → grid layout; fragmented controlKeys stay single-column.
  const useFilterGrid = !Array.isArray(controlKeys) || controlKeys.length === 0;

  const headerBlock = renderHeader ? (
    <Box
      sx={
        inlineMode
          ? {
              position: 'absolute',
              // Middle of the first main-filter row (~15px below the top edge).
              top: '5px',
              left: 0,
              height: 16,
              display: 'flex',
              alignItems: 'center',
              gap: 0.25,
              zIndex: 2,
              pointerEvents: 'none',
              '& .MuiIconButton-root': {
                pointerEvents: 'auto',
                p: 0.125,
                height: 16,
                width: 16,
              },
              '& .MuiSvgIcon-root': { fontSize: '0.95rem' },
            }
          : {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              gap: 0.25,
              mb: 0.5,
            }
      }
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          color: 'text.secondary',
          fontSize: '0.65rem',
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
        }}
      >
        Additional Filters
      </Typography>
      {onLayoutToggle ? (
        <Tooltip
          title={inlineMode ? 'Move additional filters to chart side' : 'Move additional filters into top filters'}
          arrow
        >
          <IconButton size="small" onClick={onLayoutToggle} aria-label="Toggle additional filters position" sx={{ p: 0.25 }}>
            {inlineMode ? <ExpandMore fontSize="small" /> : <ExpandLess fontSize="small" />}
          </IconButton>
        </Tooltip>
      ) : null}
    </Box>
  ) : null;

  const filterControlSx = {
    ...compactSelectSx,
    minWidth: 0,
    maxWidth: '100%',
    width: '100%',
    boxSizing: 'border-box',
  };

  const renderStaticFilterControl = ({ key, label, options, enabled, disabledOptions }) => (
    <FormControl key={key} size="small" sx={filterControlSx}>
      <InputLabel>{label}</InputLabel>
      <Select
        label={label}
        value={values[key] ?? 'All'}
        onChange={(e) => handleChange(key, e.target.value)}
        disabled={!enabled}
      >
        {options.map((opt) => (
          <MenuItem
            key={opt}
            value={opt}
            dense
            disabled={disabledOptions ? disabledOptions.includes(opt) : false}
            sx={{ py: 0.25, minHeight: 'auto', fontSize: '0.72rem' }}
          >
            {opt}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const unitPriceControl = isStickRoute && showUnitPriceControl ? (
    <FormControl size="small" sx={filterControlSx}>
      <InputLabel>Unit Price Display</InputLabel>
      <Select
        label="Unit Price Display"
        value={priceDisplayMode || 'absolute'}
        onChange={(e) => onPriceDisplayModeChange && onPriceDisplayModeChange(e.target.value)}
      >
        <MenuItem value="absolute" dense sx={{ fontSize: '0.72rem' }}>Absolute price</MenuItem>
        <MenuItem value="per_unit" dense sx={{ fontSize: '0.72rem' }}>Per unit price</MenuItem>
      </Select>
    </FormControl>
  ) : null;

  const packSizeControl = isStickRoute && showPackSizeControl && packSizeOptions ? (
    <FormControl size="small" sx={filterControlSx}>
      <InputLabel>Number of Units</InputLabel>
      <Select
        multiple
        label="Number of Units"
        value={selectedPackSizes || []}
        onChange={(e) => onPackSizeChange && onPackSizeChange(e.target.value)}
        renderValue={(sel) => (sel.length === 0 ? 'All' : sel.join(', '))}
      >
        {packSizeOptions.map((size) => (
          <MenuItem key={size} value={size} dense sx={{ py: 0.25, minHeight: 'auto', fontSize: '0.8rem' }}>
            <Checkbox size="small" checked={(selectedPackSizes || []).includes(size)} sx={{ py: 0 }} />
            <ListItemText primary={size} sx={{ my: 0, '& .MuiListItemText-primary': { fontSize: '0.8rem' } }} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  ) : null;

  const stickFlavourControl =
    includeStaticControls && isStickRoute
      ? renderStaticFilterControl({
          key: 'stickFlavour',
          label: 'Stick Flavour',
          options: stickFlavourOptions.length > 1 ? stickFlavourOptions : ['All'],
          enabled: true,
        })
      : null;

  const stickCategoryControl =
    includeStaticControls && isStickRoute
      ? renderStaticFilterControl({
          key: 'stickCategory',
          label: 'HNS / HTS',
          options: STICK_CATEGORY_FILTER_OPTIONS,
          enabled: true,
        })
      : null;

  const customizableControl = renderProductAttributesFilter ? (
    <FormControl size="small" sx={filterControlSx}>
      <InputLabel>Customizable</InputLabel>
      <Select
        label="Customizable"
        value={normalizeCustomizableFilter(customizableFilter)}
        onChange={(e) => onCustomizableFilterChange?.(e.target.value)}
      >
        {CUSTOMIZABLE_FILTER_OPTIONS.map((opt) => (
          <MenuItem key={opt} value={opt} dense sx={{ py: 0.25, minHeight: 'auto', fontSize: '0.8rem' }}>
            {opt}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  ) : null;

  const forNewUsersControl = renderProductAttributesFilter ? (
    <FormControl size="small" sx={filterControlSx}>
      <InputLabel>{PRODUCT_FLAG_LABEL_FOR_NEW_USERS_ONLY}</InputLabel>
      <Select
        label={PRODUCT_FLAG_LABEL_FOR_NEW_USERS_ONLY}
        value={normalizeForNewUsersOnlyFilter(forNewUsersOnlyFilter)}
        onChange={(e) => onForNewUsersOnlyFilterChange?.(e.target.value)}
      >
        {FOR_NEW_USERS_FILTER_OPTIONS.map((opt) => (
          <MenuItem key={opt} value={opt} dense sx={{ py: 0.25, minHeight: 'auto', fontSize: '0.8rem' }}>
            {opt}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  ) : null;

  const availabilityControl = showAvailabilityControl ? (
    <FormControl size="small" sx={filterControlSx}>
      <InputLabel id="additional-availability-select-label">Availability</InputLabel>
      <Select
        labelId="additional-availability-select-label"
        multiple
        value={normalizeAvailabilitySelection(availabilityValue)}
        label="Availability"
        input={<OutlinedInput label="Availability" />}
        onChange={(e) => {
          const next = (Array.isArray(e.target.value) ? e.target.value : []).filter(
            (v) => v && v !== '__all__'
          );
          onAvailabilityChange?.(next.length ? next : [...DEFAULT_AVAILABILITY_SELECTION]);
        }}
        renderValue={(selected) =>
          Array.isArray(selected)
            ? selected.map((v) => (v === 'Out of Stock' ? 'OOS' : v)).join(', ')
            : ''
        }
        MenuProps={{ PaperProps: { style: { maxHeight: 280, width: 260 } }, autoFocus: false }}
      >
        {AVAILABILITY_OPTION_LABELS.map((option) => (
          <MenuItem
            key={`availability-${option}`}
            value={option}
            dense
            sx={{
              py: 0.25,
              minHeight: 'auto',
              '& .MuiListItemText-primary': { fontSize: '0.875rem' },
              '& .MuiCheckbox-root': { py: 0.5 },
            }}
          >
            <Checkbox
              checked={normalizeAvailabilitySelection(availabilityValue).includes(option)}
              size="small"
              sx={{ py: 0 }}
            />
            <ListItemText primary={option} sx={{ my: 0 }} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  ) : null;

  const groupByControl = showAvailabilityControl && scopeMode === 'old' ? (
    <FormControl size="small" sx={filterControlSx}>
      <InputLabel>Group by</InputLabel>
      <Select
        value={groupBy || 'all'}
        label="Group by"
        onChange={(e) => onGroupByChange && onGroupByChange(e.target.value)}
      >
        <MenuItem value="all" sx={{ fontSize: '0.8rem' }}>No grouping</MenuItem>
        <MenuItem value="countries" sx={{ fontSize: '0.8rem' }}>Countries</MenuItem>
        <MenuItem value="products" sx={{ fontSize: '0.8rem' }}>Products</MenuItem>
      </Select>
    </FormControl>
  ) : null;

  const filterColumnSx = {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.75, // match main filter column stacking so row 3 lines up
    width: '100%',
    minWidth: 0,
    maxWidth: '100%',
    // Keep visible so outlined InputLabels (which sit on the top border) are not clipped.
    overflow: 'visible',
  };

  // Devices / legacy: keep auto-flow grid of controls in DOM order.
  const controlsInner = (
    <>
      {includeStaticControls && !isStickRoute && staticFilters.map(renderStaticFilterControl)}
      {unitPriceControl}
      {packSizeControl}
      {stickFlavourControl}
      {customizableControl}
      {forNewUsersControl}
      {availabilityControl}
      {groupByControl}
      {stickCategoryControl}
    </>
  );

  const sticksGridBlock = (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, minmax(128px, 128px))',
        columnGap: 1,
        rowGap: 0.75, // match main filter stacking so row 3 lines up
        width: 'max-content',
        maxWidth: 'none',
        minWidth: 'max-content',
        flex: '0 0 auto',
        alignItems: 'start',
        overflow: 'visible',
        '& .MuiFormControl-root': { maxWidth: '100%', minWidth: 0, overflow: 'visible' },
        '& .MuiSelect-select': {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
      }}
    >
      <Box sx={filterColumnSx}>
        {unitPriceControl}
        {packSizeControl}
      </Box>
      <Box sx={filterColumnSx}>
        {stickFlavourControl}
        {customizableControl}
      </Box>
      <Box sx={filterColumnSx}>
        {forNewUsersControl}
        {availabilityControl}
        {groupByControl}
      </Box>
      <Box sx={filterColumnSx}>
        {stickCategoryControl}
      </Box>
    </Box>
  );

  const devicesGridBlock = (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(128px, 128px))',
        columnGap: 1,
        rowGap: 0.75, // match main filter stacking so row 3 lines up
        width: 'max-content',
        maxWidth: 'none',
        minWidth: 'max-content',
        flex: '0 0 auto',
        alignItems: 'start',
        overflow: 'visible',
        '& .MuiFormControl-root': { maxWidth: '100%', minWidth: 0, overflow: 'visible' },
        '& .MuiSelect-select': {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
      }}
    >
      {includeStaticControls && staticFilters.map(renderStaticFilterControl)}
      {customizableControl}
      {forNewUsersControl}
      {availabilityControl}
      {groupByControl}
    </Box>
  );

  const sideColumnBlock = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.75,
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        overflowX: 'hidden',
        '& .MuiFormControl-root': { maxWidth: '100%', minWidth: 0, width: '100%' },
        '& .MuiInputBase-root': { maxWidth: '100%', minWidth: 0 },
        '& .MuiSelect-select': {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        },
      }}
    >
      {controlsInner}
    </Box>
  );

  const controlsBlock = renderControls ? (
    useFilterGrid ? (
      // Top: multi-column grid. Side (next to chart): single vertical column.
      inlineMode ? (isStickRoute ? sticksGridBlock : devicesGridBlock) : sideColumnBlock
    ) : (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, width: '100%', minWidth: 0 }}>
        {controlsInner}
      </Box>
    )
  ) : null;

  const content = (
    <>
      {headerBlock}
      {controlsBlock}
    </>
  );

  if (inlineMode) {
    return (
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 0,
          width: 'max-content',
          maxWidth: 'none',
          minWidth: 0,
          flex: '0 0 auto',
          overflowX: 'visible',
          // Align first additional-filter row with second main-filter row (30px control + 0.75 gap).
          pt: 'calc(30px + 6px)',
        }}
      >
        {content}
      </Box>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        p: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        overflowX: 'hidden',
        overflowY: 'auto',
        backgroundColor: styleTokens.panelAlt,
        border: `1px solid ${styleTokens.border}`,
      }}
    >
      {content}
    </Paper>
  );
};

const MARKET_COUNTRY_ALIASES = {
  uk: ['uk', 'united kingdom', 'gb', 'gbr', 'great britain', 'england'],
  italy: ['italy', 'it', 'ita', 'italia'],
  romania: ['romania', 'ro', 'rou'],
  germany: ['germany', 'de', 'deu', 'deutschland'],
  france: ['france', 'fr', 'fra', 'francia'],
  spain: ['spain', 'es', 'esp', 'espana'],
  poland: ['poland', 'pl', 'pol', 'polska'],
  czech: ['czech', 'cz', 'czech republic', 'czechia', 'ceska', 'cesko'],
};

const resolveMarketCountry = (marketId, availableCountries) => {
  if (!marketId || !Array.isArray(availableCountries) || availableCountries.length === 0) {
    return null;
  }
  const aliases = MARKET_COUNTRY_ALIASES[marketId];
  if (!aliases) return null;
  return availableCountries.find((country) => aliases.includes(normalizeValue(country))) || null;
};

/**
 * Set of normalized country codes that are currently in scope (or null = no country restriction):
 * - explicit selectedCountries when any are chosen
 * - otherwise, in market mode, the market's country
 * Used to scope filter options (Brand, Manufacturer) to what actually exists in the active country.
 */
const computeCountryScopeSet = ({ selectedCountries, isMarketScope, selectedMarket, availableCountries }) => {
  if (Array.isArray(selectedCountries) && selectedCountries.length > 0) {
    return new Set(selectedCountries.map((c) => normalizeValue(c)));
  }
  if (isMarketScope) {
    const inferred = resolveMarketCountry(selectedMarket, availableCountries);
    return inferred ? new Set([normalizeValue(inferred)]) : null;
  }
  return null;
};

const resolveMarketCurrency = (marketId, availableCountries, sourceData) => {
  if (!marketId || !Array.isArray(availableCountries) || !Array.isArray(sourceData) || sourceData.length === 0) {
    return null;
  }
  const marketCountry = resolveMarketCountry(marketId, availableCountries);
  if (!marketCountry) return null;
  const targetCountry = normalizeValue(marketCountry);
  const currencyCounts = new Map();
  sourceData.forEach((item) => {
    if (!item || !item.country) return;
    if (normalizeValue(item.country) !== targetCountry) return;
    if (!item.currency) return;
    currencyCounts.set(item.currency, (currencyCounts.get(item.currency) || 0) + 1);
  });
  if (currencyCounts.size === 0) return null;
  return Array.from(currencyCounts.entries()).sort((a, b) => b[1] - a[1])[0][0];
};

const resolveMarketDefaultCurrency = (marketId, availableCountries, sourceData) => {
  return resolveMarketCurrency(marketId, availableCountries, sourceData);
};

const OnExportClick = ({ data }) => {
  const handleExportClick = () => {
    const csvData = prepareCSVData(data);
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, 'jti_exported_data.csv');
  };

  const prepareCSVData = data => {
    // Convert data to CSV format
    // Example: Convert array of objects to comma-separated values
    // Skip internal (`__`-prefixed) fields such as the precomputed `__ts` timestamp.
    return data
      .map(item =>
        Object.entries(item)
          .filter(([key]) => !key.startsWith('__'))
          .map(([, value]) => value)
          .join(','),
      )
      .join('\n');
  };
};

/** Rendered end-label text for a dataset at the last index (matches the datalabels formatter). */
function endLabelTextForDataset(ds, lastIdx) {
  if (!ds) return '';
  let raw = '';
  if (ds.stockSegment && Array.isArray(ds.groupedVariantTitlesAtIndex) && lastIdx >= 0) {
    const at = ds.groupedVariantTitlesAtIndex[lastIdx];
    if (Array.isArray(at) && at.length) raw = buildPinRowLineTitle(ds, at);
  }
  if (!raw) {
    raw = ds.legendLabel || ds.displayLabel || ds.label || '';
    raw = appendBundleLabelSuffix(raw, ds);
  }
  return stripFlagEmoji(raw);
}

/** True if a dataset renders an end label at the last index (same conditions as the datalabels `display`). */
function datasetRendersEndLabel(ds, lastIdx) {
  if (!ds || ds.hidden) return false;
  if (ds.countryChartLineFormat) return false;
  const v = ds.data?.[lastIdx];
  if (v == null) return false;
  if (ds.priceType != null && typeof v === 'number' && v <= 0) return false;
  return true;
}

/**
 * Set of dataset indices whose end label should be shown after removing duplicates: lines that
 * render the same end-label text (e.g. a listed and promo line for the same product/bundle) keep
 * only the first occurrence so the right-hand line labels aren't repeated.
 */
function computeDedupedEndLabelDatasetIndices(datasets, lastIdx) {
  const shown = new Set();
  const seenText = new Set();
  (datasets || []).forEach((ds, datasetIndex) => {
    if (!datasetRendersEndLabel(ds, lastIdx)) return;
    const text = endLabelTextForDataset(ds, lastIdx);
    if (text && seenText.has(text)) return;
    if (text) seenText.add(text);
    shown.add(datasetIndex);
  });
  return shown;
}

/**
 * Precompute chart end-label offsets for chartjs-plugin-datalabels: cluster by similar last Y,
 * then fan out vertically (and slightly horizontally for deep stacks) to reduce overlap.
 */
function computeEndLabelStaggerOffsets(datasets, labels, options = {}) {
  const { isIndexMode = false } = options;
  const result = { offsets: new Map(), maxExtraRight: 0 };
  if (!Array.isArray(datasets) || !Array.isArray(labels) || labels.length === 0) {
    return result;
  }

  const lastIdx = labels.length - 1;
  const entries = [];
  // Only stagger labels that are actually shown (deduplicated), so no vertical space is
  // reserved for hidden duplicate labels.
  const shownIndices = computeDedupedEndLabelDatasetIndices(datasets, lastIdx);

  datasets.forEach((ds, datasetIndex) => {
    if (!shownIndices.has(datasetIndex)) return;
    const data = ds.data || [];
    const v = data[lastIdx];
    if (v == null || v === undefined) return;
    if (ds.priceType != null && typeof v === 'number' && v <= 0) return;
    const y = typeof v === 'number' ? v : parseFloat(v);
    if (!Number.isFinite(y)) return;
    entries.push({ datasetIndex, lastY: y });
  });

  if (entries.length === 0) return result;

  entries.sort((a, b) =>
    a.lastY === b.lastY ? a.datasetIndex - b.datasetIndex : a.lastY - b.lastY
  );

  const ys = entries.map((e) => e.lastY);
  const yMin = Math.min(...ys);
  const yMax = Math.max(...ys);
  const ySpan = yMax - yMin || 1;
  const epsilon = isIndexMode
    ? Math.max(0.25, ySpan * 0.015)
    : Math.max(0.5, ySpan * 0.015);

  const buckets = [];
  let cur = [entries[0]];
  for (let i = 1; i < entries.length; i += 1) {
    if (Math.abs(entries[i].lastY - entries[i - 1].lastY) <= epsilon) {
      cur.push(entries[i]);
    } else {
      buckets.push(cur);
      cur = [entries[i]];
    }
  }
  buckets.push(cur);

  const STEP_Y = 12;
  const STEP_X = 10;
  const HORIZONTAL_BAND = 8;

  buckets.forEach((bucket) => {
    const n = bucket.length;
    if (n === 0) return;
    const mid = (n - 1) / 2;
    bucket.forEach((e, slot) => {
      const dy = (slot - mid) * STEP_Y;
      const dx = n > 1 ? Math.floor(slot / HORIZONTAL_BAND) * STEP_X : 0;
      result.offsets.set(e.datasetIndex, { x: dx, y: dy });
      result.maxExtraRight = Math.max(result.maxExtraRight, dx);
    });
  });

  return result;
}

function JTIPriceAndPromotionsView({ scopeMode, selectedMarket }) {
  const { projectKey, productType } = useParams();
  const { username, uiStyle, isAdmin } = useAuth();
  const { isModuleEnabled } = useUserModules();
  const showAiSummary = isAdmin && isModuleEnabled('experimental-ai-price-summary');
  const allowChartAdvanced = isAdmin && isModuleEnabled('experimental-chart-advanced-options');
  const isMarketScope = scopeMode === 'market';
  const isHqScope = scopeMode === 'hq';
  const isStickRoute = PRODUCT_TYPE_ALIASES.sticks.includes(normalizeValue(productType));
  const isDevicesRoute = PRODUCT_TYPE_ALIASES.devices.includes(normalizeValue(productType));
  const {
    getDisplayTitle: getSkuDisplayTitle,
    isTranslationActive: isSkuTranslationActive,
    isTranslatingTitle: isSkuTitleTranslating,
    toggleSkuTitleTranslation,
  } = useSkuTitleTranslations();
  
  // State for data
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [columns, setColumns] = useState([]);
  
  // State for filters
  const [selectedManufacturers, setSelectedManufacturers] = useState([]);
  const [manufacturerSearchTerm, setManufacturerSearchTerm] = useState('');
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedRetailers, setSelectedRetailers] = useState([]);
  const [countrySearchTerm, setCountrySearchTerm] = useState('');
  const [retailerSearchTerm, setRetailerSearchTerm] = useState('');
  const [normalizeToEur, setNormalizeToEur] = useState(false);
  
  // State for base product and AI analysis
  const [baseProduct, setBaseProduct] = useState(null);
  const [baseProductSkuOverride, setBaseProductSkuOverride] = useState('');
  const [baseProductAutoQueues, setBaseProductAutoQueues] = useState(() =>
    readBaseProductAutoQueuesFromStorage()
  );
  const [baseProductAutoQueueDialogOpen, setBaseProductAutoQueueDialogOpen] = useState(false);
  const [baseProductAutoQueueDraft, setBaseProductAutoQueueDraft] = useState([]);
  const initialBaseProductDefaultAppliedRef = useRef(false);
  const [compareToPriceType, setCompareToPriceType] = useState(BASE_COMPARE_TYPE_AUTO);
  const [additionalFilters, setAdditionalFilters] = useState({
    deviceColour: 'All',
    stickFlavour: 'All',
    stickCategory: 'All',
    deviceType: 'All',
    deviceStickType: 'All',
    limitedEdition: LIMITED_EDITION_FILTER_DEFAULT,
  });
  const [aiSummary, setAiSummary] = useState(null);
  const [aiAnalysisOpen, setAiAnalysisOpen] = useState(false);
  const [additionalFiltersLayout, setAdditionalFiltersLayout] = useState('top');
  const [isAiAnalysisLoading, setIsAiAnalysisLoading] = useState(false);

  // State for flavour filters
  const [selectedFlavours, setSelectedFlavours] = useState([]);
  const [flavourButtonsStates, setFlavourButtonsStates] = useState([]);
  
  // State for category group filters
  const [selectedCategoryGroups, setSelectedCategoryGroups] = useState(() => {
    const routeDefault = resolveDefaultCategoryGroupForRoute(productType);
    return routeDefault ? [routeDefault] : [];
  });
  const [brandSearchTerm, setBrandSearchTerm] = useState('');
  const [categoryGroupSearchTerm, setCategoryGroupSearchTerm] = useState('');

  // State for pack size filter
  const [selectedPackSizes, setSelectedPackSizes] = useState([]);
  const [packSizeSearchTerm, setPackSizeSearchTerm] = useState('');
  const [customizableFilter, setCustomizableFilter] = useState(CUSTOMIZABLE_FILTER_DEFAULT);
  const [forNewUsersOnlyFilter, setForNewUsersOnlyFilter] = useState(FOR_NEW_USERS_FILTER_DEFAULT);
  const [starterKitProductFilter, setStarterKitProductFilter] = useState(STARTER_KIT_FILTER_DEFAULT);

  useEffect(() => {
    if (!isStickRoute) return;
    setStarterKitProductFilter(STARTER_KIT_FILTER_DEFAULT);
  }, [isStickRoute]);

  // Price display mode: 'absolute' (raw price) or 'per_unit' (price ÷ pack size)
  // Only relevant for sticks/sticks bundles routes. Default to per_unit for sticks.
  const [priceDisplayMode, setPriceDisplayMode] = useState(isStickRoute ? 'per_unit' : 'absolute');

  // Price type selection: multi-select by default (toggleable in chart settings).
  const [activePriceTypes, setActivePriceTypes] = useState(() => defaultActivePriceTypes(scopeMode === 'market'));
  const [allowMultiplePriceTypes, setAllowMultiplePriceTypes] = useState(true);
  const [rememberBrandFiltersOnScopeChange, setRememberBrandFiltersOnScopeChange] = useState(false);
  const [priceTypeStyles, setPriceTypeStyles] = useState(
    () => Object.fromEntries(PRICE_TYPE_OPTIONS.map((o) => [o.key, o.defaultStyle]))
  );

  const handlePriceTypeToggle = useCallback((key, checked) => {
    const next = new Set(activePriceTypes);
    checked ? next.add(key) : next.delete(key);
    setActivePriceTypes(next);
  }, [activePriceTypes]);
  const handlePriceTypeSelect = useCallback((key) => {
    setActivePriceTypes(new Set([key]));
  }, []);
  const handlePriceTypeStyleChange = useCallback((key, style) => {
    setPriceTypeStyles((prev) => ({ ...prev, [key]: style }));
  }, []);

  const isBundlesOnlySelection = useMemo(
    () => isBundlesOnlyCategorySelection(selectedCategoryGroups),
    [selectedCategoryGroups]
  );

  // Bundles-only selection (e.g. just "Device Bundles") has no regular price, so activate Promo
  // and drop Regular.
  useEffect(() => {
    if (!isBundlesOnlySelection) return;
    setActivePriceTypes((prev) => {
      const next = new Set(prev);
      next.delete('regular');
      next.delete('starter_kit');
      next.add('promo');
      const unchanged = next.size === prev.size && [...next].every((key) => prev.has(key));
      return unchanged ? prev : next;
    });
  }, [isBundlesOnlySelection]);

  const [stockLineStyles, setStockLineStyles] = useState(() =>
    Object.fromEntries(STOCK_SEGMENT_LINE_OPTIONS.map((o) => [o.key, o.defaultStyle]))
  );
  const handleStockLineStyleChange = useCallback((key, style) => {
    setStockLineStyles((prev) => ({ ...prev, [key]: style }));
  }, []);

  // Drop legacy starter_kit price type if it is still present in state
  useEffect(() => {
    setActivePriceTypes((prev) => {
      const next = sanitizeActivePriceTypes(prev);
      const unchanged = next.size === prev.size && [...next].every((key) => prev.has(key));
      return unchanged ? prev : next;
    });
    setCompareToPriceType((prev) => (prev === 'starter_kit' ? BASE_COMPARE_TYPE_AUTO : prev));
  }, []);

  // Default to per_unit price display when switching to sticks route
  const prevStickRouteRef = React.useRef(false);
  useEffect(() => {
    if (isStickRoute && !prevStickRouteRef.current) {
      setPriceDisplayMode('per_unit');
    }
    prevStickRouteRef.current = isStickRoute;
  }, [isStickRoute]);

  const [packSizeOverridesFromStorage, setPackSizeOverridesFromStorage] = useState({});
  const [productFlagsFromStorage, setProductFlagsFromStorage] = useState({});

  const getItemPackSize = useCallback((item) => {
    if (!item) return null;
    const saved = packSizeOverridesFromStorage[item.title];
    if (saved) {
      if (saved.packSize === 'other') {
        const n = Number(saved.customPackSize);
        return !isNaN(n) && n > 0 ? n : null;
      }
      const n = Number(saved.packSize);
      return !isNaN(n) && n > 0 ? n : null;
    }
    const raw = item['Units (#)'];
    const n = raw !== undefined && raw !== null && raw !== '' ? Number(raw) : NaN;
    return !isNaN(n) && n > 0 ? n : null;
  }, [packSizeOverridesFromStorage]);
  
  // State for date range filter
  const [dateRange, setDateRange] = useState([null, null]);
  const [previewDateRange, setPreviewDateRange] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const [dateSliderValue, setDateSliderValue] = useState([0, 0]);
  
  // State for unique values
  const [columnsUniqueValues, setColumnsUniqueValues] = useState(null);

  // Persisted product recategorization overrides (title -> category_group)
  const [categoryOverrides, setCategoryOverrides] = useState({});
  
  // Matching groups (loaded from localStorage)
  const [matchingGroups, setMatchingGroups] = useState(new Map());
  
  // Helper to get effective category group (with overrides applied)
  const getEffectiveCategoryGroup = useCallback((item) => {
    if (!item) return '';
    const title = item.title;
    return (title && categoryOverrides?.[title]) || item.category_group || '';
  }, [categoryOverrides]);
  
  // Reverse map: groupName -> [productTitle, ...] — declared early so the filter useEffect can use it
  const groupNameToProducts = useMemo(() => {
    const map = new Map();
    forEachMatchingInfo(matchingGroups, (info, title) => {
      const gName = info?.groupName;
      if (!gName) return;
      if (!map.has(gName)) map.set(gName, []);
      map.get(gName).push(title);
    });
    return map;
  }, [matchingGroups]);

  // Reverse map: modelSeries -> [productTitle, ...] (models that share a Model Series in matching)
  const modelGroupToProducts = useMemo(() => {
    const map = new Map();
    forEachMatchingInfo(matchingGroups, (info, title) => {
      const mg = (info?.modelGroup || '').trim();
      if (!mg) return;
      if (!map.has(mg)) map.set(mg, []);
      map.get(mg).push(title);
    });
    return map;
  }, [matchingGroups]);

  const skuCountryByTitle = useMemo(() => {
    const map = new Map();
    (data || []).forEach((item) => {
      if (!item?.title || !item?.country || map.has(item.title)) return;
      map.set(item.title, item.country);
    });
    return map;
  }, [data]);

  const getSkuCountryForTitle = useCallback(
    (title) => {
      if (Array.isArray(selectedCountries) && selectedCountries.length === 1) {
        return selectedCountries[0];
      }
      return skuCountryByTitle.get(title) || selectedCountries?.[0] || selectedMarket || '';
    },
    [selectedCountries, skuCountryByTitle, selectedMarket]
  );
  
  // State for filtered data
  const [filteredData, setFilteredData] = useState([]);
  const [pinPriceSourceRows, setPinPriceSourceRows] = useState([]);
  const [gridData, setGridData] = useState(null);
  const [priceHistoryData, setPriceHistoryData] = useState(null);
  const [visibilityHistoryData, setVisibilityHistoryData] = useState(null);
  
  // State for chart tabs
  const [activeChartTab, setActiveChartTab] = useState(0);
  const [priceViewMode, setPriceViewMode] = useState('chart');
  const [visibilityViewMode, setVisibilityViewMode] = useState('chart');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const showAdvancedOptions = allowChartAdvanced && showAdvanced;
  const [axisAnchorEl, setAxisAnchorEl] = useState(null);
  const [legendExpanded, setLegendExpanded] = useState(true);
  const [yMin, setYMin] = useState('');
  const [yMax, setYMax] = useState('');
  const [showChartLabels, setShowChartLabels] = useState(true);
  const [productPopupOpen, setProductPopupOpen] = useState(false);
  const [productPopupData, setProductPopupData] = useState({ productName: null, productImage: null, productData: null });
  const [priceChartPinIndex, setPriceChartPinIndex] = useState(null);
  const [visibilityChartPinIndex, setVisibilityChartPinIndex] = useState(null);
  /** Defers heavy chart/grid derivation so filter UI can paint before main-thread work runs. Cleared on effect cleanup. */
  const chartDerivedBuildTimerRef = useRef(null);
  /** Keeps spinner visible until at least one paint after chart state is applied. */
  const chartPendingRafRef = useRef(null);
  /** True while deferred `generatePriceHistoryData` / visibility chart build is scheduled or running. */
  const [chartComputationPending, setChartComputationPending] = useState(false);
  const [yStepSize, setYStepSize] = useState('');
  const [indexDecimalPlaces, setIndexDecimalPlaces] = useState(0);
  const [priceDecimalPlaces, setPriceDecimalPlaces] = useState(2);
  const [xDateFormat, setXDateFormat] = useState('auto');
  const [xMaxTicks, setXMaxTicks] = useState('');
  // Same default as HQ ("all" / no grouping) so country chart labels (cg:) match HQ; "products" is still available via filters where exposed.
  const [groupBy, setGroupBy] = useState('all');
  const marketAnnouncementContext = useMemo(
    () => ({
      isStickRoute,
      isMarketScope,
      selectedMarket,
      selectedCountry: selectedCountries?.[0] || '',
      selectedCountries,
      isHqScope,
      groupBy,
      filteredDataRaw: data,
    }),
    [isStickRoute, isMarketScope, selectedMarket, selectedCountries, isHqScope, groupBy, data]
  );
  const {
    messages: marketAnnouncementMessages,
    hasNotice: showMarketAnnouncementsNotice,
    showAboveChart: showMarketAnnouncementsAboveChart,
  } = useResolvedMarketAnnouncements(marketAnnouncementContext);
  const showItalySticksUnavailableNotice = showMarketAnnouncementsNotice;
  const showItalySticksNoticeAboveChart = showMarketAnnouncementsAboveChart;
  const [visiblePriceDatasets, setVisiblePriceDatasets] = useState(null);
  const [visibleVisibilityDatasets, setVisibleVisibilityDatasets] = useState(new Set());
  const [priceLegendSearch, setPriceLegendSearch] = useState('');
  const [visibilityLegendSearch, setVisibilityLegendSearch] = useState('');
  const savedPriceVisibilityRef = useRef(null);
  const savedVisibilityRef = useRef(null);
  const [topN, setTopN] = useState(20); // Show top 20 products by default
  const [topFilters, setTopFilters] = useState(() => createInitialTopFilters(productType, scopeMode === 'market'));
  const [savedViews, setSavedViews] = useState([]);
  const [selectedViewName, setSelectedViewName] = useState('');
  const [isSaveViewOpen, setIsSaveViewOpen] = useState(false);
  const [newViewName, setNewViewName] = useState('');
  const hasAppliedInitialFilterResetRef = useRef(false);
  const [isInitialBrandSelectionResolved, setIsInitialBrandSelectionResolved] = useState(false);
  const isApplyingViewRef = useRef(false);
  const applyingViewTimeoutRef = useRef(null);
  const lastProductTypeRef = useRef(productType);
  const previousScopeForBrandRef = useRef(scopeMode);
  const previousProductTypeForBrandRef = useRef(productType);
  const previousMarketForBrandRef = useRef(selectedMarket);
  const hasAppliedMarketProductDefaultRef = useRef(false);
  const previousScopeRef = useRef(scopeMode);
  const previousMarketRef = useRef(selectedMarket);
  /** Last non-null currency so Index ↔ Absolute keeps the same selection (local default is restored via pickMarketDefault). */
  const lastCurrencyPersistRef = useRef(null);
  const styleTokens = useMemo(() => {
    if (uiStyle === 'white-label') {
      if (projectKey === 'pmi') return STYLE_PRESETS.pmiWhiteLabel;
      if (projectKey === 'bat') return STYLE_PRESETS.batWhiteLabel;
      return STYLE_PRESETS.whiteLabel;
    }
    return STYLE_PRESETS.classic;
  }, [uiStyle, projectKey]);

  /** Accent-aligned primary palette so filters (Select, Checkbox, Tabs, ToggleButton, focus rings) are not default MUI blue. */
  const pricePromotionsTheme = useMemo(
    () =>
      createTheme({
        palette: {
          primary: {
            main: styleTokens.accent,
            contrastText: '#fff',
          },
        },
        components: {
          MuiOutlinedInput: {
            styleOverrides: {
              root: {
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: styleTokens.accent,
                },
              },
            },
          },
          MuiInputLabel: {
            styleOverrides: {
              root: {
                '&.Mui-focused': {
                  color: styleTokens.accent,
                },
              },
            },
          },
          MuiFormLabel: {
            styleOverrides: {
              root: {
                '&.Mui-focused': {
                  color: styleTokens.accent,
                },
              },
            },
          },
          MuiCheckbox: {
            styleOverrides: {
              root: {
                '&.Mui-checked, &.MuiCheckbox-indeterminate': {
                  color: styleTokens.accent,
                },
              },
            },
          },
          MuiRadio: {
            styleOverrides: {
              root: {
                '&.Mui-checked': {
                  color: styleTokens.accent,
                },
              },
            },
          },
          MuiSwitch: {
            styleOverrides: {
              switchBase: {
                '&.Mui-checked': {
                  color: styleTokens.accent,
                  '& + .MuiSwitch-track': {
                    backgroundColor: styleTokens.accent,
                  },
                },
              },
            },
          },
          MuiTab: {
            styleOverrides: {
              root: {
                '&.Mui-selected': {
                  color: styleTokens.accent,
                },
              },
            },
          },
          MuiTabs: {
            styleOverrides: {
              indicator: {
                backgroundColor: styleTokens.accent,
              },
            },
          },
          MuiToggleButton: {
            styleOverrides: {
              root: {
                '&.Mui-selected': {
                  color: styleTokens.accent,
                  backgroundColor: styleTokens.chipBg,
                  '&:hover': {
                    backgroundColor: styleTokens.accentSoft,
                  },
                },
              },
            },
          },
          MuiMenuItem: {
            styleOverrides: {
              root: {
                '&.Mui-selected': {
                  backgroundColor: styleTokens.accentSoft,
                },
                '&.Mui-selected:hover': {
                  backgroundColor: styleTokens.accentSoft,
                },
              },
            },
          },
          MuiListItemButton: {
            styleOverrides: {
              root: {
                '&.Mui-selected': {
                  backgroundColor: styleTokens.chipBg,
                },
                '&.Mui-selected:hover': {
                  backgroundColor: styleTokens.accentSoft,
                },
              },
            },
          },
          MuiSlider: {
            styleOverrides: {
              root: {
                color: styleTokens.accent,
              },
            },
          },
          MuiChip: {
            styleOverrides: {
              colorPrimary: {
                backgroundColor: styleTokens.accentSoft,
                color: styleTokens.accent,
              },
            },
          },
          MuiAutocomplete: {
            styleOverrides: {
              option: {
                '&[aria-selected="true"]': {
                  backgroundColor: styleTokens.accentSoft,
                },
                '&.Mui-focused': {
                  backgroundColor: styleTokens.accentSoft,
                },
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              containedPrimary: {
                backgroundColor: styleTokens.accent,
                '&:hover': {
                  backgroundColor: styleTokens.accent,
                  filter: 'brightness(0.9)',
                },
              },
              outlinedPrimary: {
                color: styleTokens.accent,
                borderColor: styleTokens.accent,
                '&:hover': {
                  borderColor: styleTokens.accent,
                  backgroundColor: styleTokens.chipBg,
                },
              },
              textPrimary: {
                color: styleTokens.accent,
              },
            },
          },
        },
      }),
    [styleTokens.accent, styleTokens.accentSoft, styleTokens.chipBg]
  );

  const handleProductClick = useCallback((productName, productImage, meta = {}) => {
    const groupNames = meta.groupProductNames;
    const { chartPinnedDateLabel, pinnedPopupPricing } = meta;
    const country =
      meta.country ||
      (Array.isArray(selectedCountries) && selectedCountries.length === 1 ? selectedCountries[0] : '');
    const names =
      Array.isArray(groupNames) && groupNames.length > 0
        ? groupNames
        : productName
          ? [productName]
          : [];
    const dateLabel = toSqlDateKey(
      chartPinnedDateLabel ||
      (dateRange[1]?.isValid?.() ? dateRange[1].format('YYYY-MM-DD') : null)
    ) || null;
    const sourceRows = pinPriceSourceRows?.length ? pinPriceSourceRows : data;
    const stockLabelFromRows = (datedRows) => {
      const stockClasses = datedRows
        .map((item) => classifyStockClassFromItem(item))
        .filter((cls) => cls && cls !== 'unknown');
      const hasAvail = stockClasses.includes('avail');
      const hasOos = stockClasses.includes('oos');
      if (hasAvail && hasOos) return 'Mixed (In stock / Out of stock)';
      if (hasAvail) return 'In stock';
      if (hasOos) return 'Out of stock';
      const raw = datedRows[0]?.stock_status || datedRows[0]?.availability;
      return raw != null && String(raw).trim() !== '' ? String(raw).trim() : null;
    };
    const buildRowForName = (name) => {
      const items = filterRowsForProductPopup(sourceRows, name, country);
      const datedRows = rowsOnLatestDateOnOrBefore(items, dateLabel);
      const base = datedRows[0] || (!dateLabel ? pickLatestRowOnOrBeforeDate(items, null) : null);
      if (!base) return null;
      const stockLabel = datedRows.length ? stockLabelFromRows(datedRows) : null;
      return {
        ...base,
        category_group: categoryOverrides[name] || base.category_group,
        ...(stockLabel ? { stock_status: stockLabel } : {}),
      };
    };
    const primaryTitle = names[0] || productName;
    const primary = buildRowForName(primaryTitle);
    const snap = pinnedPopupPricing && typeof pinnedPopupPricing === 'object' ? pinnedPopupPricing : null;
    const mergedPrimaryRaw =
      primary && snap
        ? {
            ...primary,
            ...(snap.currency != null && snap.currency !== '' ? { currency: snap.currency } : {}),
            ...(snap.actual_price != null && snap.actual_price !== ''
              ? { actual_price: snap.actual_price }
              : {}),
            ...(snap.regular_price != null && snap.regular_price !== ''
              ? { regular_price: snap.regular_price }
              : {}),
            ...(snap.stock_status != null && snap.stock_status !== ''
              ? { stock_status: snap.stock_status }
              : {}),
          }
        : primary;
    const mergedPrimary = attachLocalCurrencyPricing(mergedPrimaryRaw, snap, primary, {
      priceDisplayMode,
      getItemPackSize,
    });
    const samePopupSku = (a, b) =>
      a &&
      b &&
      productTitleCountryKey(a.title, a.country) === productTitleCountryKey(b.title, b.country);
    let popupRows = names.map((name) => {
      const row = buildRowForName(name);
      if (!row) return null;
      if (mergedPrimary && samePopupSku(row, mergedPrimary)) return mergedPrimary;
      return attachLocalCurrencyPricing(row, null, row, { priceDisplayMode, getItemPackSize });
    }).filter(Boolean);
    if (mergedPrimary) {
      const mergedIdx = popupRows.findIndex((row) => samePopupSku(row, mergedPrimary));
      if (mergedIdx >= 0) popupRows[mergedIdx] = mergedPrimary;
      else popupRows = [mergedPrimary, ...popupRows];
    }
    const resolvedDate = toSqlDateKey(dateLabel || productRowDateKey(mergedPrimary)) || undefined;
    setProductPopupData({
      productName: primaryTitle || productName,
      productImage,
      productData: mergedPrimary
        ? { ...mergedPrimary, country: mergedPrimary.country || country }
        : mergedPrimary,
      productDataRows: popupRows,
      groupProductNames: names.length > 1 ? names : undefined,
      packSizeOverrides: packSizeOverridesFromStorage,
      productFlags: productFlagsFromStorage,
      pricingAsOfDate: resolvedDate,
      country,
      pinnedChartPrices: !!(
        dateLabel &&
        mergedPrimary &&
        (
          (snap && (snap.actual_price != null || snap.regular_price != null || snap.stock_status != null)) ||
          mergedPrimary.actual_price != null ||
          mergedPrimary.regular_price != null ||
          mergedPrimary.stock_status != null
        )
      ),
    });
    setProductPopupOpen(true);
  }, [data, pinPriceSourceRows, categoryOverrides, packSizeOverridesFromStorage, productFlagsFromStorage, priceDisplayMode, getItemPackSize, selectedCountries, dateRange]);

  const productImageUrlByTitle = useMemo(() => {
    const m = new Map();
    (data || []).forEach((item) => {
      const title = item?.title;
      if (!title || m.has(title)) return;
      // Try image, box_image_urls (GLO), image_urls (IQOS), media_gallery_urls (GLO/Italy),
      // resolving each in priority order so an unresolvable earlier field never hides a valid later one.
      const url = resolveProductImageUrlFromFields(item.image, item.box_image_urls, item.image_urls, item.media_gallery_urls);
      if (url) m.set(title, url);
    });
    return m;
  }, [data]);

  /** Same Product detail popup as pin-panel variant clicks — uses series row SKU titles and last-seen date label. */
  const handleSeriesSummaryRowClick = useCallback(
    (row) => {
      if (!row) return;
      let titles;
      if (Array.isArray(row.skuTitlesList) && row.skuTitlesList.length > 0) {
        titles = row.skuTitlesList;
      } else if (Array.isArray(row.skuTitlesList) && row.skuTitlesList.length === 0) {
        return;
      } else {
        titles = String(row.skuTitles || '')
          .split(';')
          .map((s) => s.trim())
          .filter(Boolean);
      }
      if (!titles.length) return;
      const primary = titles[0];
      const img =
        (Array.isArray(row.skuImageUrls) && row.skuImageUrls.find(Boolean)) ||
        productImageUrlByTitle.get(primary) ||
        null;
      const dateLabel =
        row.lastSeen != null && row.lastSeen !== '' && row.lastSeen !== '—' ? String(row.lastSeen) : undefined;
      handleProductClick(primary, img, {
        groupProductNames: titles.length > 1 ? titles : undefined,
        chartPinnedDateLabel: dateLabel,
        country: row.country || row.legendCountryIso,
      });
    },
    [handleProductClick, productImageUrlByTitle]
  );

  const handlePriceChartPin = useCallback((clickedIndex) => {
    setPriceChartPinIndex((prev) => (prev === clickedIndex ? null : clickedIndex));
  }, []);
  const handleClearPriceChartPin = useCallback(() => {
    setPriceChartPinIndex(null);
  }, []);
  const handleVisibilityChartPin = useCallback((clickedIndex) => {
    setVisibilityChartPinIndex((prev) => (prev === clickedIndex ? null : clickedIndex));
  }, []);
  const handleClearVisibilityChartPin = useCallback(() => {
    setVisibilityChartPinIndex(null);
  }, []);

  // Fetch data from the JTI API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [dataResponse, overridesResponse, brandOverridesResponse, flagsResponse, packSizeResponse, matchingResponse] = await Promise.all([
          axios.get(`${API_URL}/jti/global-merged-data`),
          axios.get(`${API_URL}/jti/product-category-overrides`).catch(() => ({ data: { overrides: {} } })),
          axios.get(`${API_URL}/jti/product-brand-overrides`).catch(() => ({ data: { overrides: {} } })),
          axios.get(`${API_URL}/jti/product-flags`).catch(() => ({ data: { flags: {} } })),
          axios.get(`${API_URL}/jti/pack-size-overrides`).catch(() => ({ data: { overrides: {} } })),
          axios.get(`${API_URL}/jti/matching-groups`).catch(() => ({ data: { tables: {} } })),
        ]);
        
        const { columns: apiColumns, data: apiData } = dataResponse.data;
        
        // Convert raw data to objects with column names.
        // Pre-parse the row timestamp ONCE here (dayjs string parsing is expensive) and store it
        // as a numeric epoch in `__ts`. The date-range filter then compares numbers instead of
        // re-parsing every row on every filter change, which is the main performance hotspot.
        const processedDataRaw = apiData.map(row => {
          const obj = {};
          apiColumns.forEach((col, index) => {
            obj[col] = row[index];
          });
          const rawDate = obj.updated_at || obj.date;
          if (rawDate) {
            const parsed = dayjs(rawDate);
            obj.__ts = parsed.isValid() ? parsed.valueOf() : null;
          } else {
            obj.__ts = null;
          }
          return obj;
        });

        const overrides = indexOverridesByTitle(overridesResponse?.data?.overrides || {});
        const brandOverrides = brandOverridesResponse?.data?.overrides || {};
        setCategoryOverrides(overrides);
        setProductFlagsFromStorage(flagsResponse?.data?.flags || {});
        setPackSizeOverridesFromStorage(packSizeResponse?.data?.overrides || {});
        setMatchingGroups(buildMatchingGroupsMapFromTables(matchingResponse?.data?.tables || {}));

        // Apply category + brand overrides so all downstream filtering/display uses them
        const processedData = processedDataRaw.map((item) =>
          mergeJtiProductRowOverrides(item, overrides, brandOverrides)
        );

        setData(processedData);
        setColumns(apiColumns);
        
        // Extract unique values for filters
        const uniqueValues = {};
        apiColumns.forEach(col => {
          const values = [...new Set(processedData.map(item => item[col]).filter(val => val !== null && val !== undefined))];
          uniqueValues[col] = values;
        });
        
        setColumnsUniqueValues(uniqueValues);
        
        // Extract and set available dates for date range
        // API provides `updated_at` (and not always `date`), so use updated_at as the canonical timeline.
        const rawDates = processedData
          .map((item) => item.updated_at || item.date)
          .filter((date) => date && date !== null && date !== undefined)
          .map((date) => dayjs(date))
          .filter((date) => date.isValid())
          .map((date) => date.startOf('day'))
          .sort((a, b) => a.valueOf() - b.valueOf());
        const dates = [];
        const seenDates = new Set();
        rawDates.forEach((date) => {
          const key = date.format('YYYY-MM-DD');
          if (!seenDates.has(key)) {
            seenDates.add(key);
            dates.push(date);
          }
        });
        
        if (dates.length > 0) {
          setAvailableDates(dates);
          setDateRange([dates[0], dates[dates.length - 1]]);
        }
        
        // Set default values once on initial load.
        // Guarded so StrictMode/dev double-effect does not wipe already-applied defaults (e.g. default Ploom).
        if (!hasAppliedInitialFilterResetRef.current) {
          setSelectedCountries([]);
          setSelectedBrands([]);
          setSelectedRetailers([]);
          // Product category defaults to route match (Devices / Sticks) via routeCategoryGroup effect — do not clear here
          setSelectedProducts([]);
          if (uniqueValues.flavour) {
            setFlavourButtonsStates(new Array(uniqueValues.flavour.length).fill(false));
            setSelectedFlavours([]);
          }
          hasAppliedInitialFilterResetRef.current = true;
        }
        
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load JTI data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filterToAvailable = (values, options) => {
    if (!Array.isArray(values)) return [];
    if (!Array.isArray(options) || options.length === 0) return values;
    return values.filter((value) => options.includes(value));
  };

  const applySavedFilters = (savedFilters) => {
    if (!savedFilters || typeof savedFilters !== 'object') return;
    isApplyingViewRef.current = true;
    if (applyingViewTimeoutRef.current) {
      clearTimeout(applyingViewTimeoutRef.current);
    }

    if (Array.isArray(savedFilters.selectedManufacturers)) {
      setSelectedManufacturers(filterToAvailable(savedFilters.selectedManufacturers, MANUFACTURER_OPTIONS));
    }
    if (savedFilters.selectedBrands) {
      setSelectedBrands(filterToAvailable(savedFilters.selectedBrands, brandOptions));
    }
    if (savedFilters.selectedCategoryGroups) {
      const filtered = filterToAvailable(savedFilters.selectedCategoryGroups, categoryGroupOptions);
      if (filtered.length > 0) {
        setSelectedCategoryGroups(filtered);
      } else {
        const fallback = resolveCategoryGroupForType(productType, categoryGroupOptions)
          || resolveDefaultCategoryGroupForRoute(productType);
        setSelectedCategoryGroups(fallback ? [fallback] : []);
      }
    }
    if (savedFilters.selectedCountries && !isMarketScope) {
      setSelectedCountries(filterToAvailable(savedFilters.selectedCountries, countryOptions));
    }
    if (savedFilters.selectedRetailers) {
      setSelectedRetailers(filterToAvailable(savedFilters.selectedRetailers, retailerOptions));
    }
    if (savedFilters.selectedFlavours) {
      setSelectedFlavours(savedFilters.selectedFlavours);
    }
    if (savedFilters.selectedProducts) {
      setSelectedProducts(upgradeLegacyModelSelections(savedFilters.selectedProducts, groupNameToProducts));
      hasAppliedMarketProductDefaultRef.current = true;
    }
    if (savedFilters.selectedCurrency) {
      setSelectedCurrency(savedFilters.selectedCurrency);
    }
    if (Array.isArray(savedFilters.selectedPackSizes)) {
      setSelectedPackSizes(filterToAvailable(savedFilters.selectedPackSizes, packSizeOptions));
    }
    if (savedFilters.customizableFilter != null || Array.isArray(savedFilters.selectedProductAttributes)) {
      setCustomizableFilter(
        normalizeCustomizableFilter(
          savedFilters.customizableFilter ?? savedFilters.selectedProductAttributes
        )
      );
    }
    if (savedFilters.forNewUsersOnlyFilter != null || Array.isArray(savedFilters.selectedProductAttributes)) {
      setForNewUsersOnlyFilter(
        normalizeForNewUsersOnlyFilter(
          savedFilters.forNewUsersOnlyFilter ?? savedFilters.selectedProductAttributes
        )
      );
    }
    if (Array.isArray(savedFilters.selectedProductAttributes)) {
      // Migrate legacy "limited_edition" product-attribute checkbox into the dedicated edition filter
      if (
        savedFilters.selectedProductAttributes.includes('limited_edition') &&
        savedFilters.limitedEditionFilter == null &&
        savedFilters.additionalFilters?.limitedEdition == null
      ) {
        setAdditionalFilters((prev) => ({
          ...prev,
          limitedEdition: LIMITED_EDITION_FILTER_ONLY,
        }));
      }
    }
    if (savedFilters.limitedEditionFilter != null) {
      setAdditionalFilters((prev) => ({
        ...prev,
        limitedEdition: normalizeLimitedEditionFilter(savedFilters.limitedEditionFilter),
      }));
    } else if (savedFilters.additionalFilters?.limitedEdition != null) {
      setAdditionalFilters((prev) => ({
        ...prev,
        limitedEdition: normalizeLimitedEditionFilter(savedFilters.additionalFilters.limitedEdition),
      }));
    }
  if (savedFilters.starterKitProductFilter != null) {
      setStarterKitProductFilter(normalizeStarterKitFilter(savedFilters.starterKitProductFilter));
    }
    if (typeof savedFilters.normalizeToEur === 'boolean') {
      setNormalizeToEur(savedFilters.normalizeToEur);
    }
    if (Array.isArray(savedFilters.dateRange)) {
      const [start, end] = savedFilters.dateRange;
      const startDate = start ? dayjs(start) : null;
      const endDate = end ? dayjs(end) : null;
      setDateRange([
        startDate && startDate.isValid() ? startDate : null,
        endDate && endDate.isValid() ? endDate : null,
      ]);
    }
    if (typeof savedFilters.activeChartTab === 'number') {
      setActiveChartTab(savedFilters.activeChartTab);
    }
    if (typeof savedFilters.topN === 'number') {
      setTopN(Math.min(savedFilters.topN, MAX_GROUPS));
    }
    if (Array.isArray(savedFilters.visiblePriceDatasets)) {
      savedPriceVisibilityRef.current = savedFilters.visiblePriceDatasets;
      setVisiblePriceDatasets(new Set(savedFilters.visiblePriceDatasets));
    }
    if (Array.isArray(savedFilters.visibleVisibilityDatasets)) {
      savedVisibilityRef.current = savedFilters.visibleVisibilityDatasets;
      setVisibleVisibilityDatasets(new Set(savedFilters.visibleVisibilityDatasets));
    }
    applyingViewTimeoutRef.current = setTimeout(() => {
      isApplyingViewRef.current = false;
    }, 0);
  };

  useEffect(() => {
    if (!username) return;

    const loadViews = async () => {
      const storageKey = `jti_price_filter_views_${username}_${projectKey || username}`;
      let loadedViews = null;

      try {
        const response = await axios.get(`${API_URL}/user-filters/${username}`, {
          params: {
            project_key: projectKey || username,
          },
          withCredentials: true,
        });
        loadedViews = response.data?.views || null;
      } catch (error) {
        console.warn('Failed to load filter views from API, falling back to localStorage:', error);
      }

      if (!loadedViews) {
        try {
          const stored = localStorage.getItem(storageKey);
          if (stored) {
            const parsed = JSON.parse(stored);
            loadedViews = parsed?.views || null;
          }
        } catch (error) {
          console.warn('Failed to load filter views from localStorage:', error);
        }
      }

      if (Array.isArray(loadedViews)) {
        setSavedViews(loadedViews);
        setNewViewName(`Filter ${loadedViews.length + 1}`);
      } else {
        setNewViewName('Filter 1');
      }
    };

    loadViews();
  }, [username, projectKey]);

  const brandOptions = useMemo(() => {
    // Limit brand options to brands present in the active category scope
    // (e.g. /sticks should only show brands found in Sticks/Sticks Bundles).
    const defaultCategoryScope = isStickRoute
      ? new Set(['Sticks', 'Sticks Bundles'])
      : new Set(['Devices', 'Device Bundles']);
    const categoryScope =
      selectedCategoryGroups && selectedCategoryGroups.length > 0
        ? new Set(selectedCategoryGroups)
        : defaultCategoryScope;

    // Only show brands present in the active country scope (e.g. Pulze exists only in CZ, so it
    // must not appear in the Poland market view).
    const availableCountries = Array.from(new Set((data || []).map((i) => i?.country).filter(Boolean)));
    const countryScope = computeCountryScopeSet({ selectedCountries, isMarketScope, selectedMarket, availableCountries });

    let brands = Array.from(
      new Set(
        (data || [])
          .filter((item) => {
            if (countryScope && !countryScope.has(normalizeValue(item?.country))) return false;
            const effectiveGroup = getEffectiveCategoryGroup(item);
            if (!effectiveGroup) return false;
            return categoryScope.has(effectiveGroup);
          })
          .map((item) => item?.brand)
          .filter(Boolean)
      )
    );
    // When manufacturers are selected, filter to matching brand(s)
    if (selectedManufacturers.length > 0) {
      const matchingBrands = new Set();
      selectedManufacturers.forEach(m => {
        getManufacturerBrandKeywords(m).forEach((kw) => matchingBrands.add(kw));
      });
      if (matchingBrands.size > 0) {
        brands = brands.filter(b => {
          const bNorm = normalizeValue(b);
          return [...matchingBrands].some(mb => bNorm === mb || bNorm.includes(mb));
        });
      }
    }
    return brands.sort();
  }, [data, getEffectiveCategoryGroup, isStickRoute, selectedCategoryGroups, selectedManufacturers, selectedCountries, isMarketScope, selectedMarket]);

  // Only show manufacturers whose brand actually occurs in the active country + category scope
  // (e.g. a manufacturer with no products in the Poland market is hidden there).
  const manufacturerOptions = useMemo(() => {
    const availableCountries = Array.from(new Set((data || []).map((i) => i?.country).filter(Boolean)));
    const countryScope = computeCountryScopeSet({ selectedCountries, isMarketScope, selectedMarket, availableCountries });
    const defaultCategoryScope = isStickRoute
      ? new Set(['Sticks', 'Sticks Bundles'])
      : new Set(['Devices', 'Device Bundles']);
    const categoryScope =
      selectedCategoryGroups && selectedCategoryGroups.length > 0
        ? new Set(selectedCategoryGroups)
        : defaultCategoryScope;
    const presentBrands = new Set();
    (data || []).forEach((item) => {
      if (countryScope && !countryScope.has(normalizeValue(item?.country))) return;
      const effectiveGroup = getEffectiveCategoryGroup(item);
      if (!effectiveGroup || !categoryScope.has(effectiveGroup)) return;
      const b = item?.brand;
      if (b) presentBrands.add(normalizeValue(b));
    });
    return MANUFACTURER_OPTIONS.filter((m) => {
      const keywords = getManufacturerBrandKeywords(m);
      if (!keywords.length) return false;
      return [...presentBrands].some((b) => keywords.some((kw) => b === kw || b.includes(kw)));
    });
  }, [data, getEffectiveCategoryGroup, isStickRoute, selectedCategoryGroups, selectedCountries, isMarketScope, selectedMarket]);

  useEffect(() => {
    if (!selectedManufacturers.length) return;
    const allowed = new Set(manufacturerOptions);
    const next = selectedManufacturers.filter((m) => allowed.has(m));
    if (next.length !== selectedManufacturers.length) setSelectedManufacturers(next);
  }, [manufacturerOptions, selectedManufacturers]);

  useEffect(() => {
    if (!columnsUniqueValues) return;

    const productTypeChanged = previousProductTypeForBrandRef.current !== productType;
    const scopeChanged = previousScopeForBrandRef.current !== scopeMode;
    const marketChanged = previousMarketForBrandRef.current !== selectedMarket;
    previousProductTypeForBrandRef.current = productType;
    previousScopeForBrandRef.current = scopeMode;
    previousMarketForBrandRef.current = selectedMarket;

    const ploomBrand = findPloomBrand(brandOptions);
    const applyPloomDefault = () => {
      // Market view includes all brands by default — leave the brand filter empty.
      if (isMarketScope) {
        setSelectedBrands([]);
        return;
      }
      if (ploomBrand) setSelectedBrands([ploomBrand]);
    };
    const applyMarketProductDefault = (force = false) => {
      if (!isMarketScope || (!force && isApplyingViewRef.current)) return false;
      if (!groupNameToProducts?.size) return false;
      setSelectedProducts(resolveMarketDefaultProductSelections(productType, groupNameToProducts));
      hasAppliedMarketProductDefaultRef.current = true;
      return true;
    };

    if (productTypeChanged) {
      hasAppliedMarketProductDefaultRef.current = false;
      setSelectedManufacturers([]);
      applyPloomDefault();
      if (!applyMarketProductDefault()) setSelectedProducts([]);
      setIsInitialBrandSelectionResolved(true);
      return;
    }

    if (scopeChanged) {
      hasAppliedMarketProductDefaultRef.current = false;
      if (rememberBrandFiltersOnScopeChange) {
        setSelectedBrands((prev) => filterToAvailable(prev, brandOptions));
      } else {
        setSelectedManufacturers([]);
        applyPloomDefault();
        if (!applyMarketProductDefault()) setSelectedProducts([]);
      }
      setIsInitialBrandSelectionResolved(true);
      return;
    }

    if (marketChanged && isMarketScope) {
      hasAppliedMarketProductDefaultRef.current = false;
      setSelectedManufacturers([]);
      applyPloomDefault();
      if (!applyMarketProductDefault(true)) setSelectedProducts([]);
      setIsInitialBrandSelectionResolved(true);
      return;
    }

    if (
      selectedBrands.length === 0 &&
      selectedProducts.length === 0 &&
      selectedManufacturers.length === 0
    ) {
      applyPloomDefault();
    }
    if (!hasAppliedMarketProductDefaultRef.current && selectedProducts.length === 0) {
      applyMarketProductDefault();
    }
    setIsInitialBrandSelectionResolved(true);
  }, [
    columnsUniqueValues,
    brandOptions,
    productType,
    scopeMode,
    selectedMarket,
    rememberBrandFiltersOnScopeChange,
    selectedBrands.length,
    selectedProducts.length,
    selectedManufacturers.length,
    groupNameToProducts,
    isMarketScope,
  ]);

  const currencyOptions = useMemo(() => {
    const raw = columnsUniqueValues?.currency || [];
    const normalized = raw.map(normalizeCurrencyCode).filter(Boolean);
    return Array.from(new Set(normalized)).sort();
  }, [columnsUniqueValues]);

  const countryOptions = useMemo(() => {
    return (columnsUniqueValues?.country || []).filter(Boolean).sort();
  }, [columnsUniqueValues]);

  const localCurrencyCode = useMemo(() => {
    if (!isMarketScope) return null;
    return resolveMarketDefaultCurrency(selectedMarket, countryOptions, data);
  }, [isMarketScope, selectedMarket, countryOptions, data]);

  const categoryGroupOptions = useMemo(() => {
    // Compute effective category groups from data with overrides applied
    const effectiveGroups = new Set();
    data.forEach(item => {
      const effectiveGroup = getEffectiveCategoryGroup(item);
      if (effectiveGroup) effectiveGroups.add(effectiveGroup);
    });
    const allOptions = Array.from(effectiveGroups).sort();
    
    if (!productType) return allOptions;
    
    const normalizedType = normalizeValue(productType);
    const isDevices = PRODUCT_TYPE_ALIASES.devices.includes(normalizedType);
    const isSticks = PRODUCT_TYPE_ALIASES.sticks.includes(normalizedType);
    const isAccessories = PRODUCT_TYPE_ALIASES.accessories.includes(normalizedType);
    
    if (isDevices) {
      // For /devices: show only "Devices", "Device Bundles"
      return allOptions.filter(opt => 
        opt === 'Devices' || opt === 'Device Bundles'
      );
    } else if (isSticks) {
      // For /sticks: show only "Sticks", "Sticks Bundles"
      return allOptions.filter(opt => 
        opt === 'Sticks' || opt === 'Sticks Bundles'
      );
    } else if (isAccessories) {
      return allOptions.filter((opt) => opt === 'Accessories');
    }
    
    return allOptions;
  }, [data, getEffectiveCategoryGroup, productType]);

  const routeCategoryGroup = useMemo(
    () => resolveCategoryGroupForType(productType, categoryGroupOptions),
    [productType, categoryGroupOptions]
  );
  const routeCategoryGroupFallback = useMemo(
    () => resolveDefaultCategoryGroupForRoute(productType),
    [productType]
  );

  const packSizeOptions = useMemo(() => {
    if (!data.length) return [];
    const sizes = new Set();
    data.forEach((item) => {
      const n = getItemPackSize(item);
      if (n !== null) sizes.add(n);
    });
    return Array.from(sizes).sort((a, b) => a - b);
  }, [data, getItemPackSize]);

  useEffect(() => {
    const productTypeChanged = lastProductTypeRef.current !== productType;
    const nextDefault = routeCategoryGroup || routeCategoryGroupFallback;
    // Always set default when productType changes or when no category is selected
    if (nextDefault && (productTypeChanged || selectedCategoryGroups.length === 0)) {
      setSelectedCategoryGroups([nextDefault]);
    }
    lastProductTypeRef.current = productType;
  }, [routeCategoryGroup, routeCategoryGroupFallback, productType, selectedCategoryGroups.length]);

  const handleCategoryGroupChange = useCallback((next) => {
    const arr = Array.isArray(next) ? next.filter(Boolean) : [];
    if (arr.length === 0) {
      const fallback = routeCategoryGroup || routeCategoryGroupFallback;
      if (fallback) {
        setSelectedCategoryGroups([fallback]);
      } else {
        setSelectedCategoryGroups([]);
      }
      return;
    }
    setSelectedCategoryGroups(arr);
  }, [routeCategoryGroup, routeCategoryGroupFallback]);

  const retailerOptions = useMemo(() => {
    // Show only retailers that actually have rows in the active country scope:
    // - Market mode: selected market country (or selectedCountries override)
    // - HQ mode: selectedCountries (or all countries when none selected)
    const countryFilter =
      selectedCountries.length > 0
        ? new Set(selectedCountries.map((c) => normalizeValue(c)))
        : (isMarketScope
            ? (() => {
                const inferred = resolveMarketCountry(selectedMarket, countryOptions);
                return inferred ? new Set([normalizeValue(inferred)]) : null;
              })()
            : null);
    const retailers = new Set();
    data.forEach((item) => {
      const retailer = (item?.retailer || '').toString().trim();
      if (!retailer) return;
      if (countryFilter && !countryFilter.has(normalizeValue(item?.country))) return;
      retailers.add(retailer);
    });
    return Array.from(retailers).sort();
  }, [isMarketScope, selectedCountries, selectedMarket, countryOptions, data]);

  useEffect(() => {
    if (!selectedRetailers.length) return;
    const allowed = new Set(retailerOptions);
    const next = selectedRetailers.filter((r) => allowed.has(r));
    if (next.length !== selectedRetailers.length) {
      setSelectedRetailers(next);
    }
  }, [retailerOptions, selectedRetailers]);

  // Filter data based on selected filters
  useEffect(() => {
    if (!isInitialBrandSelectionResolved) {
      setChartComputationPending(false);
      return;
    }
    if (data.length === 0) {
      setChartComputationPending(false);
      return;
    }

    let filtered = [...data];

    // Date-range bounds computed ONCE (not per row). Rows carry a pre-parsed numeric `__ts`
    // (see data load), so the date filter is a cheap numeric comparison instead of three
    // dayjs parses per row across the whole dataset.
    const hasDateRange = Boolean(dateRange[0] && dateRange[1]);
    const dateLowerBound = hasDateRange ? dateRange[0].subtract(1, 'day').valueOf() : null;
    const dateUpperBound = hasDateRange ? dateRange[1].add(1, 'day').valueOf() : null;
    const matchesDateRange = (item) => {
      let ts = item.__ts;
      if (ts === undefined) {
        const parsed = dayjs(item.updated_at || item.date);
        ts = parsed.isValid() ? parsed.valueOf() : null;
      }
      return ts != null && ts > dateLowerBound && ts < dateUpperBound;
    };

    // Filter by manufacturer (maps to brand: JTI→Ploom, BAT→glo, PMI→IQOS)
    if (selectedManufacturers.length > 0) {
      const matchingBrands = new Set();
      selectedManufacturers.forEach(m => {
        getManufacturerBrandKeywords(m).forEach((kw) => matchingBrands.add(kw));
      });
      if (matchingBrands.size > 0) {
        filtered = filtered.filter(item => {
          const itemBrand = normalizeValue(item.brand || '');
          return [...matchingBrands].some(mb => itemBrand === mb || itemBrand.includes(mb));
        });
      }
    }

    // Filter by brand
    if (selectedBrands.length > 0) {
      filtered = filtered.filter(item => selectedBrands.includes(item.brand));
    }

    // Handle currency: filter if local currency (single market), convert if non-local or HQ (multi-market)
    let shouldConvertCurrency = false;
    let targetCurrencyForConversion = null;
    if (selectedCurrency && !normalizeToEur) {
      const targetCurrency = normalizeCurrencyCode(selectedCurrency);
      const normalizedLocalCurrency = localCurrencyCode ? normalizeCurrencyCode(localCurrencyCode) : null;
      
      // HQ view (no local market): always convert to selected currency so we show all data
      // Market view: filter if local currency, convert if user picked a different currency
      if (!normalizedLocalCurrency || targetCurrency !== normalizedLocalCurrency) {
        shouldConvertCurrency = true;
        targetCurrencyForConversion = targetCurrency;
      } else {
        filtered = filtered.filter(item => normalizeCurrencyCode(item.currency) === targetCurrency);
      }
    }

    // Filter by countries
    if (selectedCountries.length > 0) {
      filtered = filtered.filter(item => selectedCountries.includes(item.country));
    }

    // Filter by retailers
    if (selectedRetailers.length > 0) {
      filtered = filtered.filter(item => selectedRetailers.includes(item.retailer));
    }

    // Filter by flavours
    if (selectedFlavours.length > 0) {
      filtered = filtered.filter(item => selectedFlavours.includes(item.flavour));
    }

    // Filter by category group (using effective category group with overrides)
    if (selectedCategoryGroups.length > 0) {
      filtered = filtered.filter(item => {
        const effectiveGroup = getEffectiveCategoryGroup(item);
        return selectedCategoryGroups.includes(effectiveGroup);
      });
    }

    // Filter by pack size (use getItemPackSize to respect user overrides)
    if (selectedPackSizes.length > 0) {
      filtered = filtered.filter(item => {
        const itemSize = getItemPackSize(item);
        return itemSize !== null && selectedPackSizes.includes(itemSize);
      });
    }

    filtered = filtered.filter((item) =>
      rowMatchesCustomizableFilter(item, customizableFilter, productFlagsFromStorage)
    );
    filtered = filtered.filter((item) =>
      rowMatchesForNewUsersOnlyFilter(item, forNewUsersOnlyFilter, productFlagsFromStorage)
    );

    filtered = filtered.filter((item) =>
      rowMatchesLimitedEditionFilter(
        item,
        additionalFilters?.limitedEdition,
        productFlagsFromStorage
      )
    );

    if (isDevicesRoute) {
      filtered = filtered.filter((item) =>
        rowMatchesStarterKitProductFilter(
          item,
          starterKitProductFilter,
          productFlagsFromStorage
        )
      );
    }

    const filteredBeforeAvailability = [...filtered];
    filtered = filtered.filter((item) => rowMatchesAvailability(item, topFilters.availability));

    // Price chart: include all stock states so lines can break / style by availability (not only OOS/avail rows).
    const filteredChartBeforeDateRange = [...filteredBeforeAvailability];

    // In HQ mode, keep a copy before date filter so country chart can show full time period
    // (whole time period for given countries where at least one product exists; filters still apply)
    const filteredBeforeDateRange = [...filtered];

    // Filter by date range
    if (hasDateRange) {
      filtered = filtered.filter(matchesDateRange);
    }

    // Keep unfiltered copy for country chart — country view should average ALL products per market,
    // not just selected ones, so lines extend through full date range when data exists
    const filteredBeforeProductFilter = [...filtered];

    // Filter by selected products (if any); expand model series / models to their constituent titles
    if (selectedProducts.length > 0) {
      const expandedTitles = expandProductSelections(
        selectedProducts,
        groupNameToProducts,
        modelGroupToProducts
      );
      filtered = filtered.filter(item => expandedTitles.has(item.title));
    }

    // Filter by device colour — individual colour or legacy variant-group name
    if (!isStickRoute && additionalFilters?.deviceColour && additionalFilters.deviceColour !== 'All') {
      const selectedColour = additionalFilters.deviceColour;
      filtered = filtered.filter((item) => itemMatchesSelectedVariant(item, selectedColour, matchingGroups));
    }

    // Filter by stick flavour — individual flavour or legacy variant-group name
    if (isStickRoute && additionalFilters?.stickFlavour && additionalFilters.stickFlavour !== 'All') {
      const selectedFlavour = additionalFilters.stickFlavour;
      filtered = filtered.filter((item) => itemMatchesSelectedVariant(item, selectedFlavour, matchingGroups));
    }

    // Filter by HNS / HTS stick category (matching group name, variant group, or title keywords)
    if (isStickRoute && additionalFilters?.stickCategory && additionalFilters.stickCategory !== 'All') {
      const selectedCategory = additionalFilters.stickCategory;
      filtered = filtered.filter((item) => matchesStickCategoryFilter(item, matchingGroups, selectedCategory));
    }

    // Same filters as `filtered` but without availability row filter — chart can segment by stock_status.
    let chartFiltered = [...filteredChartBeforeDateRange];
    if (hasDateRange) {
      chartFiltered = chartFiltered.filter(matchesDateRange);
    }
    if (selectedProducts.length > 0) {
      const expandedTitlesChart = expandProductSelections(
        selectedProducts,
        groupNameToProducts,
        modelGroupToProducts
      );
      chartFiltered = chartFiltered.filter((item) => expandedTitlesChart.has(item.title));
    }
    if (!isStickRoute && additionalFilters?.deviceColour && additionalFilters.deviceColour !== 'All') {
      const selectedColour = additionalFilters.deviceColour;
      chartFiltered = chartFiltered.filter((item) => itemMatchesSelectedVariant(item, selectedColour, matchingGroups));
    }
    if (isStickRoute && additionalFilters?.stickFlavour && additionalFilters.stickFlavour !== 'All') {
      const selectedFlavour = additionalFilters.stickFlavour;
      chartFiltered = chartFiltered.filter((item) => itemMatchesSelectedVariant(item, selectedFlavour, matchingGroups));
    }
    if (isStickRoute && additionalFilters?.stickCategory && additionalFilters.stickCategory !== 'All') {
      const selectedCategory = additionalFilters.stickCategory;
      chartFiltered = chartFiltered.filter((item) => matchesStickCategoryFilter(item, matchingGroups, selectedCategory));
    }

    // Build normalizedFilteredForCountry: same row window as chartFiltered (incl. date range) so the
    // datetime slider filters the country/HQ chart. Applies to both HQ and market mode.
    let normalizedFilteredForCountry = null;
    const isCountryView = groupBy === 'countries' || groupBy === 'all';
    if (isCountryView) {
      const countryChartSource = filteredChartBeforeDateRange;
      let forCountry = countryChartSource;
      // Apply selectedProducts filter when user has selected products — chart should recalculate
      if (selectedProducts.length > 0) {
        const expandedTitlesForCountry = expandProductSelections(
          selectedProducts,
          groupNameToProducts,
          modelGroupToProducts
        );
        forCountry = forCountry.filter(item => expandedTitlesForCountry.has(item.title));
      }
      if (!isStickRoute && additionalFilters?.deviceColour && additionalFilters.deviceColour !== 'All') {
        const selectedColour = additionalFilters.deviceColour;
        forCountry = forCountry.filter((item) => itemMatchesSelectedVariant(item, selectedColour, matchingGroups));
      }
      if (isStickRoute && additionalFilters?.stickFlavour && additionalFilters.stickFlavour !== 'All') {
        const selectedFlavour = additionalFilters.stickFlavour;
        forCountry = forCountry.filter((item) => itemMatchesSelectedVariant(item, selectedFlavour, matchingGroups));
      }
      if (isStickRoute && additionalFilters?.stickCategory && additionalFilters.stickCategory !== 'All') {
        const selectedCategory = additionalFilters.stickCategory;
        forCountry = forCountry.filter((item) => matchesStickCategoryFilter(item, matchingGroups, selectedCategory));
      }
      if (hasDateRange) {
        forCountry = forCountry.filter(matchesDateRange);
      }
      let currencyConvertedForCountry = forCountry;
      if (shouldConvertCurrency && targetCurrencyForConversion) {
        currencyConvertedForCountry = forCountry.map((item) => {
          const convertedItem = { ...item, currency: targetCurrencyForConversion };
          if (item.actual_price !== undefined) convertedItem.actual_price = convertToCurrency(item.actual_price, item.currency, targetCurrencyForConversion);
          if (item.regular_price !== undefined) convertedItem.regular_price = convertToCurrency(item.regular_price, item.currency, targetCurrencyForConversion);
          if (item.price !== undefined) convertedItem.price = convertToCurrency(item.price, item.currency, targetCurrencyForConversion);
          if (item.current_price !== undefined) convertedItem.current_price = convertToCurrency(item.current_price, item.currency, targetCurrencyForConversion);
          if (item.original_price !== undefined) convertedItem.original_price = convertToCurrency(item.original_price, item.currency, targetCurrencyForConversion);
          return convertedItem;
        });
      }
      normalizedFilteredForCountry = normalizeToEur
        ? currencyConvertedForCountry.map((item) => {
            const rate = EXCHANGE_RATES_TO_EUR[normalizeCurrencyCode(item.currency)];
            if (!rate) return item;
            const normalizedItem = { ...item, currency: 'EUR' };
            if (item.actual_price !== undefined) normalizedItem.actual_price = convertToEur(item.actual_price, item.currency);
            if (item.regular_price !== undefined) normalizedItem.regular_price = convertToEur(item.regular_price, item.currency);
            if (item.price !== undefined) normalizedItem.price = convertToEur(item.price, item.currency);
            if (item.current_price !== undefined) normalizedItem.current_price = convertToEur(item.current_price, item.currency);
            if (item.original_price !== undefined) normalizedItem.original_price = convertToEur(item.original_price, item.currency);
            return normalizedItem;
          })
        : currencyConvertedForCountry;
    }

    // Convert currency if needed (before EUR normalization)
    let currencyConverted = filtered;
    if (shouldConvertCurrency && targetCurrencyForConversion) {
      currencyConverted = filtered.map((item) => {
        const convertedItem = {
          ...item,
          currency: targetCurrencyForConversion,
        };
        if (item.actual_price !== undefined) {
          convertedItem.actual_price = convertToCurrency(item.actual_price, item.currency, targetCurrencyForConversion);
        }
        if (item.regular_price !== undefined) {
          convertedItem.regular_price = convertToCurrency(item.regular_price, item.currency, targetCurrencyForConversion);
        }
        if (item.price !== undefined) {
          convertedItem.price = convertToCurrency(item.price, item.currency, targetCurrencyForConversion);
        }
        if (item.current_price !== undefined) {
          convertedItem.current_price = convertToCurrency(item.current_price, item.currency, targetCurrencyForConversion);
        }
        if (item.original_price !== undefined) {
          convertedItem.original_price = convertToCurrency(item.original_price, item.currency, targetCurrencyForConversion);
        }
        return convertedItem;
      });
    }

    const normalizedFiltered = normalizeToEur
      ? currencyConverted.map((item) => {
        const rate = EXCHANGE_RATES_TO_EUR[normalizeCurrencyCode(item.currency)];
        if (!rate) return item;
        const normalizedItem = {
          ...item,
          currency: 'EUR',
        };
        if (item.actual_price !== undefined) {
          normalizedItem.actual_price = convertToEur(item.actual_price, item.currency);
        }
        if (item.regular_price !== undefined) {
          normalizedItem.regular_price = convertToEur(item.regular_price, item.currency);
        }
        if (item.price !== undefined) {
          normalizedItem.price = convertToEur(item.price, item.currency);
        }
        if (item.current_price !== undefined) {
          normalizedItem.current_price = convertToEur(item.current_price, item.currency);
        }
        if (item.original_price !== undefined) {
          normalizedItem.original_price = convertToEur(item.original_price, item.currency);
        }
        return normalizedItem;
      })
      : currencyConverted;

    let currencyConvertedChart = chartFiltered;
    if (shouldConvertCurrency && targetCurrencyForConversion) {
      currencyConvertedChart = chartFiltered.map((item) => {
        const convertedItem = {
          ...item,
          currency: targetCurrencyForConversion,
        };
        if (item.actual_price !== undefined) {
          convertedItem.actual_price = convertToCurrency(item.actual_price, item.currency, targetCurrencyForConversion);
        }
        if (item.regular_price !== undefined) {
          convertedItem.regular_price = convertToCurrency(item.regular_price, item.currency, targetCurrencyForConversion);
        }
        if (item.price !== undefined) {
          convertedItem.price = convertToCurrency(item.price, item.currency, targetCurrencyForConversion);
        }
        if (item.current_price !== undefined) {
          convertedItem.current_price = convertToCurrency(item.current_price, item.currency, targetCurrencyForConversion);
        }
        if (item.original_price !== undefined) {
          convertedItem.original_price = convertToCurrency(item.original_price, item.currency, targetCurrencyForConversion);
        }
        return convertedItem;
      });
    }

    const normalizedChartFiltered = normalizeToEur
      ? currencyConvertedChart.map((item) => {
        const rate = EXCHANGE_RATES_TO_EUR[normalizeCurrencyCode(item.currency)];
        if (!rate) return item;
        const normalizedItem = {
          ...item,
          currency: 'EUR',
        };
        if (item.actual_price !== undefined) {
          normalizedItem.actual_price = convertToEur(item.actual_price, item.currency);
        }
        if (item.regular_price !== undefined) {
          normalizedItem.regular_price = convertToEur(item.regular_price, item.currency);
        }
        if (item.price !== undefined) {
          normalizedItem.price = convertToEur(item.price, item.currency);
        }
        if (item.current_price !== undefined) {
          normalizedItem.current_price = convertToEur(item.current_price, item.currency);
        }
        if (item.original_price !== undefined) {
          normalizedItem.original_price = convertToEur(item.original_price, item.currency);
        }
        return normalizedItem;
      })
      : currencyConvertedChart;

    // Apply matching group grouping: show one representative per group if prices match.
    // Capture SKU titles allowed by attribute filters BEFORE grouping expands to full matching groups.
    const allowedSkuTitlesForChart = new Set(
      (normalizedChartFiltered || []).map((item) => item?.title).filter(Boolean)
    );
    const groupedFiltered = groupProductsByMatching(normalizedFiltered, matchingGroups);
    const groupedChartFiltered = groupProductsByMatching(normalizedChartFiltered, matchingGroups);

    setFilteredData(groupedFiltered);
    setChartComputationPending(true);

    if (chartDerivedBuildTimerRef.current != null) {
      clearTimeout(chartDerivedBuildTimerRef.current);
      chartDerivedBuildTimerRef.current = null;
    }
    if (chartPendingRafRef.current != null) {
      cancelAnimationFrame(chartPendingRafRef.current);
      chartPendingRafRef.current = null;
    }

    // Derive the product list: use groups from the filter, restricted to those that have data.
    const titlesWithData = [...new Set(groupedFiltered.map(item => item.title).filter(Boolean))];
    const titlesWithDataSet = new Set(titlesWithData);
    // Compute groups in filter (same logic as filteredProductsWithGroups) from data before date filter
    const uniqueTitlesBeforeDate = [...new Set((filteredBeforeDateRange || filtered).map((item) => item.title).filter(Boolean))].sort();
    const productsInGroupsSet = new Set();
    forEachMatchingInfo(matchingGroups, (info, title) => {
      if (info?.groupName) productsInGroupsSet.add(title);
    });
    const groupNamesFromFilter = new Set();
    const uniqueTitlesSet = new Set(uniqueTitlesBeforeDate);
    forEachMatchingInfo(matchingGroups, (info, title) => {
      if (uniqueTitlesSet.has(title) && info?.groupName) groupNamesFromFilter.add(info.groupName);
    });
    const ungroupedFromFilter = uniqueTitlesBeforeDate.filter((t) => !productsInGroupsSet.has(t));
    const groupsInFilter = [...Array.from(groupNamesFromFilter).sort(), ...ungroupedFromFilter];

    // Build productsToChart: for each selection, find chart titles — dedupe so a SKU that
    // belongs to multiple matching groups only produces one chart line.
    const limit = Math.max(1, Math.min(topN, MAX_GROUPS));
    const productsToChart = [];
    const seenChartTitles = new Set();
    const chartTitleCoversSku = new Map(); // chartTitle -> Set(sku)
    const addChartProduct = (title, memberSkus = null) => {
      if (!title || seenChartTitles.has(title) || productsToChart.length >= limit) return false;
      const members = memberSkus || groupNameToProducts.get(title) || [title];
      // Skip if every member SKU is already represented by an earlier chart line
      const uncovered = members.filter((sku) => {
        for (const covered of chartTitleCoversSku.values()) {
          if (covered.has(sku)) return false;
        }
        return true;
      });
      if (members.length && uncovered.length === 0) return false;
      seenChartTitles.add(title);
      chartTitleCoversSku.set(title, new Set(members));
      productsToChart.push(title);
      return true;
    };

    if (selectedProducts.length > 0) {
      for (const sel of selectedProducts) {
        if (productsToChart.length >= limit) break;
        const mgName = parseModelGroupSelection(sel);
        if (mgName != null) {
          // One chart series per model under the model series
          const modelsSeen = new Set();
          forEachMatchingInfo(matchingGroups, (info, title) => {
            if (productsToChart.length >= limit) return;
            if ((info?.modelGroup || '').trim() !== mgName || !info?.groupName) return;
            if (!titlesWithDataSet.has(title) && !titlesWithDataSet.has(info.groupName)) return;
            if (modelsSeen.has(info.groupName)) return;
            modelsSeen.add(info.groupName);
            const members = groupNameToProducts.get(info.groupName) || [title];
            if (titlesWithDataSet.has(info.groupName)) {
              addChartProduct(info.groupName, members);
            } else {
              addChartProduct(title, members);
            }
          });
          continue;
        }
        const modelName = parseModelSelection(sel);
        if (modelName != null) {
          const members = groupNameToProducts.get(modelName) || [modelName];
          if (titlesWithDataSet.has(modelName)) {
            addChartProduct(modelName, members);
          } else {
            const firstWithData = members.find((t) => titlesWithDataSet.has(t));
            if (firstWithData) addChartProduct(firstWithData, members);
          }
          continue;
        }
        if (titlesWithDataSet.has(sel)) {
          // SKU title — never expand via groupNameToProducts, or a namesake SKU
          // (same title as its model) would chart the entire model.
          addChartProduct(sel, [sel]);
        } else {
          const groupList = groupNameToProducts.get(sel);
          if (groupList?.length && !groupList.includes(sel)) {
            const firstWithData = groupList.find((t) => titlesWithDataSet.has(t));
            if (firstWithData) addChartProduct(firstWithData, groupList);
          }
        }
      }
    } else {
      for (const g of groupsInFilter) {
        if (productsToChart.length >= limit) break;
        if (titlesWithDataSet.has(g)) {
          addChartProduct(g, groupNameToProducts.get(g) || [g]);
        } else {
          const groupList = groupNameToProducts.get(g);
          if (groupList) {
            const firstWithData = groupList.find((t) => titlesWithDataSet.has(t));
            if (firstWithData) addChartProduct(firstWithData, groupList);
          }
        }
      }
    }
    if (productsToChart.length === 0 && titlesWithData.length > 0) {
      for (const t of titlesWithData) {
        if (productsToChart.length >= limit) break;
        addChartProduct(t, groupNameToProducts.get(t) || [t]);
      }
    }

    // Price / visibility / grid: defer to next task so the browser can paint filter changes before
    // heavy chart generation (large synchronous CPU on the main thread).
    const priceSourceForChart = isCountryView
      ? normalizedFilteredForCountry || normalizedFiltered
      : groupedChartFiltered;
    const pinSourceForChart = isCountryView
      ? (normalizedFilteredForCountry || normalizedFiltered)
      : normalizedChartFiltered;
    setPinPriceSourceRows(pinSourceForChart || []);
    chartDerivedBuildTimerRef.current = setTimeout(() => {
      chartDerivedBuildTimerRef.current = null;
      const priceData = generatePriceHistoryData(
        priceSourceForChart,
        productsToChart,
        data,
        allowedSkuTitlesForChart
      );
      setPriceHistoryData(priceData);

      const visibilityData = generateVisibilityHistoryData(normalizedFiltered, productsToChart);
      setVisibilityHistoryData(visibilityData);
      chartPendingRafRef.current = requestAnimationFrame(() => {
        chartPendingRafRef.current = null;
        setChartComputationPending(false);
      });

      // The legacy raw filtered-data grid is permanently hidden (HIDE_CHART_TABLE), so skip
      // building gridData entirely — mapping the full filtered dataset with object spread on
      // every filter change / tab switch is pure wasted work + an extra re-render.
      if (!HIDE_CHART_TABLE) {
        if (normalizedFiltered.length > 0) {
          const gridRows = normalizedFiltered.map((item, index) => ({
            id: index,
            ...item
          }));

          const gridColumns = columns.map((col) => ({
            field: col,
            headerName: col.charAt(0).toUpperCase() + col.slice(1).replace(/_/g, ' '),
            width: col.toLowerCase().includes('url') ? 300 : col.toLowerCase() === 'title' ? 400 : 150,
            flex: 1,
            minWidth: col.toLowerCase() === 'title' ? 300 : 100,
          }));

          setGridData({
            rows: gridRows,
            columns: gridColumns
          });
        } else {
          setGridData(null);
        }
      }
    }, 0);

    return () => {
      if (chartDerivedBuildTimerRef.current != null) {
        clearTimeout(chartDerivedBuildTimerRef.current);
        chartDerivedBuildTimerRef.current = null;
      }
      if (chartPendingRafRef.current != null) {
        cancelAnimationFrame(chartPendingRafRef.current);
        chartPendingRafRef.current = null;
      }
    };
  }, [
    data,
    selectedBrands,
    selectedProducts,
    selectedCurrency,
    selectedCountries,
    selectedRetailers,
    selectedFlavours,
    selectedCategoryGroups,
    selectedPackSizes,
    customizableFilter,
    forNewUsersOnlyFilter,
    starterKitProductFilter,
    topN,
    dateRange,
    columns,
    normalizeToEur,
    localCurrencyCode,
    baseProduct,
    topFilters.currencyIndex,
    topFilters.availability,
    selectedManufacturers,
    isInitialBrandSelectionResolved,
    priceDisplayMode,
    getItemPackSize,
    groupBy,
    productFlagsFromStorage,
    categoryOverrides,
    groupNameToProducts,
    modelGroupToProducts,
    additionalFilters,
    matchingGroups,
    isStickRoute,
    isDevicesRoute,
  ]);

  useEffect(() => {
    if (!isMarketScope) return;
    const nextCountry = resolveMarketCountry(selectedMarket, countryOptions);
    if (nextCountry) {
      if (selectedCountries.length !== 1 || selectedCountries[0] !== nextCountry) {
        setSelectedCountries([nextCountry]);
      }
    }
  }, [isMarketScope, selectedMarket, countryOptions, selectedCountries]);

  useEffect(() => {
    if (selectedCurrency) {
      lastCurrencyPersistRef.current = normalizeCurrencyCode(selectedCurrency);
    }
  }, [selectedCurrency]);

  useEffect(() => {
    const previousScope = previousScopeRef.current;
    const previousMarket = previousMarketRef.current;
    const scopeChanged = previousScope !== scopeMode;
    const marketChanged = previousMarket !== selectedMarket;

    if (scopeChanged) {
      if (scopeMode === 'hq' || scopeMode === 'market') setGroupBy('all');
      if (scopeMode === 'hq') {
        setTopFilters((prev) => (
          prev.currencyIndex === 'Index' ? prev : { ...prev, currencyIndex: 'Index' }
        ));
        setPriceDisplayMode(isStickRoute ? 'per_unit' : 'absolute');
        setActivePriceTypes(defaultActivePriceTypes());
      } else if (scopeMode === 'market') {
        setTopFilters((prev) => (
          prev.currencyIndex === 'Absolute' ? prev : { ...prev, currencyIndex: 'Absolute' }
        ));
        setPriceDisplayMode(isStickRoute ? 'per_unit' : 'absolute');
        setBaseProduct(null);
        setActivePriceTypes(defaultActivePriceTypes(true));
      }
    }

    if (!currencyOptions.length) {
      previousScopeRef.current = scopeMode;
      previousMarketRef.current = selectedMarket;
      return;
    }

    const pickMarketDefault = () => {
      const d = localCurrencyCode || resolveMarketDefaultCurrency(selectedMarket, countryOptions, data);
      return d ? normalizeCurrencyCode(d) : null;
    };

    setSelectedCurrency((prev) => {
      const prevN = prev ? normalizeCurrencyCode(prev) : null;
      const prevValid = prevN && currencyOptions.includes(prevN);

      if (scopeChanged || marketChanged) {
        if (isMarketScope) {
          const d = pickMarketDefault();
          if (d && currencyOptions.includes(d)) return d;
        }
        if (currencyOptions.includes('EUR')) return 'EUR';
        return currencyOptions[0];
      }

      if (prevValid) return prevN;

      const last = lastCurrencyPersistRef.current;
      if (last && currencyOptions.includes(last)) return last;

      if (isMarketScope) {
        const d = pickMarketDefault();
        if (d && currencyOptions.includes(d)) return d;
      }
      if (currencyOptions.includes('EUR')) return 'EUR';
      return currencyOptions[0];
    });

    previousScopeRef.current = scopeMode;
    previousMarketRef.current = selectedMarket;
  }, [isMarketScope, isStickRoute, scopeMode, selectedMarket, currencyOptions, localCurrencyCode, countryOptions, data]);


  const handleFlavourButtonClick = (index) => {
    const updatedButtonsStates = [...flavourButtonsStates];
    updatedButtonsStates[index] = !updatedButtonsStates[index];
    setFlavourButtonsStates(updatedButtonsStates);

    const newSelectedFlavours = columnsUniqueValues?.flavour?.filter((_, i) => updatedButtonsStates[i]) || [];
    setSelectedFlavours(newSelectedFlavours);
  };

  const filteredProducts = useMemo(() => {
    if (!data.length) return [];
    let items = data;
    // Filter by manufacturer (JTI→Ploom, BAT→glo, PMI→IQOS)
    if (selectedManufacturers.length > 0) {
      const matchingBrands = new Set();
      selectedManufacturers.forEach(m => {
        getManufacturerBrandKeywords(m).forEach((kw) => matchingBrands.add(kw));
      });
      if (matchingBrands.size > 0) {
        items = items.filter(item => {
          const itemBrand = normalizeValue(item.brand || '');
          return [...matchingBrands].some(mb => itemBrand === mb || itemBrand.includes(mb));
        });
      }
    }
    return items
      .filter(item => !selectedBrands.length || selectedBrands.includes(item.brand))
      .filter(item => !selectedCategoryGroups.length || selectedCategoryGroups.includes(item.category_group))
      .filter(item => !selectedCountries.length || selectedCountries.includes(item.country))
      .filter(item => !selectedRetailers.length || selectedRetailers.includes(item.retailer))
      .filter(item => !selectedPackSizes.length || selectedPackSizes.includes(getItemPackSize(item)))
      .map(item => item.title)
      .filter((title, index, self) => title && self.indexOf(title) === index)
      .sort();
  }, [data, selectedBrands, selectedCategoryGroups, selectedCountries, selectedRetailers, selectedManufacturers, selectedPackSizes, getItemPackSize]);

  /** Base-product selector options should not be constrained by Brand/Product selections. */
  const filteredProductsForBase = useMemo(() => {
    if (!data.length) return [];
    let items = data;
    // Keep manufacturer constraint (maps to brand family) to stay inside the selected market universe.
    if (selectedManufacturers.length > 0) {
      const matchingBrands = new Set();
      selectedManufacturers.forEach((m) => {
        getManufacturerBrandKeywords(m).forEach((kw) => matchingBrands.add(kw));
      });
      if (matchingBrands.size > 0) {
        items = items.filter((item) => {
          const itemBrand = normalizeValue(item.brand || '');
          return [...matchingBrands].some((mb) => itemBrand === mb || itemBrand.includes(mb));
        });
      }
    }
    return items
      .filter((item) => !selectedCategoryGroups.length || selectedCategoryGroups.includes(item.category_group))
      .filter((item) => !selectedCountries.length || selectedCountries.includes(item.country))
      .filter((item) => !selectedRetailers.length || selectedRetailers.includes(item.retailer))
      .filter((item) => !selectedPackSizes.length || selectedPackSizes.includes(getItemPackSize(item)))
      .map((item) => item.title)
      .filter((title, index, self) => title && self.indexOf(title) === index)
      .sort();
  }, [data, selectedCategoryGroups, selectedCountries, selectedRetailers, selectedManufacturers, selectedPackSizes, getItemPackSize]);

  // Product filter options: group names (bold, at top) + individual product titles
  const groupNames = useMemo(() => {
    const filteredSet = new Set(filteredProducts);
    const names = new Set();
    forEachMatchingInfo(matchingGroups, (info, title) => {
      if (filteredSet.has(title) && info?.groupName) names.add(info.groupName);
    });
    return names;
  }, [filteredProducts, matchingGroups]);

  // Product filter: show groups (bold) + ungrouped products only. Products in a group (devices + starter kits) are not shown individually—select the group, then use Price Type to filter by actual/listed/starter kit prices.
  const filteredProductsWithGroups = useMemo(() => {
    const productsInGroups = new Set();
    forEachMatchingInfo(matchingGroups, (info, title) => {
      if (info?.groupName) productsInGroups.add(title);
    });
    const ungroupedOnly = filteredProducts.filter((title) => !productsInGroups.has(title));
    return [...Array.from(groupNames).sort(), ...ungroupedOnly];
  }, [filteredProducts, groupNames, matchingGroups]);

  const groupNamesForBase = useMemo(() => {
    const filteredSet = new Set(filteredProductsForBase);
    const names = new Set();
    forEachMatchingInfo(matchingGroups, (info, title) => {
      if (filteredSet.has(title) && info?.groupName) names.add(info.groupName);
    });
    return names;
  }, [filteredProductsForBase, matchingGroups]);

  const filteredProductsWithGroupsForBase = useMemo(() => {
    const productsInGroups = new Set();
    forEachMatchingInfo(matchingGroups, (info, title) => {
      if (info?.groupName) productsInGroups.add(title);
    });
    const ungroupedOnly = filteredProductsForBase.filter((title) => !productsInGroups.has(title));
    return [...Array.from(groupNamesForBase).sort(), ...ungroupedOnly];
  }, [filteredProductsForBase, groupNamesForBase, matchingGroups]);

  const totalProductsCount = useMemo(() => {
    return selectedProducts.length > 0 ? selectedProducts.length : filteredProductsWithGroups.length;
  }, [selectedProducts, filteredProductsWithGroups]);

  const productsForCharts = useMemo(() => {
    const source = selectedProducts.length > 0 ? selectedProducts : filteredProductsWithGroups;
    if (!source.length) return [];
    const limit = Math.max(1, Math.min(topN, MAX_GROUPS, source.length));
    return source.slice(0, limit);
  }, [selectedProducts, filteredProductsWithGroups, topN]);

  // Products to derive variant filter options from. When products selected: use those. Else: use ALL products in route (devices or sticks) so options are never empty.
  const productsForVariantFilter = useMemo(() => {
    if (selectedProducts.length > 0) {
      return [...expandProductSelections(selectedProducts, groupNameToProducts, modelGroupToProducts)];
    }
    const routeCats = isStickRoute ? ['Sticks', 'Sticks Bundles'] : ['Devices', 'Device Bundles'];
    const titles = new Set();
    data.forEach((item) => {
      const eg = getEffectiveCategoryGroup(item);
      if (routeCats.includes(eg)) titles.add(item.title);
    });
    return [...titles];
  }, [selectedProducts, data, groupNameToProducts, modelGroupToProducts, isStickRoute, getEffectiveCategoryGroup]);

  // Build options from variant group names only (not individual colours / flavours)
  const deviceColourOptions = useMemo(() => {
    if (isStickRoute) return ['All'];
    const groupNames = new Set();
    productsForVariantFilter.forEach((title) => {
      matchingInfosForSku(matchingGroups, title).forEach((info) => {
        (info?.variantGroups || []).forEach((vg) => {
          const name = (vg.name || '').trim();
          if (name) groupNames.add(name);
        });
      });
    });
    return ['All', ...[...groupNames].sort((a, b) => a.localeCompare(b))];
  }, [productsForVariantFilter, matchingGroups, isStickRoute]);

  const stickFlavourOptions = useMemo(() => {
    if (!isStickRoute) return ['All'];
    const groupNames = new Set();
    productsForVariantFilter.forEach((title) => {
      matchingInfosForSku(matchingGroups, title).forEach((info) => {
        (info?.variantGroups || []).forEach((vg) => {
          const name = (vg.name || '').trim();
          if (name) groupNames.add(name);
        });
      });
    });
    return ['All', ...[...groupNames].sort((a, b) => a.localeCompare(b))];
  }, [productsForVariantFilter, matchingGroups, isStickRoute]);

  // Base product options: products + groups (same as product selector) so user can pick a group as baseline
  const baseProductOptions = useMemo(() => {
    return filteredProductsWithGroupsForBase;
  }, [filteredProductsWithGroupsForBase]);

  const isDevicesRouteForBase = productType && PRODUCT_TYPE_ALIASES.devices.includes(normalizeValue(productType));
  const baseProductAutoQueueRouteKey = isStickRoute ? 'sticks' : 'devices';

  const autoBaseProductQueueChoices = useMemo(
    () =>
      buildAutoBaseProductQueueChoices(
        baseProductOptions,
        filteredProductsForBase,
        false,
        groupNameToProducts
      ),
    [baseProductOptions, filteredProductsForBase, groupNameToProducts]
  );

  const openBaseProductAutoQueueDialog = useCallback(() => {
    const key = baseProductAutoQueueRouteKey;
    const source =
      baseProductAutoQueues[key]?.length
        ? baseProductAutoQueues[key]
        : DEFAULT_BASE_PRODUCT_AUTO_QUEUES[key] || [];
    setBaseProductAutoQueueDraft(
      source.map((entry) =>
        migrateLegacyQueueEntry(entry, autoBaseProductQueueChoices, baseProductOptions, false)
      )
    );
    setBaseProductAutoQueueDialogOpen(true);
  }, [baseProductAutoQueueRouteKey, baseProductAutoQueues, autoBaseProductQueueChoices, baseProductOptions]);

  const effectiveBaseProduct = useMemo(() => {
    if (baseProduct === BASE_PRODUCT_AUTO) {
      const queue = baseProductAutoQueues[baseProductAutoQueueRouteKey] || [];
      return pickBaseProductFromAutoQueue(queue, baseProductOptions, false, groupNameToProducts);
    }
    return baseProduct;
  }, [
    baseProduct,
    baseProductAutoQueues,
    baseProductAutoQueueRouteKey,
    baseProductOptions,
    groupNameToProducts,
  ]);

  const specificBaseSkuOptions = useMemo(() => {
    if (!effectiveBaseProduct) return [];
    const groupSkus = groupNameToProducts.get(effectiveBaseProduct);
    const allowed = new Set(filteredProductsForBase);
    if (groupSkus && groupSkus.length > 0) {
      return Array.from(new Set(groupSkus))
        .filter((sku) => allowed.has(sku))
        .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    }
    return allowed.has(effectiveBaseProduct) ? [effectiveBaseProduct] : [];
  }, [effectiveBaseProduct, groupNameToProducts, filteredProductsForBase]);

  useEffect(() => {
    setBaseProductSkuOverride('');
  }, [effectiveBaseProduct]);

  // Reset base product if it's no longer in options (products or groups)
  useEffect(() => {
    if (
      baseProduct &&
      baseProduct !== BASE_PRODUCT_AUTO &&
      baseProductOptions.length > 0 &&
      !baseProductOptions.includes(baseProduct)
    ) {
      setBaseProduct(null);
      setAiSummary(null);
      setAiAnalysisOpen(false);
    }
  }, [baseProduct, baseProductOptions]);

  useEffect(() => {
    if (!baseProductSkuOverride) return;
    if (!specificBaseSkuOptions.includes(baseProductSkuOverride)) {
      setBaseProductSkuOverride('');
    }
  }, [baseProductSkuOverride, specificBaseSkuOptions]);

  const activeBaseProduct = baseProductSkuOverride || effectiveBaseProduct;

  /** Rows used to decide which base product exists per country (same filters as base product options). */
  const baseRowsForAutoQueue = useMemo(() => {
    if (!data.length) return [];
    let items = data;
    if (selectedManufacturers.length > 0) {
      const matchingBrands = new Set();
      selectedManufacturers.forEach((m) => {
        getManufacturerBrandKeywords(m).forEach((kw) => matchingBrands.add(kw));
      });
      if (matchingBrands.size > 0) {
        items = items.filter((item) => {
          const itemBrand = normalizeValue(item.brand || '');
          return [...matchingBrands].some((mb) => itemBrand === mb || itemBrand.includes(mb));
        });
      }
    }
    return items.filter(
      (item) =>
        (!selectedCategoryGroups.length || selectedCategoryGroups.includes(item.category_group)) &&
        (!selectedCountries.length || selectedCountries.includes(item.country)) &&
        (!selectedRetailers.length || selectedRetailers.includes(item.retailer)) &&
        (!selectedPackSizes.length || selectedPackSizes.includes(getItemPackSize(item)))
    );
  }, [
    data,
    selectedCategoryGroups,
    selectedCountries,
    selectedRetailers,
    selectedManufacturers,
    selectedPackSizes,
    getItemPackSize,
  ]);

  const autoBaseProductByCountry = useMemo(() => {
    if (baseProduct !== BASE_PRODUCT_AUTO) return null;
    const queue = baseProductAutoQueues[baseProductAutoQueueRouteKey] || [];
    if (!queue.length || !baseRowsForAutoQueue.length) return new Map();
    return buildAutoBaseProductByCountryMap({
      queueEntries: queue,
      rows: baseRowsForAutoQueue,
      matchingGroups,
      groupNameToProducts,
      isStickRoute: false,
    });
  }, [
    baseProduct,
    baseProductAutoQueues,
    baseProductAutoQueueRouteKey,
    baseRowsForAutoQueue,
    matchingGroups,
    groupNameToProducts,
  ]);

  const resolveIndexBaseProductForCountry = useCallback(
    (countryCode) => {
      if (baseProductSkuOverride) return baseProductSkuOverride;
      if (
        baseProduct === BASE_PRODUCT_AUTO &&
        countryCode &&
        autoBaseProductByCountry?.has(countryCode)
      ) {
        return autoBaseProductByCountry.get(countryCode);
      }
      return activeBaseProduct;
    },
    [baseProduct, baseProductSkuOverride, autoBaseProductByCountry, activeBaseProduct]
  );

  const indexCompareBaseProductForDataset = useCallback(
    (dataset) => {
      const country = dataset?.baseProductCountry;
      if (country) return resolveIndexBaseProductForCountry(country);
      return activeBaseProduct;
    },
    [resolveIndexBaseProductForCountry, activeBaseProduct]
  );

  // Countries where base product has no data (for Index mode alert).
  // Intentionally ignores Brand/Product filters so warning is about true availability, not user-filtered exclusion.
  const baseAvailabilityRows = useMemo(() => {
    if (!data?.length) return [];
    let rows = [...data];

    if (selectedManufacturers.length > 0) {
      const matchingBrands = new Set();
      selectedManufacturers.forEach((m) => {
        getManufacturerBrandKeywords(m).forEach((kw) => matchingBrands.add(kw));
      });
      if (matchingBrands.size > 0) {
        rows = rows.filter((item) => {
          const itemBrand = normalizeValue(item.brand || '');
          return [...matchingBrands].some((mb) => itemBrand === mb || itemBrand.includes(mb));
        });
      }
    }

    if (selectedCountries.length > 0) {
      rows = rows.filter((item) => selectedCountries.includes(item.country));
    }
    if (selectedRetailers.length > 0) {
      rows = rows.filter((item) => selectedRetailers.includes(item.retailer));
    }
    if (selectedFlavours.length > 0) {
      rows = rows.filter((item) => selectedFlavours.includes(item.flavour));
    }
    if (selectedCategoryGroups.length > 0) {
      rows = rows.filter((item) => selectedCategoryGroups.includes(item.category_group));
    }
    if (selectedPackSizes.length > 0) {
      rows = rows.filter((item) => {
        const itemSize = getItemPackSize(item);
        return itemSize !== null && selectedPackSizes.includes(itemSize);
      });
    }

    rows = rows.filter((item) => rowMatchesAvailability(item, topFilters.availability));

    if (dateRange[0] && dateRange[1]) {
      // Numeric comparison against pre-parsed `__ts` (see data load) — avoids per-row dayjs parsing.
      const lowerBound = dateRange[0].subtract(1, 'day').valueOf();
      const upperBound = dateRange[1].add(1, 'day').valueOf();
      rows = rows.filter((item) => {
        let ts = item.__ts;
        if (ts === undefined) {
          const parsed = dayjs(item.updated_at || item.date);
          ts = parsed.isValid() ? parsed.valueOf() : null;
        }
        return ts != null && ts > lowerBound && ts < upperBound;
      });
    }

    if (!isStickRoute && additionalFilters?.deviceColour && additionalFilters.deviceColour !== 'All') {
      rows = rows.filter((item) => itemMatchesSelectedVariant(item, additionalFilters.deviceColour, matchingGroups));
    }

    if (isStickRoute && additionalFilters?.stickFlavour && additionalFilters.stickFlavour !== 'All') {
      rows = rows.filter((item) => itemMatchesSelectedVariant(item, additionalFilters.stickFlavour, matchingGroups));
    }

    if (isStickRoute && additionalFilters?.stickCategory && additionalFilters.stickCategory !== 'All') {
      const selectedCategory = additionalFilters.stickCategory;
      rows = rows.filter((item) => matchesStickCategoryFilter(item, matchingGroups, selectedCategory));
    }

    return rows;
  }, [
    data,
    selectedManufacturers,
    selectedCountries,
    selectedRetailers,
    selectedFlavours,
    selectedCategoryGroups,
    selectedPackSizes,
    getItemPackSize,
    topFilters.availability,
    dateRange,
    isStickRoute,
    additionalFilters,
    matchingGroups,
  ]);

  const baseProductMissingInCountries = useMemo(() => {
    if (!activeBaseProduct || !baseAvailabilityRows?.length) return [];
    const selected = selectedCountries?.length > 0 ? selectedCountries : [];
    if (!selected.length) return [];

    const countryHasBase = (c) => {
      const baseKey =
        baseProduct === BASE_PRODUCT_AUTO && autoBaseProductByCountry?.has(c)
          ? autoBaseProductByCountry.get(c)
          : activeBaseProduct;
      if (!baseKey) return false;
      const baseTitles = new Set(groupNameToProducts.get(baseKey) || [baseKey]);
      baseTitles.add(baseKey);
      return baseAvailabilityRows.some(
        (item) => item?.country === c && baseTitles.has(item?.title)
      );
    };

    return selected.filter((c) => !countryHasBase(c));
  }, [
    activeBaseProduct,
    baseAvailabilityRows,
    selectedCountries,
    groupNameToProducts,
    baseProduct,
    autoBaseProductByCountry,
  ]);

  useEffect(() => {
    if (topFilters.currencyIndex !== 'Index') return;
    if (initialBaseProductDefaultAppliedRef.current) return;
    if (baseProduct != null) {
      initialBaseProductDefaultAppliedRef.current = true;
      return;
    }
    if (!baseProductOptions.length) return;
    if (isStickRoute || isDevicesRouteForBase) {
      setBaseProduct(BASE_PRODUCT_AUTO);
    }
    initialBaseProductDefaultAppliedRef.current = true;
  }, [topFilters.currencyIndex, baseProduct, baseProductOptions, isStickRoute, isDevicesRouteForBase]);

  const formatLabel = useCallback((value) => {
    if (!value || typeof value !== 'string') return value;
    if (countryOptions && countryOptions.includes(value)) {
      const name = formatCountryName(value);
      return groupBy === 'products' ? `${name} (${(value || '').toUpperCase()})` : name;
    }
    return `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
  }, [countryOptions, groupBy]);

  const findClosestDateIndex = (targetDate) => {
    if (!targetDate || !availableDates.length) return -1;
    let closestIdx = 0;
    let smallestDiff = Infinity;
    availableDates.forEach((date, idx) => {
      const diff = Math.abs(date.diff(targetDate, 'millisecond'));
      if (diff < smallestDiff) {
        smallestDiff = diff;
        closestIdx = idx;
      }
    });
    return closestIdx;
  };

  useEffect(() => {
    if (!availableDates.length) return;
    const firstDate = availableDates[0];
    const lastDate = availableDates[availableDates.length - 1];
    const startDate = dateRange[0] && dateRange[0].isValid() ? dateRange[0] : firstDate;
    const endDate = dateRange[1] && dateRange[1].isValid() ? dateRange[1] : lastDate;
    const clampedStart = startDate.isBefore(firstDate, 'day') ? firstDate : startDate;
    const clampedEnd = endDate.isAfter(lastDate, 'day') ? lastDate : endDate;
    let startIdx = availableDates.findIndex((d) => d.isSame(clampedStart, 'day'));
    let endIdx = availableDates.findIndex((d) => d.isSame(clampedEnd, 'day'));
    if (startIdx === -1) startIdx = findClosestDateIndex(clampedStart);
    if (endIdx === -1) endIdx = findClosestDateIndex(clampedEnd);
    const safeStart = startIdx === -1 ? 0 : startIdx;
    const safeEnd = endIdx === -1 ? availableDates.length - 1 : endIdx;
    const nextStart = availableDates[safeStart] || firstDate;
    const nextEnd = availableDates[safeEnd] || lastDate;
    if (
      !dateRange[0] ||
      !dateRange[1] ||
      !dateRange[0].isValid() ||
      !dateRange[1].isValid() ||
      !dateRange[0].isSame(nextStart, 'day') ||
      !dateRange[1].isSame(nextEnd, 'day')
    ) {
      setDateRange([nextStart, nextEnd]);
      return;
    }
    setDateSliderValue([Math.min(safeStart, safeEnd), Math.max(safeStart, safeEnd)]);
  }, [availableDates, dateRange]);

  const handleDateSliderChange = (event, newValue) => {
    const nextValue = Array.isArray(newValue) ? newValue : [newValue, newValue];
    const [rawStart, rawEnd] = nextValue;
    const startIdx = Math.min(rawStart, rawEnd);
    const endIdx = Math.max(rawStart, rawEnd);
    setDateSliderValue([startIdx, endIdx]);
    if (!availableDates.length) return;
    const nextStart = availableDates[startIdx];
    const nextEnd = availableDates[endIdx];
    if (!nextStart || !nextEnd) return;
    setPreviewDateRange([nextStart, nextEnd]);
  };

  const handleDateSliderCommit = (event, newValue) => {
    setPreviewDateRange(null);
    if (!availableDates.length) return;
    const [rawStart, rawEnd] = Array.isArray(newValue)
      ? newValue
      : [newValue, newValue];
    const startIdx = Math.min(rawStart, rawEnd);
    const endIdx = Math.max(rawStart, rawEnd);
    setDateRange([availableDates[startIdx], availableDates[endIdx]]);
  };

  const applyBaseProductSuggestedDateRange = useCallback((startKey, endKey) => {
    const start = dayjs(startKey);
    const end = dayjs(endKey);
    if (!start.isValid() || !end.isValid()) return;
    const lo = start.isBefore(end, 'day') ? start : end;
    const hi = start.isBefore(end, 'day') ? end : start;
    setDateRange([lo, hi]);
  }, []);

  const excludeCountryForBaseProductIndex = useCallback((code) => {
    if (code == null || code === '') return;
    const norm = String(code).toLowerCase();
    setSelectedCountries((prev) => prev.filter((c) => String(c).toLowerCase() !== norm));
  }, []);

  const handleChartTabChange = (event, newValue) => {
    setActiveChartTab(newValue);
  };

  const handleTopFilterChange = (key, value) => {
    setTopFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
    // Reset base product when switching away from Index mode
    if (key === 'currencyIndex' && value !== 'Index') {
      setBaseProduct(null);
      setAiSummary(null);
      setAiAnalysisOpen(false);
    }
  };

  const handleAiAnalysis = async () => {
    if (!priceHistoryData?.datasets?.length) {
      return;
    }
    const isIndexPriceView = topFilters.currencyIndex === 'Index';
    if (isIndexPriceView && !activeBaseProduct) {
      return;
    }

    const analysisPayload = buildPriceChartAiPayload(priceHistoryData, {
      baseProduct: activeBaseProduct,
      priceMode: topFilters.currencyIndex,
      currency: priceDisplayCurrency,
      scopeMode: isMarketScope ? 'market' : 'hq',
    });
    if (!analysisPayload?.series?.length) {
      setAiSummary(['No chart series available to analyze for the current filters.']);
      setAiAnalysisOpen(true);
      return;
    }

    setAiAnalysisOpen(true);
    setAiSummary(null);
    setIsAiAnalysisLoading(true);
    try {
      const response = await axios.post(`${API_URL}/jti/analyze-price-index`, analysisPayload);
      const summary = response.data?.summary;
      if (response.data?.success && summary) {
        setAiSummary(Array.isArray(summary) ? summary : [String(summary)]);
      } else {
        setAiSummary(['Unable to generate AI summary at this time.']);
      }
    } catch (error) {
      console.error('Error generating AI summary:', error);
      setAiSummary(['Unable to generate AI summary at this time.']);
    } finally {
      setIsAiAnalysisLoading(false);
    }
  };

  const buildChartOptions = (yAxisTitle, yAxisUnit, tooltipFormatter, hasData = true, showLabels = true) => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    scales: {
      x: {
        ticks: {
          autoSkip: true,
          maxTicksLimit: xMaxTicks !== '' ? Number(xMaxTicks) : 10,
          ...(xDateFormat !== 'auto' ? {
            callback: function(value) {
              const label = this.getLabelForValue(value);
              const parsed = dayjs(label);
              return parsed.isValid() ? parsed.format(xDateFormat) : label;
            }
          } : {}),
        },
      },
      y: {
        title: {
          display: true,
          text: yAxisUnit ? `${yAxisTitle} (${yAxisUnit})` : yAxisTitle,
          font: {
            size: 16,
            weight: 'bold',
          },
        },
      },
    },
    elements: {
      point: {
        radius: 0,
        hoverRadius: 0,
      },
    },
    layout: {
      padding: {
        right: showLabels ? 200 : 10,
        top: 10,
        left: 10,
        bottom: 10,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      datalabels: {
        display: showLabels ? (ctx) => {
          const ds = ctx.dataset;
          if (ds.hidden) return false;
          if (ds.countryChartLineFormat) return false;
          const chart = ctx.chart;
          const labels = chart.data.labels;
          if (!labels || labels.length === 0) return false;
          const lastChartIdx = labels.length - 1;
          if (ctx.dataIndex !== lastChartIdx) return false;
          const data = ctx.dataset.data;
          const v = data[lastChartIdx];
          if (v == null || v === undefined) return false;
          // Price lines: no end label when value is masked out (0) at the last date
          if (ds.priceType != null && typeof v === 'number' && v <= 0) return false;
          // Deduplicate: hide this end label if an earlier line already renders the same text.
          return computeDedupedEndLabelDatasetIndices(chart.data.datasets || [], lastChartIdx).has(ctx.datasetIndex);
        } : false,
        align: 'right',
        anchor: 'end',
        color: (ctx) => ctx.dataset.borderColor || '#666',
        formatter: (v, ctx) => {
          const ds = ctx.dataset;
          const chart = ctx.chart;
          const labels = chart.data.labels || [];
          const lastIdx = labels.length - 1;
          let raw = '';
          if (
            ctx.dataIndex === lastIdx &&
            ds.stockSegment &&
            Array.isArray(ds.groupedVariantTitlesAtIndex) &&
            lastIdx >= 0
          ) {
            const at = ds.groupedVariantTitlesAtIndex[lastIdx];
            if (Array.isArray(at) && at.length) {
              raw = buildPinRowLineTitle(ds, at);
            }
          }
          if (!raw) {
            raw = ds.legendLabel || ds.displayLabel || ds.label || '';
            raw = appendBundleLabelSuffix(raw, ds);
          }
          return stripFlagEmoji(raw);
        },
        offset: 4,
        font: { weight: 600, size: 10 },
      },
      tooltip: {
        enabled: true,
        mode: 'index',
        intersect: false,
        itemSort: function (a, b) {
          const aValue = a.parsed.y !== null ? a.parsed.y : -Infinity;
          const bValue = b.parsed.y !== null ? b.parsed.y : -Infinity;
          return bValue - aValue;
        },
        callbacks: {
          label: tooltipFormatter,
        },
      },
      crosshair: hasData ? {
        line: {
          color: styleTokens.accent,
          width: 1
        },
        snap: {
          enabled: true,
        },
        sync: {
          enabled: false,
          suppressTooltips: false,
        },
        zoom: {
          enabled: false
        }
      } : false
    },
  });

  const priceDisplayCurrency = normalizeToEur ? 'EUR' : (selectedCurrency || '');
  const isIndexMode = topFilters.currencyIndex === 'Index' && activeBaseProduct;

  const priceTooltipFormatter = useCallback((context) => {
    const dataset = context.dataset || {};
    const value = context.parsed.y;
    const index = context.dataIndex;
    const chartDatasets = context?.chart?.data?.datasets || [];
    const priceKeyForTooltipMerge = (v) => {
      if (v == null || !Number.isFinite(v) || v <= 0) return '';
      if (isIndexMode || dataset.isIndexMode) {
        return `idx:${Number(v).toFixed(indexDecimalPlaces)}`;
      }
      const maxD = Math.max(0, priceDecimalPlaces);
      const rounded = Math.round(v * Math.pow(10, maxD)) / Math.pow(10, maxD);
      return `px:${parseFloat(rounded.toFixed(maxD)).toString()}`;
    };
    const getCountryTooltipAggregateVariantCount = () => {
      if (!dataset.countryChartLineFormat) return null;
      const mergeKey = countryChartTooltipMergeKey(dataset);
      const valueKey = priceKeyForTooltipMerge(value);
      if (!mergeKey || !valueKey) return null;
      let total = 0;
      chartDatasets.forEach((ds) => {
        if (!ds?.countryChartLineFormat || ds.hidden) return;
        if (countryChartTooltipMergeKey(ds) !== mergeKey) return;
        if ((ds.priceType || '') !== (dataset.priceType || '')) return;
        const vAt = ds.data?.[index];
        const vKey = priceKeyForTooltipMerge(vAt);
        if (!vKey || vKey !== valueKey) return;
        let vc =
          Array.isArray(ds.variantCountAtIndex) && ds.variantCountAtIndex[index] != null
            ? ds.variantCountAtIndex[index]
            : ds.groupedVariantCount;
        if (ds.stockSegment && Array.isArray(ds.groupedVariantTitlesAtIndex)) {
          const seg = ds.groupedVariantTitlesAtIndex[index];
          if (Array.isArray(seg)) vc = seg.length;
        }
        if (Number.isFinite(vc) && vc > 0) total += vc;
      });
      return total > 0 ? total : null;
    };

    // Country × product lines: same naming as end labels, then price / index (no price bucket in the name).
    if (dataset.countryChartLineFormat) {
      let vcAt =
        Array.isArray(dataset.variantCountAtIndex) && dataset.variantCountAtIndex[index] != null
          ? dataset.variantCountAtIndex[index]
          : dataset.groupedVariantCount;
      if (dataset.stockSegment && Array.isArray(dataset.groupedVariantTitlesAtIndex)) {
        const seg = dataset.groupedVariantTitlesAtIndex[index];
        if (Array.isArray(seg)) vcAt = seg.length;
      }
      const vcAgg = getCountryTooltipAggregateVariantCount();
      const vc = vcAgg != null ? vcAgg : (vcAt != null && vcAt >= 0 ? vcAt : 1);
      const gidStr = dataset.legendGroupId != null ? String(dataset.legendGroupId) : '';
      let head = '';
      if (gidStr.startsWith('cg:')) {
        const rest = gidStr.slice(3);
        const pipeIdx = rest.indexOf('|');
        const country = pipeIdx >= 0 ? rest.slice(0, pipeIdx) : '';
        const groupName = pipeIdx >= 0 ? rest.slice(pipeIdx + 1) : '';
        const rawCc = (country || '').toLowerCase();
        const cc = rawCc === 'gb' || rawCc === 'uk' ? 'UK' : (country || '').toUpperCase();
        head = `${groupName || dataset.productGroupName || ''} (${cc})`;
        if (dataset.stickPackUnitsBracket) head += dataset.stickPackUnitsBracket;
        head = appendBundleLabelSuffix(head, dataset);
      } else {
        const tt = dataset.tooltipTitle && String(dataset.tooltipTitle).trim();
        if (tt) {
          head = tt;
        } else if (dataset.legendLabel) {
          head = String(dataset.legendLabel).replace(/\s*-\s*\d+\s+variants?$/i, '').trim();
        } else if (dataset.productName) {
          head = String(dataset.productName)
            .replace(/\s*·\s*[\d.,]+\s*/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
        }
        if (!head) {
          head = (dataset.label || '').replace(
            /\s*-\s*(Actual Price|Regular Price|Promo Price|Promo Index|Regular Index|Index|Starter Kit\s*\/?\s*Reg\.?\s*Price|Starter Kit Index).*$/i,
            ''
          ).trim();
        }
      }
      const tooltipSkuTitles = collectSkuTitlesFromPriceDataset(dataset, index);
      const namePart = appendLimitedEditionLabelSuffix(
        vc <= 1 ? head : `${head} - ${vc} variants`,
        tooltipSkuTitles,
        productFlagsFromStorage
      );

      if (isIndexMode || dataset.isIndexMode) {
        const fmt = (v) => Number(v).toFixed(indexDecimalPlaces);
        let idxSuffix = '';
        if (
          (dataset.priceType === 'actual' || dataset.priceType === 'promo') &&
          Array.isArray(dataset.regularPrices)
        ) {
          const regularValue = dataset.regularPrices[index];
          if (regularValue > 0 && regularValue !== value && !dataset.isBundleLine) {
            idxSuffix = ` (Regular Price: ${fmt(regularValue)}%)`;
          }
        }
        let line = `${namePart}: ${fmt(value)}%${idxSuffix}`;
        line += formatIndexCompareHoverSuffix(
          indexCompareBaseProductForDataset(dataset),
          dataset.indexCompareRequestedBaseType ||
            indexCompareRequestForPriceType(dataset.priceType),
          dataset.indexCompareBaseTypeAtIndex?.[index],
          dataset.legendCountryIso
        );
        return line;
      }

      const currency = priceDisplayCurrency;
      const priceFmt = (v) => {
        if (v == null || isNaN(v)) return '';
        const maxD = Math.max(0, priceDecimalPlaces);
        const rounded = Math.round(v * Math.pow(10, maxD)) / Math.pow(10, maxD);
        return parseFloat(rounded.toFixed(maxD)).toString();
      };
      let regSuffix = '';
      if (
        (dataset.priceType === 'actual' || dataset.priceType === 'promo') &&
        Array.isArray(dataset.regularPrices)
      ) {
        const regularValue = dataset.regularPrices[index];
        if (regularValue > 0 && regularValue !== value && !dataset.isBundleLine) {
          regSuffix = ` (Regular Price: ${priceFmt(regularValue)} ${currency})`;
        }
      }
      return `${namePart}: ${priceFmt(value)} ${currency}${regSuffix}`;
    }

    const label = appendLimitedEditionLabelSuffix(
      appendBundleLabelSuffix(
        dataset.tooltipTitle ||
          dataset.displayLabel ||
          dataset.productName ||
          (dataset.label || '').replace(
            /\s*-\s*(Actual Price|Regular Price|Promo Price|Promo Index|Regular Index|Index|Starter Kit\s*\/?\s*Reg\.?\s*Price|Starter Kit Index).*$/i,
            ''
          ).trim(),
        dataset
      ),
      collectSkuTitlesFromPriceDataset(dataset, index),
      productFlagsFromStorage
    );
    let suffix = '';

    if (isIndexMode || dataset.isIndexMode) {
      const fmt = (v) => Number(v).toFixed(indexDecimalPlaces);
      if (
        (dataset.priceType === 'actual' || dataset.priceType === 'promo') &&
        Array.isArray(dataset.regularPrices)
      ) {
        const regularValue = dataset.regularPrices[index];
        if (regularValue > 0 && regularValue !== value && !dataset.isBundleLine) {
          suffix = ` (Regular Price: ${fmt(regularValue)}%)`;
        }
      }
      let line = `${label}: ${fmt(value)}%${suffix}`;
      line += formatIndexCompareHoverSuffix(
        indexCompareBaseProductForDataset(dataset),
        dataset.indexCompareRequestedBaseType ||
          indexCompareRequestForPriceType(dataset.priceType),
        dataset.indexCompareBaseTypeAtIndex?.[index],
        dataset.legendCountryIso
      );
      let vc =
        Array.isArray(dataset.variantCountAtIndex) && dataset.variantCountAtIndex[index] != null
          ? dataset.variantCountAtIndex[index]
          : dataset.groupedVariantCount;
      if (dataset.stockSegment && Array.isArray(dataset.groupedVariantTitlesAtIndex)) {
        const seg = dataset.groupedVariantTitlesAtIndex[index];
        if (Array.isArray(seg)) vc = seg.length;
      }
      if (dataset.countryChartLineFormat && vc >= 1) {
        line += ` - ${vc} variant${vc === 1 ? '' : 's'}`;
      } else if (vc > 1) {
        line += ` — ${vc} variants available`;
      }
      return line;
    }

    const currency = priceDisplayCurrency;
    // Show up to priceDecimalPlaces decimals, strip trailing zeros (20.00 -> 20, 20.50 -> 20.5)
    const priceFmt = (v) => {
      if (v == null || isNaN(v)) return '';
      const maxD = Math.max(0, priceDecimalPlaces);
      const rounded = Math.round(v * Math.pow(10, maxD)) / Math.pow(10, maxD);
      return parseFloat(rounded.toFixed(maxD)).toString();
    };
    if (
      (dataset.priceType === 'actual' || dataset.priceType === 'promo') &&
      Array.isArray(dataset.regularPrices)
    ) {
      const regularValue = dataset.regularPrices[index];
      if (regularValue > 0 && regularValue !== value && !dataset.isBundleLine) {
        suffix = ` (Regular Price: ${priceFmt(regularValue)} ${currency})`;
      }
    }
    let line = `${label}: ${priceFmt(value)} ${currency}${suffix}`;
    let vc =
      Array.isArray(dataset.variantCountAtIndex) && dataset.variantCountAtIndex[index] != null
        ? dataset.variantCountAtIndex[index]
        : dataset.groupedVariantCount;
    if (dataset.stockSegment && Array.isArray(dataset.groupedVariantTitlesAtIndex)) {
      const seg = dataset.groupedVariantTitlesAtIndex[index];
      if (Array.isArray(seg)) vc = seg.length;
    }
    if (dataset.countryChartLineFormat && vc >= 1) {
      line += ` - ${vc} variant${vc === 1 ? '' : 's'}`;
    } else if (vc > 1) {
      line += ` — ${vc} variants available`;
    }
    return line;
  }, [isIndexMode, priceDisplayCurrency, indexDecimalPlaces, priceDecimalPlaces, indexCompareBaseProductForDataset, productFlagsFromStorage]);

  const priceChartOptions = useMemo(() => {
    const hasData = priceHistoryData && priceHistoryData.datasets && priceHistoryData.datasets.length > 0;
    const priceYAxisTitle = isIndexMode
      ? 'Price Index'
      : (isStickRoute && priceDisplayMode === 'per_unit' ? 'Unit price' : 'Price');
    const base = buildChartOptions(
      priceYAxisTitle,
      isIndexMode ? '%' : priceDisplayCurrency,
      priceTooltipFormatter,
      hasData,
      showChartLabels
    );
    const endLabelStagger = computeEndLabelStaggerOffsets(
      priceHistoryData?.datasets,
      priceHistoryData?.labels,
      { isIndexMode }
    );
    const basePadRight = showChartLabels ? 200 : 10;
    const padRight = showChartLabels
      ? Math.max(basePadRight, basePadRight + endLabelStagger.maxExtraRight)
      : 10;
    const minVal = yMin !== '' ? Number(yMin) : 0;
    const maxVal = yMax !== '' ? Number(yMax) : undefined;
    const stepVal = yStepSize !== '' ? Number(yStepSize) : undefined;
    const promoListedTooltipFilter =
      activePriceTypes?.has('promo') && activePriceTypes?.has('regular')
        ? (tooltipItem) => {
            const chart = tooltipItem.chart;
            const ds = chart?.data?.datasets?.[tooltipItem.datasetIndex];
            if (!ds?.countryChartLineFormat || ds.priceType !== 'regular') return true;
            const idx = tooltipItem.dataIndex;
            const mergeKey = countryChartTooltipMergeKey(ds);
            for (let j = 0; j < chart.data.datasets.length; j += 1) {
              const d = chart.data.datasets[j];
              if (d === ds || !d?.countryChartLineFormat || d.priceType !== 'promo') continue;
              if (countryChartTooltipMergeKey(d) !== mergeKey) continue;
              const pv = d.data?.[idx];
              if (pv != null && pv !== undefined && !(typeof pv === 'number' && Number.isNaN(pv))) {
                return false;
              }
            }
            return true;
          }
        : undefined;
    const groupedSamePriceTooltipFilter = (tooltipItem) => {
      const chart = tooltipItem.chart;
      const ds = chart?.data?.datasets?.[tooltipItem.datasetIndex];
      if (!ds?.countryChartLineFormat) return true;
      const idx = tooltipItem.dataIndex;
      const val = ds.data?.[idx];
      if (val == null || !Number.isFinite(val) || val <= 0) return false;
      const valueKey = isIndexMode
        ? `idx:${Number(val).toFixed(indexDecimalPlaces)}`
        : (() => {
            const maxD = Math.max(0, priceDecimalPlaces);
            const rounded = Math.round(val * Math.pow(10, maxD)) / Math.pow(10, maxD);
            return `px:${parseFloat(rounded.toFixed(maxD)).toString()}`;
          })();
      const mergeKey = countryChartTooltipMergeKey(ds);
      const typeKey = ds.priceType || '';
      let minVisibleIndex = null;
      for (let j = 0; j < (chart?.data?.datasets || []).length; j += 1) {
        const dj = chart.data.datasets[j];
        if (!dj?.countryChartLineFormat || dj.hidden) continue;
        if (countryChartTooltipMergeKey(dj) !== mergeKey) continue;
        if ((dj.priceType || '') !== typeKey) continue;
        const vj = dj.data?.[idx];
        if (vj == null || !Number.isFinite(vj) || vj <= 0) continue;
        const vjKey = isIndexMode
          ? `idx:${Number(vj).toFixed(indexDecimalPlaces)}`
          : (() => {
              const maxD = Math.max(0, priceDecimalPlaces);
              const rounded = Math.round(vj * Math.pow(10, maxD)) / Math.pow(10, maxD);
              return `px:${parseFloat(rounded.toFixed(maxD)).toString()}`;
            })();
        if (vjKey !== valueKey) continue;
        if (minVisibleIndex == null || j < minVisibleIndex) minVisibleIndex = j;
      }
      return minVisibleIndex === tooltipItem.datasetIndex;
    };
    const combinedTooltipFilter = (tooltipItem) => {
      if (promoListedTooltipFilter && !promoListedTooltipFilter(tooltipItem)) return false;
      return groupedSamePriceTooltipFilter(tooltipItem);
    };

    return {
      ...base,
      layout: {
        ...base.layout,
        padding: {
          ...base.layout.padding,
          right: padRight,
        },
      },
      scales: {
        ...base.scales,
        y: {
          ...base.scales.y,
          ...(minVal !== undefined ? { min: minVal } : {}),
          ...(maxVal !== undefined ? { max: maxVal } : {}),
          ...(maxVal === undefined ? { grace: '10%' } : {}),
          ticks: {
            ...(base.scales.y.ticks || {}),
            ...(stepVal !== undefined ? { stepSize: stepVal } : {}),
            ...(isIndexMode ? {
              callback: function(value) {
                return Number(value).toFixed(indexDecimalPlaces) + '%';
              }
            } : {
              callback: function(value) {
                const maxD = Math.max(0, priceDecimalPlaces);
                const rounded = Math.round(value * Math.pow(10, maxD)) / Math.pow(10, maxD);
                return parseFloat(rounded.toFixed(maxD)).toString();
              }
            }),
          },
        },
      },
      plugins: {
        ...base.plugins,
        adminScrapeGapHighlight: {
          enabled: false,
          gapIndices: priceHistoryData?.adminScrapeGapMeta?.gapIndices || [],
        },
        tooltip: {
          ...base.plugins.tooltip,
          enabled: false,
          external: createPriceChartExternalTooltipHandler(priceTooltipFormatter),
          filter: combinedTooltipFilter,
        },
      },
    };
  }, [isIndexMode, isStickRoute, priceDisplayCurrency, priceTooltipFormatter, priceHistoryData, priceDisplayMode, yMin, yMax, yStepSize, indexDecimalPlaces, priceDecimalPlaces, xDateFormat, xMaxTicks, showChartLabels, styleTokens.accent, activePriceTypes]);

  const visibilityTooltipFormatter = useCallback((context) => {
    const label = context.dataset.label || '';
    const value = context.parsed.y;
    return `${label}: Position ${value}`;
  }, []);

  const visibilityChartOptions = useMemo(() => {
    const hasData = visibilityHistoryData && visibilityHistoryData.datasets && visibilityHistoryData.datasets.length > 0;
    const baseOptions = buildChartOptions('Position', null, visibilityTooltipFormatter, hasData, showChartLabels);
    const minVal = yMin !== '' ? Number(yMin) : undefined;
    const maxVal = yMax !== '' ? Number(yMax) : undefined;
    const stepVal = yStepSize !== '' ? Number(yStepSize) : 1;
    return {
      ...baseOptions,
      scales: {
        ...baseOptions.scales,
        y: {
          ...baseOptions.scales.y,
          reverse: true,
          ...(minVal !== undefined ? { min: minVal } : {}),
          ...(maxVal !== undefined ? { max: maxVal } : {}),
          ...(maxVal === undefined ? { grace: '10%' } : {}),
          ticks: {
            stepSize: stepVal,
            precision: 0,
          },
        },
      },
    };
  }, [visibilityTooltipFormatter, visibilityHistoryData, yMin, yMax, yStepSize, xDateFormat, xMaxTicks, showChartLabels, styleTokens.accent]);

  /** All series for current price-type / style settings (before legend visibility). Used for InteractiveLegend so toggling lines does not remove legend rows. */
  const priceChartDataAllSeries = useMemo(() => {
    if (!priceHistoryData) return null;

    // Products that have an explicit "regular" price dataset (actual ≠ listed)
    const productsWithRegular = new Set(
      priceHistoryData.datasets
        .filter((ds) => ds.priceType === 'regular')
        .map((ds) => ds.productName)
    );

    const dashForDataset = (ds) => {
      if (ds.stockSegment === 'oos') {
        const st = stockLineStyles.oos || 'dotted';
        return PRICE_LINE_STYLE_MAP[st] || [];
      }
      // In-stock primary series are stored as priceType 'actual' (see generatePriceHistoryData) but should
      // follow Regular Price line style, not Actual Price — otherwise avail lines stay dashed when Listed is solid.
      let styleKey = ds.priceType;
      if (ds.stockSegment === 'avail' && ds.priceType === 'actual') {
        styleKey = 'regular';
      }
      const pt = priceTypeStyles[styleKey] || 'solid';
      return PRICE_LINE_STYLE_MAP[pt] || [];
    };

    // Out-of-stock segments render as grey dots regardless of the product's own colour.
    const styleForDataset = (ds) => {
      const out = { borderDash: dashForDataset(ds) };
      if (ds.stockSegment === 'oos') {
        out.borderColor = OOS_LINE_COLOR;
        out.pointBackgroundColor = OOS_LINE_COLOR;
        out.pointBorderColor = OOS_LINE_COLOR;
      }
      return out;
    };

    const datasets = [];
    priceHistoryData.datasets.forEach((ds) => {
      if (ds.priceType === 'actual') {
        if (activePriceTypes.has('actual')) {
          datasets.push({ ...ds, ...styleForDataset(ds) });
        }
        // Listed-price fallback: when actual === listed (no separate regular dataset),
        // show this line with the 'regular' style only if 'actual' is NOT also active
        // (to avoid duplicate lines). Keep original label for visibility-filter compatibility;
        // set displayLabel so the legend/tooltip shows just the product name, not "Actual Price".
        if (activePriceTypes.has('regular') && !productsWithRegular.has(ds.productName) && !activePriceTypes.has('actual')) {
          datasets.push({ ...ds, displayLabel: ds.productName, ...styleForDataset(ds) });
        }
      } else if (ds.priceType === 'promo') {
        if (activePriceTypes.has('promo')) {
          datasets.push({ ...ds, ...styleForDataset(ds) });
        }
      } else if (ds.priceType === 'regular') {
        if (activePriceTypes.has('regular')) {
          datasets.push({ ...ds, ...styleForDataset(ds) });
        }
      }
    });

    return { ...priceHistoryData, datasets };
  }, [priceHistoryData, activePriceTypes, priceTypeStyles, stockLineStyles]);

  const priceChartData = useMemo(() => {
    if (!priceChartDataAllSeries) return null;
    const datasets = priceChartDataAllSeries.datasets.map((ds) => ({
      ...ds,
      hidden: !isPriceLegendSeriesVisible(visiblePriceDatasets, ds.label),
    }));
    return { ...priceChartDataAllSeries, datasets };
  }, [priceChartDataAllSeries, visiblePriceDatasets]);

  const showStockLineLegendStyles = useMemo(
    () => !!(priceHistoryData?.datasets?.some((d) => d.stockSegment)),
    [priceHistoryData]
  );

  // Count unique products actually displayed in price chart (from prepared series labels)
  const chartProductCount = useMemo(() => {
    if (!priceChartDataAllSeries?.datasets?.length) return 0;
    const products = new Set(
      priceChartDataAllSeries.datasets
        .filter((ds) => isPriceLegendSeriesVisible(visiblePriceDatasets, ds.label))
        .map((ds) => (ds.label || '').replace(/\s*-\s*(Actual|Listed|Regular|Promo|Starter Kit\s*\/?\s*Reg\.?)\s*(Price|Index)?.*$/i, '').trim())
        .filter(Boolean)
    );
    return products.size;
  }, [priceChartDataAllSeries, visiblePriceDatasets]);

  const visibilityChartProductCount = useMemo(() => {
    if (!visibilityHistoryData?.datasets?.length) return 0;
    const visible = visibilityHistoryData.datasets.filter(
      (ds) => !visibleVisibilityDatasets.size || visibleVisibilityDatasets.has(ds.label)
    );
    return visible.length; // visibility has 1 dataset per product
  }, [visibilityHistoryData, visibleVisibilityDatasets]);

  const visibilityChartData = useMemo(() => {
    if (!visibilityHistoryData) return null;
    if (!visibleVisibilityDatasets.size) return visibilityHistoryData;
    return {
      ...visibilityHistoryData,
      datasets: visibilityHistoryData.datasets.filter((ds) => visibleVisibilityDatasets.has(ds.label))
    };
  }, [visibilityHistoryData, visibleVisibilityDatasets]);

  const priceChartPinPanel = useMemo(() => {
    if (priceChartPinIndex == null || !priceChartData?.labels) return null;
    const labels = priceChartData.labels;
    const idx = priceChartPinIndex;
    if (idx < 0 || idx >= labels.length) return null;
    const raw = labels[idx];
    const d = dayjs(raw);
    const dateLabel = d.isValid() ? d.format('YYYY-MM-DD') : String(raw);
    const rows = buildPinRowsForChart(priceChartData, idx, 'price', productImageUrlByTitle, {
      dedupePromoListedPairs: activePriceTypes?.has('promo') && activePriceTypes?.has('regular'),
    });
    const dateKey =
      (Array.isArray(priceChartData.rawDateKeys) && priceChartData.rawDateKeys[idx]) ||
      (d.isValid() ? d.format('YYYY-MM-DD') : null);
    const typeLabelByKey = new Map(PRICE_TYPE_OPTIONS.map((opt) => [opt.key, opt.label]));
    const selectedPriceTypes = ['promo', 'regular', 'actual'].filter((key) =>
      activePriceTypes?.has(key)
    );
    const formatPinnedPriceValue = (value) => {
      const maxD = Math.max(0, priceDecimalPlaces);
      const rounded = Math.round(value * Math.pow(10, maxD)) / Math.pow(10, maxD);
      return parseFloat(rounded.toFixed(maxD)).toString();
    };
    const parsePinnedNumericValue = (value) => {
      if (value === null || value === undefined) return NaN;
      if (typeof value === 'number') return value;
      const rawVal = String(value).trim();
      if (!rawVal) return NaN;
      let cleaned = rawVal.replace(/[^\d.,-]/g, '');
      if (!cleaned) return NaN;
      const hasComma = cleaned.includes(',');
      const hasDot = cleaned.includes('.');
      if (hasComma && hasDot) cleaned = cleaned.replace(/,/g, '');
      else if (hasComma) cleaned = cleaned.replace(/,/g, '.');
      const parsed = parseFloat(cleaned);
      return Number.isFinite(parsed) ? parsed : NaN;
    };
    const priceByTitle = new Map();
    const pinnedPopupPricingByTitle = new Map();
    const lookupPinPricing = (map, title, country) => {
      if (!title) return null;
      const keyed = map.get(productTitleCountryKey(title, country));
      if (keyed) return keyed;
      // Only fall back to title-only when the row has no country; never reuse another market's price.
      if (!productDetailCountrySlug(country)) {
        return map.get(productTitleCountryKey(title, '')) || null;
      }
      return null;
    };
    if (dateKey && selectedPriceTypes.length > 0 && Array.isArray(pinPriceSourceRows)) {
      const perTitleAgg = new Map();
      const perTitleLocalAgg = new Map();
      const latestRowsBySku = new Map();
      pinPriceSourceRows.forEach((item) => {
        if (!item?.title || !item?.updated_at) return;
        let rowDateKey = null;
        try {
          const dt = new Date(item.updated_at);
          if (Number.isNaN(dt.getTime())) return;
          rowDateKey = dt.toISOString().slice(0, 10);
        } catch (e) {
          return;
        }
        if (rowDateKey > dateKey) return;
        const skuKey = productTitleCountryKey(item.title, item.country);
        const prev = latestRowsBySku.get(skuKey);
        if (!prev || rowDateKey > prev.dateKey) {
          latestRowsBySku.set(skuKey, { dateKey: rowDateKey, rows: [item] });
          return;
        }
        if (rowDateKey === prev.dateKey) {
          prev.rows.push(item);
        }
      });

      latestRowsBySku.forEach(({ rows: latestRows }, skuKey) => {
        latestRows.forEach((item) => {
          let actual = parsePinnedNumericValue(item?.actual_price || item?.price || item?.current_price);
          let regular = parsePinnedNumericValue(item?.regular_price || item?.original_price);
          ({ actual, regular } = normalizeActualAndRegularPrices(actual, regular));
          if (priceDisplayMode === 'per_unit') {
            const packSize = getItemPackSize(item);
            if (packSize && packSize > 1) {
              if (Number.isFinite(actual)) actual = actual / packSize;
              if (Number.isFinite(regular)) regular = regular / packSize;
            }
          }
          const srcCurrency = normalizeCurrencyCode(item?.currency);
          const localAgg =
            perTitleLocalAgg.get(skuKey) ||
            { actual: [], regular: [], currency: srcCurrency };
          if (Number.isFinite(actual) && actual > 0) localAgg.actual.push(actual);
          if (Number.isFinite(regular) && regular > 0) localAgg.regular.push(regular);
          if (srcCurrency && !localAgg.currency) localAgg.currency = srcCurrency;
          perTitleLocalAgg.set(skuKey, localAgg);

          if (normalizeToEur) {
            if (Number.isFinite(actual)) actual = convertToEur(actual, srcCurrency);
            if (Number.isFinite(regular)) regular = convertToEur(regular, srcCurrency);
          } else if (selectedCurrency) {
            const targetCurrency = normalizeCurrencyCode(selectedCurrency);
            if (targetCurrency) {
              if (Number.isFinite(actual)) actual = convertToCurrency(actual, srcCurrency, targetCurrency);
              if (Number.isFinite(regular)) regular = convertToCurrency(regular, srcCurrency, targetCurrency);
            }
          }

          const titleAgg =
            perTitleAgg.get(skuKey) ||
            { actual: [], regular: [], promo: [] };
          if (Number.isFinite(actual) && actual > 0) titleAgg.actual.push(actual);
          if (Number.isFinite(regular) && regular > 0) titleAgg.regular.push(regular);
          if (
            Number.isFinite(actual) &&
            Number.isFinite(regular) &&
            actual > 0 &&
            regular > 0 &&
            actual !== regular
          ) {
            titleAgg.promo.push(actual);
          }
          perTitleAgg.set(skuKey, titleAgg);
        });
        const stockClasses = latestRows
          .map((item) => classifyStockClassFromItem(item))
          .filter(Boolean);
        const hasAvail = stockClasses.includes('avail');
        const hasOos = stockClasses.includes('oos');
        const stockLabel = hasAvail && hasOos
          ? 'Mixed (In stock / Out of stock)'
          : hasAvail
            ? 'In stock'
            : hasOos
              ? 'Out of stock'
              : null;
        if (stockLabel) {
          const existing = pinnedPopupPricingByTitle.get(skuKey) || {};
          pinnedPopupPricingByTitle.set(skuKey, {
            ...existing,
            stock_status: stockLabel,
          });
        }
      });

      const currencySuffix = normalizeToEur ? 'EUR' : (selectedCurrency || '');
      const pinIndexActive = isIndexMode && activeBaseProduct;
      const baseTitlesForPinIndex = (() => {
        if (!pinIndexActive || !activeBaseProduct) return [];
        const fromGroup = groupNameToProducts.get(activeBaseProduct);
        if (fromGroup?.length) return [...new Set([...fromGroup, activeBaseProduct].filter(Boolean))];
        return [activeBaseProduct];
      })();
      const baseAvgByType = { actual: null, regular: null, promo: null };
      if (pinIndexActive && baseTitlesForPinIndex.length) {
        const avgOf = (vals) => {
          if (!vals?.length) return null;
          const x = vals.reduce((a, b) => a + b, 0) / vals.length;
          return x > 0 && Number.isFinite(x) ? x : null;
        };
        const titleFromSkuKey = (skuKey) => {
          const ix = String(skuKey).indexOf('\x1f');
          return ix >= 0 ? skuKey.slice(0, ix) : skuKey;
        };
        const mergeBaseType = (ptype) => {
          const nums = [];
          baseTitlesForPinIndex.forEach((bt) => {
            perTitleAgg.forEach((agg, skuKey) => {
              if (titleFromSkuKey(skuKey) !== bt) return;
              const v = avgOf(agg?.[ptype]);
              if (v != null) nums.push(v);
            });
          });
          if (!nums.length) return null;
          return nums.reduce((a, b) => a + b, 0) / nums.length;
        };
        ['actual', 'regular', 'promo'].forEach((t) => {
          baseAvgByType[t] = mergeBaseType(t);
        });
      }
      const formatPinnedIndexPct = (pct) => {
        if (!Number.isFinite(pct) || pct <= 0) return null;
        const d = Math.max(0, indexDecimalPlaces);
        const rounded = Math.round(pct * Math.pow(10, d)) / Math.pow(10, d);
        return `${parseFloat(rounded.toFixed(d)).toString()}%`;
      };
      const baseAvgKeyForCompareType = (compareType) => {
        if (compareType === 'regular') return 'regular';
        if (compareType === 'promo') return 'promo';
        return 'actual';
      };
      const resolvePinnedCompareBase = (requestedType) => {
        const effectiveRequested = resolveEffectiveIndexCompareRequest(requestedType, compareToPriceType);
        const tryType = (t) => {
          const key = baseAvgKeyForCompareType(t);
          const v = baseAvgByType[key];
          return v != null && v > 0 ? { type: t, value: v } : null;
        };
        const primary = tryType(effectiveRequested);
        if (primary) return primary;
        for (const t of INDEX_COMPARE_BASE_TYPES) {
          if (t === effectiveRequested) continue;
          const alt = tryType(t);
          if (alt) return alt;
        }
        return null;
      };
      const baseDenomForPinnedLineType = (lineType, skuKey) => {
        let requested = indexCompareRequestForPriceType(lineType);
        if (lineType === 'actual' && skuKey && compareToPriceType === BASE_COMPARE_TYPE_AUTO) {
          const agg = perTitleAgg.get(skuKey);
          if (agg) {
            const avgActual = agg.actual?.length
              ? agg.actual.reduce((a, b) => a + b, 0) / agg.actual.length
              : null;
            const avgRegular = agg.regular?.length
              ? agg.regular.reduce((a, b) => a + b, 0) / agg.regular.length
              : null;
            const tol = priceSpreadTolerance(priceDecimalPlaces);
            if (isPromoPriceSpread(avgActual, avgRegular, tol)) {
              requested = 'promo';
            }
          }
        }
        return resolvePinnedCompareBase(requested)?.value ?? null;
      };

      perTitleAgg.forEach((agg, skuKey) => {
        const details = selectedPriceTypes
          .map((type) => {
            const vals = agg[type] || [];
            if (!vals.length) return null;
            const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
            let valueStr = `${formatPinnedPriceValue(avg)}${currencySuffix ? ` ${currencySuffix}` : ''}`;
            if (pinIndexActive) {
              const denom = baseDenomForPinnedLineType(type, skuKey);
              if (denom != null && denom > 0 && Number.isFinite(avg) && avg > 0) {
                const idxStr = formatPinnedIndexPct((avg / denom) * 100);
                if (idxStr) valueStr = `${valueStr} / ${idxStr}`;
              }
            }
            return {
              type,
              label: typeLabelByKey.get(type) || type,
              value: valueStr,
            };
          })
          .filter(Boolean);
        if (details.length) priceByTitle.set(skuKey, details);
      });

      const popupCurrency =
        normalizeToEur ? 'EUR' : (normalizeCurrencyCode(selectedCurrency) || '');
      perTitleAgg.forEach((agg, skuKey) => {
        const actualVals = agg.actual || [];
        const regularVals = agg.regular || [];
        const avgActual = actualVals.length
          ? actualVals.reduce((a, b) => a + b, 0) / actualVals.length
          : null;
        const avgRegular = regularVals.length
          ? regularVals.reduce((a, b) => a + b, 0) / regularVals.length
          : null;
        if (avgActual == null && avgRegular == null) return;
        const existing = pinnedPopupPricingByTitle.get(skuKey) || {};
        const localAgg = perTitleLocalAgg.get(skuKey);
        const localCurrency = localAgg?.currency || null;
        const localCurrencyNorm = normalizeCurrencyCode(localCurrency);
        const popupCurrencyNorm = normalizeCurrencyCode(popupCurrency);
        const showLocal =
          localCurrencyNorm &&
          popupCurrencyNorm &&
          localCurrencyNorm !== popupCurrencyNorm;
        const localActualVals = localAgg?.actual || [];
        const localRegularVals = localAgg?.regular || [];
        const avgLocalActual = localActualVals.length
          ? localActualVals.reduce((a, b) => a + b, 0) / localActualVals.length
          : null;
        const avgLocalRegular = localRegularVals.length
          ? localRegularVals.reduce((a, b) => a + b, 0) / localRegularVals.length
          : null;
        pinnedPopupPricingByTitle.set(skuKey, {
          ...existing,
          ...(popupCurrency ? { currency: popupCurrency } : {}),
          ...(avgActual != null ? { actual_price: formatPinnedPriceValue(avgActual) } : {}),
          ...(avgRegular != null ? { regular_price: formatPinnedPriceValue(avgRegular) } : {}),
          ...(showLocal && localCurrency ? { local_currency: localCurrency } : {}),
          ...(showLocal && avgLocalActual != null
            ? { local_actual_price: formatPinnedPriceValue(avgLocalActual) }
            : {}),
          ...(showLocal && avgLocalRegular != null
            ? { local_regular_price: formatPinnedPriceValue(avgLocalRegular) }
            : {}),
        });
      });
    }
    const rowsWithPrices = sortPinPanelRowsByChartValue(
      rows.map((row) => ({
        ...row,
        variants: (row.variants || []).map((variant) => {
          const variantCountry = variant.country || row.country;
          return {
            ...variant,
            priceDetails: lookupPinPricing(priceByTitle, variant.name, variantCountry) || [],
            pinnedPopupPricing: lookupPinPricing(pinnedPopupPricingByTitle, variant.name, variantCountry) || null,
          };
        }),
      }))
    );
    const scrapeGapPairs = null;
    return { dateLabel, rows: rowsWithPrices, scrapeGapPairs };
  }, [
    priceChartPinIndex,
    priceChartData,
    productImageUrlByTitle,
    activePriceTypes,
    pinPriceSourceRows,
    normalizeToEur,
    selectedCurrency,
    priceDecimalPlaces,
    priceDisplayMode,
    compareToPriceType,
    groupNameToProducts,
    indexDecimalPlaces,
    isIndexMode,
    activeBaseProduct,
    topFilters.currencyIndex,
    getItemPackSize,
    productFlagsFromStorage,
    filteredData,
    priceHistoryData,
  ]);

  const visibilityChartPinPanel = useMemo(() => {
    if (visibilityChartPinIndex == null || !visibilityChartData?.labels) return null;
    const labels = visibilityChartData.labels;
    const idx = visibilityChartPinIndex;
    if (idx < 0 || idx >= labels.length) return null;
    const raw = labels[idx];
    const d = dayjs(raw);
    const dateLabel = d.isValid() ? d.format('YYYY-MM-DD') : String(raw);
    const rows = buildPinRowsForChart(visibilityChartData, idx, 'visibility', productImageUrlByTitle);
    return { dateLabel, rows };
  }, [visibilityChartPinIndex, visibilityChartData, productImageUrlByTitle]);

  useEffect(() => {
    setPriceChartPinIndex(null);
  }, [priceHistoryData]);

  useEffect(() => {
    setVisibilityChartPinIndex(null);
  }, [visibilityHistoryData]);

  useEffect(() => {
    setPriceChartPinIndex(null);
    setVisibilityChartPinIndex(null);
  }, [activeChartTab]);

  useEffect(() => {
    if (priceChartPinIndex == null || !priceChartData?.labels?.length) return;
    if (priceChartPinIndex < 0 || priceChartPinIndex >= priceChartData.labels.length) {
      setPriceChartPinIndex(null);
    }
  }, [priceChartPinIndex, priceChartData]);

  useEffect(() => {
    if (visibilityChartPinIndex == null || !visibilityChartData?.labels?.length) return;
    if (visibilityChartPinIndex < 0 || visibilityChartPinIndex >= visibilityChartData.labels.length) {
      setVisibilityChartPinIndex(null);
    }
  }, [visibilityChartPinIndex, visibilityChartData]);

  const priceChartTableSeries = useMemo(
    () => groupDatasetsForChartTable(priceChartDataAllSeries?.datasets, visiblePriceDatasets),
    [priceChartDataAllSeries, visiblePriceDatasets]
  );

  const formatPriceChartTableCell = useCallback(
    (num, dataset) => {
      if (num == null) return '';
      if (isIndexMode || dataset?.isIndexMode) {
        return `${Number(num).toFixed(Math.max(0, indexDecimalPlaces))}%`;
      }
      return Number(num).toFixed(2);
    },
    [isIndexMode, indexDecimalPlaces]
  );

  const priceChartTableRows = useMemo(() => {
    if (!priceChartDataAllSeries?.labels || !priceChartTableSeries.length) return [];
    return priceChartDataAllSeries.labels.map((date, i) => {
      const row = { id: i, date };
      priceChartTableSeries.forEach(({ field, group }) => {
        row[field] = formatPriceChartTableCell(pickMergedChartTableValueAt(group, i), group[0]);
      });
      return row;
    });
  }, [priceChartDataAllSeries, priceChartTableSeries, formatPriceChartTableCell]);

  const priceChartTableColumns = useMemo(() => {
    if (!priceChartTableSeries.length) return [];
    return [
      { field: 'date', headerName: 'Date', width: 110, headerAlign: 'center', renderCell: renderChartTableCell },
      ...priceChartTableSeries.map(({ field, rep }) => ({
        field,
        headerName: chartTableSeriesHeaderName(rep),
        width: CHART_TABLE_PRICE_COL_WIDTH,
        minWidth: 72,
        maxWidth: 100,
        headerAlign: 'center',
        align: 'center',
        renderCell: renderChartTableCell,
      })),
    ];
  }, [priceChartTableSeries]);

  const handleExportPriceCSV = useCallback(() => {
    if (!priceChartDataAllSeries?.labels || !priceChartTableSeries.length) return;
    const headers = ['Date', ...priceChartTableSeries.map(({ rep }) => `"${chartTableSeriesHeaderName(rep)}"`)];
    const dataRows = priceChartDataAllSeries.labels.map((date, i) => {
      const vals = priceChartTableSeries.map(({ group }) =>
        formatPriceChartTableCell(pickMergedChartTableValueAt(group, i), group[0])
      );
      return [date, ...vals].join(',');
    });
    const csv = [headers.join(','), ...dataRows].join('\n');
    saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8' }), 'jti_price_data.csv');
  }, [priceChartDataAllSeries, priceChartTableSeries, formatPriceChartTableCell]);

  const visibilityChartTableSeries = useMemo(
    () =>
      groupDatasetsForChartTable(
        visibilityHistoryData?.datasets,
        visibleVisibilityDatasets.size ? visibleVisibilityDatasets : null
      ),
    [visibilityHistoryData, visibleVisibilityDatasets]
  );

  const visibilityChartTableRows = useMemo(() => {
    if (!visibilityHistoryData?.labels || !visibilityChartTableSeries.length) return [];
    return visibilityHistoryData.labels.map((date, i) => {
      const row = { id: i, date };
      visibilityChartTableSeries.forEach(({ field, group }) => {
        const n = pickMergedChartTableValueAt(group, i);
        row[field] = n == null ? '' : Number(n).toFixed(2);
      });
      return row;
    });
  }, [visibilityHistoryData, visibilityChartTableSeries]);

  const visibilityChartTableColumns = useMemo(() => {
    if (!visibilityChartTableSeries.length) return [];
    return [
      { field: 'date', headerName: 'Date', width: 110, headerAlign: 'center', renderCell: renderChartTableCell },
      ...visibilityChartTableSeries.map(({ field, rep }) => ({
        field,
        headerName: chartTableSeriesHeaderName(rep),
        width: CHART_TABLE_PRICE_COL_WIDTH,
        minWidth: 72,
        maxWidth: 100,
        headerAlign: 'center',
        align: 'center',
        renderCell: renderChartTableCell,
      })),
    ];
  }, [visibilityChartTableSeries]);

  const priceTypeLabelByKey = useMemo(
    () => new Map(PRICE_TYPE_OPTIONS.map((o) => [o.key, o.label])),
    []
  );

  const priceChartSeriesSummaryRows = useMemo(() => {
    if (!priceChartDataAllSeries?.labels?.length || !priceChartDataAllSeries?.datasets?.length) return [];
    const labels = priceChartDataAllSeries.labels;
    const rawKeys = priceChartDataAllSeries.rawDateKeys;
    const datasets = priceChartDataAllSeries.datasets.filter((ds) =>
      isPriceLegendSeriesVisible(visiblePriceDatasets, ds.label)
    );
    return datasets.flatMap((ds, rowIdx) => {
      const { first, last } = getFiniteValueSpanIndices(ds.data);
      const firstSeen = first >= 0 ? labels[first] : '—';
      const lastSeen = last >= 0 ? labels[last] : '—';
      const priceTypeKey = ds.priceType || '';
      const stock =
        ds.stockSegment === 'oos'
          ? 'Out of stock'
          : ds.stockSegment === 'avail'
            ? 'In stock'
            : '';
      const skuTitlesList = collectSkuTitlesFromPriceDataset(ds, last);
      const idxSnap = last >= 0 ? last : -1;
      const cluster = priceChartDataAllSeries.datasets.filter(
        (d) => clusterSeriesKeyForSummary(d) === clusterSeriesKeyForSummary(ds)
      );
      const snap = snapshotPricesFromClusterAtIndex(cluster, idxSnap);
      const idxMode = isIndexMode || !!ds.isIndexMode;
      const fmt = (v) => formatSummaryPriceDisplay(v, idxMode, indexDecimalPlaces, priceDecimalPlaces);
      const linePriceRaw =
        snapshotValueForLinePriceType(snap, priceTypeKey) ??
        (idxSnap >= 0 ? readChartPointAt(ds, idxSnap) : null);
      const countryNorm = extractCountryFilterFromPriceDataset(ds);
      const lastDateKey =
        Array.isArray(rawKeys) && idxSnap >= 0 && idxSnap < rawKeys.length ? rawKeys[idxSnap] : null;
      const currencyOrIndex = idxMode
        ? `Index (${activeBaseProduct || 'base'})`
        : priceDisplayCurrency || '—';
      const seriesLabel = summaryModelTitleFromPriceDataset(ds);
      const expandTitles = skuTitlesList.length > 0 ? skuTitlesList : [null];

      return expandTitles.map((skuTitle, skuIdx) => {
        const titleForRow = skuTitle || null;
        const titleSet = titleForRow ? new Set([titleForRow]) : new Set();
        const geo = summarizeRetailersCountriesBrands(filteredData || [], titleSet, countryNorm);
        const lastStock = stockStatusAtLastObservation(
          filteredData || [],
          titleForRow ? [titleForRow] : [],
          countryNorm,
          lastDateKey
        );
        const skuUrls = titleForRow
          ? pickSingleListingUrlForSkuRow(
              filteredData || [],
              columns,
              titleForRow,
              countryNorm,
              lastDateKey
            ) || '—'
          : '—';
        const skuImageUrls = [
          titleForRow
            ? productImageUrlByTitle.get(titleForRow) || (skuIdx === 0 ? ds.productImage : null)
            : ds.productImage || null,
        ];
        const rowIdBase = ds.label || `price-series-${rowIdx}`;
        const id = titleForRow
          ? `${rowIdBase}__${skuIdx}__${encodeURIComponent(titleForRow)}`
          : `${rowIdBase}__${skuIdx}__none`;
        return {
          id,
          series: seriesLabel,
          skuTitlesList: titleForRow ? [titleForRow] : [],
          skuTitles: titleForRow || '—',
          skuImageUrls,
          skuUrls,
          retailer: geo.retailers,
          country: geo.countries,
          brand: geo.brands,
          currencyOrIndex,
          price: fmt(linePriceRaw),
          lastStock,
          priceType: priceTypeLabelByKey.get(priceTypeKey) || priceTypeKey || '—',
          stock: stock || '—',
          firstSeen,
          lastSeen,
        };
      });
    });
  }, [
    priceChartDataAllSeries,
    visiblePriceDatasets,
    priceTypeLabelByKey,
    filteredData,
    columns,
    isIndexMode,
    activeBaseProduct,
    priceDisplayCurrency,
    indexDecimalPlaces,
    priceDecimalPlaces,
    productImageUrlByTitle,
  ]);

  const renderSkuImagesCell = useCallback((params) => {
    const urls = params.row?.skuImageUrls;
    if (!Array.isArray(urls) || urls.length === 0) return '—';
    const hasAny = urls.some(Boolean);
    if (!hasAny) return '—';
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', justifyContent: 'flex-start', py: 0.25 }}>
        {urls.map((url, i) =>
          url ? (
            <Box
              key={`sku-img-${i}-${String(url).slice(0, 48)}`}
              component="img"
              src={url}
              alt=""
              sx={{
                width: 36,
                height: 36,
                objectFit: 'cover',
                borderRadius: 0.75,
                border: '1px solid',
                borderColor: 'divider',
                flexShrink: 0,
                display: 'block',
                bgcolor: 'action.hover',
              }}
              onError={(e) => {
                e.currentTarget.style.visibility = 'hidden';
              }}
            />
          ) : (
            <Box
              key={`sku-img-ph-${i}`}
              sx={{
                width: 36,
                height: 36,
                borderRadius: 0.75,
                border: '1px dashed',
                borderColor: 'divider',
                flexShrink: 0,
                bgcolor: 'action.hover',
              }}
            />
          )
        )}
      </Box>
    );
  }, []);

  const renderSkuUrlsCell = useCallback((params) => {
    const raw = params.value;
    if (!raw || raw === '—') return '—';
    const parts = String(raw)
      .split(';')
      .map((s) => s.trim())
      .filter((u) => /^https?:\/\//i.test(u));
    if (!parts.length) return raw;
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, py: 0.25 }}>
        {parts.map((u, i) => (
          <a key={`${i}-${u.slice(0, 48)}`} href={u} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all', fontSize: 12 }}>
            {u}
          </a>
        ))}
      </Box>
    );
  }, []);

  const priceChartSeriesSummaryColumns = useMemo(() => {
    const headerWithLastDateInfo = (label, headerAlign) => (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          justifyContent:
            headerAlign === 'right' ? 'flex-end' : headerAlign === 'center' ? 'center' : 'flex-start',
          width: '100%',
        }}
      >
        <span>{label}</span>
        <Tooltip title={PRICE_TABLE_LAST_DATE_INFO} arrow placement="top">
          <IconButton
            size="small"
            aria-label={`${label}: column info`}
            sx={{ p: 0.25 }}
            onClick={(e) => e.stopPropagation()}
          >
            <InfoOutlined sx={{ fontSize: '1rem', color: 'text.secondary' }} />
          </IconButton>
        </Tooltip>
      </Box>
    );

    const cols = [
      {
        field: 'skuImageUrls',
        headerName: 'SKU image(s)',
        width: 120,
        align: 'center',
        headerAlign: 'center',
        renderCell: renderSkuImagesCell,
      },
      { field: 'skuTitles', headerName: 'SKU title', flex: 1, minWidth: 200 },
      { field: 'series', headerName: 'Model title', flex: 1, minWidth: 160 },
      {
        field: 'skuUrls',
        headerName: 'Listing URL(s)',
        flex: 1,
        minWidth: 220,
        renderCell: renderSkuUrlsCell,
      },
      { field: 'retailer', headerName: 'Retailer(s)', flex: 1, minWidth: 130 },
      { field: 'country', headerName: 'Country', width: 120 },
      { field: 'brand', headerName: 'Brand', flex: 1, minWidth: 100 },
      { field: 'currencyOrIndex', headerName: 'Currency / index', width: 150, headerAlign: 'center' },
      {
        field: 'price',
        headerName: headerWithLastDateInfo('Price', 'right'),
        width: 120,
        align: 'right',
        headerAlign: 'right',
      },
    ];
    cols.push({
      field: 'lastStock',
      headerName: headerWithLastDateInfo('Stock status', 'center'),
      width: 168,
      headerAlign: 'center',
    });
    cols.push({ field: 'priceType', headerName: 'Price type', width: 150, headerAlign: 'center' });
    cols.push(
      { field: 'firstSeen', headerName: 'First seen', width: 130, headerAlign: 'center' },
      { field: 'lastSeen', headerName: 'Last seen', width: 130, headerAlign: 'center' }
    );
    return cols;
  }, [renderSkuUrlsCell, renderSkuImagesCell]);

  const visibilityChartSeriesSummaryRows = useMemo(() => {
    if (!visibilityHistoryData?.labels?.length || !visibilityHistoryData?.datasets?.length) return [];
    const labels = visibilityHistoryData.labels;
    const datasets = visibleVisibilityDatasets.size
      ? visibilityHistoryData.datasets.filter((ds) => visibleVisibilityDatasets.has(ds.label))
      : visibilityHistoryData.datasets;
    const rawKeysVis = visibilityHistoryData.rawDateKeys;
    return datasets.flatMap((ds, rowIdx) => {
      const { first, last } = getFiniteValueSpanIndices(ds.data);
      const firstSeen = first >= 0 ? labels[first] : '—';
      const lastSeen = last >= 0 ? labels[last] : '—';
      let lastDateKeyVis = null;
      if (Array.isArray(rawKeysVis) && last >= 0 && last < rawKeysVis.length) {
        const rk = String(rawKeysVis[last]).slice(0, 10);
        if (/^\d{4}-\d{2}-\d{2}$/.test(rk)) lastDateKeyVis = rk;
      } else if (last >= 0 && labels[last]) {
        const d = dayjs(labels[last]);
        if (d.isValid()) lastDateKeyVis = d.format('YYYY-MM-DD');
      }
      const skuTitlesList = collectSkuTitlesFromVisibilityDataset(ds);
      const seriesLabel = stripFlagEmoji(ds.displayLabel || ds.label || '');
      const expandTitles = skuTitlesList.length > 0 ? skuTitlesList : [null];

      return expandTitles.map((skuTitle, skuIdx) => {
        const titleForRow = skuTitle || null;
        const skuUrls = titleForRow
          ? pickSingleListingUrlForSkuRow(
              filteredData || [],
              columns,
              titleForRow,
              null,
              lastDateKeyVis
            ) || '—'
          : '—';
        const skuImageUrls = [
          titleForRow
            ? productImageUrlByTitle.get(titleForRow) || (skuIdx === 0 ? ds.productImage : null)
            : ds.productImage || null,
        ];
        const rowIdBase = ds.label || `vis-series-${rowIdx}`;
        const id = titleForRow
          ? `${rowIdBase}__${skuIdx}__${encodeURIComponent(titleForRow)}`
          : `${rowIdBase}__${skuIdx}__none`;
        return {
          id,
          series: seriesLabel,
          skuTitlesList: titleForRow ? [titleForRow] : [],
          skuTitles: titleForRow || '—',
          skuImageUrls,
          skuUrls,
          firstSeen,
          lastSeen,
        };
      });
    });
  }, [visibilityHistoryData, visibleVisibilityDatasets, filteredData, columns, productImageUrlByTitle]);

  const visibilityChartSeriesSummaryColumns = useMemo(
    () => [
      {
        field: 'skuImageUrls',
        headerName: 'SKU image(s)',
        width: 120,
        align: 'center',
        headerAlign: 'center',
        renderCell: renderSkuImagesCell,
      },
      { field: 'skuTitles', headerName: 'SKU title', flex: 1, minWidth: 200 },
      { field: 'series', headerName: 'Model title', flex: 1, minWidth: 160 },
      {
        field: 'skuUrls',
        headerName: 'Listing URL(s)',
        flex: 1,
        minWidth: 220,
        renderCell: renderSkuUrlsCell,
      },
      { field: 'firstSeen', headerName: 'First seen', width: 130, headerAlign: 'center' },
      { field: 'lastSeen', headerName: 'Last seen', width: 130, headerAlign: 'center' },
    ],
    [renderSkuUrlsCell, renderSkuImagesCell]
  );

  const changeFeedFormatOptions = useMemo(
    () => ({
      isIndexMode: topFilters.currencyIndex === 'Index' && !!activeBaseProduct,
      currency: priceDisplayCurrency,
      priceDecimalPlaces,
      indexDecimalPlaces,
    }),
    [topFilters.currencyIndex, activeBaseProduct, priceDisplayCurrency, priceDecimalPlaces, indexDecimalPlaces]
  );

  const priceChangeFeedEvents = useMemo(
    () =>
      buildDatasetChangeFeed('price', priceHistoryData, null, pinPriceSourceRows, {
        activePriceTypes,
        productFlags: productFlagsFromStorage,
        categoryOverrides,
        priceDecimalPlaces,
        priceTypeLabelByKey,
        isIndexMode: topFilters.currencyIndex === 'Index' && !!activeBaseProduct,
        activeBaseProduct,
        groupNameToProducts,
        matchingGroups,
        currency: priceDisplayCurrency,
      }),
    [
      priceHistoryData,
      pinPriceSourceRows,
      activePriceTypes,
      productFlagsFromStorage,
      categoryOverrides,
      priceDecimalPlaces,
      priceTypeLabelByKey,
      topFilters.currencyIndex,
      activeBaseProduct,
      groupNameToProducts,
      matchingGroups,
      priceDisplayCurrency,
    ]
  );

  const visibilityChangeFeedEvents = useMemo(
    () =>
      buildDatasetChangeFeed('visibility', null, visibilityHistoryData, null, {
        visibleVisibilityDatasets,
      }),
    [visibilityHistoryData, visibleVisibilityDatasets]
  );

  const handleExportVisibilityCSV = useCallback(() => {
    if (!visibilityHistoryData?.labels || !visibilityChartTableSeries.length) return;
    const headers = ['Date', ...visibilityChartTableSeries.map(({ rep }) => `"${chartTableSeriesHeaderName(rep)}"`)];
    const dataRows = visibilityHistoryData.labels.map((date, i) => {
      const vals = visibilityChartTableSeries.map(({ group }) => {
        const n = pickMergedChartTableValueAt(group, i);
        return n == null ? '' : Number(n).toFixed(2);
      });
      return [date, ...vals].join(',');
    });
    const csv = [headers.join(','), ...dataRows].join('\n');
    saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8' }), 'jti_visibility_data.csv');
  }, [visibilityHistoryData, visibilityChartTableSeries]);

  useEffect(() => {
    if (priceChartDataAllSeries?.datasets?.length) {
      if (savedPriceVisibilityRef.current) {
        const available = new Set(priceChartDataAllSeries.datasets.map((ds) => ds.label));
        const filtered = savedPriceVisibilityRef.current.filter((label) => available.has(label));
        if (filtered.length > 0) {
          setVisiblePriceDatasets(new Set(filtered));
        } else {
          setVisiblePriceDatasets(new Set(priceChartDataAllSeries.datasets.map((ds) => ds.label)));
        }
        savedPriceVisibilityRef.current = null;
      } else {
        setVisiblePriceDatasets(new Set(priceChartDataAllSeries.datasets.map((ds) => ds.label)));
      }
    }
  }, [priceChartDataAllSeries]);

  useEffect(() => {
    if (visibilityHistoryData?.datasets?.length) {
      if (savedVisibilityRef.current) {
        const available = new Set(visibilityHistoryData.datasets.map((ds) => ds.label));
        const filtered = savedVisibilityRef.current.filter((label) => available.has(label));
        if (filtered.length > 0) {
          setVisibleVisibilityDatasets(new Set(filtered));
        } else {
          setVisibleVisibilityDatasets(new Set(visibilityHistoryData.datasets.map((ds) => ds.label)));
        }
        savedVisibilityRef.current = null;
      } else {
        setVisibleVisibilityDatasets(new Set(visibilityHistoryData.datasets.map((ds) => ds.label)));
      }
    }
  }, [visibilityHistoryData]);

  const handleTogglePriceDataset = useCallback((labelOrGroupId) => {
    setVisiblePriceDatasets((prev) => {
      const datasets = priceChartDataAllSeries?.datasets;
      if (!datasets?.length) {
        const next = new Set(prev ?? []);
        if (next.has(labelOrGroupId)) next.delete(labelOrGroupId);
        else next.add(labelOrGroupId);
        return next;
      }
      const base =
        prev == null
          ? new Set(datasets.map((d) => d.label))
          : new Set(prev);
      const gidStr = labelOrGroupId != null ? String(labelOrGroupId) : '';
      const inGroup = datasets.filter((d) => {
        if (d.legendGroupId != null && String(d.legendGroupId) === gidStr) return true;
        return d.label === labelOrGroupId;
      });
      const labelsToToggle =
        inGroup.length > 0
          ? [...new Set(inGroup.map((d) => d.label))]
          : [labelOrGroupId];
      const allOn = labelsToToggle.length > 0 && labelsToToggle.every((l) => base.has(l));
      const next = new Set(base);
      labelsToToggle.forEach((l) => {
        if (allOn) next.delete(l);
        else next.add(l);
      });
      return next;
    });
  }, [priceChartDataAllSeries]);

  const handleToggleVisibilityDataset = (label) => {
    setVisibleVisibilityDatasets((prev) => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return next;
    });
  };

  const handleShowAllPrice = () => {
    if (!priceChartDataAllSeries?.datasets) return;
    setVisiblePriceDatasets(new Set(priceChartDataAllSeries.datasets.map((ds) => ds.label)));
  };

  const handleHideAllPrice = () => {
    setVisiblePriceDatasets(new Set());
  };

  const handleShowAllVisibility = () => {
    if (!visibilityHistoryData?.datasets) return;
    setVisibleVisibilityDatasets(new Set(visibilityHistoryData.datasets.map((ds) => ds.label)));
  };

  const handleHideAllVisibility = () => {
    setVisibleVisibilityDatasets(new Set());
  };

  const serializeFilters = () => ({
    selectedManufacturers,
    selectedBrands,
    selectedProducts,
    selectedCurrency,
    selectedCountries,
    selectedRetailers,
    selectedFlavours,
    selectedCategoryGroups,
    selectedPackSizes,
    customizableFilter: normalizeCustomizableFilter(customizableFilter),
    forNewUsersOnlyFilter: normalizeForNewUsersOnlyFilter(forNewUsersOnlyFilter),
    limitedEditionFilter: normalizeLimitedEditionFilter(
      additionalFilters?.limitedEdition || LIMITED_EDITION_FILTER_DEFAULT
    ),
    starterKitProductFilter: normalizeStarterKitFilter(starterKitProductFilter),
    normalizeToEur,
    dateRange: [
      dateRange[0] ? dayjs(dateRange[0]).toISOString() : null,
      dateRange[1] ? dayjs(dateRange[1]).toISOString() : null,
    ],
    activeChartTab,
    topN,
    visiblePriceDatasets: visiblePriceDatasets == null ? null : Array.from(visiblePriceDatasets),
    visibleVisibilityDatasets: Array.from(visibleVisibilityDatasets),
  });

  const handleOpenSaveView = () => {
    setNewViewName(`View ${savedViews.length + 1}`);
    setIsSaveViewOpen(true);
  };

  const handleSaveView = async () => {
    if (!username) return;
    const storageKey = `jti_price_filter_views_${username}_${projectKey || username}`;
    const viewName = newViewName.trim() || `Filter ${savedViews.length + 1}`;
    const filters = serializeFilters();

    let nextViews = [];
    try {
      const response = await axios.post(
        `${API_URL}/user-filters/${username}/views`,
        { name: viewName, filters },
        {
          params: {
            project_key: projectKey || username,
          },
          withCredentials: true,
        }
      );
      nextViews = response.data?.views || [];
    } catch (error) {
      console.warn('Failed to save filter view to API, saving to localStorage:', error);
      nextViews = savedViews.filter((view) => view.name !== viewName);
      nextViews.push({ name: viewName, filters });
    }

    setSavedViews(nextViews);
    setSelectedViewName(viewName);
    setIsSaveViewOpen(false);
    setNewViewName(`Filter ${nextViews.length + 1}`);

    try {
      localStorage.setItem(storageKey, JSON.stringify({ views: nextViews }));
    } catch (error) {
      console.warn('Failed to save filter views to localStorage:', error);
    }
  };

  const handleViewChange = (event, newValue) => {
    if (!newValue) return;
    isApplyingViewRef.current = true;
    const nextName = typeof newValue === 'string' ? newValue : newValue;
    setSelectedViewName(nextName);
    const selectedView = savedViews.find((view) => view.name === nextName);
    if (selectedView?.filters) {
      applySavedFilters(selectedView.filters);
    }
  };

  const sanitizeSelectValues = (values) => {
    if (!Array.isArray(values)) return values;
    return values.filter((value) => value !== '__all__');
  };

  const handleDeleteView = async (viewName) => {
    if (!username) return;
    const storageKey = `jti_price_filter_views_${username}_${projectKey || username}`;
    let nextViews = savedViews.filter((view) => view.name !== viewName);
    setSavedViews(nextViews);
    if (selectedViewName === viewName) {
      setSelectedViewName('');
    }

    try {
      const response = await axios.delete(
        `${API_URL}/user-filters/${username}/views/${encodeURIComponent(viewName)}`,
        {
          params: {
            project_key: projectKey || username,
          },
          withCredentials: true,
        }
      );
      nextViews = response.data?.views || nextViews;
      setSavedViews(nextViews);
    } catch (error) {
      console.warn('Failed to delete filter view from API, updating localStorage only:', error);
    }

    try {
      localStorage.setItem(storageKey, JSON.stringify({ views: nextViews }));
    } catch (error) {
      console.warn('Failed to save filter views to localStorage:', error);
    }
  };

  const resetFiltersToDefaults = () => {
    setSelectedManufacturers([]);
    const ploomBrand = findPloomBrand(brandOptions);
    // Market view includes all brands by default — leave the brand filter empty.
    setSelectedBrands(isMarketScope ? [] : (ploomBrand ? [ploomBrand] : []));
    setSelectedCategoryGroups(
      (routeCategoryGroup || routeCategoryGroupFallback)
        ? [routeCategoryGroup || routeCategoryGroupFallback]
        : []
    );
    if (isMarketScope) {
      const nextCountry = resolveMarketCountry(selectedMarket, countryOptions);
      setSelectedCountries(nextCountry ? [nextCountry] : []);
    } else {
      setSelectedCountries([]);
    }
    setSelectedRetailers([]);
    const marketDefaultProducts = isMarketScope
      ? resolveMarketDefaultProductSelections(productType, groupNameToProducts)
      : [];
    setSelectedProducts(marketDefaultProducts);
    if (isMarketScope) hasAppliedMarketProductDefaultRef.current = true;
    setSelectedFlavours([]);
    setCustomizableFilter(CUSTOMIZABLE_FILTER_DEFAULT);
    setForNewUsersOnlyFilter(FOR_NEW_USERS_FILTER_DEFAULT);
    setStarterKitProductFilter(STARTER_KIT_FILTER_DEFAULT);
    setAdditionalFilters({
      deviceColour: 'All',
      stickFlavour: 'All',
      stickCategory: 'All',
      deviceType: 'All',
      deviceStickType: 'All',
      limitedEdition: LIMITED_EDITION_FILTER_DEFAULT,
    });
    const defaultMarketCurrency = resolveMarketDefaultCurrency(selectedMarket, countryOptions, data);
    setSelectedCurrency(
      isMarketScope && defaultMarketCurrency
        ? normalizeCurrencyCode(defaultMarketCurrency)
        : 'EUR'
    );
    setNormalizeToEur(false);
    if (availableDates.length > 0) {
      setDateRange([availableDates[0], availableDates[availableDates.length - 1]]);
    } else {
      setDateRange([null, null]);
    }
    setActiveChartTab(0);
    setTopN(99999);
    setFlavourButtonsStates(new Array(columnsUniqueValues?.flavour?.length || 0).fill(false));
    setTopFilters(createInitialTopFilters(productType, isMarketScope));
    setActivePriceTypes(defaultActivePriceTypes(isMarketScope));
    setAllowMultiplePriceTypes(true);
  };

  const handleResetView = () => {
    setSelectedViewName('');
    resetFiltersToDefaults();
  };

  const arePrimitiveArraysEqual = (a, b) => {
    if (a === b) return true;
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i += 1) {
      if (a[i] !== b[i]) return false;
    }
    return true;
  };

  const areStringSetsEqual = (a, b) => {
    if (a === b) return true;
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    const aSorted = [...a].sort();
    const bSorted = [...b].sort();
    for (let i = 0; i < aSorted.length; i += 1) {
      if (aSorted[i] !== bSorted[i]) return false;
    }
    return true;
  };

  const areSerializedFiltersEqual = (current, saved) => {
    if (!current || !saved) return false;
    return (
      arePrimitiveArraysEqual(current.selectedManufacturers || [], saved.selectedManufacturers || [])
      && arePrimitiveArraysEqual(current.selectedBrands || [], saved.selectedBrands || [])
      && arePrimitiveArraysEqual(
        upgradeLegacyModelSelections(current.selectedProducts || [], groupNameToProducts),
        upgradeLegacyModelSelections(saved.selectedProducts || [], groupNameToProducts)
      )
      && current.selectedCurrency === saved.selectedCurrency
      && arePrimitiveArraysEqual(current.selectedCountries || [], saved.selectedCountries || [])
      && arePrimitiveArraysEqual(current.selectedRetailers || [], saved.selectedRetailers || [])
      && arePrimitiveArraysEqual(current.selectedFlavours || [], saved.selectedFlavours || [])
      && arePrimitiveArraysEqual(current.selectedCategoryGroups || [], saved.selectedCategoryGroups || [])
      && arePrimitiveArraysEqual(current.selectedPackSizes || [], saved.selectedPackSizes || [])
      && current.normalizeToEur === saved.normalizeToEur
      && arePrimitiveArraysEqual(current.dateRange || [], saved.dateRange || [])
      && current.activeChartTab === saved.activeChartTab
      && current.topN === saved.topN
      && areStringSetsEqual(current.visiblePriceDatasets || [], saved.visiblePriceDatasets || [])
      && areStringSetsEqual(current.visibleVisibilityDatasets || [], saved.visibleVisibilityDatasets || [])
    );
  };

  useEffect(() => {
    if (!filteredProducts.length) return;
    const validSkuSet = new Set(filteredProducts);
    setSelectedProducts((prev) =>
      prev.filter((product) => {
        const expanded = expandProductSelections([product], groupNameToProducts, modelGroupToProducts);
        if (!expanded.size) return false;
        for (const t of expanded) {
          if (validSkuSet.has(t)) return true;
        }
        return false;
      })
    );
  }, [filteredProducts, groupNameToProducts, modelGroupToProducts]);

  useEffect(() => {
    if (isApplyingViewRef.current) return;
    if (!selectedViewName) return;
    if (selectedViewName === 'Unsaved view') return;

    const selectedView = savedViews.find((view) => view.name === selectedViewName);
    if (!selectedView?.filters) {
      setSelectedViewName('Unsaved view');
      return;
    }

    const currentFilters = serializeFilters();
    if (areSerializedFiltersEqual(currentFilters, selectedView.filters)) return;
    setSelectedViewName('Unsaved view');
  }, [
    selectedViewName,
    savedViews,
    selectedManufacturers,
    selectedBrands,
    selectedProducts,
    selectedCurrency,
    selectedCountries,
    selectedRetailers,
    selectedFlavours,
    selectedCategoryGroups,
    selectedPackSizes,
    dateRange,
    activeChartTab,
    topN,
    visiblePriceDatasets,
    visibleVisibilityDatasets,
    normalizeToEur,
  ]);

  // Modify columns to include renderCell for URLs
  const columnsWithClickableUrls = gridData?.columns.map((col) => {
    if (col.field.toLowerCase().includes('url')) {
      return {
        ...col,
        renderCell: (params) => (
          <a href={params.value} target="_blank" rel="noopener noreferrer">
            {params.value}
          </a>
        ),
      };
    }
    return col;
  });

  const clampNumber = (value, min, max) => Math.min(max, Math.max(min, value));

  const hexToHsl = (hex) => {
    if (!hex || typeof hex !== 'string' || !hex.startsWith('#') || (hex.length !== 7 && hex.length !== 4)) {
      return { h: 0, s: 0, l: 45 };
    }
    const fullHex = hex.length === 4
      ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`
      : hex;
    const r = parseInt(fullHex.slice(1, 3), 16) / 255;
    const g = parseInt(fullHex.slice(3, 5), 16) / 255;
    const b = parseInt(fullHex.slice(5, 7), 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0;
    let s = 0;
    const l = (max + min) / 2;
    const d = max - min;
    if (d !== 0) {
      s = d / (1 - Math.abs(2 * l - 1));
      switch (max) {
        case r:
          h = ((g - b) / d) % 6;
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        default:
          h = (r - g) / d + 4;
          break;
      }
      h *= 60;
      if (h < 0) h += 360;
    }
    return { h, s: s * 100, l: l * 100 };
  };

  const sanitizeHsl = ({ h, s, l }) => ({
    h: Number.isFinite(h) ? h : 0,
    s: Number.isFinite(s) ? s : 60,
    l: Number.isFinite(l) ? l : 45,
  });

  const hslString = (raw) => {
    const { h, s, l } = sanitizeHsl(raw || {});
    return `hsl(${Math.round(h)}, ${Math.round(clampNumber(s, 0, 100))}%, ${Math.round(clampNumber(l, 0, 100))}%)`;
  };

  const getBrandBaseHsl = (brandKey) => {
    // Special tuning per request: Ploom should be very dark, near-black, leaning green.
    if (brandKey === 'PLOOM') {
      return { h: 165, s: 70, l: 20 };
    }
    const baseHex = COLOR_PALETTE?.brands?.[brandKey] || '#666666';
    const baseHsl = hexToHsl(baseHex);
    return { h: baseHsl.h, s: baseHsl.s, l: baseHsl.l };
  };

  const normalizeBrandKey = (value) => {
    const key = (value || '').toString().trim().toUpperCase();
    if (!key) return null;
    if (key.includes('PLOOM')) return 'PLOOM';
    if (key.includes('GLO')) return 'GLO';
    if (key.includes('IQOS')) return 'IQOS';
    return null;
  };

  const resolveDeviceLine = (title, brandKey) => {
    const t = (title || '').toString().toLowerCase();
    // Requested device families
    if ((brandKey === 'PLOOM' || t.includes('ploom')) && t.includes('aura')) return { key: 'ploom_aura', brandKey: 'PLOOM', hueShift: -10 };
    if ((brandKey === 'PLOOM' || t.includes('ploom')) && t.includes('cube')) return { key: 'ploom_cube', brandKey: 'PLOOM', hueShift: 10 };
    if ((brandKey === 'GLO' || t.includes('glo')) && t.includes('hyper')) return { key: 'glo_hyper', brandKey: 'GLO', hueShift: 0 };
    if ((brandKey === 'IQOS' || t.includes('iqos')) && t.includes('iluma')) return { key: 'iqos_iluma', brandKey: 'IQOS', hueShift: 0 };

    // Fallback brand-based coloring for other devices
    if (brandKey === 'PLOOM' || t.includes('ploom')) return { key: 'ploom', brandKey: 'PLOOM', hueShift: 0 };
    if (brandKey === 'GLO' || t.includes('glo')) return { key: 'glo', brandKey: 'GLO', hueShift: 0 };
    if (brandKey === 'IQOS' || t.includes('iqos')) return { key: 'iqos', brandKey: 'IQOS', hueShift: 0 };
    return null;
  };

  const buildDeviceColorMap = (productNames, productBrandKeyMap = {}) => {
    const fallbackColors = [
      'hsl(0, 70%, 45%)',
      'hsl(120, 70%, 45%)',
      'hsl(240, 70%, 45%)',
      'hsl(60, 70%, 45%)',
      'hsl(300, 70%, 45%)',
    ];

    const byGroup = new Map();
    productNames.forEach((name) => {
      const brandKey = productBrandKeyMap[name] || null;
      const line = resolveDeviceLine(name, brandKey);
      if (!line) return;
      const groupKey = line.key;
      const existing = byGroup.get(groupKey) || { line, names: [] };
      existing.names.push(name);
      byGroup.set(groupKey, existing);
    });

    const map = {};
    productNames.forEach((name, idx) => {
      const brandKey = productBrandKeyMap[name] || null;
      if (brandKey) {
        const baseHsl = getBrandBaseHsl(brandKey);
        // Default brand hue, vary lightness slightly by index for distinctness
        const lMin = brandKey === 'PLOOM' ? 14 : 34;
        const lMax = brandKey === 'PLOOM' ? 32 : 68;
        const l = clampNumber(baseHsl.l + ((idx % 5) - 2) * 3, lMin, lMax);
        map[name] = hslString({ h: baseHsl.h, s: clampNumber(baseHsl.s + 5, 35, 85), l });
      } else {
        map[name] = fallbackColors[idx % fallbackColors.length];
      }
    });

    for (const { line, names } of byGroup.values()) {
      const baseHsl = getBrandBaseHsl(line.brandKey);
      const shiftedHue = (baseHsl.h + (line.hueShift || 0) + 360) % 360;
      const base = {
        h: shiftedHue,
        s: clampNumber(baseHsl.s + 5, 35, 85),
        l: baseHsl.l,
      };

      const count = names.length;
      // Create distinct variants in the same hue family (wider lightness + hue spectrum for multiple records)
      const isPloom = line.brandKey === 'PLOOM';
      const minL = isPloom ? 8 : 28;
      const maxL = isPloom ? 42 : 78;
      const hueRange = count > 1 ? 25 : 0;
      const sortedNames = [...names].sort();
      sortedNames.forEach((name, i) => {
        const l = count <= 1
          ? (isPloom ? clampNumber(base.l, 16, 28) : clampNumber(base.l, 40, 60))
          : (minL + (maxL - minL) * i / Math.max(1, count - 1));
        const h = count <= 1 ? base.h : (base.h - hueRange / 2 + (hueRange * i / Math.max(1, count - 1)) + 360) % 360;
        map[name] = hslString({ h, s: base.s, l });
      });
    }

    return map;
  };

  // Resolve a specific palette color for a stick product by matching name fragments
  // against COLOR_PALETTE.brandProducts keys (e.g. "TEREA", "NEO", "EVO", etc.)
  const resolveStickColor = (productName) => {
    const t = (productName || '').toLowerCase();
    const bp = COLOR_PALETTE?.brandProducts;
    if (!bp) return null;
    for (const categories of Object.values(bp)) {
      for (const products of Object.values(categories)) {
        for (const [productKey, color] of Object.entries(products)) {
          if (t.includes(productKey.toLowerCase())) return color;
        }
      }
    }
    return null;
  };

  const buildStickColorMap = (productNames, productBrandKeyMap = {}) => {
    const fallbackColors = [
      'hsl(0, 70%, 45%)',
      'hsl(120, 70%, 45%)',
      'hsl(240, 70%, 45%)',
      'hsl(60, 70%, 45%)',
      'hsl(300, 70%, 45%)',
    ];
    const map = {};
    const byBrand = new Map();
    let fallbackIdx = 0;
    productNames.forEach((name) => {
      const paletteColor = resolveStickColor(name);
      if (paletteColor) {
        map[name] = paletteColor;
        return;
      }
      const brandKey = productBrandKeyMap[name] || null;
      if (brandKey) {
        if (!byBrand.has(brandKey)) byBrand.set(brandKey, []);
        byBrand.get(brandKey).push(name);
      } else {
        map[name] = fallbackColors[fallbackIdx++ % fallbackColors.length];
      }
    });
    byBrand.forEach((names, brandKey) => {
      const baseHsl = getBrandBaseHsl(brandKey);
      const count = names.length;
      const minL = 28;
      const maxL = 78;
      const hueRange = count > 1 ? 25 : 0;
      const sortedNames = [...names].sort();
      sortedNames.forEach((name, i) => {
        const l = count <= 1 ? clampNumber(baseHsl.l, 40, 60) : (minL + (maxL - minL) * i / Math.max(1, count - 1));
        const h = count <= 1 ? baseHsl.h : (baseHsl.h - hueRange / 2 + (hueRange * i / Math.max(1, count - 1)) + 360) % 360;
        map[name] = hslString({ h, s: clampNumber(baseHsl.s + 5, 35, 85), l });
      });
    });
    return map;
  };

  // Return a semi-transparent version of any CSS color (hex or hsl)
  const colorWithAlpha = (color, alpha = 0.1) => {
    if (!color) return `rgba(100,100,100,${alpha})`;
    if (color.startsWith('#')) {
      const hex = color.replace('#', '');
      const full = hex.length === 3 ? hex.split('').map(c => c + c).join('') : hex;
      const r = parseInt(full.slice(0, 2), 16);
      const g = parseInt(full.slice(2, 4), 16);
      const b = parseInt(full.slice(4, 6), 16);
      return `rgba(${r},${g},${b},${alpha})`;
    }
    if (color.startsWith('hsl(')) {
      return color.replace('hsl(', 'hsla(').replace(')', `, ${alpha})`);
    }
    return color;
  };

  const buildColorMap = (productNames, productBrandKeyMap = {}) =>
    isStickRoute
      ? buildStickColorMap(productNames, productBrandKeyMap)
      : buildDeviceColorMap(productNames, productBrandKeyMap);

  const parseNumericValue = (value) => {
    if (value === null || value === undefined) return NaN;
    if (typeof value === 'number') return value;
    const raw = (value || '').toString().trim();
    if (!raw) return NaN;
    // Keep digits, separators, and minus sign; strip currency symbols/text.
    let cleaned = raw.replace(/[^\d.,-]/g, '');
    if (!cleaned) return NaN;
    // If both separators are present, assume comma is thousands separator: "1,234.56"
    const hasComma = cleaned.includes(',');
    const hasDot = cleaned.includes('.');
    if (hasComma && hasDot) {
      cleaned = cleaned.replace(/,/g, '');
    } else if (hasComma && !hasDot) {
      // Assume comma decimal: "1234,56"
      cleaned = cleaned.replace(/,/g, '.');
    }
    const num = parseFloat(cleaned);
    return Number.isFinite(num) ? num : NaN;
  };

  // Generate price history data for the selected products
  const generatePriceHistoryData = (
    sourceFiltered = filteredData,
    productsOverride = null,
    /** Same effect run as `setFilteredData(groupedFiltered)` — used for index base merge; do not use `filteredData` state (stale with deferred chart build). */
    baseFallbackSource = null,
    /** SKU titles that passed attribute filters (customizable / starter kit / etc.) before matching-group expansion. */
    allowedSkuTitles = null
  ) => {
    const effectiveProducts = productsOverride || productsForCharts;
    if (!sourceFiltered || sourceFiltered.length === 0) return null;

    const skuAllowed = (title) => {
      if (!allowedSkuTitles || !title) return true;
      if (allowedSkuTitles.has(title)) return true;
      // productsToChart may hold a matching-group name — allow if any member SKU passed filters
      const members = groupNameToProducts.get(title);
      if (members?.some((t) => allowedSkuTitles.has(t))) return true;
      const info = matchingInfosForSku(matchingGroups, title)[0];
      if (
        info?.groupSkus?.some((s) => {
          const t = typeof s === 'string' ? s : s?.title;
          return t && allowedSkuTitles.has(t);
        })
      ) {
        return true;
      }
      return false;
    };
    const skuInFilteredSet = (title) => !allowedSkuTitles || !title || allowedSkuTitles.has(title);

    // For country path, skip product-related checks — we use ALL sourceFiltered items
    const isCountryView = groupBy === 'countries' || groupBy === 'all';
    if (!isCountryView && effectiveProducts.length === 0) return null;
    
    const isIndexMode = topFilters.currencyIndex === 'Index' && activeBaseProduct;
    const baseProductImputedGapLabels = [];
    const baseProductGapSuggestions = [];
    const ffOn = TEMP_FORWARD_FILL_ENABLED;
    const priceGapFill = ffOn ? 'sku' : 'none';
    const plainGapFill = ffOn ? 'plain' : 'none';

    // Group products by matching groups first to determine which products to include
    const productsByGroupMap = new Map(); // groupName -> productNames[]
    const ungroupedProductsList = [];
    const allProductNamesForData = new Set(
      effectiveProducts.filter((t) => skuAllowed(t) && (skuInFilteredSet(t) || groupNameToProducts.has(t) || matchingInfosForSku(matchingGroups, t)[0]))
    );
    
    effectiveProducts.forEach((productName) => {
      if (!skuAllowed(productName)) return;
      const groupInfo = matchingInfosForSku(matchingGroups, productName)[0];
      if (groupInfo && groupInfo.groupName) {
        const groupName = groupInfo.groupName;
        if (!productsByGroupMap.has(groupName)) {
          const skuTitles = (groupInfo.groupSkus || [])
            .map((sku) => (typeof sku === 'string' ? sku : sku?.title))
            .filter((t) => t && skuInFilteredSet(t));
          productsByGroupMap.set(groupName, skuTitles);
        }
        (groupInfo.groupSkus || []).forEach((sku) => {
          const skuTitle = typeof sku === 'string' ? sku : sku?.title;
          if (skuTitle && skuInFilteredSet(skuTitle)) allProductNamesForData.add(skuTitle);
        });
      } else if (groupNameToProducts.has(productName)) {
        const groupName = productName;
        const productNames = (groupNameToProducts.get(groupName) || []).filter(skuInFilteredSet);
        if (!productsByGroupMap.has(groupName)) {
          productsByGroupMap.set(groupName, productNames);
        }
        productNames.forEach((t) => allProductNamesForData.add(t));
      } else if (skuInFilteredSet(productName)) {
        ungroupedProductsList.push(productName);
        allProductNamesForData.add(productName);
      }
    });
    
    // Use sourceFiltered (filteredData) so chart data matches filtered results
    // Include all products that might be in groups
    let chartData = sourceFiltered.filter(item => allProductNamesForData.has(item.title));

    // Build product title -> image URL map (first occurrence per title)
    const productTitleToImage = new Map();
    sourceFiltered.forEach((item) => {
      const title = item?.title;
      if (!title || productTitleToImage.has(title)) return;
      // Try image, box_image_urls (GLO), image_urls (IQOS), media_gallery_urls (GLO/Italy),
      // resolving each in priority order so an unresolvable earlier field never hides a valid later one.
      const url = resolveProductImageUrlFromFields(item.image, item.box_image_urls, item.image_urls, item.media_gallery_urls);
      if (url) productTitleToImage.set(title, url);
    });

    // If index mode, also include base product data (product or group)
    if (isIndexMode && activeBaseProduct) {
      const indexBaseTitles = new Set();
      const registerIndexBase = (baseName) => {
        if (!baseName) return;
        const groupList = groupNameToProducts.get(baseName);
        if (groupList?.length) groupList.forEach((t) => indexBaseTitles.add(t));
        indexBaseTitles.add(baseName);
      };
      if (baseProduct === BASE_PRODUCT_AUTO && autoBaseProductByCountry?.size) {
        autoBaseProductByCountry.forEach(registerIndexBase);
      } else {
        registerIndexBase(activeBaseProduct);
      }
      indexBaseTitles.forEach((t) => {
        if (!allProductNamesForData.has(t)) {
          const extra = sourceFiltered.filter((item) => item.title === t);
          chartData = [...chartData, ...extra];
          allProductNamesForData.add(t);
        }
      });
    }

    // For product path, require chartData; country path uses sourceFiltered directly
    if (!isCountryView && chartData.length === 0) return null;

    // Build (title -> sorted entries) and unique date keys in one pass
    const neededTitles = allProductNamesForData;

    const rowsForSeriesBuild = (() => {
      if (!(isIndexMode && activeBaseProduct && baseFallbackSource?.length)) return chartData;
      const seen = new Set();
      const merged = [];
      [...chartData, ...baseFallbackSource].forEach((item) => {
        const key = `${item?.country || ''}\x00${item?.retailer || ''}\x00${item?.title || ''}\x00${item?.updated_at || item?.date || ''}`;
        if (seen.has(key)) return;
        seen.add(key);
        merged.push(item);
      });
      return merged;
    })();

    const entriesByTitle = new Map();
    const allDates = new Set();
    rowsForSeriesBuild.forEach((item) => {
      const title = item?.title;
      if (!title || !neededTitles.has(title) || !item?.updated_at) return;
      let dateKey = null;
      let ts = null;
      try {
        const d = new Date(item.updated_at);
        if (isNaN(d.getTime())) return;
        ts = d.getTime();
        dateKey = d.toISOString().slice(0, 10); // YYYY-MM-DD
      } catch (e) {
        return;
      }
      allDates.add(dateKey);

      let actual = parseNumericValue(item?.actual_price || item?.price || item?.current_price);
      let regular = parseNumericValue(item?.regular_price || item?.original_price);
      let base = parseNumericValue(
        item?.actual_price ||
        item?.price ||
        item?.current_price ||
        item?.regular_price ||
        item?.original_price
      );

      // Divide by pack size when per-unit mode is active
      if (priceDisplayMode === 'per_unit') {
        const packSize = getItemPackSize(item);
        if (packSize && packSize > 1) {
          if (Number.isFinite(actual)) actual = actual / packSize;
          if (Number.isFinite(regular)) regular = regular / packSize;
          if (Number.isFinite(base)) base = base / packSize;
        }
      }

      // Keep index math in the same currency domain as visible chart series
      // (important when fallback base rows come from raw/unfiltered source).
      const srcCurrency = normalizeCurrencyCode(item?.currency);
      if (normalizeToEur) {
        if (Number.isFinite(actual)) actual = convertToEur(actual, srcCurrency);
        if (Number.isFinite(regular)) regular = convertToEur(regular, srcCurrency);
        if (Number.isFinite(base)) base = convertToEur(base, srcCurrency);
      } else if (selectedCurrency) {
        const targetCurrency = normalizeCurrencyCode(selectedCurrency);
        if (targetCurrency) {
          if (Number.isFinite(actual)) actual = convertToCurrency(actual, srcCurrency, targetCurrency);
          if (Number.isFinite(regular)) regular = convertToCurrency(regular, srcCurrency, targetCurrency);
          if (Number.isFinite(base)) base = convertToCurrency(base, srcCurrency, targetCurrency);
        }
      }

      const list = entriesByTitle.get(title) || [];
      const { actual: normalizedActual, regular: normalizedRegular } =
        normalizeActualAndRegularPrices(actual, regular);
      list.push({
        title,
        dateKey,
        ts,
        country: item?.country || '',
        retailer: (item?.retailer && String(item.retailer).trim()) || '',
        actual: normalizedActual,
        regular: normalizedRegular,
        base: Number.isFinite(base) ? base : 0,
        stockClass: classifyStockClassFromItem(item),
      });
      entriesByTitle.set(title, list);
    });

    let earliestPriceDateKey = null;
    entriesByTitle.forEach((list) => {
      list.forEach((e) => {
        if (e.actual > 0 || e.regular > 0 || e.base > 0) {
          if (!earliestPriceDateKey || e.dateKey < earliestPriceDateKey) {
            earliestPriceDateKey = e.dateKey;
          }
        }
      });
    });

    // Sorted calendar days from data; union with picker range. `availableDates` / the slider only contain
    // days that appear in the data, so dateRange[1] often equals the last data day (e.g. 03-01) and
    // 03-02 never appears — extend the chart end by one day after the last observed data day when the
    // picker end is not already past that day, so forward-fill can render the next calendar day.
    let dates = Array.from(allDates).sort();
    if (dateRange?.[0]?.isValid?.() && dateRange?.[1]?.isValid?.()) {
      const fullSet = new Set(dates);
      let end = dateRange[1];
      if (allDates.size > 0) {
        const sortedData = [...allDates].sort();
        const maxDataStr = sortedData[sortedData.length - 1];
        const maxDataDay = dayjs(maxDataStr);
        if (maxDataDay.isValid() && !end.isAfter(maxDataDay)) {
          end = maxDataDay.add(1, 'day');
        }
      }
      let d = dateRange[0];
      // Do not extend the x-axis before the first day any series has a positive price — avoids a long
      // empty gap when the date slider starts earlier than usable prices (e.g. July–Feb with data from February).
      const clipStartKey = earliestPriceDateKey || (dates.length > 0 ? dates[0] : null);
      if (clipStartKey) {
        const firstDataDay = dayjs(clipStartKey);
        if (firstDataDay.isValid() && d.isBefore(firstDataDay, 'day')) {
          d = firstDataDay;
        }
      }
      while (!d.isAfter(end)) {
        fullSet.add(d.format('YYYY-MM-DD'));
        d = d.add(1, 'day');
      }
      dates = Array.from(fullSet).sort();
    }

    // If no dates found, return null
    if (dates.length === 0) return null;

    const datasets = [];

    const availSel = normalizeAvailabilitySelection(topFilters.availability);
    const wantAvailChart = availSel.includes('Available');
    const wantOosChart = availSel.includes('Out of Stock');
    const stockChartSplit = wantAvailChart && wantOosChart;
    const stockChartSingleMask = wantAvailChart !== wantOosChart;
    const useStockGaps = stockChartSplit || stockChartSingleMask;

    // Starter kit / Reg. Price: Product Detail flag OR category "Starter Kits" (same rule as Products Overview export)
    const firstRowByTitle = new Map();
    sourceFiltered.forEach((item) => {
      const t = item?.title;
      if (!t || firstRowByTitle.has(t)) return;
      firstRowByTitle.set(t, item);
    });
    const isStarterKitProduct = (title) => {
      if (!title) return false;
      if (productFlagsFromStorage[title]?.isStarterKitOrRegistration) return true;
      const row = firstRowByTitle.get(title);
      if (!row) return false;
      const catNorm = String(getEffectiveCategoryGroup(row) || '').trim().toLowerCase();
      return catNorm === 'starter kits' || catNorm === 'starter kit';
    };
    const isBundleProduct = (title) => {
      if (!title || isStarterKitProduct(title)) return false;
      const row = firstRowByTitle.get(title);
      if (!row) return false;
      return isBundleCategoryGroup(getEffectiveCategoryGroup(row));
    };

    const pairKeyOfEntry = (e) => `${e.country || ''}\x00${(e.retailer || '').trim()}`;

    /** Per (country, retailer): sorted market days + titles present on each day (any SKU scrape = market day). */
    const buildPairMarketMapsFromRows = (rows) => {
      const pairMarketDaysScratch = new Map();
      const pairDateToTitles = new Map();
      (rows || []).forEach((item) => {
        const country = item?.country;
        const title = item?.title;
        const retailer = (item?.retailer && String(item.retailer).trim()) || '';
        if (!country || !title || !item?.updated_at) return;
        let dateKey;
        try {
          const d = new Date(item.updated_at);
          if (isNaN(d.getTime())) return;
          dateKey = d.toISOString().slice(0, 10);
        } catch (e) {
          return;
        }
        const pk = `${country}\x00${retailer}`;
        if (!pairMarketDaysScratch.has(pk)) pairMarketDaysScratch.set(pk, new Set());
        pairMarketDaysScratch.get(pk).add(dateKey);
        const dkey = `${pk}\x00${dateKey}`;
        if (!pairDateToTitles.has(dkey)) pairDateToTitles.set(dkey, new Set());
        pairDateToTitles.get(dkey).add(title);
      });
      const pairMarketDaysSorted = new Map();
      pairMarketDaysScratch.forEach((set, pk) => {
        pairMarketDaysSorted.set(pk, Array.from(set).sort());
      });
      return { pairMarketDaysSorted, pairDateToTitles };
    };

    /** Union chart rows with full-dataset rows in the same countries (for delist / market-day detection). */
    const buildMarketDayContextRows = (chartRows, fallbackRows) => {
      const countriesInScope = new Set((chartRows || []).map((i) => i?.country).filter(Boolean));
      if (!countriesInScope.size) return chartRows || [];
      const seen = new Set();
      const out = [];
      const push = (item) => {
        if (!item?.country || !item?.title || !item?.updated_at) return;
        if (!countriesInScope.has(item.country)) return;
        const key = `${item.country}\x00${item.retailer || ''}\x00${item.title}\x00${item.updated_at || item.date || ''}`;
        if (seen.has(key)) return;
        seen.add(key);
        out.push(item);
      };
      (chartRows || []).forEach(push);
      (fallbackRows || []).forEach(push);
      return out;
    };

    const marketDayContextRows = buildMarketDayContextRows(sourceFiltered, baseFallbackSource);
    const { pairMarketDaysSorted, pairDateToTitles } = buildPairMarketMapsFromRows(marketDayContextRows);

    // Sort entries once per title (ascending by timestamp); merge duplicate dateKey rows only within same (country, retailer)
    entriesByTitle.forEach((list, title) => {
      list.sort((a, b) => a.ts - b.ts);
      const merged = [];
      for (const e of list) {
        const last = merged[merged.length - 1];
        if (last && last.dateKey === e.dateKey && pairKeyOfEntry(last) === pairKeyOfEntry(e)) {
          last.actual = e.actual;
          last.regular = e.regular;
          last.base = e.base;
          last.ts = e.ts;
          last.stockClass = mergeStockClassesList([last.stockClass || 'unknown', e.stockClass || 'unknown']);
        } else {
          merged.push({ ...e });
        }
      }
      entriesByTitle.set(title, merged);
    });

    const fillForwardSeries = (entries, valueKey) => {
      const entrySeriesValue = (entry, key) => {
        if (!entry) return 0;
        if (key === 'regular') {
          if (entry.regular > 0) return entry.regular;
          if (entry.actual > 0) return entry.actual;
          return 0;
        }
        if (key === 'actual') return entry.actual > 0 ? entry.actual : 0;
        const v = entry[key];
        return Number.isFinite(v) && v > 0 ? v : 0;
      };
      let idx = 0;
      let last = null;
      const out = new Array(dates.length);
      for (let i = 0; i < dates.length; i += 1) {
        const dateKey = dates[i];
        while (idx < entries.length && entries[idx].dateKey <= dateKey) {
          last = entries[idx];
          idx += 1;
        }
        const value = last ? entrySeriesValue(last, valueKey) : 0;
        out[i] = Number.isFinite(value) ? value : 0;
      }
      return out;
    };

    /** For each date index pick the price bucket with the most contributing variants (titles/keys). */
    const pickDominantSeriesByVariantCount = (seriesArrays, dateKeys) => {
      if (!Array.isArray(seriesArrays) || seriesArrays.length === 0) {
        return (dateKeys || []).map(() => 0);
      }
      const length = dateKeys?.length || seriesArrays[0]?.length || 0;
      const out = new Array(length).fill(0);
      for (let i = 0; i < length; i += 1) {
        const buckets = new Map(); // roundedPrice -> { count, sum }
        seriesArrays.forEach((arr) => {
          const v = arr?.[i];
          if (!Number.isFinite(v) || v <= 0) return;
          const key = Number(v).toFixed(4);
          const cur = buckets.get(key) || { count: 0, sum: 0 };
          cur.count += 1;
          cur.sum += v;
          buckets.set(key, cur);
        });
        if (buckets.size === 0) {
          out[i] = 0;
          continue;
        }
        let best = null;
        buckets.forEach((info, key) => {
          if (!best || info.count > best.count) {
            best = { key, count: info.count, sum: info.sum };
          }
        });
        out[i] = best && best.count > 0 ? (best.sum / best.count) : 0;
      }
      return out;
    };

    const fillForwardStockClass = (entries) => {
      let idx = 0;
      let last = null;
      const out = new Array(dates.length);
      for (let i = 0; i < dates.length; i += 1) {
        const dateKey = dates[i];
        while (idx < entries.length && entries[idx].dateKey <= dateKey) {
          last = entries[idx];
          idx += 1;
        }
        out[i] = last ? (last.stockClass || 'unknown') : 'unknown';
      }
      return out;
    };

    /** When base (100%) is missing on calendar days, use last known base strictly before, else first known after. */
    const imputeBaseSeriesFromNearestDays = (series, dateKeys) => {
      const keys = dateKeys && dateKeys.length ? dateKeys : dates;
      if (!series || !series.length || series.length !== keys.length) {
        return { series: series || [], gapRanges: [], observedRange: null };
      }
      const n = series.length;
      const orig = series.map((v) => (Number.isFinite(v) && v > 0 ? v : 0));
      const out = orig.slice();
      const synthetic = Array(n).fill(false);
      for (let i = 0; i < n; i += 1) {
        if (out[i] > 0) continue;
        let prevVal = 0;
        for (let j = i - 1; j >= 0; j -= 1) {
          if (orig[j] > 0) {
            prevVal = orig[j];
            break;
          }
        }
        if (prevVal > 0) {
          out[i] = prevVal;
          synthetic[i] = true;
          continue;
        }
        let nextVal = 0;
        for (let k = i + 1; k < n; k += 1) {
          if (orig[k] > 0) {
            nextVal = orig[k];
            break;
          }
        }
        if (nextVal > 0) {
          out[i] = nextVal;
          synthetic[i] = true;
        }
      }
      const gapRanges = [];
      let runStart = null;
      for (let i = 0; i <= n; i += 1) {
        const inRun = i < n && synthetic[i];
        if (inRun) {
          if (runStart === null) runStart = i;
        } else if (runStart !== null) {
          gapRanges.push({ startKey: keys[runStart], endKey: keys[i - 1] });
          runStart = null;
        }
      }
      let firstObs = null;
      let lastObs = null;
      for (let i = 0; i < n; i += 1) {
        if (orig[i] > 0) {
          if (firstObs === null) firstObs = i;
          lastObs = i;
        }
      }
      const observedRange =
        firstObs !== null && lastObs !== null
          ? { startKey: keys[firstObs], endKey: keys[lastObs] }
          : null;
      return { series: out, gapRanges, observedRange };
    };

    const formatBaseGapDate = (isoDay) => {
      if (!isoDay) return '';
      const d = dayjs(isoDay);
      if (!d.isValid()) return String(isoDay);
      return d.toDate().toLocaleDateString();
    };

    /**
     * Per-(country, retailer) row index for exact same-SKU observations on a chart day.
     */
    const indexEntriesByDateKey = (entries) => {
      const byDate = new Map();
      const list = entries || [];
      for (let ei = 0; ei < list.length; ei += 1) {
        const e = list[ei];
        const d = e.dateKey;
        let bucket = byDate.get(d);
        if (!bucket) {
          bucket = [];
          byDate.set(d, bucket);
        }
        bucket.push(e);
      }
      return byDate;
    };

    const mergePairRowsForTitle = (rows) => {
      const sorted = [...rows].sort((a, b) => a.ts - b.ts);
      const merged = [];
      for (const e of sorted) {
        const last = merged[merged.length - 1];
        if (last && last.dateKey === e.dateKey) {
          last.actual = e.actual;
          last.regular = e.regular;
          last.base = e.base;
          last.ts = e.ts;
          last.stockClass = mergeStockClassesList([last.stockClass || 'unknown', e.stockClass || 'unknown']);
        } else {
          merged.push({ ...e });
        }
      }
      return merged;
    };

    const entrySeriesValue = (entry, key) => {
      if (!entry) return 0;
      if (key === 'regular') {
        if (entry.regular > 0) return entry.regular;
        if (entry.actual > 0) return entry.actual;
        return 0;
      }
      if (key === 'actual') return entry.actual > 0 ? entry.actual : 0;
      const v = entry[key];
      return Number.isFinite(v) && v > 0 ? v : 0;
    };

    const groupEntriesByCountryRetailer = (list) => {
      const byPair = new Map();
      for (let ei = 0; ei < list.length; ei += 1) {
        const e = list[ei];
        const c = e.country || '';
        const r = (e.retailer || '').trim();
        const key = `${c}\x00${r}`;
        let bucket = byPair.get(key);
        if (!bucket) {
          bucket = { country: c, retailer: r, rows: [] };
          byPair.set(key, bucket);
        }
        bucket.rows.push(e);
      }
      return byPair;
    };

    const priceFromEntriesOnDate = (byDate, dateKey, valueKey) => {
      const hits = byDate.get(dateKey);
      if (!hits || !hits.length) return 0;
      const vals = hits.map((h) => entrySeriesValue(h, valueKey)).filter((v) => Number.isFinite(v) && v > 0);
      return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
    };

    const diffCalendarDays = (fromKey, toKey) => {
      const a = dayjs(fromKey);
      const b = dayjs(toKey);
      if (!a.isValid() || !b.isValid()) return Infinity;
      return b.diff(a, 'day');
    };

    /** Sorted unique dates where this exact SKU has any row for the pair. */
    const skuObservationDatesForPair = (merged) => {
      const seen = new Set();
      const out = [];
      for (const e of merged || []) {
        const dk = e?.dateKey;
        if (!dk || seen.has(dk)) continue;
        seen.add(dk);
        out.push(dk);
      }
      return out.sort();
    };

    /**
     * True on chart indices between two same-SKU observations when the SKU returns within
     * GAP_FILL_MAX_DAYS after the gap starts (day after the prior observation).
     */
    const buildSkuBridgeableGapMask = (merged, chartDates) => {
      const mask = new Array(chartDates.length).fill(false);
      const obsDates = skuObservationDatesForPair(merged);
      if (obsDates.length < 2) return mask;

      for (let oi = 0; oi < obsDates.length - 1; oi += 1) {
        const prevObs = obsDates[oi];
        const nextObs = obsDates[oi + 1];
        const gapStartKey = dayjs(prevObs).add(1, 'day').format('YYYY-MM-DD');
        if (gapStartKey >= nextObs) continue;
        if (diffCalendarDays(gapStartKey, nextObs) > GAP_FILL_MAX_DAYS) continue;

        for (let i = 0; i < chartDates.length; i += 1) {
          const dk = chartDates[i];
          if (dk > prevObs && dk < nextObs) mask[i] = true;
        }
      }
      return mask;
    };

    const lastSkuObservationBefore = (obsDates, dk) => {
      if (!obsDates?.length || !dk) return null;
      let lo = 0;
      let hi = obsDates.length - 1;
      let ans = -1;
      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (obsDates[mid] < dk) {
          ans = mid;
          lo = mid + 1;
        } else {
          hi = mid - 1;
        }
      }
      return ans >= 0 ? obsDates[ans] : null;
    };

    /**
     * Per (country, retailer): exact when this SKU has a row that day; bridge interior gaps only
     * when the same SKU is observed again within GAP_FILL_MAX_DAYS after the gap starts.
     */
    const segmentSeriesValueByPair = ({
      entries,
      valueKey,
      chartDates,
      pairMarketDaysSortedMap,
      pairDateToTitlesMap,
      currentTitle,
      gapFillMode = 'sku',
    }) => {
      const list = entries || [];
      if (list.length === 0) return chartDates.map(() => 0);

      const pairSeriesArrays = [];
      for (const { country, retailer, rows } of groupEntriesByCountryRetailer(list).values()) {
        const merged = mergePairRowsForTitle(rows);
        const filled = fillForwardSeries(merged, valueKey);
        const byDate = indexEntriesByDateKey(merged);
        const obsDates = skuObservationDatesForPair(merged);
        const bridgeableMask =
          gapFillMode === 'sku' ? buildSkuBridgeableGapMask(merged, chartDates) : null;
        const exact = chartDates.map((dateKey) => priceFromEntriesOnDate(byDate, dateKey, valueKey));
        pairSeriesArrays.push(
          chartDates.map((dk, i) => {
            const hits = byDate.get(dk);
            if (hits && hits.length) return exact[i];
            if (gapFillMode === 'none') return 0;
            if (gapFillMode === 'plain') return filled[i];
            if (!bridgeableMask?.[i]) return 0;
            const prevObs = lastSkuObservationBefore(obsDates, dk);
            if (!prevObs) return 0;
            const ffPrice = priceFromEntriesOnDate(byDate, prevObs, valueKey);
            return ffPrice > 0 ? ffPrice : filled[i];
          })
        );
      }

      if (pairSeriesArrays.length === 1) return pairSeriesArrays[0];

      return chartDates.map((_, i) => {
        const vals = [];
        for (const s of pairSeriesArrays) {
          const v = s[i];
          if (Number.isFinite(v) && v > 0) vals.push(v);
        }
        return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
      });
    };

    const segmentStockClassByPair = ({
      entries,
      chartDates,
      pairMarketDaysSortedMap,
      pairDateToTitlesMap,
      currentTitle,
      gapFillMode = 'sku',
    }) => {
      const list = entries || [];
      if (list.length === 0) return chartDates.map(() => 'unknown');

      const pairStockArrays = [];
      for (const { country, retailer, rows } of groupEntriesByCountryRetailer(list).values()) {
        const merged = mergePairRowsForTitle(rows);
        const filled = fillForwardStockClass(merged);
        const byDate = indexEntriesByDateKey(merged);
        const obsDates = skuObservationDatesForPair(merged);
        const bridgeableMask =
          gapFillMode === 'sku' ? buildSkuBridgeableGapMask(merged, chartDates) : null;
        const exact = chartDates.map((dateKey) => {
          const hits = byDate.get(dateKey);
          if (!hits || !hits.length) return 'unknown';
          return mergeStockClassesList(hits.map((h) => h.stockClass || 'unknown'));
        });
        pairStockArrays.push(
          chartDates.map((dk, i) => {
            const hits = byDate.get(dk);
            if (hits && hits.length) return exact[i];
            if (gapFillMode === 'none') return 'unknown';
            if (gapFillMode === 'plain') return filled[i];
            if (!bridgeableMask?.[i]) return 'unknown';
            const prevObs = lastSkuObservationBefore(obsDates, dk);
            if (!prevObs) return 'unknown';
            const lmHits = byDate.get(prevObs);
            if (lmHits && lmHits.length) {
              return mergeStockClassesList(lmHits.map((h) => h.stockClass || 'unknown'));
            }
            return filled[i];
          })
        );
      }

      if (pairStockArrays.length === 1) return pairStockArrays[0];

      return chartDates.map((_, i) => {
        const classes = pairStockArrays.map((arr) => arr[i] || 'unknown');
        return mergeStockClassesList(classes);
      });
    };

    const segmentSeriesValueForTitle = (entries, valueKey) =>
      segmentSeriesValueByPair({
        entries,
        valueKey,
        chartDates: dates,
        pairMarketDaysSortedMap: pairMarketDaysSorted,
        pairDateToTitlesMap: pairDateToTitles,
        currentTitle: entries?.[0]?.title,
        gapFillMode: priceGapFill,
      });

    const segmentStockClassForTitle = (entries) =>
      segmentStockClassByPair({
        entries,
        chartDates: dates,
        pairMarketDaysSortedMap: pairMarketDaysSorted,
        pairDateToTitlesMap: pairDateToTitles,
        currentTitle: entries?.[0]?.title,
        gapFillMode: priceGapFill,
      });

    /** Latest dateKey where any raw row was classified OOS (not forward-filled). Used to end OOS lines there. */
    const getLastOosDateKeyForTitles = (titles) => {
      let max = null;
      for (const t of titles) {
        const list = entriesByTitle.get(t) || [];
        for (const e of list) {
          if (e.stockClass === 'oos' && (!max || e.dateKey > max)) max = e.dateKey;
        }
      }
      return max;
    };

    const maskPriceByStockSeries = (prices, stockArr, segment, dateKeys, lastOosDateKey) =>
      prices.map((p, i) => {
        if (!Number.isFinite(p) || p <= 0) return p;
        const sc = stockArr[i];
        if (segment === 'avail' && !stockClassMatchesAvailChartSegment(sc)) return 0;
        if (segment === 'oos') {
          if (sc !== 'oos') return 0;
          if (lastOosDateKey && dateKeys && dateKeys[i] > lastOosDateKey) return 0;
        }
        return p;
      });

    const mergeStockSeriesForTitles = (titles) => {
      const arrs = titles.map((t) =>
        ffOn
          ? fillForwardStockClass(entriesByTitle.get(t) || [])
          : segmentStockClassForTitle(entriesByTitle.get(t) || [])
      );
      return dates.map((_, i) => mergeStockClassesAtIndex(arrs, i));
    };

    /**
     * Per-title stock split lines: average only SKUs in the segment (not aggregate price × merged mask).
     * `segmentSeriesList` = { actual: number[][], regular: number[][] } aligned with `titlesForStock`.
     */
    const buildProductStockVariants = (titlesForStock, ap, rp, segmentSeriesList) => {
      if (!useStockGaps) return [{ ap, rp, stockSegment: null, suffix: '' }];
      const lastOosDateKey = getLastOosDateKeyForTitles(titlesForStock);

        const buildSegPair = (segment) => {
        const usePerTitleSeg =
          titlesForStock.length > 1 &&
          segmentSeriesList &&
          Array.isArray(segmentSeriesList.actual) &&
          segmentSeriesList.actual.length === titlesForStock.length;
        if (!usePerTitleSeg) {
          const stockMerged = mergeStockSeriesForTitles(titlesForStock);
          return {
            ap: maskPriceByStockSeries(ap, stockMerged, segment, dates, segment === 'oos' ? lastOosDateKey : null),
            rp: maskPriceByStockSeries(rp, stockMerged, segment, dates, segment === 'oos' ? lastOosDateKey : null),
          };
        }
        const actArr = segmentSeriesList.actual;
        const regArr = segmentSeriesList.regular || segmentSeriesList.actual;
        const stockPerTitleSeg = titlesForStock.map((t) =>
          segmentStockClassForTitle(entriesByTitle.get(t) || [])
        );
        const apSeg = dates.map((dk, i) => {
          if (segment === 'oos' && lastOosDateKey && dk > lastOosDateKey) return 0;
          const vals = [];
          for (let j = 0; j < titlesForStock.length; j++) {
            const v = actArr[j][i];
            if (!Number.isFinite(v) || v <= 0) continue;
            const sc = stockPerTitleSeg[j][i] || 'unknown';
            if (segment === 'avail') {
              if (!stockClassMatchesAvailChartSegment(sc)) continue;
            } else if (segment === 'oos') {
              if (sc !== 'oos') continue;
            }
            vals.push(v);
          }
          return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
        });
        const rpSeg = dates.map((dk, i) => {
          if (segment === 'oos' && lastOosDateKey && dk > lastOosDateKey) return 0;
          const vals = [];
          for (let j = 0; j < titlesForStock.length; j++) {
            const v = regArr[j][i];
            if (!Number.isFinite(v) || v <= 0) continue;
            const sc = stockPerTitleSeg[j][i] || 'unknown';
            if (segment === 'avail') {
              if (!stockClassMatchesAvailChartSegment(sc)) continue;
            } else if (segment === 'oos') {
              if (sc !== 'oos') continue;
            }
            vals.push(v);
          }
          return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
        });
        return { ap: apSeg, rp: rpSeg };
      };

      if (stockChartSplit) {
        return [
          { ...buildSegPair('avail'), stockSegment: 'avail', suffix: '' },
          { ...buildSegPair('oos'), stockSegment: 'oos', suffix: '' },
        ];
      }
      const seg = wantAvailChart ? 'avail' : 'oos';
      return [{ ...buildSegPair(seg), stockSegment: seg, suffix: '' }];
    };

    /** Per chart date index: product titles that contribute a positive price (and stock segment when set). */
    const groupedVariantTitlesAtIndexForMerged = (titles, seriesList, getActualAt, stockSegment) => {
      if (!titles || titles.length <= 1) return null;
      const stockPerTitle = titles.map((t) =>
        segmentStockClassForTitle(entriesByTitle.get(t) || [])
      );
      const lastOosDateKey = stockSegment === 'oos' ? getLastOosDateKeyForTitles(titles) : null;
      return dates.map((_, i) =>
        titles.filter((title, j) => {
          const ap = getActualAt(seriesList[j], i);
          if (!Number.isFinite(ap) || ap <= 0) return false;
          if (!stockSegment) return true;
          const sc = stockPerTitle[j][i] || 'unknown';
          if (stockSegment === 'avail') return stockClassMatchesAvailChartSegment(sc);
          if (stockSegment === 'oos') {
            if (sc !== 'oos') return false;
            if (lastOosDateKey && dates[i] > lastOosDateKey) return false;
            return true;
          }
          return true;
        })
      );
    };

    const zeroToNull = (arr) => arr.map((v) => (v > 0 ? v : null));

    // === Country grouping path ===
    if (isCountryView) {
      // Use ALL sourceFiltered items (not just top-N products) so every product for every
      // country contributes to its country average.
      let countrySourceData = [...sourceFiltered];
      const includeBaseInVisibleCountrySeries =
        isIndexMode &&
        baseProduct &&
        selectedBrands.length === 0 &&
        selectedProducts.length === 0;
      if (includeBaseInVisibleCountrySeries) {
        const baseTitlesWithGroup = new Set();
        const registerBaseForMerge = (baseName) => {
          if (!baseName) return;
          const fromGroup = groupNameToProducts.get(baseName) || [baseName];
          fromGroup.forEach((t) => baseTitlesWithGroup.add(t));
          baseTitlesWithGroup.add(baseName);
        };
        if (baseProduct === BASE_PRODUCT_AUTO && autoBaseProductByCountry?.size) {
          autoBaseProductByCountry.forEach(registerBaseForMerge);
        } else {
          registerBaseForMerge(activeBaseProduct);
        }
        const existingTitles = new Set(sourceFiltered.map((i) => i?.title));
        const missingBase = [...baseTitlesWithGroup].every((t) => !existingTitles.has(t));
        if (missingBase && baseFallbackSource?.length) {
          const baseItems = baseFallbackSource.filter((item) =>
            baseTitlesWithGroup.has(item?.title)
          );
          if (baseItems.length > 0) {
            const seen = new Set(countrySourceData.map((item) => `${item?.country}__${item?.title}__${item?.updated_at || item?.date}`));
            baseItems.forEach((item) => {
              const key = `${item?.country}__${item?.title}__${item?.updated_at || item?.date}`;
              if (seen.has(key)) return;
              seen.add(key);
              countrySourceData.push(item);
            });
          }
        }
      }

      const packSizeByTitleForStick = new Map();
      if (isStickRoute) {
        countrySourceData.forEach((item) => {
          const t = item?.title;
          if (!t || packSizeByTitleForStick.has(t)) return;
          packSizeByTitleForStick.set(t, getItemPackSize(item));
        });
      }

      const countryAllDates = new Set();
      const entriesByCountryProduct = new Map(); // `${country}__${title}` -> [{dateKey, ts, actual, regular}]

      countrySourceData.forEach((item) => {
        const country = item?.country;
        const title = item?.title;
        if (!country || !title || !item?.updated_at) return;
        let dateKey, ts;
        try {
          const d = new Date(item.updated_at);
          if (isNaN(d.getTime())) return;
          ts = d.getTime();
          dateKey = d.toISOString().slice(0, 10);
        } catch (e) { return; }

        let actual = parseNumericValue(item?.actual_price || item?.price || item?.current_price);
        let regular = parseNumericValue(item?.regular_price || item?.original_price);
        if (priceDisplayMode === 'per_unit') {
          const packSize = getItemPackSize(item);
          if (packSize && packSize > 1) {
            if (Number.isFinite(actual)) actual /= packSize;
            if (Number.isFinite(regular)) regular /= packSize;
          }
        }

        const { actual: actualVal, regular: regularVal } = normalizeActualAndRegularPrices(actual, regular);

        // Skip entries with no valid price — a zero entry would corrupt fill-forward
        // and cause the line to disappear for all subsequent dates.
        if (actualVal === 0 && regularVal === 0) return;

        countryAllDates.add(dateKey);

        const key = `${country}__${title}`;
        const list = entriesByCountryProduct.get(key) || [];
        list.push({
          dateKey,
          ts,
          country,
          retailer: (item?.retailer && String(item.retailer).trim()) || '',
          actual: actualVal,
          regular: regularVal,
          stockClass: classifyStockClassFromItem(item),
        });
        entriesByCountryProduct.set(key, list);
      });

      // Use full date range from picker so x-axis matches product view and lines extend
      // through January when data exists in other countries/products (fill-forward handles gaps)
      let countryDates = Array.from(countryAllDates).sort();
      if (dateRange?.[0]?.isValid?.() && dateRange?.[1]?.isValid?.()) {
        const rangeStart = dateRange[0].format('YYYY-MM-DD');
        const rangeEnd = dateRange[1].format('YYYY-MM-DD');
        const fullSet = new Set(countryDates);
        let d = dateRange[0];
        if (countryDates.length > 0) {
          const firstDataDay = dayjs(countryDates[0]);
          if (firstDataDay.isValid() && d.isBefore(firstDataDay, 'day')) {
            d = firstDataDay;
          }
        }
        while (!d.isAfter(dateRange[1])) {
          fullSet.add(d.format('YYYY-MM-DD'));
          d = d.add(1, 'day');
        }
        countryDates = Array.from(fullSet).sort();
      }
      if (countryDates.length === 0) return null;

      // Sort each product's entries once
      entriesByCountryProduct.forEach(list => list.sort((a, b) => a.ts - b.ts));

      const splitCountryProductKey = (pk) => {
        const ix = pk.indexOf('__');
        if (ix === -1) return { country: '', title: pk };
        return { country: pk.slice(0, ix), title: pk.slice(ix + 2) };
      };

      /** Per (country, retailer) from full market context — delist needs every SKU seen on scrape days. */
      const countryMarketDayRows = buildMarketDayContextRows(countrySourceData, baseFallbackSource);
      const { pairMarketDaysSorted: crPairMarketDaysSorted, pairDateToTitles: crPairDateToTitles } =
        buildPairMarketMapsFromRows(countryMarketDayRows);

      /** Index / aggregated series only — no per-SKU last-market-day gate, but delist still ends the line. */
      const fillForwardCountryPlain = (entries, valueKey, title = null) =>
        segmentSeriesValueByPair({
          entries,
          valueKey,
          chartDates: countryDates,
          pairMarketDaysSortedMap: crPairMarketDaysSorted,
          pairDateToTitlesMap: crPairDateToTitles,
          currentTitle: title,
          gapFillMode: plainGapFill,
        });

      const fillForwardCountry = (entries, valueKey, _country, title) =>
        segmentSeriesValueByPair({
          entries,
          valueKey,
          chartDates: countryDates,
          pairMarketDaysSortedMap: crPairMarketDaysSorted,
          pairDateToTitlesMap: crPairDateToTitles,
          currentTitle: title,
          gapFillMode: priceGapFill,
        });

      const fillForwardCountryStock = (entries, _country, title) =>
        segmentStockClassByPair({
          entries,
          chartDates: countryDates,
          pairMarketDaysSortedMap: crPairMarketDaysSorted,
          pairDateToTitlesMap: crPairDateToTitles,
          currentTitle: title,
          gapFillMode: priceGapFill,
        });

      // Build per-country base series for Index mode (HQ: each line uses base product in its country).
      // Base fallback rows are used only for baseline computation, never for displayed series.
      const baseSeriesByCountry = new Map();
      if (isIndexMode && activeBaseProduct) {
        const resolveBaseTitlesForCountry = (countryCode) => {
          const countryBase = resolveIndexBaseProductForCountry(countryCode);
          if (!countryBase) return [];
          const exactInCountry =
            entriesByCountryProduct.has(`${countryCode}__${countryBase}`) ||
            !!(baseFallbackSource || []).find(
              (item) => item?.country === countryCode && item?.title === countryBase
            );
          if (exactInCountry) return [countryBase];
          const fromGroup = groupNameToProducts.get(countryBase);
          if (fromGroup && fromGroup.length > 0) {
            return fromGroup.includes(countryBase) ? fromGroup : [...fromGroup, countryBase];
          }
          return [countryBase];
        };
        const countrySetForBase = new Set(Array.from(entriesByCountryProduct.keys()).map((k) => k.split('__')[0]).filter(Boolean));
        if (baseFallbackSource?.length) {
          baseFallbackSource.forEach((item) => {
            if (!item?.country) return;
            countrySetForBase.add(item.country);
          });
        }
        countrySetForBase.forEach((country) => {
          const baseTitlesWithGroup = resolveBaseTitlesForCountry(country);
          const baseKeys = baseTitlesWithGroup.map((t) => `${country}__${t}`).filter((k) => entriesByCountryProduct.has(k));
          if (baseKeys.length > 0) {
            const perKeySeries = baseKeys.map((key) => {
              const entries = entriesByCountryProduct.get(key) || [];
              const { title: t0 } = splitCountryProductKey(key);
              return fillForwardCountryPlain(entries, 'actual', t0);
            });
            baseSeriesByCountry.set(
              country,
              pickDominantSeriesByVariantCount(perKeySeries, countryDates)
            );
            return;
          }
          // Fallback when the base keys are fully filtered out from visible rows:
          // build per-title base series, then apply the same dominant-variant logic as the visible path.
          const fallbackEntriesByTitle = new Map();
          if (baseFallbackSource?.length) {
            baseFallbackSource.forEach((item) => {
              if (!item || item.country !== country) return;
              if (!baseTitlesWithGroup.includes(item.title)) return;
              const rawDate = item.updated_at || item.date;
              const dateObj = rawDate ? new Date(rawDate) : null;
              if (!dateObj || Number.isNaN(dateObj.getTime())) return;
              const dateKey = dateObj.toISOString().slice(0, 10);
              let actual = parseNumericValue(item?.actual_price || item?.price || item?.current_price);
              let regular = parseNumericValue(item?.regular_price || item?.original_price);
              if (priceDisplayMode === 'per_unit') {
                const packSize = getItemPackSize(item);
                if (packSize && packSize > 1) {
                  if (Number.isFinite(actual)) actual /= packSize;
                  if (Number.isFinite(regular)) regular /= packSize;
                }
              }
              const srcCurrency = normalizeCurrencyCode(item?.currency);
              if (normalizeToEur) {
                if (Number.isFinite(actual)) actual = convertToEur(actual, srcCurrency);
                if (Number.isFinite(regular)) regular = convertToEur(regular, srcCurrency);
              } else if (selectedCurrency) {
                const targetCurrency = normalizeCurrencyCode(selectedCurrency);
                if (targetCurrency) {
                  if (Number.isFinite(actual)) actual = convertToCurrency(actual, srcCurrency, targetCurrency);
                  if (Number.isFinite(regular)) regular = convertToCurrency(regular, srcCurrency, targetCurrency);
                }
              }
              const price = Number.isFinite(actual) && actual > 0
                ? actual
                : (Number.isFinite(regular) && regular > 0 ? regular : 0);
              if (price <= 0) return;
              const title = item.title;
              const list = fallbackEntriesByTitle.get(title) || [];
              list.push({
                dateKey,
                ts: dateObj.getTime(),
                actual: price,
              });
              fallbackEntriesByTitle.set(title, list);
            });
          }
          if (fallbackEntriesByTitle.size > 0) {
            const perTitleSeries = Array.from(fallbackEntriesByTitle.values()).map((entries) => {
              const sorted = [...entries].sort((a, b) => a.ts - b.ts);
              return fillForwardCountryPlain(sorted, 'actual');
            });
            baseSeriesByCountry.set(
              country,
              pickDominantSeriesByVariantCount(perTitleSeries, countryDates)
            );
          }
        });
        if (isIndexMode && activeBaseProduct && baseSeriesByCountry.size > 0 && ffOn) {
          baseSeriesByCountry.forEach((arr, country) => {
            if (!arr || arr.length !== countryDates.length) return;
            const { series, gapRanges, observedRange } = imputeBaseSeriesFromNearestDays(arr, countryDates);
            baseSeriesByCountry.set(country, series);
            if (!gapRanges.length) return;
            const countryLabel = formatCountryName(country) || String(country || '').toUpperCase();
            gapRanges.forEach((r, gapIdx) => {
              const line = `Base product (${countryLabel}) was not available in date range: ${formatBaseGapDate(r.startKey)} – ${formatBaseGapDate(r.endKey)}`;
              baseProductImputedGapLabels.push(line);
              baseProductGapSuggestions.push({
                id: `idx-gap-${country}-${r.startKey}-${r.endKey}-${gapIdx}`,
                summaryLine: line,
                countryCode: country || null,
                gapStartKey: r.startKey,
                gapEndKey: r.endKey,
                suggestedStartKey: observedRange?.startKey ?? null,
                suggestedEndKey: observedRange?.endKey ?? null,
              });
            });
          });
        }
      }

      /** Per-product line series (same transforms as chart) for clustering country × SKU keys. */
      const getCountrySeriesForKey = (country, pk) => {
        const { country: c0, title: t0 } = splitCountryProductKey(pk);
        const filledActual = [
          fillForwardCountry(entriesByCountryProduct.get(pk) || [], 'actual', c0 || country, t0),
        ];
        const filledRegular = [
          fillForwardCountry(entriesByCountryProduct.get(pk) || [], 'regular', c0 || country, t0),
        ];
        let actualPrices = countryDates.map((_, i) => {
          const vals = filledActual.map((s) => s[i]).filter((v) => v > 0);
          return vals.length > 0 ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
        });
        let regularPrices = countryDates.map((_, i) => {
          const vals = filledRegular.map((s) => s[i]).filter((v) => v > 0);
          return vals.length > 0 ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
        });
        const bs = baseSeriesByCountry.get(country);
        const useIdx = isIndexMode && bs && bs.length === countryDates.length;
        if (useIdx) {
          actualPrices = actualPrices.map((value, i) => {
            const base = bs[i] || 0;
            if (!base || base <= 0) return 0;
            if (!value || value <= 0) return 0;
            return Math.round((value / base) * 100 * 100) / 100;
          });
          regularPrices = regularPrices.map((value, i) => {
            const base = bs[i] || 0;
            if (!base || base <= 0) return 0;
            if (!value || value <= 0) return 0;
            return Math.round((value / base) * 100 * 100) / 100;
          });
        }
        return { actualPrices, regularPrices };
      };

      // Map product title -> matched group name (for display)
      const productToGroupName = (productTitle) => matchingInfosForSku(matchingGroups, productTitle)[0]?.groupName || productTitle;

      // Collect unique countries and products
      const countrySet = new Set();
      const productSet = new Set();
      entriesByCountryProduct.forEach((_, key) => {
        const [c, p] = key.split('__');
        countrySet.add(c);
        productSet.add(p);
      });
      const uniqueProducts = Array.from(productSet).sort();
      const uniqueGroups = [...new Set(uniqueProducts.map(productToGroupName))].sort();
      const hasMultipleProducts = uniqueProducts.length > 1;

      // For "no grouping" (all): build brand color map from sourceFiltered
      const isNoGrouping = groupBy === 'all';
      let noGroupingColorMap = {};
      if (isNoGrouping) {
        const countryProductBrandKeyMap = {};
        sourceFiltered.forEach((item) => {
          const title = item?.title;
          if (!title || !productSet.has(title) || countryProductBrandKeyMap[title]) return;
          const brandKey = normalizeBrandKey(item?.brand);
          if (brandKey) countryProductBrandKeyMap[title] = brandKey;
        });
        noGroupingColorMap = buildColorMap(uniqueProducts, countryProductBrandKeyMap);
      }

      const getLastOosDateKeyForCountryKeys = (keys) => {
        let max = null;
        for (const pk of keys) {
          const list = entriesByCountryProduct.get(pk) || [];
          for (const e of list) {
            if (e.stockClass === 'oos' && (!max || e.dateKey > max)) max = e.dateKey;
          }
        }
        return max;
      };

      const mergeCountryKeysToSeries = (keys, country) => {
        const filledActual = keys.map((k) => {
          const { country: c0, title: t0 } = splitCountryProductKey(k);
          return fillForwardCountry(entriesByCountryProduct.get(k), 'actual', c0, t0);
        });
        const filledRegular = keys.map((k) => {
          const { country: c0, title: t0 } = splitCountryProductKey(k);
          return fillForwardCountry(entriesByCountryProduct.get(k), 'regular', c0, t0);
        });
        const filledStock = keys.map((k) => {
          const { country: c0, title: t0 } = splitCountryProductKey(k);
          return fillForwardCountryStock(entriesByCountryProduct.get(k) || [], c0, t0);
        });
        const variantCountAtIndex = countryDates.map((_, i) => filledActual.filter((s) => s[i] > 0).length);
        const mergedStock = countryDates.map((_, i) => mergeStockClassesAtIndex(filledStock, i));
        let actualPrices = countryDates.map((_, i) => {
          const vals = filledActual.map((s) => s[i]).filter((v) => v > 0);
          return vals.length > 0 ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
        });
        let regularPrices = countryDates.map((_, i) => {
          const vals = filledRegular.map((s) => s[i]).filter((v) => v > 0);
          return vals.length > 0 ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
        });
        const baseSeries = baseSeriesByCountry.get(country);
        const useIdx = isIndexMode && baseSeries && baseSeries.length === countryDates.length;
        if (useIdx) {
          actualPrices = actualPrices.map((value, i) => {
            const base = baseSeries[i] || 0;
            if (!base || base <= 0) return 0;
            if (!value || value <= 0) return 0;
            return Math.round((value / base) * 100 * 100) / 100;
          });
          regularPrices = regularPrices.map((value, i) => {
            const base = baseSeries[i] || 0;
            if (!base || base <= 0) return 0;
            if (!value || value <= 0) return 0;
            return Math.round((value / base) * 100 * 100) / 100;
          });
        }
        return { actualPrices, regularPrices, variantCountAtIndex, mergedStock };
      };

      const addCountryProductLine = (country, productKeys, countryIdx, productIdxForColor, displayProductName, options = {}) => {
        const {
          precomputedActual,
          precomputedRegular,
          groupedVariantCount = 0,
          groupedVariantTitles = null,
          variantCountAtIndex: variantCountAtIndexOpt,
          legendGroupId: legendGroupIdOpt,
          legendLabel: legendLabelOpt,
          tooltipTitle: tooltipTitleOpt,
          countryChartLineFormat: countryChartLineFormatOpt,
          /** 'promo' = Promo offer: single primary as Promo Price (no regular/promo split). */
          primaryPriceType: primaryPriceTypeOpt = 'actual',
          productGroupName: productGroupNameOpt,
          mergedStock: mergedStockOpt,
          isBundleLine: isBundleLineOpt = false,
        } = options;

        const variantTitlesForPack = productKeys.map((pk) => splitCountryProductKey(pk).title).filter(Boolean);
        const stickPackUnitsBracket =
          isStickRoute && packSizeByTitleForStick && packSizeByTitleForStick.size > 0
            ? formatStickPackUnitsBracketForTitles(variantTitlesForPack, packSizeByTitleForStick)
            : '';

        const isPromoPrimary = primaryPriceTypeOpt === 'promo';

        const countryLabel = formatCountryName(country);
        const countryCode = (country || '').toUpperCase();

        let actualPrices;
        let regularPrices;
        const baseSeries = baseSeriesByCountry.get(country);
        const useIndexMode = isIndexMode && baseSeries && baseSeries.length === countryDates.length;

        const filledActual = productKeys.map((k) => {
          const { country: c0, title: t0 } = splitCountryProductKey(k);
          return fillForwardCountry(entriesByCountryProduct.get(k), 'actual', c0, t0);
        });
        const filledRegular = productKeys.map((k) => {
          const { country: c0, title: t0 } = splitCountryProductKey(k);
          return fillForwardCountry(entriesByCountryProduct.get(k), 'regular', c0, t0);
        });
        const filledStockPerKey = productKeys.map((k) => {
          const { country: c0, title: t0 } = splitCountryProductKey(k);
          return fillForwardCountryStock(entriesByCountryProduct.get(k) || [], c0, t0);
        });
        const lastOosDateKeyCountry = getLastOosDateKeyForCountryKeys(productKeys);

        /** Per key × chart date: values only when a row exists for that exact date (no carry-forward). */
        const exactCellByKeyAndDate = productKeys.map((pk) => {
          const entries = entriesByCountryProduct.get(pk) || [];
          const byDate = new Map();
          for (const e of entries) {
            if (!byDate.has(e.dateKey)) byDate.set(e.dateKey, []);
            byDate.get(e.dateKey).push(e);
          }
          return countryDates.map((dk) => {
            const hits = byDate.get(dk);
            if (!hits || hits.length === 0) {
              return { actual: 0, regular: 0, stockClass: 'unknown', hasObservation: false };
            }
            const actualVals = hits.map((h) => h.actual).filter((v) => Number.isFinite(v) && v > 0);
            const regVals = hits.map((h) => h.regular).filter((v) => Number.isFinite(v) && v > 0);
            const actual = actualVals.length ? actualVals.reduce((a, b) => a + b, 0) / actualVals.length : 0;
            const regular = regVals.length ? regVals.reduce((a, b) => a + b, 0) / regVals.length : 0;
            const stockClass = mergeStockClassesList(hits.map((h) => h.stockClass || 'unknown'));
            return { actual, regular, stockClass, hasObservation: true };
          });
        });

        /**
         * Per SKU×country: exact when this variant has a row that day; on gaps use fillForwardCountry
         * (last market day per retailer union — see skuEligibleForCountryGap).
         */
        const cellForStockSegmentAt = (j, i) => {
          const exact = exactCellByKeyAndDate[j][i];
          const hasRealPrice =
            exact.hasObservation && (exact.actual > 0 || exact.regular > 0);
          if (hasRealPrice) return exact;
          if (!ffOn) {
            return { actual: 0, regular: 0, stockClass: 'unknown', hasObservation: false };
          }
          return {
            actual: filledActual[j][i],
            regular: filledRegular[j][i],
            stockClass: filledStockPerKey[j][i] || 'unknown',
            hasObservation: filledActual[j][i] > 0 || filledRegular[j][i] > 0,
          };
        };

        if (
          precomputedActual &&
          precomputedRegular &&
          precomputedActual.length === countryDates.length &&
          precomputedRegular.length === countryDates.length
        ) {
          actualPrices = [...precomputedActual];
          regularPrices = [...precomputedRegular];
        } else {
          actualPrices = countryDates.map((_, i) => {
            const vals = filledActual.map((s) => s[i]).filter((v) => v > 0);
            return vals.length > 0 ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
          });
          regularPrices = countryDates.map((_, i) => {
            const vals = filledRegular.map((s) => s[i]).filter((v) => v > 0);
            return vals.length > 0 ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
          });

          if (useIndexMode) {
            actualPrices = actualPrices.map((value, i) => {
              const base = baseSeries[i] || 0;
              if (!base || base <= 0) return 0;
              if (!value || value <= 0) return 0;
              return Math.round((value / base) * 100 * 100) / 100;
            });
            regularPrices = regularPrices.map((value, i) => {
              const base = baseSeries[i] || 0;
              if (!base || base <= 0) return 0;
              if (!value || value <= 0) return 0;
              return Math.round((value / base) * 100 * 100) / 100;
            });
          }
        }

        const variantTitlesAtIndexForPrice = countryDates.map((_, i) =>
          productKeys
            .filter((_, j) => filledActual[j][i] > 0)
            .map((pk) => pk.split('__')[1])
        );

        const variantTitlesAtIndexForSv = (sv) => {
          if (!groupedVariantTitles?.length) return null;
          if (!sv.stockSegment) return variantTitlesAtIndexForPrice;
          return countryDates.map((_, i) =>
            productKeys
              .filter((pk, j) => {
                const cell = cellForStockSegmentAt(j, i);
                if (!cell.hasObservation || cell.actual <= 0) return false;
                const sc = cell.stockClass || 'unknown';
                if (sv.stockSegment === 'avail') return stockClassMatchesAvailChartSegment(sc);
                if (sv.stockSegment === 'oos') {
                  if (sc !== 'oos') return false;
                  if (lastOosDateKeyCountry && countryDates[i] > lastOosDateKeyCountry) return false;
                  return true;
                }
                return true;
              })
              .map((pk) => pk.split('__')[1])
          );
        };

        let mergedStockResolved = mergedStockOpt;
        if (productKeys.length > 0 && useStockGaps) {
          mergedStockResolved = countryDates.map((_, i) =>
            mergeStockClassesAtIndex(filledStockPerKey, i)
          );
        }

        /** Segment average: exact row when present; else per-SKU forward-fill (avail and oos). */
        const buildCountrySegmentRawSeries = (segment, priceKey) =>
          countryDates.map((dk, i) => {
            if (segment === 'oos' && lastOosDateKeyCountry && dk > lastOosDateKeyCountry) return 0;
            const vals = [];
            for (let j = 0; j < productKeys.length; j++) {
              const cell = cellForStockSegmentAt(j, i);
              if (!cell.hasObservation) continue;
              const v = priceKey === 'actual' ? cell.actual : cell.regular;
              if (!Number.isFinite(v) || v <= 0) continue;
              const sc = cell.stockClass || 'unknown';
              if (segment === 'avail') {
                if (!stockClassMatchesAvailChartSegment(sc)) continue;
              } else if (segment === 'oos') {
                if (sc !== 'oos') continue;
              } else continue;
              vals.push(v);
            }
            return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
          });

        /** Sparse segment line: no fillForwardCountryLevel (gaps stay gaps). */
        const finalizeCountryPriceSeriesFromRaw = (raw) => {
          let s = raw;
          if (useIndexMode) {
            s = s.map((value, i) => {
              const base = baseSeries[i] || 0;
              if (!base || base <= 0) return 0;
              if (!value || value <= 0) return 0;
              return Math.round((value / base) * 100 * 100) / 100;
            });
          }
          return s;
        };

        const stockVariants = [];
        if (!useStockGaps || !mergedStockResolved) {
          stockVariants.push({ stockSegment: null, suffix: '', ap: actualPrices, rp: regularPrices });
        } else if (stockChartSingleMask) {
          const seg = wantAvailChart ? 'avail' : 'oos';
          stockVariants.push({
            stockSegment: seg,
            suffix: '',
            ap: finalizeCountryPriceSeriesFromRaw(buildCountrySegmentRawSeries(seg, 'actual')),
            rp: finalizeCountryPriceSeriesFromRaw(buildCountrySegmentRawSeries(seg, 'regular')),
          });
        } else {
          stockVariants.push(
            {
              stockSegment: 'avail',
              suffix: '',
              ap: finalizeCountryPriceSeriesFromRaw(buildCountrySegmentRawSeries('avail', 'actual')),
              rp: finalizeCountryPriceSeriesFromRaw(buildCountrySegmentRawSeries('avail', 'regular')),
            },
            {
              stockSegment: 'oos',
              suffix: '',
              ap: finalizeCountryPriceSeriesFromRaw(buildCountrySegmentRawSeries('oos', 'actual')),
              rp: finalizeCountryPriceSeriesFromRaw(buildCountrySegmentRawSeries('oos', 'regular')),
            }
          );
        }

        const firstProductName = productKeys[0]?.split('__')[1];
        const productColor = isNoGrouping && firstProductName
          ? (noGroupingColorMap[firstProductName] || COUNTRY_COLORS[productIdxForColor % COUNTRY_COLORS.length])
          : COUNTRY_COLORS[productIdxForColor % COUNTRY_COLORS.length];
        const displayName = isNoGrouping
          ? `${displayProductName || productToGroupName(firstProductName || '')} (${countryCode})${stickPackUnitsBracket}`
          : hasMultipleProducts
            ? `${countryLabel} - ${displayProductName}`
            : `${countryLabel}${stickPackUnitsBracket}`;
        const legendCountryIsoResolved = resolveCountryIsoForFlagAsset(country);
        const stableCgGroup =
          productGroupNameOpt ||
          String(displayProductName || '').trim() ||
          productToGroupName(firstProductName || '') ||
          'group';

        const productImage = firstProductName ? productTitleToImage.get(firstProductName) : null;
        const priceLabelSuffix = isPromoPrimary
          ? (useIndexMode ? ' - Promo Index' : ' - Promo Price')
          : (useIndexMode ? ' - Index' : ' - Actual Price');
        const listedLabelSuffix = useIndexMode ? ' - Regular Index' : ' - Regular Price';
        const variantMeta = {
          ...(groupedVariantTitles?.length ? { groupedVariantTitles } : {}),
          ...(Array.isArray(variantCountAtIndexOpt) && variantCountAtIndexOpt.length
            ? { variantCountAtIndex: variantCountAtIndexOpt }
            : {}),
          ...(groupedVariantCount > 1 &&
          !(Array.isArray(variantCountAtIndexOpt) && variantCountAtIndexOpt.length)
            ? { groupedVariantCount }
            : {}),
          ...(countryChartLineFormatOpt ? { countryChartLineFormat: true } : {}),
          ...(productGroupNameOpt ? { productGroupName: productGroupNameOpt } : {}),
          ...(isBundleLineOpt ? { isBundleLine: true } : {}),
        };
        const legendGroupId =
          legendGroupIdOpt != null
            ? legendGroupIdOpt
            : countryChartLineFormatOpt
              ? `cg:${country}|${stableCgGroup}`
              : displayName;
        const legendLabelResolved = legendLabelOpt || displayName;
        const tooltipTitleResolved = tooltipTitleOpt || displayName;

        stockVariants.forEach((sv) => {
          const ap = sv.ap;
          const rp = sv.rp;
          const gvCountry = variantTitlesAtIndexForSv(sv);
          const hasActualPrices = ap.some((p) => p > 0);
          const hasRegularDifferent =
            !isPromoPrimary &&
            rp.some((p, i) => p > 0 && ap[i] > 0 && p !== ap[i]);
          const actualPointCount = ap.filter((p) => p > 0).length;
          const spanGapsLine = sv.stockSegment ? false : ffOn;
          // Legend + end-of-chart datalabels use legendLabelResolved; full `label` keeps country + group for Chart.js.
          if (hasActualPrices) {
            datasets.push({
              label: `${displayName}${sv.suffix}${priceLabelSuffix}`,
              displayLabel: legendLabelResolved,
              legendLabel: legendLabelResolved,
              tooltipTitle: tooltipTitleResolved,
              legendGroupId,
              legendCountryIso: legendCountryIsoResolved || undefined,
              legendPrimary: true,
              data: zeroToNull(ap),
              borderColor: productColor,
              backgroundColor: colorWithAlpha(productColor),
              tension: 0,
              spanGaps: spanGapsLine,
              pointRadius: actualPointCount <= 1 ? 3 : 0,
              pointHoverRadius: actualPointCount <= 1 ? 4 : 0,
              priceType: isPromoPrimary ? 'promo' : 'actual',
              productName: displayName,
              regularPrices: rp,
              isIndexMode: useIndexMode,
              baseProductCountry: useIndexMode ? country : undefined,
              productImage,
              ...(stickPackUnitsBracket ? { stickPackUnitsBracket } : {}),
              ...(sv.stockSegment ? { stockSegment: sv.stockSegment } : {}),
              ...variantMeta,
              ...(gvCountry ? { groupedVariantTitlesAtIndex: gvCountry } : {}),
            });
          }
          if (hasRegularDifferent) {
            const listedPrices = rp;
            const regularPointCount = listedPrices.filter((p) => p > 0).length;
            datasets.push({
              label: `${displayName}${sv.suffix}${listedLabelSuffix}`,
              displayLabel: legendLabelResolved,
              legendLabel: legendLabelResolved,
              tooltipTitle: tooltipTitleResolved,
              legendGroupId,
              legendCountryIso: legendCountryIsoResolved || undefined,
              legendPrimary: false,
              data: zeroToNull(listedPrices),
              borderColor: productColor,
              backgroundColor: colorWithAlpha(productColor),
              tension: 0,
              spanGaps: spanGapsLine,
              pointRadius: regularPointCount <= 1 ? 3 : 0,
              pointHoverRadius: regularPointCount <= 1 ? 4 : 0,
              priceType: 'regular',
              productName: displayName,
              actualPrices: ap,
              isIndexMode: useIndexMode,
              baseProductCountry: useIndexMode ? country : undefined,
              productImage,
              ...(stickPackUnitsBracket ? { stickPackUnitsBracket } : {}),
              ...(sv.stockSegment ? { stockSegment: sv.stockSegment } : {}),
              ...variantMeta,
              ...(gvCountry ? { groupedVariantTitlesAtIndex: gvCountry } : {}),
            });

            const promoPrices = ap.map((actual, i) => {
              const regular = rp[i];
              const isPromo = actual > 0 && regular > 0 && actual !== regular;
              if (isPromo) return actual;
              const leftPromo = i > 0 && ap[i - 1] > 0 && rp[i - 1] > 0 && ap[i - 1] !== rp[i - 1];
              const rightPromo = i < ap.length - 1 && ap[i + 1] > 0 && rp[i + 1] > 0 && ap[i + 1] !== rp[i + 1];
              if ((leftPromo || rightPromo) && regular > 0) return regular;
              return 0;
            });
            const promoPointCount = promoPrices.filter((p) => p > 0).length;
            if (promoPointCount > 0) {
              datasets.push({
                label: `${displayName}${sv.suffix}${useIndexMode ? ' - Promo Index' : ' - Promo Price'}`,
                displayLabel: legendLabelResolved,
                legendLabel: legendLabelResolved,
                tooltipTitle: tooltipTitleResolved,
                legendGroupId,
                legendCountryIso: legendCountryIsoResolved || undefined,
                legendPrimary: false,
                data: zeroToNull(promoPrices),
                borderColor: productColor,
                backgroundColor: colorWithAlpha(productColor),
                tension: 0,
                spanGaps: spanGapsLine,
                pointRadius: promoPointCount <= 1 ? 3 : 0,
                pointHoverRadius: promoPointCount <= 1 ? 4 : 0,
                priceType: 'promo',
                productName: displayName,
                regularPrices: rp,
                isIndexMode: useIndexMode,
                baseProductCountry: useIndexMode ? country : undefined,
                productImage,
                ...(stickPackUnitsBracket ? { stickPackUnitsBracket } : {}),
                ...(sv.stockSegment ? { stockSegment: sv.stockSegment } : {}),
                ...variantMeta,
                ...(gvCountry ? { groupedVariantTitlesAtIndex: gvCountry } : {}),
              });
            }
          }
        });
      };

      const lastPositiveInSeries = (arr) => {
        for (let i = arr.length - 1; i >= 0; i--) {
          if (Number.isFinite(arr[i]) && arr[i] > 0) return arr[i];
        }
        return null;
      };

      if (hasMultipleProducts) {
        // One line per distinct price path within the group (variants sharing a path are merged); no group average
        let lineIdx = 0;
        Array.from(countrySet).sort().forEach((country) => {
          uniqueGroups.forEach((groupName) => {
            const productKeys = uniqueProducts
              .filter((productName) => productToGroupName(productName) === groupName)
              .map((productName) => `${country}__${productName}`)
              .filter((key) => entriesByCountryProduct.has(key));
            if (productKeys.length === 0) return;
            const isPromoOfferTitle = (title) =>
              !!(title && productFlagsFromStorage[title]?.isPromoOffer);
            const promoKeys = productKeys.filter((pk) => isPromoOfferTitle(pk.split('__')[1]));
            const normalKeys = productKeys.filter((pk) => !isPromoOfferTitle(pk.split('__')[1]));

            const fmtLabelRoundedForCountryAgreement = (v) => {
              if (v == null || !Number.isFinite(v)) return '';
              if (isIndexMode) return String(Number(v).toFixed(indexDecimalPlaces));
              const maxD = Math.max(0, priceDecimalPlaces);
              const rounded = Math.round(v * Math.pow(10, maxD)) / Math.pow(10, maxD);
              return parseFloat(rounded.toFixed(maxD)).toString();
            };

            /** Half a “tick” at current decimal precision — separates 5 vs 4 GBP but merges near-duplicates after rounding. */
            const toleranceCountryPriceAgreement = () => {
              const maxD = Math.max(0, isIndexMode ? indexDecimalPlaces : priceDecimalPlaces);
              return Math.max(1e-9, 5 * Math.pow(10, -Math.max(maxD, isIndexMode ? 1 : 2)));
            };

            /** Merge two SKUs on one chart line iff every double-active day agrees (within tol), else split (vacuous overlap falls back to same rounded last price). */
            const pairAgreesOverlappingCountrySeries = (ap1, ap2) => {
              const tol = toleranceCountryPriceAgreement();
              let sawOverlap = false;
              const len = Math.min(ap1.length, ap2.length);
              for (let i = 0; i < len; i++) {
                const p1 = ap1[i];
                const p2 = ap2[i];
                const o1 = Number.isFinite(p1) && p1 > 0;
                const o2 = Number.isFinite(p2) && p2 > 0;
                if (o1 && o2) {
                  sawOverlap = true;
                  if (Math.abs(p1 - p2) > tol) return false;
                }
              }
              if (sawOverlap) return true;
              const l1 = lastPositiveInSeries(ap1);
              const l2 = lastPositiveInSeries(ap2);
              if (l1 == null || l1 <= 0 || l2 == null || l2 <= 0) return false;
              return fmtLabelRoundedForCountryAgreement(l1) === fmtLabelRoundedForCountryAgreement(l2);
            };

            /** Greedy clusters: variants that never disagree on overlapping days stay merged; a 4 GBP path splits from two 5 GBP paths. */
            const clusterCountryKeysByOverlappingAgreement = (pks) => {
              const remaining = [...pks];
              const out = [];
              while (remaining.length) {
                const seed = remaining.shift();
                const cluster = [seed];
                let grew = true;
                while (grew) {
                  grew = false;
                  for (let r = remaining.length - 1; r >= 0; r--) {
                    const pk = remaining[r];
                    const apK = getCountrySeriesForKey(country, pk).actualPrices;
                    const okEvery = cluster.every((c) =>
                      pairAgreesOverlappingCountrySeries(getCountrySeriesForKey(country, c).actualPrices, apK)
                    );
                    if (okEvery) {
                      cluster.push(pk);
                      remaining.splice(r, 1);
                      grew = true;
                    }
                  }
                }
                out.push(cluster);
              }
              return out;
            };

            const runCountryPriceBuckets = (keys, primaryPriceType, isBundleLine = false) => {
              if (keys.length === 0) return;
              const keysWithPrice = keys.filter((pk) => {
                const { actualPrices: ap } = getCountrySeriesForKey(country, pk);
                const lp0 = lastPositiveInSeries(ap);
                return lp0 != null && lp0 > 0;
              });
              if (keysWithPrice.length === 0) return;
              const pathClusters = clusterCountryKeysByOverlappingAgreement(keysWithPrice);
              pathClusters.forEach((clusterKeys) => {
                const cl = { keys: clusterKeys };
                if (!cl.keys?.length) return;
                const {
                  actualPrices: mergedActual,
                  regularPrices: mergedRegular,
                  variantCountAtIndex,
                  mergedStock,
                } = mergeCountryKeysToSeries(cl.keys, country);
                const lp = lastPositiveInSeries(mergedActual);
                if (lp == null || lp <= 0) return;
                let suffix = '';
                if (lp != null && lp > 0) {
                  suffix = isIndexMode
                    ? String(Number(lp).toFixed(indexDecimalPlaces))
                    : (() => {
                        const maxD = Math.max(0, priceDecimalPlaces);
                        const rounded = Math.round(lp * Math.pow(10, maxD)) / Math.pow(10, maxD);
                        return parseFloat(rounded.toFixed(maxD)).toString();
                      })();
                }
                const variantTitles = cl.keys.map((k) => k.split('__')[1]).filter(Boolean);
                const unitsBr = formatStickPackUnitsBracketForTitles(variantTitles, packSizeByTitleForStick);
                const rawCc = (country || '').toLowerCase();
                const countryCodeUpper =
                  rawCc === 'gb' || rawCc === 'uk' ? 'UK' : (country || '').toUpperCase();
                let lastDataIdx = mergedActual.length - 1;
                while (lastDataIdx >= 0 && !(mergedActual[lastDataIdx] > 0)) lastDataIdx -= 1;
                const vcAtLineEnd =
                  lastDataIdx >= 0 ? variantCountAtIndex[lastDataIdx] : Math.max(1, variantTitles.length);
                const baseLegend =
                  vcAtLineEnd <= 1
                    ? `${groupName} (${countryCodeUpper})${unitsBr}`
                    : `${groupName} (${countryCodeUpper})${unitsBr} - ${vcAtLineEnd} variants`;
                const legendLineLabel = baseLegend;
                const tooltipTitleLine = `${groupName} (${countryCodeUpper})${unitsBr}`;
                const chartIdentityName = suffix ? `${groupName} · ${suffix}` : groupName;
                const legendGroupId = `cg:${country}|${groupName}`;
                addCountryProductLine(country, cl.keys, 0, lineIdx, chartIdentityName, {
                  precomputedActual: mergedActual,
                  precomputedRegular: mergedRegular,
                  mergedStock,
                  variantCountAtIndex,
                  groupedVariantTitles: variantTitles,
                  groupedVariantCount: cl.keys.length,
                  legendGroupId,
                  legendLabel: legendLineLabel,
                  tooltipTitle: tooltipTitleLine,
                  countryChartLineFormat: true,
                  primaryPriceType,
                  productGroupName: groupName,
                  isBundleLine,
                });
                lineIdx += 1;
              });
            };

            const bundleKeys = normalKeys.filter((pk) => isBundleProduct(pk.split('__')[1]));
            const standaloneNormalKeys = normalKeys.filter((pk) => !isBundleProduct(pk.split('__')[1]));

            runCountryPriceBuckets(promoKeys, 'promo');
            runCountryPriceBuckets(bundleKeys, 'promo', true);
            runCountryPriceBuckets(standaloneNormalKeys, 'actual');
          });
        });
      } else {
        // One line per country (averaged across products)
        Array.from(countrySet).sort().forEach((country, idx) => {
          const productKeys = Array.from(entriesByCountryProduct.keys())
            .filter(k => k.startsWith(`${country}__`));
          addCountryProductLine(country, productKeys, idx, idx, '');
        });
      }

      const formattedDates = countryDates.map(date => new Date(date).toLocaleDateString());
      return {
        labels: formattedDates,
        rawDateKeys: countryDates,
        datasets,
        baseProductImputedGapLabels: baseProductImputedGapLabels.length ? baseProductImputedGapLabels : undefined,
        baseProductGapSuggestions: baseProductGapSuggestions.length ? baseProductGapSuggestions : undefined,
      };
    }

    // Device-aware color mapping (use actual brand from data so Ploom doesn't fall back)
    const productBrandKeyMap = {};
    chartData.forEach((item) => {
      const title = item?.title;
      if (!title || !neededTitles.has(title) || productBrandKeyMap[title]) return;
      const brandKey = normalizeBrandKey(item?.brand);
      if (brandKey) {
        productBrandKeyMap[title] = brandKey;
      }
    });
    // Build color map for all products (including those in groups)
    const allProductsForColorMap = new Set(effectiveProducts);
    productsByGroupMap.forEach((productNames) => {
      productNames.forEach((name) => allProductsForColorMap.add(name));
    });
    const deviceColorMap = buildColorMap(Array.from(allProductsForColorMap), productBrandKeyMap);

    // Precompute base product series for Index mode by price type
    // (regular->regular, promo->promo).
    const baseSeriesByType = new Map();
    const baseSeriesByCountryByType = new Map();
    const selectedCountryForIndex =
      selectedCountries?.length === 1 ? selectedCountries[0] : null;
    const basePriceTypeForTitle = (title) => {
      if (!title) return 'actual';
      if (isBundleProduct(title)) return 'promo';
      if (productFlagsFromStorage[title]?.isPromoOffer) return 'promo';
      return 'actual';
    };
    const titleIncludedInBaseSeries = (title, baseType) => {
      const isBundle = isBundleProduct(title);
      if (isBundle) return baseType === 'promo';
      if (baseType === 'promo') return !!productFlagsFromStorage[title]?.isPromoOffer;
      if (baseType === 'regular') return true;
      return !productFlagsFromStorage[title]?.isPromoOffer;
    };
    const buildBaseSeries = (countryCode = null, baseType = 'actual') => {
      if (!isIndexMode || !activeBaseProduct) return null;
      const baseProductKey = countryCode
        ? resolveIndexBaseProductForCountry(countryCode)
        : activeBaseProduct;
      if (!baseProductKey) return null;
      const valueKey = baseType === 'regular' ? 'regular' : 'actual';
      const seriesBucket = [];
      const exactBaseEntries = (entriesByTitle.get(baseProductKey) || []).filter(
        (entry) => !countryCode || entry.country === countryCode
      );
      if (exactBaseEntries.length > 0 && titleIncludedInBaseSeries(baseProductKey, baseType)) {
        if (baseType === 'promo') {
          const ap = segmentSeriesValueForTitle(exactBaseEntries, 'actual');
          const rp = segmentSeriesValueForTitle(exactBaseEntries, 'regular');
          seriesBucket.push(
            ap.map((a, i) => {
              const r = rp[i];
              if (a > 0 && r > 0 && a !== r) return a;
              return 0;
            })
          );
        } else {
          seriesBucket.push(segmentSeriesValueForTitle(exactBaseEntries, valueKey));
        }
      }
      const baseProductTitles = groupNameToProducts.get(baseProductKey);
      if (baseProductTitles && baseProductTitles.length > 0) {
        (baseProductTitles.includes(baseProductKey) ? baseProductTitles : [...baseProductTitles, baseProductKey]).forEach((productName) => {
          if (!titleIncludedInBaseSeries(productName, baseType)) return;
          const entries = (entriesByTitle.get(productName) || []).filter(
            (entry) => !countryCode || entry.country === countryCode
          );
          if (!entries.length) return;
          if (baseType === 'promo') {
            const ap = segmentSeriesValueForTitle(entries, 'actual');
            const rp = segmentSeriesValueForTitle(entries, 'regular');
            seriesBucket.push(
              ap.map((a, i) => {
                const r = rp[i];
                if (a > 0 && r > 0 && a !== r) return a;
                return 0;
              })
            );
          } else {
            seriesBucket.push(segmentSeriesValueForTitle(entries, valueKey));
          }
        });
      }
      if (!seriesBucket.length) return null;
      return pickDominantSeriesByVariantCount(seriesBucket, dates);
    };

    if (isIndexMode && activeBaseProduct) {
      ['actual', 'regular', 'promo'].forEach((baseType) => {
        const series = buildBaseSeries(null, baseType);
        if (series && series.length === dates.length) {
          baseSeriesByType.set(baseType, series);
        }
      });
      if (selectedCountryForIndex) {
        const byType = new Map();
        ['actual', 'regular', 'promo'].forEach((baseType) => {
          const bsCountry = buildBaseSeries(selectedCountryForIndex, baseType);
          if (bsCountry && bsCountry.length === dates.length) {
            byType.set(baseType, bsCountry);
          }
        });
        if (byType.size > 0) baseSeriesByCountryByType.set(selectedCountryForIndex, byType);
      }
    }

    if (isIndexMode && activeBaseProduct) {
      const regularBaseGlobal = baseSeriesByType.get('regular');
      if (regularBaseGlobal && regularBaseGlobal.length === dates.length) {
        const { series: imputedBase, gapRanges, observedRange } =
          imputeBaseSeriesFromNearestDays(regularBaseGlobal, dates);
        baseSeriesByType.set('regular', imputedBase);
        gapRanges.forEach((r, gapIdx) => {
          const line = `Base product was not available in date range: ${formatBaseGapDate(r.startKey)} – ${formatBaseGapDate(r.endKey)}`;
          baseProductImputedGapLabels.push(line);
          baseProductGapSuggestions.push({
            id: `idx-gap-global-${r.startKey}-${r.endKey}-${gapIdx}`,
            summaryLine: line,
            countryCode: null,
            gapStartKey: r.startKey,
            gapEndKey: r.endKey,
            suggestedStartKey: observedRange?.startKey ?? null,
            suggestedEndKey: observedRange?.endKey ?? null,
          });
        });
      }
    }

    const indexPriceEqualityTolerance = () =>
      Math.max(1e-9, 5 * Math.pow(10, -Math.max(priceDecimalPlaces, 2)));

    const getProductBaseSeriesForType = (baseType, countryCode = null) => {
      const countryTypeMap = countryCode ? baseSeriesByCountryByType.get(countryCode) : null;
      if (countryTypeMap?.get(baseType)?.length === dates.length) return countryTypeMap.get(baseType);
      if (baseSeriesByType.get(baseType)?.length === dates.length) return baseSeriesByType.get(baseType);
      return null;
    };

    const applyIndexToSeriesIfNeeded = (series, countryCode = null, requestedBaseType = 'regular', options = {}) => {
      const { compareRegularSeries = null } = options;
      const effectiveRequested = resolveEffectiveIndexCompareRequest(requestedBaseType, compareToPriceType);
      const indexCompareBaseTypeAtIndex = new Array(series.length).fill(null);
      if (!isIndexMode) {
        return { data: series, indexCompareBaseTypeAtIndex, requestedBaseType: effectiveRequested };
      }
      const tol = indexPriceEqualityTolerance();
      const data = series.map((value, i) => {
        let req = effectiveRequested;
        if (
          compareRegularSeries &&
          requestedBaseType === 'regular' &&
          compareToPriceType === BASE_COMPARE_TYPE_AUTO
        ) {
          const reg = compareRegularSeries[i];
          if (value > 0 && reg > 0 && isPromoPriceSpread(value, reg, tol)) req = 'promo';
        }
        const usedType = resolveCompareBaseTypeAtIndex(req, i, (t) =>
          getProductBaseSeriesForType(t, countryCode)
        );
        indexCompareBaseTypeAtIndex[i] = usedType;
        if (!usedType || !value || value <= 0) return 0;
        const baseArr = getProductBaseSeriesForType(usedType, countryCode);
        const base = baseArr?.[i] || 0;
        if (!base || base <= 0) return 0;
        return Math.round((value / base) * 100 * 100) / 100;
      });
      return { data, indexCompareBaseTypeAtIndex, requestedBaseType: effectiveRequested };
    };

    const getIndexedPairForProduct = (productName) => {
      const entries = entriesByTitle.get(productName) || [];
      let actualPrices = segmentSeriesValueForTitle(entries, 'actual');
      let regularPrices = segmentSeriesValueForTitle(entries, 'regular');
      const countryForBase =
        selectedCountryForIndex ||
        (() => {
          const countries = [...new Set(entries.map((e) => e.country).filter(Boolean))];
          return countries.length === 1 ? countries[0] : null;
        })();
      const actualResult = applyIndexToSeriesIfNeeded(actualPrices, countryForBase, 'regular', {
        compareRegularSeries: regularPrices,
      });
      const regularResult = applyIndexToSeriesIfNeeded(regularPrices, countryForBase, 'regular');
      return { actualPrices: actualResult.data, regularPrices: regularResult.data };
    };

    const lastPositiveInSeriesProduct = (arr) => {
      for (let i = arr.length - 1; i >= 0; i--) {
        if (Number.isFinite(arr[i]) && arr[i] > 0) return arr[i];
      }
      return null;
    };

    const fmtLabelPriceForGroup = (v) => {
      if (v == null || !Number.isFinite(v)) return '';
      const maxD = Math.max(0, priceDecimalPlaces);
      const rounded = Math.round(v * Math.pow(10, maxD)) / Math.pow(10, maxD);
      return parseFloat(rounded.toFixed(maxD)).toString();
    };

    const fmtLabelPriceOrIndexForAgreement = (v) => {
      if (v == null || !Number.isFinite(v)) return '';
      return isIndexMode ? String(Number(v).toFixed(indexDecimalPlaces)) : fmtLabelPriceForGroup(v);
    };

    const toleranceProductPriceAgreement = () => {
      const maxD = Math.max(0, isIndexMode ? indexDecimalPlaces : priceDecimalPlaces);
      return Math.max(1e-9, 5 * Math.pow(10, -Math.max(maxD, isIndexMode ? 1 : 2)));
    };

    /** Same split rule as country chart: merge variants only if every double-active day matches (else own line). */
    const pairAgreesMergedProductActualSeries = (ap1, ap2) => {
      const tol = toleranceProductPriceAgreement();
      let sawOverlap = false;
      const len = Math.min(ap1.length, ap2.length);
      for (let i = 0; i < len; i++) {
        const p1 = ap1[i];
        const p2 = ap2[i];
        const o1 = Number.isFinite(p1) && p1 > 0;
        const o2 = Number.isFinite(p2) && p2 > 0;
        if (o1 && o2) {
          sawOverlap = true;
          if (Math.abs(p1 - p2) > tol) return false;
        }
      }
      if (sawOverlap) return true;
      const lp1 = lastPositiveInSeriesProduct(ap1);
      const lp2 = lastPositiveInSeriesProduct(ap2);
      if (lp1 == null || lp1 <= 0 || lp2 == null || lp2 <= 0) return false;
      const k1 = fmtLabelPriceOrIndexForAgreement(lp1);
      const k2 = fmtLabelPriceOrIndexForAgreement(lp2);
      return k1 === k2 && k1 !== '';
    };

    const clusterTitlesGreedyByOverlappingAgreement = (nodes, getActualSeries) => {
      const remaining = [...nodes];
      const clusters = [];
      while (remaining.length) {
        const seed = remaining.shift();
        const cluster = [seed];
        let grew = true;
        while (grew) {
          grew = false;
          for (let r = remaining.length - 1; r >= 0; r--) {
            const node = remaining[r];
            const apNode = getActualSeries(node);
            if (cluster.every((c) => pairAgreesMergedProductActualSeries(getActualSeries(c), apNode))) {
              cluster.push(node);
              remaining.splice(r, 1);
              grew = true;
            }
          }
        }
        clusters.push(cluster);
      }
      return clusters;
    };

    /** One chart line per distinct price *path* within the group (overlapping days must match; no more 5+5+4 → one averaged dip). */
    const clusterTitlesByPriceBucket = (titles) => {
      const withData = titles.filter((t) => {
        const lp = lastPositiveInSeriesProduct(getIndexedPairForProduct(t).actualPrices);
        return lp != null && lp > 0;
      });
      return clusterTitlesGreedyByOverlappingAgreement(withData, (t) =>
        getIndexedPairForProduct(t).actualPrices
      ).map((ts) => ({ titles: ts }));
    };

    const clusterKitTitlesByPriceBucket = (titles, baseType = 'promo') => {
      const withData = titles.filter((t) => {
        const entries = entriesByTitle.get(t) || [];
        const countryForBase =
          selectedCountryForIndex ||
          (() => {
            const countries = [...new Set(entries.map((e) => e.country).filter(Boolean))];
            return countries.length === 1 ? countries[0] : null;
          })();
        const actualPrices = applyIndexToSeriesIfNeeded(
          segmentSeriesValueForTitle(entries, 'actual'),
          countryForBase,
          baseType
        );
        const lp = lastPositiveInSeriesProduct(actualPrices.data);
        return lp != null && lp > 0;
      });
      return clusterTitlesGreedyByOverlappingAgreement(withData, (t) => {
        const entries = entriesByTitle.get(t) || [];
        const countryForBase =
          selectedCountryForIndex ||
          (() => {
            const countries = [...new Set(entries.map((e) => e.country).filter(Boolean))];
            return countries.length === 1 ? countries[0] : null;
          })();
        return applyIndexToSeriesIfNeeded(
          segmentSeriesValueForTitle(entries, 'actual'),
          countryForBase,
          baseType
        ).data;
      }).map((ts) => ({ titles: ts }));
    };

    // Process grouped products: split by promo offer vs regular so Price Type filter controls lines
    productsByGroupMap.forEach((productNames, groupName) => {
      const promoOfferProducts = productNames.filter(
        (n) => productFlagsFromStorage[n]?.isPromoOffer
      );
      const nonPromoProducts = productNames.filter(
        (n) => !productFlagsFromStorage[n]?.isPromoOffer
      );
      const firstProductName = productNames[0];
      const productColorRaw = deviceColorMap[firstProductName];
      const productColor = typeof productColorRaw === 'string' && productColorRaw ? productColorRaw : 'hsl(210, 10%, 45%)';

      const bundleNonPromoProducts = nonPromoProducts.filter((n) => isBundleProduct(n));
      const standaloneNonPromoProducts = nonPromoProducts.filter((n) => !isBundleProduct(n));
      const promoAndBundleProducts = [
        ...promoOfferProducts,
        ...bundleNonPromoProducts.filter((n) => !promoOfferProducts.includes(n)),
      ];

      // Promo offer + category bundles: actual price shown as Promo Price; no regular split
      if (promoAndBundleProducts.length > 0) {
        clusterKitTitlesByPriceBucket(promoAndBundleProducts, 'promo').forEach((cl) => {
          const { titles } = cl;
          const titleRawActual = titles.map((t) =>
            segmentSeriesValueForTitle(entriesByTitle.get(t) || [], 'actual')
          );
          const seriesListBlend = titles.map((t) =>
            applyIndexToSeriesIfNeeded(
              segmentSeriesValueForTitle(entriesByTitle.get(t) || [], 'actual'),
              null,
              'promo'
            ).data
          );
          const seriesList = seriesListBlend;
          const rawActualPrices = dates.map((_, i) => {
            const vals = titleRawActual.map((s) => s[i]).filter((v) => v > 0);
            return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
          });
          const promoIndexMeta = isIndexMode
            ? applyIndexToSeriesIfNeeded(rawActualPrices, null, 'promo')
            : null;
          const actualPrices = promoIndexMeta ? promoIndexMeta.data : rawActualPrices;
          const variantCountAtIndex = dates.map((_, i) => seriesList.filter((s) => s[i] > 0).length);
          const lp = lastPositiveInSeriesProduct(actualPrices);
          const suffix =
            lp != null && lp > 0
              ? (isIndexMode ? String(Number(lp).toFixed(indexDecimalPlaces)) : fmtLabelPriceForGroup(lp))
              : '';
          const labelBase =
            titles.length === 1
              ? titles[0]
              : (suffix ? `${groupName} · ${suffix}` : groupName);
          const legendGroupId = `pg:${groupName}`;
          const tooltipTitle = titles.length === 1 ? titles[0] : groupName;
          const isBundleLine = titles.length > 0 && titles.every((t) => isBundleProduct(t));
          const firstTitle = titles[0];
          const lineColor = deviceColorMap[firstTitle] || productColor;
          const variantMeta =
            titles.length > 1 ? { groupedVariantTitles: titles, variantCountAtIndex } : {};
          buildProductStockVariants(titles, actualPrices, actualPrices, {
            actual: seriesListBlend,
            regular: seriesListBlend,
          }).forEach((sv) => {
            if (!sv.ap.some((p) => p > 0)) return;
            const pointCount = sv.ap.filter((p) => p > 0).length;
            const gvAtIdx = groupedVariantTitlesAtIndexForMerged(
              titles,
              seriesListBlend,
              (s, i) => s[i],
              sv.stockSegment
            );
            datasets.push({
              label: `${labelBase}${sv.suffix}${isIndexMode ? ' - Promo Index' : ' - Promo Price'}`,
              displayLabel: labelBase,
              legendLabel: labelBase,
              tooltipTitle,
              legendGroupId,
              productGroupName: groupName,
              legendPrimary: true,
              data: zeroToNull(sv.ap),
              borderColor: lineColor,
              backgroundColor: colorWithAlpha(lineColor),
              tension: 0,
              spanGaps: ffOn && !useStockGaps,
              pointRadius: pointCount <= 1 ? 3 : 0,
              pointHoverRadius: pointCount <= 1 ? 4 : 0,
              priceType: 'promo',
              productName: labelBase,
              regularPrices: sv.ap,
              isIndexMode,
              ...indexCompareDatasetMeta(promoIndexMeta),
              ...(sv.stockSegment ? { stockSegment: sv.stockSegment } : {}),
              ...variantMeta,
              ...(gvAtIdx ? { groupedVariantTitlesAtIndex: gvAtIdx } : {}),
              ...(isBundleLine ? { isBundleLine: true } : {}),
            });
          });
        });
      }

      // Standalone non-kit: one line per group × price bucket; merged series + per-date variant count
      if (standaloneNonPromoProducts.length > 0) {
        const clusters = clusterTitlesByPriceBucket(standaloneNonPromoProducts);
        clusters.forEach((cl) => {
          const { titles } = cl;
          const titleRaw = titles.map((t) => {
            const entries = entriesByTitle.get(t) || [];
            return {
              actual: segmentSeriesValueForTitle(entries, 'actual'),
              regular: segmentSeriesValueForTitle(entries, 'regular'),
            };
          });
          const countryForBase =
            selectedCountryForIndex ||
            (() => {
              const countries = [
                ...new Set(
                  titles.flatMap((t) =>
                    (entriesByTitle.get(t) || []).map((e) => e.country).filter(Boolean)
                  )
                ),
              ];
              return countries.length === 1 ? countries[0] : null;
            })();
          const mergeRawAvg = (key, i) => {
            const vals = titleRaw.map((r) => r[key][i]).filter((v) => v > 0);
            return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0;
          };
          const rawActualPrices = dates.map((_, i) => mergeRawAvg('actual', i));
          const rawRegularPrices = dates.map((_, i) => mergeRawAvg('regular', i));
          const actualIndexMeta = isIndexMode
            ? applyIndexToSeriesIfNeeded(rawActualPrices, countryForBase, 'regular', {
                compareRegularSeries: rawRegularPrices,
              })
            : null;
          const regularIndexMeta = isIndexMode
            ? applyIndexToSeriesIfNeeded(rawRegularPrices, countryForBase, 'regular')
            : null;
          const actualPrices = actualIndexMeta ? actualIndexMeta.data : rawActualPrices;
          const regularPrices = regularIndexMeta ? regularIndexMeta.data : rawRegularPrices;
          const seriesList = titles.map((t) => getIndexedPairForProduct(t));
          const seriesListBlend = titles.map((t, ti) => {
            if (!isIndexMode) {
              return {
                actualPrices: titleRaw[ti].actual,
                regularPrices: titleRaw[ti].regular,
              };
            }
            const actualResult = applyIndexToSeriesIfNeeded(
              titleRaw[ti].actual,
              countryForBase,
              'regular',
              { compareRegularSeries: titleRaw[ti].regular }
            );
            const regularResult = applyIndexToSeriesIfNeeded(
              titleRaw[ti].regular,
              countryForBase,
              'regular'
            );
            return {
              actualPrices: actualResult.data,
              regularPrices: regularResult.data,
            };
          });
          const variantCountAtIndex = dates.map((_, i) =>
            seriesList.filter((s) => s.actualPrices[i] > 0).length
          );
          const lp = lastPositiveInSeriesProduct(actualPrices);
          const suffix =
            lp != null && lp > 0
              ? (isIndexMode ? String(Number(lp).toFixed(indexDecimalPlaces)) : fmtLabelPriceForGroup(lp))
              : '';
          const labelBase =
            titles.length === 1
              ? titles[0]
              : (suffix ? `${groupName} · ${suffix}` : groupName);
          const legendGroupId = `pg:${groupName}`;
          const tooltipTitle = titles.length === 1 ? titles[0] : groupName;
          const firstTitle = titles[0];
          const lineColor = deviceColorMap[firstTitle] || productColor;
          const productImage = productTitleToImage.get(firstTitle) || productTitleToImage.get(groupName);
          const variantMeta =
            titles.length > 1 ? { groupedVariantTitles: titles, variantCountAtIndex } : {};

          buildProductStockVariants(titles, rawActualPrices, rawRegularPrices, {
            actual: titleRaw.map((r) => r.actual),
            regular: titleRaw.map((r) => r.regular),
          }).forEach((sv) => {
            const rawAp = sv.ap;
            const rawRp = sv.rp;
            const spreadTol = indexPriceEqualityTolerance();
            const hasRegularDifferent = rawRp.some((price, idx) =>
              isPromoPriceSpread(rawAp[idx], price, spreadTol)
            );
            let ap = rawAp;
            let rp = rawRp;
            let actualMeta = null;
            let regularMeta = null;
            if (isIndexMode) {
              actualMeta = applyIndexToSeriesIfNeeded(ap, countryForBase, 'regular', {
                compareRegularSeries: rp,
              });
              regularMeta = applyIndexToSeriesIfNeeded(rp, countryForBase, 'regular');
              ap = actualMeta.data;
              rp = regularMeta.data;
            }
            const primaryPrices = hasRegularDifferent ? ap : rp;
            const primaryMeta = hasRegularDifferent ? actualMeta : regularMeta;
            const gvAtIdx = groupedVariantTitlesAtIndexForMerged(
              titles,
              seriesListBlend,
              (pair, i) => pair.actualPrices[i],
              sv.stockSegment
            );
            const hasActualPrices = primaryPrices.some((p) => p > 0);
            const actualPointCount = primaryPrices.filter((p) => p > 0).length;
            if (hasActualPrices) {
              datasets.push({
                label: `${labelBase}${sv.suffix}${isIndexMode ? ' - Index' : ' - Actual Price'}`,
                displayLabel: labelBase,
                legendLabel: labelBase,
                tooltipTitle,
                legendGroupId,
                productGroupName: groupName,
                legendPrimary: true,
                data: zeroToNull(primaryPrices),
                borderColor: lineColor,
                backgroundColor: colorWithAlpha(lineColor),
                tension: 0,
                spanGaps: ffOn && !useStockGaps,
                pointRadius: actualPointCount <= 1 ? 3 : 0,
                pointHoverRadius: actualPointCount <= 1 ? 4 : 0,
                priceType: 'actual',
                productName: labelBase,
                regularPrices: rp,
                isIndexMode,
                productImage,
                ...indexCompareDatasetMeta(primaryMeta),
                ...(sv.stockSegment ? { stockSegment: sv.stockSegment } : {}),
                ...variantMeta,
                ...(gvAtIdx ? { groupedVariantTitlesAtIndex: gvAtIdx } : {}),
              });
            }
            if (hasRegularDifferent) {
              const regularPointCount = rp.filter((price, idx) => {
                const actual = ap[idx];
                return isPromoPriceSpread(actual, price, spreadTol);
              }).length;
              datasets.push({
                label: `${labelBase}${sv.suffix}${isIndexMode ? ' - Regular Index' : ' - Regular Price'}`,
                displayLabel: labelBase,
                legendLabel: labelBase,
                tooltipTitle,
                legendGroupId,
                productGroupName: groupName,
                legendPrimary: false,
                data: zeroToNull(rp),
                borderColor: lineColor,
                backgroundColor: colorWithAlpha(lineColor),
                tension: 0,
                spanGaps: ffOn && !useStockGaps,
                pointRadius: regularPointCount <= 1 ? 3 : 0,
                pointHoverRadius: regularPointCount <= 1 ? 4 : 0,
                priceType: 'regular',
                productName: labelBase,
                actualPrices: ap,
                isIndexMode,
                productImage,
                ...indexCompareDatasetMeta(regularMeta),
                ...(sv.stockSegment ? { stockSegment: sv.stockSegment } : {}),
                ...variantMeta,
                ...(gvAtIdx ? { groupedVariantTitlesAtIndex: gvAtIdx } : {}),
              });
              const rawPromoPrices = rawAp.map((actual, i) => {
                const regular = rawRp[i];
                if (!isPromoPriceSpread(actual, regular, spreadTol)) return 0;
                return actual;
              });
              const promoMeta = isIndexMode
                ? applyIndexToSeriesIfNeeded(rawPromoPrices, countryForBase, 'promo')
                : null;
              const promoPrices = promoMeta ? promoMeta.data : rawPromoPrices;
              const promoPointCount = promoPrices.filter((p) => p > 0).length;
              if (promoPointCount > 0) {
                datasets.push({
                  label: `${labelBase}${sv.suffix}${isIndexMode ? ' - Promo Index' : ' - Promo Price'}`,
                  displayLabel: labelBase,
                  legendLabel: labelBase,
                  tooltipTitle,
                  legendGroupId,
                  productGroupName: groupName,
                  legendPrimary: false,
                  data: zeroToNull(promoPrices),
                  borderColor: lineColor,
                  backgroundColor: colorWithAlpha(lineColor),
                  tension: 0,
                  spanGaps: ffOn && !useStockGaps,
                  pointRadius: promoPointCount <= 1 ? 3 : 0,
                  pointHoverRadius: promoPointCount <= 1 ? 4 : 0,
                  priceType: 'promo',
                  productName: labelBase,
                  regularPrices: rp,
                  isIndexMode,
                  productImage,
                  ...indexCompareDatasetMeta(promoMeta),
                  ...(sv.stockSegment ? { stockSegment: sv.stockSegment } : {}),
                  ...variantMeta,
                  ...(gvAtIdx ? { groupedVariantTitlesAtIndex: gvAtIdx } : {}),
                });
              }
            }
          });
        });
      }
    });

    // Process ungrouped products (show individual names)
    ungroupedProductsList.forEach((productName, productIndex) => {
      const entries = entriesByTitle.get(productName) || [];
      if (!entries.length) return;

      let rawActualPrices = segmentSeriesValueForTitle(entries, 'actual');
      let rawRegularPrices = segmentSeriesValueForTitle(entries, 'regular');
      const isBundle = isBundleProduct(productName);
      const isPromoOffer = isBundle || !!productFlagsFromStorage[productName]?.isPromoOffer;
      
      const productColorRaw = deviceColorMap[productName];
      const productColor = typeof productColorRaw === 'string' && productColorRaw ? productColorRaw : 'hsl(210, 10%, 45%)';
      const productImage = productTitleToImage.get(productName);
      const ungroupedLegendId = `u:${productName}`;
      const primaryLabelSuffix = isIndexMode
        ? isPromoOffer
          ? ' - Promo Index'
          : ' - Index'
        : isPromoOffer
          ? ' - Promo Price'
          : ' - Actual Price';
      const primaryPriceType = isPromoOffer ? 'promo' : 'actual';
      buildProductStockVariants([productName], rawActualPrices, rawRegularPrices).forEach((sv) => {
        const rawAp = sv.ap;
        const rawRp = sv.rp;
        const spreadTol = priceSpreadTolerance(priceDecimalPlaces);
        const hasRegularDifferent =
          !isPromoOffer &&
          rawRp.some((price, idx) => isPromoPriceSpread(rawAp[idx], price, spreadTol));
        let ap = rawAp;
        let rp = rawRp;
        let primaryMeta = null;
        let regularMeta = null;
        if (isIndexMode) {
          const primaryRequest = isPromoOffer ? 'promo' : 'regular';
          primaryMeta = applyIndexToSeriesIfNeeded(ap, null, primaryRequest, {
            compareRegularSeries: rp,
          });
          regularMeta = applyIndexToSeriesIfNeeded(rp, null, 'regular');
          ap = primaryMeta.data;
          rp = regularMeta.data;
        }
        const primaryPrices = hasRegularDifferent || isPromoOffer ? ap : rp;
        const primaryMetaResolved =
          hasRegularDifferent || isPromoOffer ? primaryMeta : regularMeta;
        const hasActualPrices = primaryPrices.some((price) => price > 0);
        const actualPointCount = primaryPrices.filter((price) => price > 0).length;
        if (hasActualPrices) {
          datasets.push({
            label: `${productName}${sv.suffix}${primaryLabelSuffix}`,
            displayLabel: productName,
            legendLabel: productName,
            tooltipTitle: productName,
            legendGroupId: ungroupedLegendId,
            legendPrimary: true,
            data: zeroToNull(primaryPrices),
            borderColor: productColor,
            backgroundColor: colorWithAlpha(productColor),
            tension: 0,
            spanGaps: ffOn && !useStockGaps,
            pointRadius: actualPointCount <= 1 ? 3 : 0,
            pointHoverRadius: actualPointCount <= 1 ? 4 : 0,
            priceType: primaryPriceType,
            productName,
            regularPrices: rp,
            isIndexMode,
            productImage,
            ...indexCompareDatasetMeta(primaryMetaResolved),
            ...(sv.stockSegment ? { stockSegment: sv.stockSegment } : {}),
            ...(isBundle ? { isBundleLine: true } : {}),
          });
        }

        if (hasRegularDifferent) {
          const regularPointCount = rp.filter((price, idx) =>
            isPromoPriceSpread(ap[idx], price, spreadTol)
          ).length;
          datasets.push({
            label: `${productName}${sv.suffix}${isIndexMode ? ' - Regular Index' : ' - Regular Price'}`,
            displayLabel: productName,
            legendLabel: productName,
            tooltipTitle: productName,
            legendGroupId: ungroupedLegendId,
            legendPrimary: false,
            data: zeroToNull(rp),
            borderColor: productColor,
            backgroundColor: colorWithAlpha(productColor),
            tension: 0,
            spanGaps: ffOn && !useStockGaps,
            pointRadius: regularPointCount <= 1 ? 3 : 0,
            pointHoverRadius: regularPointCount <= 1 ? 4 : 0,
            priceType: 'regular',
            productName,
            actualPrices: ap,
            isIndexMode,
            productImage,
            ...indexCompareDatasetMeta(regularMeta),
            ...(sv.stockSegment ? { stockSegment: sv.stockSegment } : {}),
          });

          const rawPromoPrices = rawAp.map((actual, i) => {
            const regular = rawRp[i];
            if (!isPromoPriceSpread(actual, regular, spreadTol)) return 0;
            return actual;
          });
          const promoMeta = isIndexMode
            ? applyIndexToSeriesIfNeeded(rawPromoPrices, null, 'promo')
            : null;
          const promoPrices = promoMeta ? promoMeta.data : rawPromoPrices;
          const promoPointCount = promoPrices.filter((p) => p > 0).length;
          if (promoPointCount > 0) {
            datasets.push({
              label: `${productName}${sv.suffix}${isIndexMode ? ' - Promo Index' : ' - Promo Price'}`,
              displayLabel: productName,
              legendLabel: productName,
              tooltipTitle: productName,
              legendGroupId: ungroupedLegendId,
              legendPrimary: false,
              data: zeroToNull(promoPrices),
              borderColor: productColor,
              backgroundColor: colorWithAlpha(productColor),
              tension: 0,
              spanGaps: ffOn && !useStockGaps,
              pointRadius: promoPointCount <= 1 ? 3 : 0,
              pointHoverRadius: promoPointCount <= 1 ? 4 : 0,
              priceType: 'promo',
              productName,
              regularPrices: rp,
              isIndexMode,
              productImage,
              ...indexCompareDatasetMeta(promoMeta),
              ...(sv.stockSegment ? { stockSegment: sv.stockSegment } : {}),
            });
          }
        }
      });
    });
    
    // Format dates for display
    const formattedDates = dates.map(date => {
      const dateObj = new Date(date);
      return dateObj.toLocaleDateString();
    });
    
    return {
      labels: formattedDates,
      rawDateKeys: dates,
      datasets: datasets,
      baseProductImputedGapLabels: baseProductImputedGapLabels.length ? baseProductImputedGapLabels : undefined,
      baseProductGapSuggestions: baseProductGapSuggestions.length ? baseProductGapSuggestions : undefined,
      adminScrapeGapMeta: (() => {
        const countriesInScope = new Set(sourceFiltered.map((i) => i?.country).filter(Boolean));
        const retailersInScope =
          Array.isArray(selectedRetailers) && selectedRetailers.length > 0
            ? new Set(selectedRetailers)
            : null;
        const pairFilter = (pairKey) => {
          const ix = pairKey.indexOf('\x00');
          const country = ix === -1 ? pairKey : pairKey.slice(0, ix);
          const retailer = ix === -1 ? '' : pairKey.slice(ix + 1);
          if (!countriesInScope.has(country)) return false;
          if (retailersInScope && !retailersInScope.has(retailer)) return false;
          return true;
        };
        return buildAdminScrapeGapMeta(dates, pairMarketDaysSorted, pairFilter);
      })(),
    };
  };

  // Generate visibility history data for the selected products
  const generateVisibilityHistoryData = (sourceFiltered = filteredData, productsOverride = null) => {
    const effectiveProducts = productsOverride || productsForCharts;
    if (!sourceFiltered || sourceFiltered.length === 0 || effectiveProducts.length === 0) return null;
    
    let chartData = sourceFiltered.filter(item => effectiveProducts.includes(item.title));
    
    if (chartData.length === 0) return null;

    const productTitleToImage = new Map();
    sourceFiltered.forEach((item) => {
      const title = item?.title;
      if (!title || productTitleToImage.has(title)) return;
      // Try image, box_image_urls (GLO), image_urls (IQOS), media_gallery_urls (GLO/Italy),
      // resolving each in priority order so an unresolvable earlier field never hides a valid later one.
      const url = resolveProductImageUrlFromFields(item.image, item.box_image_urls, item.image_urls, item.media_gallery_urls);
      if (url) productTitleToImage.set(title, url);
    });

    // Build (title -> sorted entries) and unique date keys in one pass
    const neededTitles = new Set(effectiveProducts);
    const entriesByTitle = new Map();
    const allDates = new Set();

    chartData.forEach((item) => {
      const title = item?.title;
      if (!title || !neededTitles.has(title) || !item?.updated_at) return;
      let dateKey = null;
      let ts = null;
      try {
        const d = new Date(item.updated_at);
        if (isNaN(d.getTime())) return;
        ts = d.getTime();
        dateKey = d.toISOString().slice(0, 10);
      } catch (e) {
        return;
      }
      allDates.add(dateKey);
      const position = parseNumericValue(item?.position);
      const list = entriesByTitle.get(title) || [];
      list.push({
        dateKey,
        ts,
        position: Number.isFinite(position) ? position : 0,
      });
      entriesByTitle.set(title, list);
    });

    const dates = Array.from(allDates).sort();
    
    // If no dates found, return null
    if (dates.length === 0) return null;
    
    const datasets = [];
    
    // Build title -> brandKey in a single pass (avoid O(P * N) repeated finds).
    const productBrandKeyMap = {};
    chartData.forEach((item) => {
      const title = item?.title;
      if (!title || !neededTitles.has(title) || productBrandKeyMap[title]) return;
      const brandKey = normalizeBrandKey(item?.brand);
      if (brandKey) {
        productBrandKeyMap[title] = brandKey;
      }
    });
    const deviceColorMap = buildColorMap(effectiveProducts, productBrandKeyMap);

    entriesByTitle.forEach((list) => {
      list.sort((a, b) => a.ts - b.ts);
    });

    const fillForwardPositions = (entries) => {
      let idx = 0;
      let last = null;
      const out = new Array(dates.length);
      for (let i = 0; i < dates.length; i += 1) {
        const dateKey = dates[i];
        while (idx < entries.length && entries[idx].dateKey <= dateKey) {
          last = entries[idx];
          idx += 1;
        }
        out[i] = last ? (last.position || 0) : 0;
      }
      return out;
    };
    
    // Process each selected product
    productsForCharts.forEach((productName, productIndex) => {
      const entries = entriesByTitle.get(productName) || [];
      if (!entries.length) return;

      const positions = fillForwardPositions(entries);
      
      const productColorRaw = deviceColorMap[productName];
      const productColor = typeof productColorRaw === 'string' && productColorRaw ? productColorRaw : 'hsl(210, 10%, 45%)';
      const positionPointCount = positions.filter((value) => value > 0).length;
      
             const productImage = productTitleToImage.get(productName);
             // Add position dataset for this product
       datasets.push({
         label: `${productName} - Position`,
         data: positions,
         borderColor: productColor,
         backgroundColor: colorWithAlpha(productColor),
         tension: 0,
         pointRadius: positionPointCount <= 1 ? 3 : 0,
         pointHoverRadius: positionPointCount <= 1 ? 4 : 0,
         productImage,
       });
    });
    
    // Format dates for display
    const formattedDates = dates.map(date => {
      const dateObj = new Date(date);
      return dateObj.toLocaleDateString();
    });
    
    return {
      labels: formattedDates,
      datasets: datasets,
    };
  };

  if (loading) {
    return (
      <ThemeProvider theme={pricePromotionsTheme}>
        <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
          <CircularProgress color="primary" />
        </Box>
      </ThemeProvider>
    );
  }

  if (error) {
    return (
      <ThemeProvider theme={pricePromotionsTheme}>
        <Box p={3}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={pricePromotionsTheme}>
    <>
    <div style={{
      width: '100%',
      maxWidth: '100%',
      marginInline: 'auto',
      padding: '0 24px',
      overflowX: 'auto'
    }}>
      <div style={{ minWidth: 0, width: '100%' }}>
      <Box sx={{ p: 0 }}>
      {/*  <Alert severity="info" sx={{ mb: 2 }}>
          All data for Ploom, Glo and Iqos in RO, IT, UK will be available on 8th August (data transformations are being handled)
        </Alert>*/}
      </Box> 
      <Box sx={{ mx: 2 }}>
        <CompactTopFilters
          styleTokens={styleTokens}
          values={topFilters}
          onChange={handleTopFilterChange}
          isMarketScope={isMarketScope}
          selectedCountries={selectedCountries}
          onCountryChange={setSelectedCountries}
          countryOptions={countryOptions}
          countrySearchTerm={countrySearchTerm}
          onCountrySearchChange={setCountrySearchTerm}
          selectedRetailers={selectedRetailers}
          onRetailerChange={setSelectedRetailers}
          retailerOptions={retailerOptions}
          retailerSearchTerm={retailerSearchTerm}
          onRetailerSearchChange={setRetailerSearchTerm}
          formatLabel={formatLabel}
          dateRange={dateRange}
          previewDateRange={previewDateRange}
          setPreviewDateRange={setPreviewDateRange}
          setDateRange={setDateRange}
          dateSliderValue={
            Array.isArray(dateSliderValue)
              ? dateSliderValue
              : [dateSliderValue, dateSliderValue]
          }
          onDateSliderChange={handleDateSliderChange}
          onDateSliderCommit={handleDateSliderCommit}
          availableDates={availableDates}
          sanitizeSelectValues={sanitizeSelectValues}
          selectedBrands={selectedBrands}
          onBrandChange={(nextBrands) => {
            setSelectedBrands(nextBrands);
            setSelectedProducts([]);
          }}
          brandOptions={brandOptions}
          brandSearchTerm={brandSearchTerm}
          onBrandSearchChange={setBrandSearchTerm}
          filteredProducts={filteredProducts}
          matchingGroups={matchingGroups}
          groupNameToProducts={groupNameToProducts}
          modelGroupToProducts={modelGroupToProducts}
          groupNames={groupNames}
          selectedProducts={selectedProducts}
          onProductsChange={setSelectedProducts}
          getSkuCountry={getSkuCountryForTitle}
          getSkuDisplayTitle={getSkuDisplayTitle}
          isSkuTranslationActive={isSkuTranslationActive}
          isSkuTitleTranslating={isSkuTitleTranslating}
          onToggleSkuTitleTranslate={toggleSkuTitleTranslation}
          currencyOptions={currencyOptions}
          selectedCurrency={selectedCurrency}
          onCurrencyChange={setSelectedCurrency}
          localCurrencyCode={localCurrencyCode}
          selectedCategoryGroups={selectedCategoryGroups}
          onCategoryGroupChange={handleCategoryGroupChange}
          categoryGroupOptions={categoryGroupOptions}
          categoryGroupSearchTerm={categoryGroupSearchTerm}
          onCategoryGroupSearchChange={setCategoryGroupSearchTerm}
          savedViews={savedViews}
          selectedViewName={selectedViewName}
          onViewChange={handleViewChange}
          onDeleteView={handleDeleteView}
          onOpenSaveView={handleOpenSaveView}
          onResetView={handleResetView}
          baseProduct={baseProduct}
          effectiveBaseProduct={effectiveBaseProduct}
          baseProductSkuOverride={baseProductSkuOverride}
          compareToPriceType={compareToPriceType}
          onCompareToPriceTypeChange={setCompareToPriceType}
          onBaseProductSkuOverrideChange={setBaseProductSkuOverride}
          onBaseProductChange={(v) => {
            initialBaseProductDefaultAppliedRef.current = true;
            setBaseProduct(v);
          }}
          onOpenBaseProductAutoQueueDialog={openBaseProductAutoQueueDialog}
          baseProductAutoQueueRouteKey={baseProductAutoQueueRouteKey}
          baseProductOptions={baseProductOptions}
          specificBaseSkuOptions={specificBaseSkuOptions}
          baseProductMissingInCountries={baseProductMissingInCountries}
          baseProductImputedGapLabels={priceHistoryData?.baseProductImputedGapLabels || []}
          baseProductGapSuggestions={priceHistoryData?.baseProductGapSuggestions || []}
          isHqScope={scopeMode === 'hq'}
          onApplyBaseProductSuggestedDateRange={applyBaseProductSuggestedDateRange}
          onExcludeCountryForBase={excludeCountryForBaseProductIndex}
          selectedManufacturers={selectedManufacturers}
          onManufacturerChange={(next) => {
            setSelectedManufacturers(next);
            setSelectedProducts([]);
          }}
          manufacturerOptions={manufacturerOptions}
          manufacturerSearchTerm={manufacturerSearchTerm}
          onManufacturerSearchChange={setManufacturerSearchTerm}
          selectedPackSizes={selectedPackSizes}
          onPackSizeChange={setSelectedPackSizes}
          packSizeOptions={packSizeOptions}
          packSizeSearchTerm={packSizeSearchTerm}
          onPackSizeSearchChange={setPackSizeSearchTerm}
          customizableFilter={customizableFilter}
          onCustomizableFilterChange={setCustomizableFilter}
          forNewUsersOnlyFilter={forNewUsersOnlyFilter}
          onForNewUsersOnlyFilterChange={setForNewUsersOnlyFilter}
          starterKitProductFilter={starterKitProductFilter}
          onStarterKitProductFilterChange={setStarterKitProductFilter}
          productType={productType}
          isStickRoute={isStickRoute}
          priceDisplayMode={priceDisplayMode}
          onPriceDisplayModeChange={setPriceDisplayMode}
          activePriceTypes={activePriceTypes}
          onPriceTypeToggle={handlePriceTypeToggle}
          onPriceTypeSelect={handlePriceTypeSelect}
          allowMultiplePriceTypes={
            allowMultiplePriceTypes ||
            (isMarketScope && topFilters.currencyIndex === 'Absolute') ||
            isBundlesOnlySelection
          }
          listedPriceDisabled={isBundlesOnlySelection}
          groupBy={groupBy}
          onGroupByChange={setGroupBy}
          scopeMode={scopeMode}
          deviceColourOptions={deviceColourOptions}
          stickFlavourOptions={stickFlavourOptions}
          additionalFilters={additionalFilters}
          onAdditionalFiltersChange={setAdditionalFilters}
          additionalFiltersLayout={additionalFiltersLayout}
          onAdditionalFiltersLayoutToggle={() =>
            setAdditionalFiltersLayout((prev) => (prev === 'top' ? 'side' : 'top'))
          }
        />
      </Box>
      <Box sx={{ mx: 2, mt: 1.5 }}>
        <MarketRegulationsPanel
          variant={isMarketScope ? 'market' : 'hq'}
          selectedMarket={selectedMarket}
          selectedCountries={isHqScope ? selectedCountries : []}
          accentColor={styleTokens.accent}
        />
      </Box>
      <div style={{
        display: 'flex',
        gap: '1rem',
        margin: '1rem',
        paddingRight: additionalFiltersLayout === 'top' ? '10px' : '0px',
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
          width: '100%'
        }}>
           
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'flex-start',
              flexWrap: additionalFiltersLayout === 'side' ? { xs: 'wrap', xl: 'nowrap' } : 'nowrap',
            }}
          >
            {/* Charts Container */}
            <Card
              sx={{
                flex: 1,
                minWidth: 0,
                width: { xs: '100%', xl: 'auto' },
                mr: additionalFiltersLayout === 'top' ? '10px' : 0,
              }}
            >
              <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                    {showAdvancedOptions ? (
                      <Tabs 
                        value={activeChartTab} 
                        onChange={handleChartTabChange}
                        sx={{
                          '& .MuiTabs-indicator': {
                            backgroundColor: activeChartTab === 0 ? styleTokens.accent : undefined,
                          },
                        }}
                      >
                        <Tab
                          label="Price Comparison"
                          sx={{
                            '&.Mui-selected': {
                              color: styleTokens.accent,
                            },
                          }}
                        />
                        <Tab label="Product Visibility" />
                      </Tabs>
                    ) : (
                      <Box />
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {showAdvancedOptions && (
                        <>
                          <Typography variant="caption" sx={{ whiteSpace: 'nowrap' }}>
                            Show Top:
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => setTopN((prev) => Math.max(1, prev - 1))}
                            disabled={topN <= 1}
                          >
                            <Remove fontSize="small" />
                          </IconButton>
                          <TextField
                            value={topN >= MAX_GROUPS ? 'All' : topN}
                            size="small"
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === 'All' || val === 'all') {
                                setTopN(MAX_GROUPS);
                                return;
                              }
                              const value = parseInt(val, 10);
                              if (!isNaN(value) && value >= 1) {
                                setTopN(Math.min(value, MAX_GROUPS));
                              }
                            }}
                            inputProps={{
                              type: 'text',
                              style: { textAlign: 'center', width: '50px' }
                            }}
                            sx={{ width: '60px' }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => setTopN((prev) => {
                              const max = Math.min(MAX_GROUPS, totalProductsCount || MAX_GROUPS);
                              if (prev >= max) return prev;
                              return Math.min(prev + 1, max);
                            })}
                            disabled={topN >= Math.min(MAX_GROUPS, totalProductsCount || MAX_GROUPS)}
                          >
                            <Add fontSize="small" />
                          </IconButton>
                          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                            Showing {activeChartTab === 0 ? chartProductCount : visibilityChartProductCount}/{totalProductsCount || 0} groups
                          </Typography>
                        </>
                      )}
                      {showAiSummary && activeChartTab === 0 && (
                        <Tooltip title={isAiAnalysisLoading ? 'Analyzing...' : 'AI Analysis'} arrow>
                          <span>
                            <IconButton
                              size="small"
                              onClick={handleAiAnalysis}
                              disabled={
                                isAiAnalysisLoading
                                || !priceHistoryData?.datasets?.length
                                || (topFilters.currencyIndex === 'Index' && !activeBaseProduct)
                              }
                            >
                              <AutoFixHigh fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      <Tooltip title="Export CSV" arrow>
                        <span>
                          <IconButton
                            size="small"
                            onClick={activeChartTab === 0 ? handleExportPriceCSV : handleExportVisibilityCSV}
                            disabled={activeChartTab === 0 ? !priceHistoryData?.datasets?.length : !visibilityHistoryData?.datasets?.length}
                          >
                            <FileDownload fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Chart view" arrow>
                        <IconButton
                          size="small"
                          onClick={() => activeChartTab === 0 ? setPriceViewMode('chart') : setVisibilityViewMode('chart')}
                          sx={{ color: (activeChartTab === 0 ? priceViewMode : visibilityViewMode) === 'chart' ? 'primary.main' : 'text.disabled' }}
                        >
                          <ShowChart fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Table view" arrow>
                        <IconButton
                          size="small"
                          onClick={() => activeChartTab === 0 ? setPriceViewMode('table') : setVisibilityViewMode('table')}
                          sx={{ color: (activeChartTab === 0 ? priceViewMode : visibilityViewMode) === 'table' ? 'primary.main' : 'text.disabled' }}
                        >
                          <TableRows fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="News feed" arrow>
                        <IconButton
                          size="small"
                          onClick={() => activeChartTab === 0 ? setPriceViewMode('feed') : setVisibilityViewMode('feed')}
                          sx={{ color: (activeChartTab === 0 ? priceViewMode : visibilityViewMode) === 'feed' ? 'primary.main' : 'text.disabled' }}
                        >
                          <RssFeed fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Popover
                        open={Boolean(axisAnchorEl)}
                        anchorEl={axisAnchorEl}
                        onClose={() => setAxisAnchorEl(null)}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                      >
                        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, width: 240 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: '0.04em' }}>
                            Chart Settings
                          </Typography>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={allowMultiplePriceTypes}
                                onChange={(e) => {
                                  const next = e.target.checked;
                                  setAllowMultiplePriceTypes(next);
                                  if (!next) {
                                    setActivePriceTypes((prev) => {
                                      const selected = PRICE_TYPE_OPTIONS.find(
                                        ({ key }) => prev.has(key)
                                      );
                                      return new Set([selected?.key || 'regular']);
                                    });
                                  }
                                }}
                                size="small"
                              />
                            }
                            label="Allow multiple price types"
                            sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={rememberBrandFiltersOnScopeChange}
                                onChange={(e) => setRememberBrandFiltersOnScopeChange(e.target.checked)}
                                size="small"
                              />
                            }
                            label="Remember brand filters on market/HQ view changes"
                            sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                          />
                          <Divider />
                          <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: '0.04em' }}>
                            Y-Axis Settings
                          </Typography>
                          <TextField
                            label="Min value"
                            size="small"
                            type="number"
                            value={yMin}
                            onChange={(e) => setYMin(e.target.value)}
                            placeholder="Auto"
                            InputLabelProps={{ shrink: true }}
                            sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem' } }}
                          />
                          <TextField
                            label="Max value"
                            size="small"
                            type="number"
                            value={yMax}
                            onChange={(e) => setYMax(e.target.value)}
                            placeholder="Auto"
                            InputLabelProps={{ shrink: true }}
                            sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem' } }}
                          />
                          <TextField
                            label="Tick step size"
                            size="small"
                            type="number"
                            value={yStepSize}
                            onChange={(e) => setYStepSize(e.target.value)}
                            placeholder="Auto"
                            InputLabelProps={{ shrink: true }}
                            sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem' } }}
                          />
                          {topFilters.currencyIndex === 'Index' && (
                            <TextField
                              label="Decimal places (index %)"
                              size="small"
                              type="number"
                              inputProps={{ min: 0, max: 4 }}
                              value={indexDecimalPlaces}
                              onChange={(e) => setIndexDecimalPlaces(Math.max(0, Math.min(4, parseInt(e.target.value, 10) || 0)))}
                              placeholder="0"
                              InputLabelProps={{ shrink: true }}
                              sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem' } }}
                            />
                          )}
                          {topFilters.currencyIndex === 'Absolute' && (
                            <TextField
                              label="Decimal places (price)"
                              size="small"
                              type="number"
                              inputProps={{ min: 0, max: 6 }}
                              value={priceDecimalPlaces}
                              onChange={(e) => setPriceDecimalPlaces(Math.max(0, Math.min(6, parseInt(e.target.value, 10) ?? 2)))}
                              placeholder="2"
                              InputLabelProps={{ shrink: true }}
                              sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem' } }}
                            />
                          )}
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={showChartLabels}
                                onChange={(e) => setShowChartLabels(e.target.checked)}
                                size="small"
                              />
                            }
                            label="Show chart labels"
                            sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.8rem' } }}
                          />
                          <Divider />
                          <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary', letterSpacing: '0.04em' }}>
                            X-Axis Settings
                          </Typography>
                          <FormControl size="small" sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem' } }}>
                            <InputLabel shrink sx={{ fontSize: '0.8rem' }}>Date format</InputLabel>
                            <Select
                              value={xDateFormat}
                              label="Date format"
                              onChange={(e) => setXDateFormat(e.target.value)}
                              sx={{ fontSize: '0.8rem' }}
                            >
                              <MenuItem value="auto" sx={{ fontSize: '0.8rem' }}>Auto (as-is)</MenuItem>
                              <MenuItem value="DD/MM" sx={{ fontSize: '0.8rem' }}>DD/MM &nbsp;<Typography component="span" variant="caption" color="text.secondary">e.g. 21/10</Typography></MenuItem>
                              <MenuItem value="MM/DD" sx={{ fontSize: '0.8rem' }}>MM/DD &nbsp;<Typography component="span" variant="caption" color="text.secondary">e.g. 10/21</Typography></MenuItem>
                              <MenuItem value="DD MMM" sx={{ fontSize: '0.8rem' }}>DD MMM &nbsp;<Typography component="span" variant="caption" color="text.secondary">e.g. 21 Oct</Typography></MenuItem>
                              <MenuItem value="DD MMM YY" sx={{ fontSize: '0.8rem' }}>DD MMM YY &nbsp;<Typography component="span" variant="caption" color="text.secondary">e.g. 21 Oct 25</Typography></MenuItem>
                              <MenuItem value="MMM YY" sx={{ fontSize: '0.8rem' }}>MMM YY &nbsp;<Typography component="span" variant="caption" color="text.secondary">e.g. Oct 25</Typography></MenuItem>
                              <MenuItem value="YYYY-MM-DD" sx={{ fontSize: '0.8rem' }}>YYYY-MM-DD &nbsp;<Typography component="span" variant="caption" color="text.secondary">ISO</Typography></MenuItem>
                            </Select>
                          </FormControl>
                          <TextField
                            label="Max tick count"
                            size="small"
                            type="number"
                            value={xMaxTicks}
                            onChange={(e) => setXMaxTicks(e.target.value)}
                            placeholder="10"
                            InputLabelProps={{ shrink: true }}
                            sx={{ '& .MuiInputBase-root': { fontSize: '0.8rem' } }}
                          />
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => { setYMin(''); setYMax(''); setYStepSize(''); setIndexDecimalPlaces(0); setPriceDecimalPlaces(2); setXDateFormat('auto'); setXMaxTicks(''); setShowChartLabels(true); setAllowMultiplePriceTypes(true); setRememberBrandFiltersOnScopeChange(false); setActivePriceTypes(defaultActivePriceTypes(isMarketScope)); }}
                            sx={{ fontSize: '0.7rem', py: 0.25 }}
                          >
                            Reset all to auto
                          </Button>
                        </Box>
                      </Popover>
                      {allowChartAdvanced && (
                      <Tooltip title={showAdvancedOptions ? 'Hide advanced options' : 'Show advanced options'} arrow>
                        <IconButton
                          size="small"
                          onClick={() => setShowAdvanced(prev => !prev)}
                          sx={{ color: showAdvancedOptions ? 'primary.main' : 'text.disabled' }}
                        >
                          <Tune fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      )}
                    </Box>
                  </Box>
                    
                  {activeChartTab === 0 && (
                    // Price Chart Tab
                    <Box>
                      {(priceHistoryData || chartComputationPending) ? (
                        <Box>
                          {showItalySticksNoticeAboveChart && (
                            <Box sx={{ mt: 0.5, mb: 1.5, mx: 'auto', width: '100%' }}>
                              <MarketAnnouncementBannerList messages={marketAnnouncementMessages} maxWidth={560} />
                            </Box>
                          )}
                          {showAdvancedOptions && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              {selectedProducts.length === 1 
                                ? `Current actual vs regular pricing for ${productSelectionLabel(selectedProducts[0])}`
                                : `Price comparison for ${selectedProducts.length} selected products`
                              }
                            </Typography>
                          )}
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                              {priceViewMode === 'chart' ? (
                                <>
                                  <Box
                                    sx={{
                                      resize: 'vertical',
                                      overflow: 'hidden',
                                      minHeight: 200,
                                      height: 528,
                                      display: 'flex',
                                      flexDirection: 'column',
                                      border: '1px solid transparent',
                                      borderBottomColor: 'divider',
                                      pb: '6px',
                                      position: 'relative',
                                      zIndex: 2,
                                    }}
                                  >
                                  <Box sx={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                                  {priceChartDataAllSeries && priceChartDataAllSeries.datasets && priceChartDataAllSeries.datasets.length > 0 && priceChartData ? (
                                    <Box sx={{ position: 'relative', flex: 1, minHeight: 0 }}>
                                      <ChartWithProductImages
                                        data={priceChartData}
                                        options={priceChartOptions}
                                        showImages={showChartLabels}
                                        projectKey={projectKey}
                                        onProductClick={handleProductClick}
                                        pinnedIndex={priceChartPinIndex}
                                        onChartPin={handlePriceChartPin}
                                        onClearPin={handleClearPriceChartPin}
                                        pinPanel={priceChartPinPanel}
                                        onVariantProductClick={handleProductClick}
                                        accentColor={styleTokens.accent}
                                        dedupePromoListedPairs={
                                          activePriceTypes?.has('promo') && activePriceTypes?.has('regular')
                                        }
                                      />
                                    </Box>
                                  ) : (
                                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                      <Typography variant="body2" color="text.secondary">
                                        No price data available
                                      </Typography>
                                    </Box>
                                  )}
                                  {chartComputationPending && (
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        inset: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: 'rgba(255,255,255,0.82)',
                                        zIndex: 2,
                                      }}
                                    >
                                      <CircularProgress sx={{ color: styleTokens.accent }} size={44} thickness={4} />
                                    </Box>
                                  )}
                                  {showAiSummary && aiAnalysisOpen && priceViewMode === 'chart' && (
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        inset: 0,
                                        zIndex: 4,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        p: 2,
                                        bgcolor: 'rgba(15, 23, 42, 0.42)',
                                      }}
                                      onClick={() => {
                                        if (!isAiAnalysisLoading) setAiAnalysisOpen(false);
                                      }}
                                    >
                                      <Paper
                                        elevation={8}
                                        onClick={(e) => e.stopPropagation()}
                                        sx={{
                                          width: '100%',
                                          maxWidth: 560,
                                          maxHeight: '92%',
                                          overflow: 'auto',
                                          p: 2,
                                          borderRadius: 2,
                                          border: `1px solid ${styleTokens.border}`,
                                          bgcolor: styleTokens.panelAlt,
                                        }}
                                      >
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
                                          <Typography variant="subtitle1" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                            <AutoFixHigh fontSize="small" sx={{ color: styleTokens.accent }} />
                                            AI Analysis Summary
                                          </Typography>
                                          <IconButton
                                            size="small"
                                            aria-label="Close AI analysis"
                                            disabled={isAiAnalysisLoading}
                                            onClick={() => setAiAnalysisOpen(false)}
                                          >
                                            <CloseIcon fontSize="small" />
                                          </IconButton>
                                        </Box>
                                        {topFilters.currencyIndex === 'Index' && activeBaseProduct ? (
                                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                                            Index vs &quot;{activeBaseProduct}&quot; (100% baseline)
                                          </Typography>
                                        ) : (
                                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                                            {isMarketScope ? 'Market view' : 'HQ view'}
                                            {priceDisplayCurrency ? ` · ${priceDisplayCurrency}` : ''}
                                            {' · Absolute price'}
                                          </Typography>
                                        )}
                                        {isAiAnalysisLoading ? (
                                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, gap: 1.5 }}>
                                            <CircularProgress size={36} sx={{ color: styleTokens.accent }} />
                                            <Typography variant="body2" color="text.secondary">
                                              Analyzing chart data…
                                            </Typography>
                                          </Box>
                                        ) : Array.isArray(aiSummary) ? (
                                          aiSummary.map((paragraph, index) => (
                                            <Typography
                                              key={index}
                                              variant="body2"
                                              sx={{
                                                lineHeight: 1.55,
                                                textAlign: 'justify',
                                                color: 'text.primary',
                                                fontSize: '0.85rem',
                                                mb: index < aiSummary.length - 1 ? 1.5 : 0,
                                              }}
                                            >
                                              {paragraph}
                                            </Typography>
                                          ))
                                        ) : aiSummary ? (
                                          <Typography variant="body2" sx={{ lineHeight: 1.55, fontSize: '0.85rem' }}>
                                            {aiSummary}
                                          </Typography>
                                        ) : null}
                                      </Paper>
                                    </Box>
                                  )}
                                  </Box>
                                  </Box>
                                  {priceHistoryData?.datasets?.length && !chartComputationPending ? (
                                    <InteractiveLegend
                                      datasets={priceChartDataAllSeries?.datasets || []}
                                      visibleDatasets={visiblePriceDatasets}
                                      onToggleDataset={handleTogglePriceDataset}
                                      onShowAll={handleShowAllPrice}
                                      onHideAll={handleHideAllPrice}
                                      searchTerm={priceLegendSearch}
                                      onSearchChange={setPriceLegendSearch}
                                      activePriceTypes={activePriceTypes}
                                      onPriceTypeToggle={handlePriceTypeToggle}
                                      priceTypeStyles={priceTypeStyles}
                                      onPriceTypeStyleChange={handlePriceTypeStyleChange}
                                      showStockLineStyles={showStockLineLegendStyles}
                                      stockLineStyles={stockLineStyles}
                                      onStockLineStyleChange={handleStockLineStyleChange}
                                      onAxisSettingsOpen={(anchor) => setAxisAnchorEl(anchor)}
                                      axisSettingsActive={yMin !== '' || yMax !== '' || yStepSize !== '' || (topFilters.currencyIndex === 'Index' && indexDecimalPlaces !== 0) || (topFilters.currencyIndex === 'Absolute' && priceDecimalPlaces !== 2) || xDateFormat !== 'auto' || xMaxTicks !== '' || !showChartLabels || !allowMultiplePriceTypes || rememberBrandFiltersOnScopeChange}
                                      legendExpanded={legendExpanded}
                                      onLegendExpandToggle={() => setLegendExpanded(v => !v)}
                                      accentColor={styleTokens.accent}
                                    />
                                  ) : null}
                                  {priceViewMode === 'chart' &&
                                    priceChartSeriesSummaryRows.length > 0 &&
                                    !chartComputationPending && (
                                      <Box sx={{ mt: 1.5, width: '100%', minWidth: 0 }}>
                                        
                                        <Box sx={{ height: 300, width: '100%' }}>
                                          <DataGrid
                                            rows={priceChartSeriesSummaryRows}
                                            columns={priceChartSeriesSummaryColumns}
                                            dense
                                            initialPageSize={25}
                                            pageSizeOptions={[10, 25, 50]}
                                            headerBg={styleTokens.headerBg}
                                            headerBorder={styleTokens.headerBorder}
                                            onRowClick={handleSeriesSummaryRowClick}
                                            sx={{
                                              height: '100%',
                                              border: `1px solid ${styleTokens.border}`,
                                              borderRadius: 1,
                                            }}
                                          />
                                        </Box>
                                      </Box>
                                    )}
                                </>
                              ) : priceViewMode === 'table' ? (
                                <Box sx={{ height: 528 }}>
                                  {priceChartTableRows.length > 0 ? (
                                    <DataGrid
                                      rows={priceChartTableRows}
                                      columns={priceChartTableColumns}
                                      dense
                                      initialPageSize={100}
                                      pageSizeOptions={[25, 50, 100]}
                                      headerBg={styleTokens.headerBg}
                                      headerBorder={styleTokens.headerBorder}
                                      sx={{ height: '100%' }}
                                    />
                                  ) : (
                                    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                      <Typography variant="caption" color="text.secondary">No data</Typography>
                                    </Box>
                                  )}
                                </Box>
                              ) : (
                                <DatasetChangeFeedPanel
                                  events={priceChangeFeedEvents}
                                  isLoading={chartComputationPending}
                                  formatOptions={changeFeedFormatOptions}
                                  emptyMessage="No price or stock changes in the selected range"
                                />
                              )}
                            </Box>
                          </Box>
                        </Box>
                      ) : (
                        <Box 
                          display="flex" 
                          justifyContent="center" 
                          alignItems="center" 
                          height={400}
                          flexDirection="column"
                        >
                          <Typography variant="h6" color="text.secondary" gutterBottom>
                            📊 Price Evolution Chart
                          </Typography>
                          {showItalySticksUnavailableNotice && (
                            <Box sx={{ mt: 0.5, mb: 1.5, mx: 'auto', width: '100%' }}>
                              <MarketAnnouncementBannerList messages={marketAnnouncementMessages} maxWidth={560} />
                            </Box>
                          )}
                          {!showItalySticksUnavailableNotice && (
                            <Typography variant="body1" color="text.secondary" textAlign="center">
                              Please choose a product to view price evolution
                            </Typography>
                          )}
                          {!showItalySticksUnavailableNotice && (
                            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 1 }}>
                              Select a brand and product(s) from the filters above to see pricing trends
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Box>
                  )}
                    
                  {activeChartTab === 1 && (
                    // Visibility Chart Tab
                    <Box>
                      {(visibilityHistoryData || chartComputationPending) ? (
                        <Box>
                          {showItalySticksNoticeAboveChart && (
                            <Box sx={{ mt: 0.5, mb: 1.5, mx: 'auto', width: '100%' }}>
                              <MarketAnnouncementBannerList messages={marketAnnouncementMessages} maxWidth={560} />
                            </Box>
                          )}
                          {showAdvancedOptions && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                              {selectedProducts.length === 1 
                                ? `Position tracking for ${productSelectionLabel(selectedProducts[0])} over time`
                                : `Position tracking for ${selectedProducts.length} selected products`
                              }
                            </Typography>
                          )}
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                              {visibilityViewMode === 'chart' ? (
                                <>
                                  <Box
                                    sx={{
                                      resize: 'vertical',
                                      overflow: 'hidden',
                                      minHeight: 200,
                                      height: 528,
                                      display: 'flex',
                                      flexDirection: 'column',
                                      border: '1px solid transparent',
                                      borderBottomColor: 'divider',
                                      pb: '6px',
                                      position: 'relative',
                                      zIndex: 2,
                                    }}
                                  >
                                  <Box sx={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                                  {visibilityChartData && visibilityChartData.datasets && visibilityChartData.datasets.length > 0 ? (
                                    <Box sx={{ position: 'relative', flex: 1, minHeight: 0 }}>
                                      <ChartWithProductImages
                                        data={visibilityChartData}
                                        options={visibilityChartOptions}
                                        showImages={showChartLabels}
                                        projectKey={projectKey}
                                        onProductClick={handleProductClick}
                                        pinnedIndex={visibilityChartPinIndex}
                                        onChartPin={handleVisibilityChartPin}
                                        onClearPin={handleClearVisibilityChartPin}
                                        pinPanel={visibilityChartPinPanel}
                                        onVariantProductClick={handleProductClick}
                                        accentColor={styleTokens.accent}
                                      />
                                    </Box>
                                  ) : (
                                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                      <Typography variant="body2" color="text.secondary">
                                        No visibility data available
                                      </Typography>
                                    </Box>
                                  )}
                                  {chartComputationPending && (
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        inset: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: 'rgba(255,255,255,0.82)',
                                        zIndex: 2,
                                      }}
                                    >
                                      <CircularProgress sx={{ color: styleTokens.accent }} size={44} thickness={4} />
                                    </Box>
                                  )}
                                  </Box>
                                  </Box>
                                  {visibilityHistoryData?.datasets?.length && !chartComputationPending ? (
                                    <InteractiveLegend
                                      datasets={visibilityHistoryData.datasets}
                                      visibleDatasets={visibleVisibilityDatasets}
                                      onToggleDataset={handleToggleVisibilityDataset}
                                      onShowAll={handleShowAllVisibility}
                                      onHideAll={handleHideAllVisibility}
                                      searchTerm={visibilityLegendSearch}
                                      onSearchChange={setVisibilityLegendSearch}
                                      onAxisSettingsOpen={(anchor) => setAxisAnchorEl(anchor)}
                                      axisSettingsActive={yMin !== '' || yMax !== '' || yStepSize !== '' || (topFilters.currencyIndex === 'Index' && indexDecimalPlaces !== 0) || (topFilters.currencyIndex === 'Absolute' && priceDecimalPlaces !== 2) || xDateFormat !== 'auto' || xMaxTicks !== '' || !showChartLabels || !allowMultiplePriceTypes || rememberBrandFiltersOnScopeChange}
                                      legendExpanded={legendExpanded}
                                      onLegendExpandToggle={() => setLegendExpanded(v => !v)}
                                      accentColor={styleTokens.accent}
                                    />
                                  ) : null}
                                  {visibilityViewMode === 'chart' &&
                                    visibilityChartSeriesSummaryRows.length > 0 &&
                                    !chartComputationPending && (
                                      <Box sx={{ mt: 1.5, width: '100%', minWidth: 0 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                                          Series span — first and last date with data on each chart line (introduced / last seen)
                                        </Typography>
                                        <Box sx={{ height: 300, width: '100%' }}>
                                          <DataGrid
                                            rows={visibilityChartSeriesSummaryRows}
                                            columns={visibilityChartSeriesSummaryColumns}
                                            dense
                                            initialPageSize={25}
                                            pageSizeOptions={[10, 25, 50]}
                                            headerBg={styleTokens.headerBg}
                                            headerBorder={styleTokens.headerBorder}
                                            onRowClick={handleSeriesSummaryRowClick}
                                            sx={{
                                              height: '100%',
                                              border: `1px solid ${styleTokens.border}`,
                                              borderRadius: 1,
                                            }}
                                          />
                                        </Box>
                                      </Box>
                                    )}
                                </>
                              ) : visibilityViewMode === 'table' ? (
                                <Box sx={{ height: 528 }}>
                                  {visibilityChartTableRows.length > 0 ? (
                                    <DataGrid
                                      rows={visibilityChartTableRows}
                                      columns={visibilityChartTableColumns}
                                      dense
                                      initialPageSize={100}
                                      pageSizeOptions={[25, 50, 100]}
                                      headerBg={styleTokens.headerBg}
                                      headerBorder={styleTokens.headerBorder}
                                      sx={{ height: '100%' }}
                                    />
                                  ) : (
                                    <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                      <Typography variant="caption" color="text.secondary">No data</Typography>
                                    </Box>
                                  )}
                                </Box>
                              ) : (
                                <DatasetChangeFeedPanel
                                  events={visibilityChangeFeedEvents}
                                  isLoading={chartComputationPending}
                                  formatOptions={changeFeedFormatOptions}
                                  emptyMessage="No visibility changes in the selected range"
                                />
                              )}
                            </Box>
                          </Box>
                        </Box>
                      ) : (
                        <Box 
                          display="flex" 
                          justifyContent="center" 
                          alignItems="center" 
                          height={400}
                          flexDirection="column"
                        >
                          <Typography variant="h6" color="text.secondary" gutterBottom>
                            👁️ Product Visibility Chart
                          </Typography>
                          {showItalySticksUnavailableNotice && (
                            <Box sx={{ mt: 0.5, mb: 1.5, mx: 'auto', width: '100%' }}>
                              <MarketAnnouncementBannerList messages={marketAnnouncementMessages} maxWidth={560} />
                            </Box>
                          )}
                          {!showItalySticksUnavailableNotice && (
                            <Typography variant="body1" color="text.secondary" textAlign="center">
                              Please choose a product to view visibility trends
                            </Typography>
                          )}
                          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 1 }}>
                            Select a brand and product(s) from the filters above to see position tracking
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
            {additionalFiltersLayout === 'side' && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  width: { xs: '100%', xl: 200 },
                  flex: { xs: '1 1 100%', xl: '0 0 200px' },
                  maxWidth: { xs: '100%', xl: 200 },
                  minWidth: 0,
                  boxSizing: 'border-box',
                  overflowX: 'hidden',
                }}
              >
                <MockFiltersPanel
                  styleTokens={styleTokens}
                  isStickRoute={isStickRoute}
                  packSizeOptions={packSizeOptions}
                  selectedPackSizes={selectedPackSizes}
                  onPackSizeChange={setSelectedPackSizes}
                  priceDisplayMode={priceDisplayMode}
                  onPriceDisplayModeChange={setPriceDisplayMode}
                  deviceColourOptions={deviceColourOptions}
                  stickFlavourOptions={stickFlavourOptions}
                  additionalFilters={additionalFilters}
                  onAdditionalFiltersChange={setAdditionalFilters}
                  customizableFilter={customizableFilter}
                  onCustomizableFilterChange={setCustomizableFilter}
                  forNewUsersOnlyFilter={forNewUsersOnlyFilter}
                  onForNewUsersOnlyFilterChange={setForNewUsersOnlyFilter}
                  showAvailabilityControl
                  showProductAttributesFilter
                  showPackSizeControl={isStickRoute}
                  showUnitPriceControl={isStickRoute}
                  availabilityValue={topFilters.availability}
                  onAvailabilityChange={(next) => handleTopFilterChange('availability', next)}
                  groupBy={groupBy}
                  onGroupByChange={setGroupBy}
                  scopeMode={scopeMode}
                  layoutMode="side"
                  onLayoutToggle={() =>
                    setAdditionalFiltersLayout((prev) => (prev === 'top' ? 'side' : 'top'))
                  }
                />
              </Box>
            )}
          </Box>

          {/* Data Grid - hidden for now */}
          {!HIDE_CHART_TABLE && (
          <div>
            {gridData ? (
                             <DataGrid
                 rows={gridData.rows}
                 columns={columnsWithClickableUrls.map((column) => ({
                   ...column,
                   headerAlign: column.headerAlign || 'center',
                 }))}
                 dense
                 initialPageSize={100}
                 pageSizeOptions={[25, 50, 100]}
                 headerBg={styleTokens.headerBg}
                 headerBorder={styleTokens.headerBorder}
                 sx={{
                   height: '350px',
                   overflowX: 'auto',
                 }}
               />
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                <Typography variant="body1" color="text.secondary">
                  No data available with current filters
                </Typography>
              </Box>
            )}
          </div>
          )}
          {false && <><Grid container spacing={2}>
              <Grid item xs={12} md={7}>
                <Box sx={{
                  display: 'flex',
                  gap: '1rem',
                  padding: '12px 16px',
                  backgroundColor: styleTokens.panel,
                  borderRadius: '8px',
                  alignItems: 'center'
                }}>
                  <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                    <Box sx={{ flex: 1, border: `1px solid ${styleTokens.border}`, borderRadius: 2, p: 1.5, backgroundColor: styleTokens.panelAlt }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', letterSpacing: '0.04em' }}>
                        Scope
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                        {!isMarketScope && (
                          <FormControl fullWidth size="small">
                            <InputLabel id="country-multiple-select-label">
                              Countries
                            </InputLabel>
                            <Select
                              labelId="country-multiple-select-label"
                              id="country-multiple-select"
                              value={selectedCountries}
                              label="Countries"
                              multiple
                              onChange={(e) => setSelectedCountries(sanitizeSelectValues(e.target.value))}
                              input={<OutlinedInput label="Countries" />}
                              renderValue={(selected) => selected.map(formatLabel).join(', ')}
                              MenuProps={{
                                PaperProps: {
                                  style: {
                                    maxHeight: '400px',
                                    width: '300px',
                                  },
                                },
                                autoFocus: false,
                              }}
                              onClose={() => setCountrySearchTerm('')}
                            >
                              <ListSubheader>
                                <TextField
                                  size="small"
                                  autoFocus
                                  placeholder="Search countries..."
                                  fullWidth
                                  value={countrySearchTerm}
                                  onChange={(e) => setCountrySearchTerm(e.target.value)}
                                  onKeyDown={(e) => e.stopPropagation()}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </ListSubheader>
                              <Box sx={{ px: 1, py: 0.5 }}>
                                <Button
                                  variant="text"
                                  size="small"
                                  fullWidth
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (selectedCountries.length === countryOptions.length) {
                                      setSelectedCountries([]);
                                    } else {
                                      setSelectedCountries(countryOptions);
                                    }
                                  }}
                                >
                                  Select all
                                </Button>
                              </Box>
                              {countryOptions
                                .filter((country) =>
                                  country.toLowerCase().includes(countrySearchTerm.toLowerCase())
                                )
                                .map((country) => (
                                  <MenuItem
                                    key={country}
                                    value={country}
                                    dense
                                    sx={{
                                      py: 0.25,
                                      minHeight: 'auto',
                                      '& .MuiListItemText-primary': {
                                        fontSize: '0.875rem',
                                      },
                                      '& .MuiCheckbox-root': {
                                        py: 0.5,
                                      },
                                    }}
                                  >
                                    <Checkbox checked={selectedCountries.includes(country)} size="small" sx={{ py: 0 }} />
                                    <ListItemText primary={formatLabel(country)} sx={{ my: 0 }} />
                                  </MenuItem>
                                ))}
                            </Select>
                          </FormControl>
                        )}

                        <FormControl fullWidth size="small">
                          <InputLabel id="retailer-multiple-select-label">
                            Retailers
                          </InputLabel>
                          <Select
                            labelId="retailer-multiple-select-label"
                            id="retailer-multiple-select"
                            value={selectedRetailers}
                            label="Retailers"
                            multiple
                            onChange={(e) => setSelectedRetailers(sanitizeSelectValues(e.target.value))}
                            input={<OutlinedInput label="Retailers" />}
                            renderValue={(selected) => selected.map(formatLabel).join(', ')}
                            MenuProps={{
                              PaperProps: {
                                style: {
                                  maxHeight: '400px',
                                  width: '300px',
                                },
                              },
                              autoFocus: false,
                            }}
                            onClose={() => setRetailerSearchTerm('')}
                          >
                            <ListSubheader>
                              <TextField
                                size="small"
                                autoFocus
                                placeholder="Search retailers..."
                                fullWidth
                                value={retailerSearchTerm}
                                onChange={(e) => setRetailerSearchTerm(e.target.value)}
                                onKeyDown={(e) => e.stopPropagation()}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                  ),
                                }}
                              />
                            </ListSubheader>
                            <Box sx={{ px: 1, py: 0.5 }}>
                              <Button
                                variant="text"
                                size="small"
                                fullWidth
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (selectedRetailers.length === retailerOptions.length) {
                                    setSelectedRetailers([]);
                                  } else {
                                    setSelectedRetailers(retailerOptions);
                                  }
                                }}
                              >
                                Select all
                              </Button>
                            </Box>
                            {retailerOptions
                              .filter((retailer) =>
                                retailer.toLowerCase().includes(retailerSearchTerm.toLowerCase())
                              )
                              .map((retailer) => (
                                <MenuItem
                                  key={retailer}
                                  value={retailer}
                                  dense
                                  sx={{
                                    py: 0.25,
                                    minHeight: 'auto',
                                    '& .MuiListItemText-primary': {
                                      fontSize: '0.875rem',
                                    },
                                    '& .MuiCheckbox-root': {
                                      py: 0.5,
                                    },
                                  }}
                                >
                                  <Checkbox checked={selectedRetailers.includes(retailer)} size="small" sx={{ py: 0 }} />
                                  <ListItemText primary={formatLabel(retailer)} sx={{ my: 0 }} />
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                      </Box>
                    </Box>

                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="brand-multiple-select-label">
                          Brand
                        </InputLabel>
                        <Select
                          labelId="brand-multiple-select-label"
                          id="brand-multiple-select"
                          value={selectedBrands}
                          label="Brand"
                          multiple
                          onChange={(e) => {
                            setSelectedBrands(sanitizeSelectValues(e.target.value));
                            setSelectedProducts([]);
                          }}
                          input={<OutlinedInput label="Brand" />}
                          renderValue={(selected) => selected.join(', ')}
                          MenuProps={{
                            PaperProps: {
                              style: {
                                maxHeight: '400px',
                                width: '300px',
                              },
                            },
                            autoFocus: false,
                          }}
                          onClose={() => setBrandSearchTerm('')}
                        >
                          <ListSubheader>
                            <TextField
                              size="small"
                              autoFocus
                              placeholder="Search brands..."
                              fullWidth
                              value={brandSearchTerm}
                              onChange={(e) => setBrandSearchTerm(e.target.value)}
                              onKeyDown={(e) => e.stopPropagation()}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </ListSubheader>
                          <Box sx={{ px: 1, py: 0.5 }}>
                            <Button
                              variant="text"
                              size="small"
                              fullWidth
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (selectedBrands.length === brandOptions.length) {
                                  setSelectedBrands([]);
                                } else {
                                  setSelectedBrands(brandOptions);
                                }
                                setSelectedProducts([]);
                              }}
                            >
                              Select all
                            </Button>
                          </Box>
                          {brandOptions
                            .filter((brand) =>
                              brand.toLowerCase().includes(brandSearchTerm.toLowerCase())
                            )
                            .map((brand) => (
                              <MenuItem
                                key={brand}
                                value={brand}
                                dense
                                sx={{
                                  py: 0.25,
                                  minHeight: 'auto',
                                  '& .MuiListItemText-primary': {
                                    fontSize: '0.875rem',
                                  },
                                  '& .MuiCheckbox-root': {
                                    py: 0.5,
                                  },
                                }}
                              >
                                <Checkbox checked={selectedBrands.includes(brand)} size="small" sx={{ py: 0 }} />
                                <ListItemText primary={brand} sx={{ my: 0 }} />
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>

                      <ModelProductTreeSelect
                        filteredSkuTitles={filteredProducts}
                        matchingGroups={matchingGroups}
                        groupNameToProducts={groupNameToProducts}
                        modelGroupToProducts={modelGroupToProducts}
                        selectedProducts={selectedProducts}
                        onProductsChange={setSelectedProducts}
                        label="Model / Product(s)"
                        placeholder={selectedProducts.length > 0 ? '' : 'Search models / products...'}
                        compact={false}
                        fullWidth
                        popperMinWidth={420}
                        getSkuCountry={getSkuCountryForTitle}
                        getDisplayTitle={getSkuDisplayTitle}
                        isTranslationActive={isSkuTranslationActive}
                        isTranslatingTitle={isSkuTitleTranslating}
                        onToggleTranslate={toggleSkuTitleTranslation}
                      />
                    </Box>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} md={5}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-end' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                    <FormControlLabel
                      control={(
                        <Checkbox
                          size="small"
                          checked={normalizeToEur}
                          onChange={(e) => setNormalizeToEur(e.target.checked)}
                        />
                      )}
                      label="Normalize prices to EUR"
                    />
                  </Box>

                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%' }}>
                      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'flex-end' }}>
                        <DatePicker
                          label="Start Date"
                          value={(previewDateRange || dateRange)[0]}
                          onChange={(newValue) => {
                            setPreviewDateRange(null);
                            setDateRange([newValue, dateRange[1]]);
                          }}
                          slotProps={{ textField: { size: 'small', fullWidth: true } }}
                        />
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          to
                        </Typography>
                        <DatePicker
                          label="End Date"
                          value={(previewDateRange || dateRange)[1]}
                          onChange={(newValue) => {
                            setPreviewDateRange(null);
                            setDateRange([dateRange[0], newValue]);
                          }}
                          slotProps={{ textField: { size: 'small', fullWidth: true } }}
                        />
                      </Box>
                      <Slider
                        value={dateSliderValue}
                        onChange={handleDateSliderChange}
                        onChangeCommitted={handleDateSliderCommit}
                        min={0}
                        max={Math.max(availableDates.length - 1, 0)}
                        step={1}
                        disableSwap
                        valueLabelDisplay="off"
                        sx={{
                          px: 1,
                          width: 'calc(100% - 10px)',
                          color: styleTokens.accent,
                          '& .MuiSlider-track': {
                            backgroundColor: styleTokens.accent,
                          },
                          '& .MuiSlider-rail': {
                            backgroundColor: styleTokens.accentSoft,
                          },
                          '& .MuiSlider-thumb': {
                            borderColor: styleTokens.accent,
                          },
                        }}
                      />
                    </Box>
                  </LocalizationProvider>
                </Box>
              </Grid>
            </Grid>
          </>}
        </div>
      </div>
      </div>
    </div>
    <Dialog
      open={isSaveViewOpen}
      onClose={() => setIsSaveViewOpen(false)}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>Save view</DialogTitle>
      <DialogContent>
        <TextField
          label="Name of the filter"
          value={newViewName}
          onChange={(e) => setNewViewName(e.target.value)}
          fullWidth
          margin="normal"
          autoFocus
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setIsSaveViewOpen(false)}>Cancel</Button>
        <Button variant="contained" onClick={handleSaveView}>Save</Button>
      </DialogActions>
    </Dialog>

    <Dialog
      open={baseProductAutoQueueDialogOpen}
      onClose={() => setBaseProductAutoQueueDialogOpen(false)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        Auto base product priority
        <Typography variant="caption" component="div" color="text.secondary" sx={{ mt: 0.75, fontWeight: 400 }}>
          {baseProductAutoQueueRouteKey === 'sticks'
            ? 'Sticks: walk the list top to bottom; the first product or SKU available in current filters becomes 100%.'
            : 'Devices: same logic for device baselines.'}{' '}
          Shared with Brand Board.
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 0.5 }}>
          {baseProductAutoQueueDraft.map((entry, idx) => {
            const selectedChoice =
              autoBaseProductQueueChoices.find((c) => c.value === entry) ||
              (entry
                ? {
                    value: entry,
                    label: entry.startsWith(AUTO_BASE_QUEUE_SKU_PREFIX)
                      ? entry.slice(AUTO_BASE_QUEUE_SKU_PREFIX.length)
                      : entry,
                    kind: 'legacy',
                  }
                : null);
            return (
              <Box key={`pp-auto-base-q-${idx}`} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ width: 18, flexShrink: 0, textAlign: 'right' }}>
                  {idx + 1}.
                </Typography>
                <Autocomplete
                  size="small"
                  fullWidth
                  options={autoBaseProductQueueChoices}
                  groupBy={(option) =>
                    option.kind === 'sku' ? 'Specific SKUs' : 'Base products (group / pack)'
                  }
                  getOptionLabel={(option) => option?.label || ''}
                  isOptionEqualToValue={(a, b) => a?.value === b?.value}
                  value={selectedChoice}
                  onChange={(_, option) => {
                    const next = [...baseProductAutoQueueDraft];
                    next[idx] = option?.value || '';
                    setBaseProductAutoQueueDraft(next);
                  }}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Select base product or SKU" />
                  )}
                />
                <IconButton
                  size="small"
                  aria-label="Move up"
                  disabled={idx === 0}
                  onClick={() => {
                    if (idx === 0) return;
                    const next = [...baseProductAutoQueueDraft];
                    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
                    setBaseProductAutoQueueDraft(next);
                  }}
                >
                  <ArrowUpward fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label="Move down"
                  disabled={idx >= baseProductAutoQueueDraft.length - 1}
                  onClick={() => {
                    if (idx >= baseProductAutoQueueDraft.length - 1) return;
                    const next = [...baseProductAutoQueueDraft];
                    [next[idx + 1], next[idx]] = [next[idx], next[idx + 1]];
                    setBaseProductAutoQueueDraft(next);
                  }}
                >
                  <ArrowDownward fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label="Remove"
                  onClick={() => {
                    setBaseProductAutoQueueDraft(baseProductAutoQueueDraft.filter((_, i) => i !== idx));
                  }}
                >
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </Box>
            );
          })}
          <Button
            size="small"
            variant="text"
            sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
            onClick={() => setBaseProductAutoQueueDraft([...baseProductAutoQueueDraft, ''])}
          >
            Add priority row
          </Button>
          <Button
            size="small"
            variant="text"
            sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
            onClick={() =>
              setBaseProductAutoQueueDraft(
                (DEFAULT_BASE_PRODUCT_AUTO_QUEUES[baseProductAutoQueueRouteKey] || []).map((entry) =>
                  migrateLegacyQueueEntry(entry, autoBaseProductQueueChoices, baseProductOptions, false)
                )
              )
            }
          >
            Reset to defaults
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={() => setBaseProductAutoQueueDialogOpen(false)}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={() => {
            const cleaned = baseProductAutoQueueDraft.map((s) => String(s || '').trim()).filter(Boolean);
            const fallback = DEFAULT_BASE_PRODUCT_AUTO_QUEUES[baseProductAutoQueueRouteKey] || [];
            const nextQueues = {
              ...baseProductAutoQueues,
              [baseProductAutoQueueRouteKey]: cleaned.length ? cleaned : [...fallback],
            };
            setBaseProductAutoQueues(nextQueues);
            persistBaseProductAutoQueues(nextQueues);
            setBaseProductAutoQueueDialogOpen(false);
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>

    <ProductDetailPopup
      open={productPopupOpen}
      onClose={() => setProductPopupOpen(false)}
      productName={productPopupData.productName}
      productImage={productPopupData.productImage}
      productData={productPopupData.productData}
      productDataRows={productPopupData.productDataRows}
      groupProductNames={productPopupData.groupProductNames}
      packSizeOverrides={productPopupData.packSizeOverrides}
      productFlags={productPopupData.productFlags}
        pricingAsOfDate={productPopupData.pricingAsOfDate}
        pinnedChartPrices={productPopupData.pinnedChartPrices}
        activePriceTypes={activePriceTypes}
        projectKey={projectKey}
        country={productPopupData.country || productPopupData.productData?.country}
      accentColor={styleTokens.accent}
    />
    </>
    </ThemeProvider>
  );
}

export default JTIPriceAndPromotionsView; 